import React from 'react';
import { O } from 'traks/o';
import { site_base, site_name } from './Site';
import { A } from './routes';

/* This is your translations file. It is automatically updated by `traks update`
 * which parses and rewrites it, potentially adding new translations and
 * updating underscore-prefixed fields, to reflect changes in your main source.
 * To avoid nasty surprises you should follow these guidelines:
 *  - Everything before the export statement is rewritten as-is; you can add
 *    imports, functions and whatever you like here.
 *  - Everything inside the export statement, except for function bodies, is
 *    _rebuilt_. If you change anything here, then at best it will be overwritten
 *    by `traks update` (this includes writing comments), and at worst, it might
 *    cause `traks update` to consider the file corrupt, and thus refuse to
 *    update it.
 *  - One exception is hash-prefixed keys, e.g. "#comment"; you can add these
 *    before underscore-prefixed fields and they'll be preserved. You can use
 *    these for your own needs, e.g. commenting on translations.
 *  - Function bodies can be in block-statement form, or in expression form.
 *    That is, both these examples are valid (and equivalent):
 *       "en": () => {
 *           return <O>Hello world!</O>
 *       },
 *       "en": () => <O>Hello world!</O>,
 *    Also, `traks update` will not meddle with this once a translation is there,
 *    so you can convert between these two styles as you wish. New translations are
 *    added in expression form if they're one-liners.
 *  - It's a good idea to run `traks update` before you commit since it verifies
 *    that the file is valid, and it might change formatting and update
 *    underscore-prefixed fields.
 *
 * See also the import file (src/traks.js) for more info on usage.
 */

const months = {
	en: [
		"January",
		"February",
		"March",
		"April",
		"May",
		"June",
		"July",
		"August",
		"September",
		"October",
		"November",
		"December"
	],
	da: [
		"januar",
		"februar",
		"marts",
		"april",
		"maj",
		"juni",
		"juli",
		"august",
		"september",
		"oktober",
		"november",
		"december"
	],
	sv: [
		"januari",
		"februari",
		"mars",
		"april",
		"maj",
		"juni",
		"juli",
		"augusti",
		"september",
		"oktober",
		"november",
		"december"
	],
	de: [
		"Januar",
		"Februar",
		"März",
		"April",
		"Mai",
		"Juni",
		"Juli",
		"August",
		"September",
		"Oktober",
		"November",
		"Dezember",
	],
}

const months_short = {
	en: [
		"Jan",
		"Feb",
		"Mar",
		"Apr",
		"May",
		"Jun",
		"Jul",
		"Aug",
		"Sep",
		"Oct",
		"Nov",
		"Dec"
	],
	da: [
		"jan",
		"feb",
		"mar",
		"apr",
		"maj",
		"jun",
		"jul",
		"aug",
		"sep",
		"okt",
		"nov",
		"dec"
	],
	sv: [
		"jan",
		"feb",
		"mar",
		"apr",
		"maj",
		"jun",
		"jul",
		"aug",
		"sep",
		"okt",
		"nov",
		"dec"
	],
	de: [
		"Jan",
		"Feb",
		"Mär",
		"Apr",
		"Mai",
		"Jun",
		"Jul",
		"Aug",
		"Sep",
		"Okt",
		"Nov",
		"Dez",
	],
}

const weekday_short = {
	en: [
		"Sun",
		"Mon",
		"Tue",
		"Wed",
		"Thu",
		"Fri",
		"Sat",
	],
	da: [
		"søn",
		"man",
		"tir",
		"ons",
		"tor",
		"fre",
		"lør",
	],
	sv: [
		"sön",
		"mån",
		"tis",
		"ons",
		"tor",
		"fre",
		"lör",
	],
	de: [
		"So.",
		"Mo.",
		"Di.",
		"Mi.",
		"Do.",
		"Fr.",
		"Sa.",
	],
}

const ts2date = (ts) => new Date(ts * 1000);

function npad(value, n) {
	value = ""+value;
	while (value.length < n) value = "0" + value;
	return value;
}

function format_number(floatval, n_decimals, thousand_separator, decimal_separator) {
	var intval = Math.round(floatval * Math.pow(10, n_decimals));

	var is_positive = true;
	if (intval < 0) {
		is_positive = false;
		intval = -intval;
	}

	intval = intval.toString();
	while (intval.length <= n_decimals) intval = "0" + intval;
	const fraction = intval.substring(intval.length - n_decimals);
	var rest = intval.substring(0, intval.length - n_decimals);
	var head = "";
	while (rest.length > 3) {
		head = thousand_separator + rest.substring(rest.length - 3) + head;
		rest = rest.substring(0, rest.length - 3);
	}
	head = rest + head;
	if (n_decimals > 0) head += (decimal_separator + fraction);
	return (is_positive ? "" : "-") + head;
}

function format_number_en(floatval, n_decimals) {
	return format_number(floatval, n_decimals, ",", ".");
}

function format_number_da(floatval, n_decimals) {
	return format_number(floatval, n_decimals, ".", ",");
}

function format_number_sv(floatval, n_decimals) {
	return format_number(floatval, n_decimals, "\u00A0", ","); // (0x00A0 is non-breaking space)
}

const format_number_de = format_number_da; // same as denmark

function format_duration(units, total_seconds) {
	total_seconds = Math.floor(total_seconds);

	const one_minute = 60;
	const one_hour = 60 * one_minute;
	const one_day = 24 * one_hour;

	const days = Math.floor(total_seconds / one_day);
	const hours = Math.floor(total_seconds / one_hour) % 24;
	const minutes = Math.floor(total_seconds / one_minute) % 60;
	const seconds = total_seconds % 60;

	const fmt = (f) => {
		var str = "";
		for (var i = 0; i < f.length; i++) {
			const ch = f[i];
			const value = {d:days, h:hours, m:minutes, s:seconds}[ch];
			if (value !== undefined) {
				str += value + units[ch];
			} else {
				str += ch;
			}
		}
		return str;
	};

	if (total_seconds > one_day) {
		return fmt("dh");
	} else if (total_seconds > one_hour) {
		return fmt("hm");
	} else if (total_seconds > one_minute) {
		return fmt("ms");
	} else {
		return fmt("s");
	}
}

export default {
	"292a33159bbc": {
		"en": () => <O>Earned more than 10 times on a stock of less value than 1 DKK/USD at the time of purchase (UNIMPLEMENTED)</O>,
		"da": () => <O>Tjente mere end 1.000% på en aktie der kostede mindre end 1 DKK/USD på købstidspunktet (UNIMPLEMENTED)</O>,
		"sv": () => <O>Tjänade mer än 1&nbsp;000% på en aktie som kostade mindre än 1 SEK/USD vid köptillfället (UNIMPLEMENTED)</O>,
		"de": () => <O>Du hast mehr als das Zehnfache an einer Aktie verdient, die beim Kauf weniger als 1€ wert war. (NICHT IMPLEMENTIERT)</O>,
	},

	"060af1453f47": {
		"en": () => <O>Won a game without ever selling any stock (the game must have at least 5 players)</O>,
		"da": () => <O>Vandt et spil uden nogensinde at sælge noget (spillet skal have mindst 5 spillere)</O>,
		"sv": () => <O>Vann ett spel utan att sälja någon aktie (spelet måste bestå av minst 5 spelare)</O>,
		"de": () => <O>Du hast ein Spiel gewonnen, ohne jemals eine Aktie zu verkaufen. (Das Spiel braucht mindestens 5 Spieler)</O>,
	},

	"817adac85736": {
		"en": () => <O>Sold stock just before it went up by 50% (UNIMPLEMENTED)</O>,
		"da": () => <O>Solgte et værdipapir lige før det steg mere end 50% (UNIMPLEMENTED)</O>,
		"sv": () => <O>Sålde aktien precis innan den steg med 50% (UNIMPLEMENTED)</O>,
		"de": () => <O>Du hast eine Aktie verkauft, kurz bevor diese um 50% gestiegen ist. (NICHT IMPLEMENTIERT)</O>,
	},

	"bdecd7c731bb": {
		"en": (site_name) => <O>Get even more out of {site_name}! - Upgrade to premium</O>,
		"da": (site_name) => <O>Få endnu mere ud af {site_name} - opgradér til Premium!</O>,
		"sv": (site_name) => <O>Få ännu mer ut av {site_name}! - Uppgradera till premium</O>,
		"de": (site_name) => <O>Holen Sie noch mehr aus {site_name} heraus! - Upgrade auf Premium</O>,
	},

	"d8dc1a2beb42": {
		"en": (site_name) => <O>Are you ready to make your games even more fun, realistic, and educational? When you upgrade to Premium, you'll become the gamemaster and gain access to create games with many more possibilities. You choose how many users you want to invite to your Premium games, and you are free to choose from all the new setup options on {site_name}.</O>,
		"da": (site_name) => <O>Er du klar til at gøre dine spil endnu sjovere, mere realistiske og mere lærerige? Når du opgraderer til Premium, bliver du gamemaster og får adgang til at oprette spil med mange flere muligheder. Du vælger selv, hvor mange brugere du ønsker at invitere til dine Premium-spil og du kan frit vælge mellem alle de nye opsætningsmuligheder på {site_name}.</O>,
		"sv": (site_name) => <O>Är du redo att göra dina spel ännu roligare, mer realistiska och lärorika? När du uppgraderar till Premium blir du spelledare och får möjlighet att skapa spel med många fler möjligheter. Du väljer själv hur många användare du vill bjuda in till dina Premium-spel, och du är fri att välja mellan alla de nya inställningsalternativen på {site_name}.</O>,
		"de": (site_name) => <O>Sind Sie bereit, Ihre Spiele noch unterhaltsamer, realistischer und lehrreicher zu gestalten? Wenn Sie auf Premium upgraden, werden Sie zum Spielleiter und erhalten Zugang zu zahlreichen neuen Möglichkeiten, Spiele zu erstellen. Sie wählen selbst, wie viele Benutzer Sie zu Ihren Premium-Spielen einladen möchten, und Sie haben die Freiheit, aus allen neuen Einrichtungsmöglichkeiten auf {site_name} zu wählen.</O>,
	},

	"606c8e0ee5a8": {
		"en": () => <O>Upgrade to premium</O>,
		"da": () => <O>Opgrader til premium</O>,
		"sv": () => <O>Uppgradera till premium</O>,
		"de": () => <O>Upgrade auf Premium</O>,
	},

	"4c60481eb6bf": {
		"en": () => <O>FAQ</O>,
		"da": () => <O>FAQ</O>,
		"sv": () => <O>FAQ</O>,
		"de": () => <O>FAQ</O>,
	},

	"fbc219b7bc48": {
		"en": () => <O>This is how you do it</O>,
		"da": () => <O>Sådan gør du</O>,
		"sv": () => <O>Så här gör du</O>,
		"de": () => <O>So geht's</O>,
	},

	"fec222a67476": {
		"en": () => <O>Select a time period</O>,
		"da": () => <O>Vælg periode</O>,
		"sv": () => <O>Välj en tidsperiod</O>,
		"de": () => <O>Wähle einen Zeitraum</O>,
	},

	"b8f8ee0cce22": {
		"en": () => <O>Choose the number of participants you would like to invite to your game(s). The invitations are not personal, which means you can transfer a key from one player to another if you wish.</O>,
		"da": () => <O>Vælg det antal deltagere du gerne vil invitere til dit eller dine spil. Invitationerne er ikke personlige, hvilket betyder, at du kan overføre en nøgle fra en spiller til en anden, hvis du ønsker det.</O>,
		"sv": () => <O>Välj hur många deltagare du vill bjuda in till ditt eller dina spel. Inbjudningarna är inte personliga, vilket betyder att du kan överföra en nyckel från en spelare till en annan om du vill.</O>,
		"de": () => <O>Wähle die Anzahl der Teilnehmer, die du zu deinem Spiel oder deinen Spielen einladen möchtest. Die Einladungen sind nicht persönlich, das heißt, du kannst einen Schlüssel von einem Spieler auf einen anderen übertragen, wenn du möchtest.</O>,
	},

	"52cb7128a231": {
		"en": () => <O>You can now create premium games</O>,
		"da": () => <O>Du kan nu oprette premiumspil</O>,
		"sv": () => <O>Du kan nu skapa premiumspel</O>,
		"de": () => <O>Du kannst jetzt Premium-Spiele erstellen</O>,
	},

	"9336537a41bc": {
		"en": () => <O>Once we have received your upgrade request, we will process it as quickly as possible. Normally, your upgrade will be approved within 24 hours. After that, you can start your premium game.</O>,
		"da": () => <O>Når vi har modtaget din anmodning om opgradering, behandler vi den hurtigst muligt. Normalt vil din opgradering godkendes inden for 24 timer. Du kan herefter starte dit premium-spil.</O>,
		"sv": () => <O>När vi har mottagit din uppgraderingsförfrågan behandlar vi den så snabbt som möjligt. Normalt godkänns uppgraderingen inom 24 timmar. Därefter kan du starta ditt premiumspel.</O>,
		"de": () => <O>Sobald wir deine Anfrage zur Hochstufung erhalten haben, bearbeiten wir sie so schnell wie möglich. In der Regel wird dein Upgrade innerhalb von 24 Stunden genehmigt. Danach kannst du dein Premium-Spiel starten.</O>,
	},

	"93df0f9f1a66": {
		"en": (site_name) => <O>Why choose premium on {site_name}</O>,
		"da": (site_name) => <O>Hvorfor vælge premium på {site_name}</O>,
		"sv": (site_name) => <O>Varför välja premium på {site_name}</O>,
		"de": (site_name) => <O>Warum Premium auf {site_name} wählen</O>,
	},

	"2a2f04a87a88": {
		"en": () => <O>Play with as many participants as you want</O>,
		"da": () => <O>Spil med så mange deltagere, du vil</O>,
		"sv": () => <O>Spela med så många deltagare du vill</O>,
		"de": () => <O>Spiele mit so vielen Teilnehmern, wie du möchtest</O>,
	},

	"46d50c86282c": {
		"en": () => <O>With premium, you choose how many people you’d like to be able to invite to your game. Once you've selected your number above, you'll then have the opportunity to create games with more than 30 participants.</O>,
		"da": () => <O>Med premium vælger du, hvor mange du gerne vil kunne invitere til dit spil. Når du har valgt dit antal herover, får du muligheden for at oprette spil med mere end 30 deltagere.</O>,
		"sv": () => <O>Med premium väljer du hur många du vill kunna bjuda in till ditt spel. När du har valt ditt antal ovan får du möjlighet att skapa spel med fler än 30 deltagare.</O>,
		"de": () => <O>Mit Premium wählst du, wie viele Personen du zu deinem Spiel einladen möchtest. Sobald du deine Anzahl oben ausgewählt hast, kannst du Spiele mit mehr als 30 Teilnehmern erstellen.</O>,
	},

	"ea172c2339db": {
		"en": () => <O>Standard</O>,
		"da": () => <O>Standard</O>,
		"sv": () => <O>Standard</O>,
		"de": () => <O>Standard</O>,
	},

	"4dff67a28ec4": {
		"en": () => <O>No administration</O>,
		"da": () => <O>Ingen administration</O>,
		"sv": () => <O>Ingen administration</O>,
		"de": () => <O>Keine Verwaltung</O>,
	},

	"c7e46d861366": {
		"en": () => <O>Stock price development</O>,
		"da": () => <O>Kursudvikling på aktier</O>,
		"sv": () => <O>Aktiekursutveckling</O>,
		"de": () => <O>Aktienkursentwicklung</O>,
	},

	"36d26b185464": {
		"en": () => <O>Winner based on returns</O>,
		"da": () => <O>Vinder på afkast</O>,
		"sv": () => <O>Vinnare baserat på avkastning</O>,
		"de": () => <O>Gewinner basierend auf Rendite</O>,
	},

	"11299b8af4a7": {
		"en": () => <O>1 stock market per game</O>,
		"da": () => <O>1 aktiemarked pr. spil</O>,
		"sv": () => <O>1 aktiemarknad per spel</O>,
		"de": () => <O>1 Aktienmarkt pro Spiel</O>,
	},

	"abddb46b0f06": {
		"en": () => <O>Play with up to 30 participants</O>,
		"da": () => <O>Spil med op til 30 deltagere</O>,
		"sv": () => <O>Spela med upp till 30 deltagare</O>,
		"de": () => <O>Spielen Sie mit bis zu 30 Teilnehmern</O>,
	},

	"d89653e7fb65": {
		"en": () => <O>Possibility for greater diversification</O>,
		"da": () => <O>Mulighed for større diversificering</O>,
		"sv": () => <O>Möjlighet till större diversifiering</O>,
		"de": () => <O>Möglichkeit zur größeren Diversifikation</O>,
	},

	"bd613be00bd7": {
		"en": (site_name) => <O>Expanding to include more markets on {site_name} gives participants the possibility to achieve greater diversification in their portfolio.</O>,
		"da": (site_name) => <O>Udvidelse med flere markeder på {site_name} giver deltagerne mulighed for at opnå større diversificering i deres portefølje.</O>,
		"sv": (site_name) => <O>Att utöka med fler marknader på {site_name} ger deltagarna möjlighet till större diversifiering i sin portfölj.</O>,
		"de": (site_name) => <O>Die Erweiterung um mehr Märkte auf {site_name} gibt den Teilnehmern die Möglichkeit, eine größere Diversifikation in ihrem Portfolio zu erreichen.</O>,
	},

	"c86b8ed8f529": {
		"en": () => <O>Possibility to make risk visible</O>,
		"da": () => <O>Mulighed for at synliggøre risiko</O>,
		"sv": () => <O>Möjlighet att göra risk synlig</O>,
		"de": () => <O>Möglichkeit, das Risiko sichtbar zu machen</O>,
	},

	"e0c2742b4194": {
		"en": () => <O>Each stock has a risk score that participants can use to evaluate it. This score can also be used to rank participants on the leaderboard, where the ability to balance risk and return determines the winner.</O>,
		"da": () => <O>Hver aktie har en risikoscore, som deltagerne kan bruge til at vurdere den. Denne score kan også bruges til at rangere deltagerne på leaderboardet, hvor evnen til at balancere risiko og afkast afgør vinderen.</O>,
		"sv": () => <O>Varje aktie har en riskscore som deltagarna kan använda för att utvärdera den. Denna score kan också användas för att ranka deltagarna på topplistan, där förmågan att balansera risk och avkastning avgör vinnaren.</O>,
		"de": () => <O>Jede Aktie hat eine Risikobewertung, die Teilnehmer zur Bewertung verwenden können. Diese Bewertung kann auch zur Platzierung der Teilnehmer in der Rangliste verwendet werden, wobei die Fähigkeit, Risiko und Rendite auszubalancieren, den Gewinner bestimmt.</O>,
	},

	"8b55b771cb73": {
		"en": () => <O>Information on Companies</O>,
		"da": () => <O>Informationer på virksomheder</O>,
		"sv": () => <O>Information om företag</O>,
		"de": () => <O>Informationen über Unternehmen</O>,
	},

	"930efc0c2c5c": {
		"en": () => <O>Participants get access to information on companies. This includes financial statements, descriptions, risk scores, and industry indicators.</O>,
		"da": () => <O>Deltagerne får adgang til informationer på virksomheder. Disse indeholder regnskaber, beskrivelser, risikoscore og brancheindikation.</O>,
		"sv": () => <O>Deltagarna får tillgång till information om företag. Detta inkluderar finansiella rapporter, beskrivningar, riskscore och branschindikatorer.</O>,
		"de": () => <O>Teilnehmer erhalten Zugang zu Informationen über Unternehmen. Dazu gehören Finanzberichte, Beschreibungen, Risikobewertungen und Branchenindikatoren.</O>,
	},

	"48bf91b4e463": {
		"en": () => <O>With this information and key figures, participants are given the opportunity to make decisions on an informed and insightful basis.</O>,
		"da": () => <O>Med disse informationer og nøgletal får deltagerne mulighed for at træffe beslutninger på et oplyst og indsigtsfuldt grundlag.</O>,
		"sv": () => <O>Med denna information och nyckeltal får deltagarna möjlighet att fatta beslut på en informerad och insiktsfull grund.</O>,
		"de": () => <O>Mit diesen Informationen und Kennzahlen haben Teilnehmer die Möglichkeit, fundierte und durchdachte Entscheidungen zu treffen.</O>,
	},

	"0fcd3d8afd91": {
		"en": () => <O>Manage the Participants in Your Challenge</O>,
		"da": () => <O>Administrer deltagerne på din dyst</O>,
		"sv": () => <O>Hantera deltagarna i din tävling</O>,
		"de": () => <O>Verwalten Sie die Teilnehmer Ihrer Herausforderung</O>,
	},

	"8bd432a0c671": {
		"en": () => <O>An open invitation link offers considerable freedom to attract many people to your challenges. However, it can also mean that a few participants might sneak in who aren’t affiliated with you or who don’t adhere to your established rules.</O>,
		"da": () => <O>Med et åbent invitationslink medfølger en del frihed, for at få mange med på sine dyster. Det kan også gøre at ,der måske sniger sig enkelte deltagere med, som  ikke har ngoet at gøre hos jer eller som ikke overholder de spilleregler I har.</O>,
		"sv": () => <O>En öppen inbjudningslänk ger stor frihet att locka många till dina tävlingar. Det kan dock också innebära att några deltagare smyger sig in som inte hör hemma eller som inte följer dina regler.</O>,
		"de": () => <O>Ein offener Einladungslink bietet viel Freiheit, viele Teilnehmer für Ihre Herausforderungen zu gewinnen. Es kann jedoch auch bedeuten, dass sich einige Teilnehmer einschleichen, die nicht zu Ihnen gehören oder Ihre Regeln nicht einhalten.</O>,
	},

	"93d3ae42d1df": {
		"en": () => <O>With Premium, you have the option to remove unwanted participants from your challenges.</O>,
		"da": () => <O>Med Premium, får du muligheden for at fjerne uønskede deltagere fra dine dyster.</O>,
		"sv": () => <O>Med Premium har du möjlighet att ta bort oönskade deltagare från dina tävlingar.</O>,
		"de": () => <O>Mit Premium haben Sie die Möglichkeit, unerwünschte Teilnehmer aus Ihren Herausforderungen zu entfernen.</O>,
	},

	"a7df1aff0ce7": {
		"en": () => <O>Special offer for large-scale Aktiedyste</O>,
		"da": () => <O>Særligt tilbud til store Aktiedyste</O>,
		"sv": () => <O>Specialerbjudande för stora Aktiedyster</O>,
		"de": () => <O>Sonderangebot für große Aktiedysten</O>,
	},

	"9200f7713db6": {
		"en": () => <O>More than 100 participants?</O>,
		"da": () => <O>Mere end 100 deltagere?</O>,
		"sv": () => <O>Mer än 100 deltagare?</O>,
		"de": () => <O>Mehr als 100 Teilnehmer?</O>,
	},

	"3383ef6528f9": {
		"en": () => <O>Should you engage your entire school in one big game, or would you rather target your local community? There are many great reasons to create large-scale Aktiedyste—and right now we’re offering a special campaign price for the big games.</O>,
		"da": () => <O>Skal hele skolen aktiveres i et stort spil, eller vil du gerne ud og ramme lokalområdet? Der er mange gode grunde til at skabe store Aktiedyste og lige nu kører vi en kampangepris netop de store spil.</O>,
		"sv": () => <O>Ska du engagera hela skolan i ett stort spel, eller vill du hellre rikta in dig på din lokala gemenskap? Det finns många bra skäl att skapa storskaliga Aktiedyster – och just nu erbjuder vi ett specialpris för de stora spelen.</O>,
		"de": () => <O>Möchten Sie Ihre gesamte Schule in ein großes Spiel einbinden oder lieber Ihre lokale Gemeinschaft ansprechen? Es gibt viele gute Gründe, groß angelegte Aktiedyste zu erstellen – und derzeit bieten wir einen Sonderpreis für die großen Spiele an.</O>,
	},

	"dc95c7db32e7": {
		"en": () => <O> to ensure everyone signs up before kickoff.</O>,
		"da": () => <O>  til at sikre at alle for tilmeldt sig inden kickoff.</O>,
		"sv": () => <O>för att säkerställa att alla registrerar sig innan spelet börjar.</O>,
		"de": () => <O>um sicherzustellen, dass sich alle vor dem Start anmelden.</O>,
	},

	"bcc9a74f571f": {
		"en": () => <O>Click below and let’s find the perfect solution for you together :)</O>,
		"da": () => <O>Klik herunder og lad os sammen finde den perfekte løsning til jer :)</O>,
		"sv": () => <O>Klicka nedan så hittar vi tillsammans den perfekta lösningen för dig :)</O>,
		"de": () => <O>Klicken Sie unten, und wir finden gemeinsam die perfekte Lösung für Sie :)</O>,
	},

	"2d85c98987be": {
		"en": () => <O>Contact us here</O>,
		"da": () => <O>Kontakt os her</O>,
		"sv": () => <O>Kontakta oss här</O>,
		"de": () => <O>Kontaktieren Sie uns hier</O>,
	},

	"ad6d8c28305d": {
		"en": () => <O> 6 kr. per participant per month, regardless of the game’s length. Plus, you get 14 days free</O>,
		"da": () => <O> 6 kr. pr. deltager pr. måned uanset længde. +14 dages gratis</O>,
		"sv": () => <O>6 kr per deltagare och månad, oavsett spelets längd. Plus, du får 14 dagar gratis</O>,
		"de": () => <O>6 kr pro Teilnehmer und Monat, unabhängig von der Spieldauer. Zusätzlich erhalten Sie 14 Tage gratis</O>,
	},

	"bdc21e8b78cb": {
		"en": () => <O>More than 100 participants =</O>,
		"da": () => <O>Mere end 100 deltagere =</O>,
		"sv": () => <O>Mer än 100 deltagare =</O>,
		"de": () => <O>Mehr als 100 Teilnehmer =</O>,
	},

	"ba7deaa6cfe6": {
		"en": () => <O>Select how long you would like to have access to the premium benefits below.</O>,
		"da": () => <O>Vælg hvor længe du gerne vil have adgang til premiumfordelene herunder. </O>,
		"sv": () => <O>Välj hur länge du vill ha tillgång till premiumförmånerna nedan.</O>,
		"de": () => <O>Wähle, wie lange du Zugang zu den untenstehenden Premium-Vorteilen haben möchtest.</O>,
	},

	"5ac09714ed61": {
		"en": () => <O>How many do you want to invite</O>,
		"da": () => <O>Hvor mange vil du invitere</O>,
		"sv": () => <O>Hur många vill du bjuda in</O>,
		"de": () => <O>Wie viele möchten Sie einladen</O>,
	},

	"11ae88b2189c": {
		"en": () => <O>All Indexes</O>,
		"da": () => <O>Alle Indekse</O>,
		"sv": () => <O>Alla Indekse</O>,
		"de": () => <O>Alle Index</O>,
	},

	"aaf1d9a6b79f": {
		"en": () => <O>Indexes</O>,
		"da": () => <O>Indekse</O>,
		"sv": () => <O>Indekse</O>,
		"de": () => <O>Index</O>,
	},

	"559c1e8da2b0": {
		"en": () => <O>EAN *</O>,
		"da": () => <O>EAN *</O>,
		"sv": () => <O>EAN *</O>,
		"de": () => <O>EAN *</O>,
	},

	"5ce1cf836ea8": {
		"en": () => <O>CVR *</O>,
		"da": () => <O>CVR *</O>,
		"sv": () => <O>CVR *</O>,
		"de": () => <O>CVR *</O>,
	},

	"42d9c5f3f55b": {
		"en": () => <O>All the features of the free version, plus the following:</O>,
		"da": () => <O>Alle muligheder fra gratis version plus nedenstående</O>,
		"sv": () => <O>Alla funktioner från gratisversionen, plus följande:</O>,
		"de": () => <O>Alle Funktionen der kostenlosen Version, plus Folgendes:</O>,
	},

	"1d105cd78c8b": {
		"en": () => <O>A financial index produces a numeric score based on inputs such as a variety of asset prices. It can be used to track the performance of a group of assets in a standardized way. Indexes typically measure the performance of a basket of securities intended to replicate a certain area of the market.</O>,
		"da": () => <O>Et finansielt indeks producerer en numerisk score baseret på input såsom en række aktivpriser. Det kan bruges til at spore ydeevnen for en gruppe af aktiver på en standardiseret måde. Indekser måler typisk præstationen af ​​en kurv af værdipapirer, der er beregnet til at replikere et bestemt område af markedet.</O>,
		"sv": () => <O>Ett finansiellt index ger en numerisk poäng baserat på indata som en mängd olika tillgångspriser. Den kan användas för att spåra prestanda för en grupp av tillgångar på ett standardiserat sätt. Index mäter vanligtvis prestanda för en korg av värdepapper som är avsedd att replikera ett visst område av marknaden.</O>,
		"de": () => <O>Ein Finanzindex erzeugt einen numerischen Wert auf der Grundlage von Eingaben wie einer Reihe von Vermögenspreisen. Er kann verwendet werden, um die Performance einer Gruppe von Vermögenswerten auf standardisierte Weise zu verfolgen. Indizes messen normalerweise die Performance eines Wertpapierkorbs, der einen bestimmten Bereich des Marktes nachbilden soll.</O>,
	},

	"b35793238dee": {
		"en": () => <O>(Investment fund)</O>,
		"da": () => <O>(Investeringsfond)</O>,
		"sv": () => <O>(Investeringsfond)</O>,
		"de": () => <O>(Investmentfonds)</O>,
	},

	"fe2acba08a14": {
		"en": () => <O>Explore Indexes</O>,
		"da": () => <O>Udforsk Indekse</O>,
		"sv": () => <O>Utforska Indekse</O>,
		"de": () => <O>Erkunden Index</O>,
	},

	"d1bc0fc82095": {
		"en": () => <O>Sugar</O>,
		"da": () => <O>Sukker</O>,
		"sv": () => <O>Socker</O>,
		"de": () => <O>Zucker</O>,
	},

	"0ad881ebabe2": {
		"en": () => <O>Copper</O>,
		"da": () => <O>Kobber</O>,
		"sv": () => <O>Koppar</O>,
		"de": () => <O>Kupfer</O>,
	},

	"c4a4edcb50dc": {
		"en": () => <O>Gold</O>,
		"da": () => <O>Guld</O>,
		"sv": () => <O>Guld</O>,
		"de": () => <O>Gold</O>,
	},

	"93a1e2464446": {
		"en": () => <O>Aluminium</O>,
		"da": () => <O>Aluminium</O>,
		"sv": () => <O>Aluminium</O>,
		"de": () => <O>Aluminium</O>,
	},

	"c81de43f0d06": {
		"en": () => <O>Lithium</O>,
		"da": () => <O>Lithium</O>,
		"sv": () => <O>Lithium</O>,
		"de": () => <O>Lithium</O>,
	},

	"1823088bec7e": {
		"en": () => <O>Palladium</O>,
		"da": () => <O>Palladium</O>,
		"sv": () => <O>Palladium</O>,
		"de": () => <O>Palladium</O>,
	},

	"b3a8495aa2c7": {
		"en": () => <O>Platinum</O>,
		"da": () => <O>Platin</O>,
		"sv": () => <O>Platina</O>,
		"de": () => <O>Platin</O>,
	},

	"498420fcdf2c": {
		"en": () => <O>Silver</O>,
		"da": () => <O>Sølv</O>,
		"sv": () => <O>Silver</O>,
		"de": () => <O>Silber</O>,
	},

	"2df60de8393c": {
		"en": () => <O>Steel</O>,
		"da": () => <O>Stål</O>,
		"sv": () => <O>Stål</O>,
		"de": () => <O>Stahl</O>,
	},

	"b82aea60bd3d": {
		"en": () => <O>Natural Gas</O>,
		"da": () => <O>Naturgas</O>,
		"sv": () => <O>Naturgas</O>,
		"de": () => <O>Erdgas</O>,
	},

	"7c742fd9cec0": {
		"en": () => <O>Uranium</O>,
		"da": () => <O>Uran</O>,
		"sv": () => <O>Uran</O>,
		"de": () => <O>Uran</O>,
	},

	"200651d632f6": {
		"en": () => <O>Oil</O>,
		"da": () => <O>Olie</O>,
		"sv": () => <O>Olja</O>,
		"de": () => <O>Öl</O>,
	},

	"ace373fa05d6": {
		"en": () => <O>Wheat</O>,
		"da": () => <O>Hvede</O>,
		"sv": () => <O>Vete</O>,
		"de": () => <O>Weizen</O>,
	},

	"c709692bcd83": {
		"en": () => <O>Nickel</O>,
		"da": () => <O>Nikkel</O>,
		"sv": () => <O>Nickel</O>,
		"de": () => <O>Nickel</O>,
	},

	"e01692419943": {
		"en": () => <O>Timber</O>,
		"da": () => <O>Træ</O>,
		"sv": () => <O>Trä</O>,
		"de": () => <O>Holz</O>,
	},

	"3ecf6dc40540": {
		"en": () => <O>Leaderboard</O>,
		"da": () => <O>Leaderboard</O>,
		"sv": () => <O>Leaderboard</O>,
		"de": () => <O>Rangliste</O>,
	},

	"64e78e6d1555": {
		"en": () => <O>Selected austrian stocks</O>,
		"da": () => <O>Udvalgte østrigske aktier</O>,
		"sv": () => <O>Utvalda österrikiska aktier</O>,
		"de": () => <O>Ausgewählte österreichische Aktien</O>,
	},

	"cbdffb856489": {
		"en": () => <O>Random austrian stocks</O>,
		"da": () => <O>Tilfældige østrigske aktier</O>,
		"sv": () => <O>Slumpmässiga österrikiska aktier</O>,
		"de": () => <O>Zufällige österreichische Aktien</O>,
	},

	"e2b3c1d4f211": {
		"en": () => <O>Search Austrian assets</O>,
		"da": () => <O>Søg østrigske værdipapirer</O>,
		"sv": () => <O>Sök österrikiska värdepapper</O>,
		"de": () => <O>Nach österreichischen Aktien suchen</O>,
	},

	"c3fd690881ed": {
		"en": () => <O>Austria</O>,
		"da": () => <O>Østrig</O>,
		"sv": () => <O>Österrike</O>,
		"de": () => <O>Österreich</O>,
	},

	"c9df6b75d2ef": {
		"en": () => <O>Sorry, you need to be 18 years old to play this game.</O>,
		"da": () => <O>Beklager. Man skal være 18 år for at spille dette spil.</O>,
		"sv": () => <O>Förlåt, du måste vara 18 år för att spela det här spelet.</O>,
		"de": () => <O>Entschuldige, du musst 18 Jahre alt sein, um dieses Spiel zu spielen.</O>,
	},

	"387d13d59f38": {
		"en": () => <O>Type:</O>,
		"da": () => <O>Type:</O>,
		"sv": () => <O>Typ:</O>,
		"de": () => <O>Typ:</O>,
	},

	"9348f22f4e2e": {
		"en": () => <O>Teacher</O>,
		"da": () => <O>Lærer</O>,
		"sv": () => <O>Lärare</O>,
		"de": () => <O>Lehrer</O>,
	},

	"541dcfc2a1f0": {
		"en": () => <O>Company</O>,
		"da": () => <O>Virksomhed</O>,
		"sv": () => <O>Företag</O>,
		"de": () => <O>Unternehmen</O>,
	},

	"97bdfd0553b3": {
		"en": () => <O>Private</O>,
		"da": () => <O>Privat</O>,
		"sv": () => <O>Privat</O>,
		"de": () => <O>Privat</O>,
	},

	"673744fb2bdb": {
		"_deleted": true, // FIXME translation has no references; delete this entire section if you no longer need it
		"en": (InputHeader) => {
			return (
				<O><InputHeader
					txt={[["email", "Email"]]}
					err={[
						["err_email_missing", "You must enter an email"],
						["err_invalid_email", "The email is invalid"],
					]}
				/></O>
			);
		},
		"da": (InputHeader) => {
			return (
				<O><InputHeader
					txt={[["email", "Email"]]}
					err={[
						["err_email_missing", "Du skal indtaste en email"],
						["err_invalid_email", "Emailen er ugyldig"],
					]}
				/></O>
			);
		},
		"sv": (InputHeader) => {
			return (
				<O><InputHeader
					txt={[["email", "E-post"]]}
					err={[
						["err_email_missing", "Du måste ange en e-postadress"],
						["err_invalid_email", "E-postadressen är ogiltig"],
					]}
				/></O>
			);
		},
		"de": (InputHeader) => {
			return (
				<O><InputHeader
					txt={[["email", "E-Mail"]]}
					err={[
						["err_email_missing", "Sie müssen eine E-Mail-Adresse eingeben"],
						["err_invalid_email", "Die E-Mail-Adresse ist ungültig"],
					]}
				/></O>
			);
		},
	},

	"bfeb64b18a7e": {
		"_deleted": true, // FIXME translation has no references; delete this entire section if you no longer need it
		"en": (change, email_value, input_classname, state_err_email_missing, state_err_invalid_email, validate) => {
			return (
				<O><input
					placeholder="Email"
					className={input_classname("mb-4", state_err_email_missing || state_err_invalid_email)}
					type="email"
					name="email"
					value={email_value}
					onChange={change}
				                                            onBlur={validate}
				/></O>
			);
		},
		"da": (change, email_value, input_classname, state_err_email_missing, state_err_invalid_email, validate) => {
			return (
				<O><input
					placeholder="Email"
					className={input_classname("mb-4", state_err_email_missing || state_err_invalid_email)}
					type="email"
					name="email"
					value={email_value}
					onChange={change}
				                                            onBlur={validate}
				/></O>
			);
		},
		"sv": (change, email_value, input_classname, state_err_email_missing, state_err_invalid_email, validate) => {
			return (
				<O><input
					placeholder="E-post"
					className={input_classname("mb-4", state_err_email_missing || state_err_invalid_email)}
					type="email"
					name="email"
					value={email_value}
					onChange={change}
				                                            onBlur={validate}
				/></O>
			);
		},
		"de": (change, email_value, input_classname, state_err_email_missing, state_err_invalid_email, validate) => {
			return (
				<O><input
					placeholder="E-Mail"
					className={input_classname("mb-4", state_err_email_missing || state_err_invalid_email)}
					type="email"
					name="email"
					value={email_value}
					onChange={change}
				                                            onBlur={validate}
				/></O>
			);
		},
	},

	"18f6474db6b7": {
		"_deleted": true, // FIXME translation has no references; delete this entire section if you no longer need it
		"en": (InputHeader) => <O><InputHeader txt={[["phone", "Phone number"]]} /></O>,
		"da": (InputHeader) => <O><InputHeader txt={[["phone", "Telefonnummer"]]} /></O>,
		"sv": (InputHeader) => <O><InputHeader txt={[["phone", "Telefonnummer"]]} /></O>,
		"de": (InputHeader) => <O><InputHeader txt={[["phone", "Telefonnummer"]]} /></O>,
	},

	"b42458a73431": {
		"_deleted": true, // FIXME translation has no references; delete this entire section if you no longer need it
		"en": (change, input_classname, phone_value, validate) => {
			return (
				<O><input
					placeholder="Phone (optional)"
					className={input_classname("mb-4")}
					type="tel"
					name="phone"
					value={phone_value}
					onChange={change}
				                                            onBlur={validate}
				/></O>
			);
		},
		"da": (change, input_classname, phone_value, validate) => {
			return (
				<O><input
					placeholder="Telefon (valgfri)"
					className={input_classname("mb-4")}
					type="tel"
					name="phone"
					value={phone_value}
					onChange={change}
				                                            onBlur={validate}
				/></O>
			);
		},
		"sv": (change, input_classname, phone_value, validate) => {
			return (
				<O><input
					placeholder="Telefon (valfritt)"
					className={input_classname("mb-4")}
					type="tel"
					name="phone"
					value={phone_value}
					onChange={change}
				                                            onBlur={validate}
				/></O>
			);
		},
		"de": (change, input_classname, phone_value, validate) => {
			return (
				<O><input
					placeholder="Phone (optional)"
					className={input_classname("mb-4")}
					type="tel"
					name="phone"
					value={phone_value}
					onChange={change}
				                                            onBlur={validate}
				/></O>
			);
		},
	},

	"c03df97ec8a4": {
		"en": () => <O>Order</O>,
		"da": () => <O>Bestil</O>,
		"sv": () => <O>Beställ</O>,
		"de": () => <O>Befehl</O>,
	},

	"b39153ec0445": {
		"en": () => <O>Payment</O>,
		"da": () => <O>Betaling</O>,
		"sv": () => <O>Betalning</O>,
		"de": () => <O>Zahlung</O>,
	},

	"da690f01c11d": {
		"en": () => <O>No game selected</O>,
		"da": () => <O>Intet spil valgt</O>,
		"sv": () => <O>Inget spel valt</O>,
		"de": () => <O>Kein Spiel ausgewählt</O>,
	},

	"acd0f5a4b200": {
		"en": () => <O>Login to see your games</O>,
		"da": () => <O>Log ind for at se dine spil</O>,
		"sv": () => <O>Logga in för att se dina spel</O>,
		"de": () => <O>Melde dich an, um deine Spiele zu sehen</O>,
	},

	"ba48e164eb16": {
		"en": () => <O>There was an error processing your payment. Please try again.</O>,
		"da": () => <O>Der opstod en fejl ved behandling af din betaling. Prøv venligst igen.</O>,
		"sv": () => <O>Det uppstod ett fel vid bearbetningen av din betalning. Försök igen.</O>,
		"de": () => <O>Bei der Verarbeitung Ihrer Zahlung ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.</O>,
	},

	"2f0e773d81cb": {
		"en": () => <O>Retry</O>,
		"da": () => <O>Prøv igen</O>,
		"sv": () => <O>Försök igen</O>,
		"de": () => <O>Erneut versuchen</O>,
	},

	"318a143c401e": {
		"en": () => <O>Order total: </O>,
		"da": () => <O>Ordretotal: </O>,
		"sv": () => <O>Ordertotal: </O>,
		"de": () => <O>Bestellsumme: </O>,
	},

	"470e44076ecb": {
		"en": () => <O>&nbsp;excl. VAT)</O>,
		"da": () => <O>&nbsp;eks. moms)</O>,
		"sv": () => <O>&nbsp;exkl. moms)</O>,
		"de": () => <O>&nbsp;exkl. MwSt.)</O>,
	},

	"738d2fec7f64": {
		"en": () => <O>Your details</O>,
		"da": () => <O>Dine oplysninger</O>,
		"sv": () => <O>Dina uppgifter</O>,
		"de": () => <O>Ihre Angaben</O>,
	},

	"7d225ed962f7": {
		"en": () => <O>Name *</O>,
		"da": () => <O>Navn *</O>,
		"sv": () => <O>Namn *</O>,
		"de": () => <O>Name *</O>,
	},

	"798e92c18c1b": {
		"en": () => <O>Organization</O>,
		"da": () => <O>Organisation</O>,
		"sv": () => <O>Organisation</O>,
		"de": () => <O>Organisation</O>,
	},

	"c0a2d48b43c9": {
		"en": () => <O>E-mail address *</O>,
		"da": () => <O>E-mail adresse *</O>,
		"sv": () => <O>E-postadress *</O>,
		"de": () => <O>E-Mail-Adresse *</O>,
	},

	"ef46ed53e022": {
		"en": () => <O>Terms and conditions</O>,
		"da": () => <O>Vilkår og betingelser</O>,
		"sv": () => <O>Allmänna villkor</O>,
		"de": () => <O>Allgemeine Geschäftsbedingungen</O>,
	},

	"37dcb0c7e51a": {
		"en": () => <O>and that I am at least 16 years old.</O>,
		"da": () => <O>og at jeg er mindst 16 år gammel.</O>,
		"sv": () => <O>och att jag är minst 16 år gammal.</O>,
		"de": () => <O>und dass ich mindestens 16 Jahre alt bin.</O>,
	},

	"beb55b929c13": {
		"en": () => <O>terms and conditions, </O>,
		"da": () => <O>vilkår og betingelser, </O>,
		"sv": () => <O>allmänna villkor, </O>,
		"de": () => <O>Allgemeine Geschäftsbedingungen,</O>,
	},

	"341300af1f65": {
		"en": () => <O>I confirm that I have read and accept</O>,
		"da": () => <O>Jeg bekræfter, at jeg har læst og accepterer</O>,
		"sv": () => <O>Jag bekräftar att jag har läst och accepterar</O>,
		"de": () => <O>Ich bestätige, dass ich gelesen habe und akzeptiere</O>,
	},

	"5c7ab52cf6f9": {
		"en": () => <O>Payment method</O>,
		"da": () => <O>Betalingsmetode</O>,
		"sv": () => <O>Betalningsmetod</O>,
		"de": () => <O>Zahlungsmethode</O>,
	},

	"24fc1712e11f": {
		"en": () => <O>Order status</O>,
		"da": () => <O>Ordrestatus</O>,
		"sv": () => <O>Orderstatus</O>,
		"de": () => <O>Bestellstatus</O>,
	},

	"0473407a99fc": {
		"en": () => <O>Credit Card</O>,
		"da": () => <O>Kreditkort</O>,
		"sv": () => <O>Kreditkort</O>,
		"de": () => <O>Kreditkarte</O>,
	},

	"652411909d70": {
		"en": () => <O>EAN</O>,
		"da": () => <O>EAN</O>,
		"sv": () => <O>EAN</O>,
		"de": () => <O>EAN</O>,
	},

	"d3c0edfaf8c0": {
		"en": () => <O>CVR</O>,
		"da": () => <O>CVR</O>,
		"sv": () => <O>CVR</O>,
		"de": () => <O>CVR</O>,
	},

	"93d3d75a4e3d": {
		"en": () => <O>Completed</O>,
		"da": () => <O>Fuldført</O>,
		"sv": () => <O>Slutfört</O>,
		"de": () => <O>Abgeschlossen</O>,
	},

	"269f243cd4a7": {
		"en": () => <O>Created</O>,
		"da": () => <O>Oprettet</O>,
		"sv": () => <O>Skapat</O>,
		"de": () => <O>Erstellt</O>,
	},

	"17ac68f235cd": {
		"en": () => <O>has been successfully processed!</O>,
		"da": () => <O>er blevet behandlet!</O>,
		"sv": () => <O>har blivit framgångsrikt behandlad!</O>,
		"de": () => <O>wurde erfolgreich bearbeitet!</O>,
	},

	"b94d10a65448": {
		"en": () => <O>Order details</O>,
		"da": () => <O>Ordredetaljer</O>,
		"sv": () => <O>Orderdetaljer</O>,
		"de": () => <O>Bestelldetails</O>,
	},

	"fadb0d17bd4f": {
		"en": () => <O>Order id: </O>,
		"da": () => <O>Ordre id: </O>,
		"sv": () => <O>Ordernummer: </O>,
		"de": () => <O>Bestellnummer: </O>,
	},

	"b1801ad699c8": {
		"en": () => <O>Payment gateway id: </O>,
		"da": () => <O>Betalingsgateway id: </O>,
		"sv": () => <O>Betalningsgateway id: </O>,
		"de": () => <O>Zahlungs-Gateway ID: </O>,
	},

	"f0827845fb37": {
		"en": () => <O>Order value: </O>,
		"da": () => <O>Ordreværdi: </O>,
		"sv": () => <O>Ordervärde: </O>,
		"de": () => <O>Bestellwert: </O>,
	},

	"304255c788eb": {
		"en": () => <O>Processing refund...</O>,
		"da": () => <O>Behandler refundering...</O>,
		"sv": () => <O>Bearbetar återbetalning...</O>,
		"de": () => <O>Rückerstattung wird verarbeitet...</O>,
	},

	"530464c3a11e": {
		"en": () => <O>Order refunded successfully</O>,
		"da": () => <O>Ordren er refunderet succesfuldt</O>,
		"sv": () => <O>Ordern har återbetalats framgångsrikt</O>,
		"de": () => <O>Bestellung wurde erfolgreich rückerstattet</O>,
	},

	"c153e231760b": {
		"en": () => <O>Refund order</O>,
		"da": () => <O>Refunder ordre</O>,
		"sv": () => <O>Refundera order</O>,
		"de": () => <O>Bestellung rückerstatten</O>,
	},

	"3d8158f93575": {
		"en": (refundError) => <O>{refundError}</O>,
		"da": (refundError) => <O>{refundError}</O>,
		"sv": (refundError) => <O>{refundError}</O>,
		"de": (refundError) => <O>{refundError}</O>,
	},

	"cd700d84922f": {
		"en": () => <O>Order status must be Captured or PartiallyCaptured to refund</O>,
		"da": () => <O>Ordrestatus skal være "Captured" eller "PartiallyCaptured" for at refundere</O>,
		"sv": () => <O>Orderstatus måste vara "Captured" eller "PartiallyCaptured" för att refundera</O>,
		"de": () => <O>Der Bestellstatus muss "Captured" oder "PartiallyCaptured" sein, um eine Rückerstattung durchzuführen</O>,
	},

	"7928e38e8b19": {
		"en": () => <O>Refund the full order value</O>,
		"da": () => <O>Refunder hele ordreværdien</O>,
		"sv": () => <O>Återbetala hela ordervärdet</O>,
		"de": () => <O>Erstatte den gesamten Bestellwert</O>,
	},

	"7bef62ef8f96": {
		"en": () => <O>Order number</O>,
		"da": () => <O>Ordrenummer</O>,
		"sv": () => <O>Ordernummer</O>,
		"de": () => <O>Bestellnummer</O>,
	},

	"8ebf4df754a5": {
		"en": (orderDetails) => <O>{orderDetails.paymentId}</O>,
		"da": (orderDetails) => <O>{orderDetails.paymentId}</O>,
		"sv": (orderDetails) => <O>{orderDetails.paymentId}</O>,
		"de": (orderDetails) => <O>{orderDetails.paymentId}</O>,
	},

	"9e648815cefc": {
		"en": () => <O>Your order with number: </O>,
		"da": () => <O>Din ordre med nummer: </O>,
		"sv": () => <O>Din order med nummer: </O>,
		"de": () => <O>Ihre Bestellung mit der Nummer: </O>,
	},

	"aa93964ba1ee": {
		"en": () => <O>Name is required</O>,
		"da": () => <O>Navn er påkrævet</O>,
		"sv": () => <O>Namn är obligatoriskt</O>,
		"de": () => <O>Name ist erforderlich</O>,
	},

	"c8d0054fd764": {
		"en": () => <O>E-mail address is required</O>,
		"da": () => <O>E-mail adresse er påkrævet</O>,
		"sv": () => <O>E-postadress är obligatorisk</O>,
		"de": () => <O>E-Mail-Adresse ist erforderlich</O>,
	},

	"6db4cf3c1b25": {
		"en": () => <O>Phone Number is required</O>,
		"da": () => <O>Telefonnummer er påkrævet</O>,
		"sv": () => <O>Telefonnummer är obligatoriskt</O>,
		"de": () => <O>Telefonnummer ist erforderlich</O>,
	},

	"ff2ddd4b389e": {
		"en": () => <O>Country is required</O>,
		"da": () => <O>Land er påkrævet</O>,
		"sv": () => <O>Land är obligatoriskt</O>,
		"de": () => <O>Land ist erforderlich</O>,
	},

	"d4d102e38eb5": {
		"en": () => <O>Postal Code is required</O>,
		"da": () => <O>Postnummer er påkrævet</O>,
		"sv": () => <O>Postnummer är obligatoriskt</O>,
		"de": () => <O>Postleitzahl ist erforderlich</O>,
	},

	"39850373aa9a": {
		"en": () => <O>Street Address is required</O>,
		"da": () => <O>Gadeadresse er påkrævet</O>,
		"sv": () => <O>Gatuadress är obligatorisk</O>,
		"de": () => <O>Straßenadresse ist erforderlich</O>,
	},

	"125983054dca": {
		"en": () => <O>City is required</O>,
		"da": () => <O>By er påkrævet</O>,
		"sv": () => <O>Stad är obligatorisk</O>,
		"de": () => <O>Stadt ist erforderlich</O>,
	},

	"4a2d94fe9acd": {
		"en": () => <O>You need to accept the trade conditions</O>,
		"da": () => <O>Du skal acceptere handelsbetingelserne</O>,
		"sv": () => <O>Du måste acceptera handelsvillkoren</O>,
		"de": () => <O>Sie müssen die Handelsbedingungen akzeptieren</O>,
	},

	"84e3db7c4a21": {
		"en": () => <O>EAN is required</O>,
		"da": () => <O>EAN er påkrævet</O>,
		"sv": () => <O>EAN krävs</O>,
		"de": () => <O>EAN erforderlich</O>,
	},

	"a18ffc2ed375": {
		"en": () => <O>CVR is required</O>,
		"da": () => <O>CVR er påkrævet</O>,
		"sv": () => <O>CVR krävs</O>,
		"de": () => <O>CVR erforderlich</O>,
	},

	"8716d30654bb": {
		"en": () => <O>Thank You</O>,
		"da": () => <O>Tak</O>,
		"sv": () => <O>Tack</O>,
		"de": () => <O>Danke</O>,
	},

	"407ff7ebedee": {
		"en": () => <O>Phone Number *</O>,
		"da": () => <O>Telefonnummer *</O>,
		"sv": () => <O>Telefonnummer *</O>,
		"de": () => <O>Telefonnummer *</O>,
	},

	"0e60606625dc": {
		"en": () => <O>Country *</O>,
		"da": () => <O>Land *</O>,
		"sv": () => <O>Land *</O>,
		"de": () => <O>Land *</O>,
	},

	"2ed7b1eb5594": {
		"en": () => <O>Postal code *</O>,
		"da": () => <O>Postnummer *</O>,
		"sv": () => <O>Postnummer *</O>,
		"de": () => <O>Postleitzahl *</O>,
	},

	"cb853acb638f": {
		"en": () => <O>City *</O>,
		"da": () => <O>By *</O>,
		"sv": () => <O>Stad *</O>,
		"de": () => <O>Stadt *</O>,
	},

	"d090be813e10": {
		"en": () => <O>Street Address *</O>,
		"da": () => <O>Adresse *</O>,
		"sv": () => <O>Adress *</O>,
		"de": () => <O>Adresse *</O>,
	},

	"ac75c1899e9b": {
		"en": () => <O>Monthly</O>,
		"da": () => <O>Månedlig</O>,
		"sv": () => <O>Månatlig</O>,
		"de": () => <O>Monatlich</O>,
	},

	"ef9f5f2fe646": {
		"en": () => <O>Annual</O>,
		"da": () => <O>Årlig</O>,
		"sv": () => <O>Årsabonnemang</O>,
		"de": () => <O>Jährlich</O>,
	},

	"479090d4d227": {
		"en": () => <O>Subscription type</O>,
		"da": () => <O>Abonnemenstype</O>,
		"sv": () => <O>Typ av abonnemang</O>,
		"de": () => <O>Abonnementtyp</O>,
	},

	"93ef15e3035e": {
		"en": () => <O>Postcode:</O>,
		"da": () => <O>Postnummer:</O>,
		"sv": () => <O>Postnummer:</O>,
		"de": () => <O>Postleitzahl:</O>,
	},

	"a657ba659985": {
		"en": () => <O>(You must be 18 to play)</O>,
		"da": () => <O>(Du skal være 18 for at spille)</O>,
		"sv": () => <O>(Du måste vara 18 för att spela)</O>,
		"de": () => <O>(Du musst 18 Jahre alt sein, um zu spielen)</O>,
	},

	"25a8654f2811": {
		"en": () => <O>Select your birthday:</O>,
		"da": () => <O>Vælg din fødselsdag:</O>,
		"sv": () => <O>Välg din födelsedag:</O>,
		"de": () => <O>Wähle deinen Geburtstag aus:</O>,
	},

	"209fa7411c3d": {
		"en": () => <O>Why do we need this? According to Danish law our presentation of material wrt. investment has to be differentiated according to peoples age.</O>,
		"da": () => <O>Hvorfor skal vi bruge det? Ifølge dansk lov skal vi differentiere vores præsentation af materiale relaterende til investering, i forhold til folks alder.</O>,
		"sv": () => <O>Varför behöver vi den här informationen? Enligt Dansk lag måste våra presentationer gällande investering vara anpassade efter personens ålder.</O>,
		"de": () => <O>Warum benötigen wir dieses? Laut dänischem Recht müssen wir den gezeigten Inhalt zu Investitionen an das Alter der Spieler anpassen.</O>,
	},

	"b93ab6d61719": {
		"en": () => <O>Table of contents</O>,
		"da": () => <O>Indholdsfortegnelse</O>,
		"sv": () => <O>Innehållsförteckning</O>,
		"de": () => <O>Inhaltsverzeichnis</O>,
	},

	"1022c7c019df": {
		"en": () => <O>Investment ABC</O>,
		"da": () => <O>Investerings-ABC</O>,
		"sv": () => <O>Kapital ABC</O>,
		"de": () => <O>Börsenlexikon</O>,
	},

	"1ae4968a7ac8": {
		"en": () => {
			return (
				<O>
				Here you can dive into the most important stock exchange terms. If you have questions or
				suggestions regarding the terms, please <A href="/contact">contact us</A>. We are happy to support you on your
				way to becoming a stock exchange expert. Do you have questions about the game itself?
				Please <A href="/faq">visit our FAQ</A>.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Her kan du dykke ned i de mest vigtige børsbegreber. Har du spørgsmål vedrørende de
				forskellige udtryk, el. lign. er du velkommen til at <A href="/contact">kontakte os</A>. Vi vil gerne hjælpe
				dig med at blive investeringsekspert. Spørgsmål vedrørende spillet? Besøg venligst <A href="/faq">vores FAQ</A>.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				Här kan du dyka in i de viktigaste börsvillkoren. Om du har frågor eller förslag
				angående villkoren, vänligen <A href="/contact">kontakta oss</A>. Vi hjälper dig gärna på ditt sätt att bli
				börsexpert. Har du frågor om aktiespelet? Besök <A href="/faq">vår FAQ</A>.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Hier erklären wir dir die wichtigsten Börsenbegriffe. Falls du Fragen oder
				Anregungen zu Begriffen hast, <A href="/contact">kontaktiere uns</A>. Wir unterstützen dich gerne auf deinem
				Weg zum Börsenexperten. Du hast Fragen zum Börsenspiel? Besuche <A href="/faq">unsere FAQ</A>.
				</O>
			);
		},
	},

	"89c05c73b4e2": {
		"_context": "guide_a_1",
		"en": () => {
			return (
				<O>
				A stock is an ownership share in a company. By issuing stocks, a company can
				raise capital. With the purchase of a stock, you own part of the company and can
				call yourself a stock- or shareholder. As a shareholder you benefit from
				positive business developments, showing in rising stock prices and dividend
				distributions. Stocks are traded on the stock exchange like the Frankfurt Stock
				Exchange. Browse through thousands of real stocks in <A href="/explore">"Explore"</A> and
				trade them in our investment game.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				En aktie er en ejerandel af et firma. Ved at udbyde aktier kan et firma rejse kapital.
				Ved at købe en aktie ejer du en del af firmaet, og kan kalde dig selv aktionær. Som
				aktionær drager du fordel af positiv forretningsudvikling, der viser sig ved stigende
				aktiekurs og udbetaling af udbytte. Aktier handles på børser som Københavns Fondbørs. Du
				kan bladre igennem tusindvis af rigtige aktier i vores <A href="/explore">"Udforsk"</A>-sektion
				og handle dem i vores aktiespil.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				En aktie är en ägarandel i ett företag. Genom att emittera aktier kan ett företag samla
				in kapital. När du köper en aktie äger du en del av företaget och kan kalla dig
				aktieägare. Som aktieägare drar du nytta av den positiva affärsutvecklingen, vilket
				visar på stigande aktiekurser och utdelningsutdelningar.  Aktier handlas på börser som
				Frankfurter Wertpapierbörse Bläddra igenom tusentals riktiga aktier under <A href="/explore">"Utforska"</A>-sektionen
				och handla med dem i vårt investeringsspel.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Eine Aktie ist ein Anteil an einem Unternehmen, der Aktiengesellschaft. Mit dem Kauf
				einer Aktie wirst du zum Miteigentümer des Unternehmens und darfst dich Aktionär nennen.
				Ein Unternehmen gibt Aktien an der Börse aus, um sich Eigenkapital zu beschaffen. Als
				Miteigentümer profitierst du von einer positiven Unternehmensentwicklung, welche sich
				u.a. in steigenden Aktienkursen und Dividenden-, also Gewinnausschüttungen,
				widerspiegelt. Aktien werden an der Börse gekauft und verkauft, wie z.B. an der
				Frankfurter Börse. Unter <A href="/explore">"Erkunden"</A> kannst du dir tausende reale
				Aktien anschauen und in unserem Börsenspiel handeln.
				</O>
			);
		},
	},

	"1e456f2788fc": {
		"_context": "guide_a_2",
		"en": (site_name) => {
			return (
				<O>
				A portfolio describes the total amount of securities (stocks, bonds, etc.) of an
				investor. A broad portfolio helps to reduce risk, please see diversification. In {site_name}, you
				can create a portfolio to track the development of a specific stock collection like for
				example Biotech stocks.
				</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>
				En portefølje beskriver det totale antal værdipapirer (aktier, obligationer, etc.) for
				en investor. En bred portefølje hjælper med at reducere risiko (se diversificering).
				I {site_name} kan du lave porteføljer til at følge udviklingen af aktier, f.eks kan man
				lave en portefølje dedikeret til Biotech-aktier, rederier, m.m..
				</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>
				En portfölj beskriver den totala mängden värdepapper (aktier, obligationer, osv.) för en
				investerare. En bred portfölj hjälper till att minska risken, se diversifiering.
				I {site_name} kan du skapa en portfölj för att spåra utvecklingen av en specifik
				aktiesamling som till exempel Biotech-aktier.
				</O>
			);
		},
		"de": (site_name) => {
			return (
				<O>
				Ein Portfolio beschreibt den Gesamtbestand an Wertpapieren (Aktien, Anleihen etc.) eines
				Anlegers. Ein breit gestreutes Portfolio dient der Risikominderung, siehe auch
				Diversifikation. Bei {site_name} kannst du ein Portfolio anlegen, um die Entwicklung
				einer bestimmten Sammlung von Aktien zu verfolgen, z.B. Biotech-Aktien.
				</O>
			);
		},
	},

	"39db0658b868": {
		"_context": "guide_a_3",
		"en": (site_name) => {
			return (
				<O>
				A currency is a means of payment used in a specific country or in a political
				union (European Union), such as the American dollar. Currencies can also be used
				as an investment and are traded on the foreign exchange market. The so-called
				exchange rates are influenced by many factors, including major economic events
				such as the disclosure of annual figures by influential companies, political
				decisions and elections or even natural disasters. In {site_name} you can trade
				over 30 <A href="/explore-bank">currencies</A>.
				</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>
				En valuta er et betalingsmiddel brugt i et land eller en politisk union, som f.eks
				den Amerikanske Dollar (USD). Valuta kan også bruges som investeringsobjekt og handles
				på forskellige valutamarkeder. Vekselkurserne påvirkes af mange faktorer, så som store
				økonomiske hændelser, politiske beslutninger, valg eller naturkatastrofer.
				I {site_name} kan du investere i over 30 <A href="/explore-bank">valutaer</A>.
				</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>
				En valuta är ett betalningsmedel som används i ett specifikt land eller i en politisk
				union (Europeiska Unionen), till exempel den amerikanska dollarn. Valutor kan också
				användas som investeringar och handlas med på valutamarknaden. De så kallade
				växelkurserna påverkas av många faktorer, inklusive stora ekonomiska händelser, såsom
				inflytande av årliga resultat från inflytelserika företag, politiska beslut och val
				eller till och med naturkatastrofer. I {site_name} kan du handla över 30 <A href="/explore-bank">valutor</A>.
				</O>
			);
		},
		"de": (site_name) => {
			return (
				<O>
				Eine Währung ist ein Zahlungsmittel, das in einem bestimmten Land oder politischen
				Zusammenschluss (z.B. Europäische Union) verwendet wird, wie z.B. der amerikanische
				Dollar. Währungen können auch als Wertanlage genutzt und auf dem Devisenmarkt (Forex)
				gehandelt werden. Die sogenannten Devisenkurse werden von vielen Faktoren beeinflusst,
				darunter u.a. große Wirtschaftsereignisse wie die Offenlegung von Jahreszahlen
				einflussreicher Firmen, politische Entscheidungen und Wahlen oder auch
				Naturkatastrophen. Bei {site_name} kannst du über 30 <A href="/explore-bank">Währungen</A> handeln.
				</O>
			);
		},
	},

	"38d862e28f66": {
		"_context": "guide_a_4",
		"en": (site_name) => {
			return (
				<O>
				Crypto currencies are independent digital currencies, i.e. virtual money, which,
				unlike classic currencies, are not regulated by central banks. Complicated
				encryption techniques are used to ensure that crypto currencies are not
				manipulated. The low transaction fees are an advantage, as mediators like banks
				are completely eliminated. The most famous crypto currency &mdash; and the one that
				started the whole hype &mdash; is Bitcoin. You can invest in over 80 <A href="/explore-crypto">crypto
				currencies</A> in {site_name}.
				</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>
				Kryptovalutaer er uafhængige digitale valutaer, dvs. virtuelle penge, som ulig klassiske
				valutaer ikke er reguleret af centralbanker. Komplicerede krypteringsteknologier, m.m.,
				bruges til at sikre at man ikke kan manipulere dem. De lave transaktionsgebyrer er en
				fordel, da mellemstationer som banker er elimineret. Den mest berømte kryptovaluta
				&mdash; den der startede det hele er Bitcoin. Du kan investere i over
				80 <A href="/explore-crypto">kryptovalutaer</A> på {site_name}.
				</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>
				Kryptovalutor är oberoende digitala valutor, dvs. virtuella pengar, som till skillnad
				från klassiska valutor inte regleras av centralbanker. Komplicerade krypteringstekniker
				används för att säkerställa att kryptovalutorna inte manipuleras. De låga
				transaktionsavgifterna är en fördel eftersom banker helt elimineras. Den mest berömda
				kryptovaluta &mdash; och den som startade hela hypen &mdash; är Bitcoin. Du kan
				investera i över 80 <A href="/explore-crypto">kryptovalutor</A> i {site_name}.
				</O>
			);
		},
		"de": (site_name) => {
			return (
				<O>
				Kryptowährungen sind unabhängige digitale Währungen, also virtuelles Geld, welche im
				Gegensatz zu klassischen Währungen nicht von Zentralbanken reguliert werden.
				Komplizierte Verschlüsselungstechniken sollen sicherstellen, dass die Währungen nicht
				manipuliert werden. Ein Vorteil sind die niedrigen Transaktionsgebühren, da
				Mittelsmänner wie z.B. Banken komplett entfallen. Die bekannteste Kryptowährung &mdash;
				und die, mit der alles begann &mdash; ist die Bitcoin. Du kannst bei {site_name} in über
				80 <A href="/explore-crypto">Kryptowährungen</A> investieren.
				</O>
			);
		},
	},

	"d9ae25307568": {
		"_context": "guide_a_5",
		"en": (site_name) => {
			return (
				<O>
				<A href="/explore-commodities">Commodities</A> are raw materials from nature, which are consumed directly or processed
				industrially. Examples include silver and oil, which are traded on the different
				commodity markets and in {site_name}.
				</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>
				<A href="/explore-crypto">Råvarer</A> er rå materialer fra naturen som konsumeres direkte eller processeres
				industrielt. Eksempler på råvarer er olie og sølv, som handles på råvaremarkeder og
				i {site_name}.
				</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>
				<A href="/explore-crypto">Råvaror</A> är varor från naturen som konsumeras direkt eller bearbetas industriellt.
				Exempel inkluderar det silver och olja, som handlas på råvarumarknader och i {site_name}.
				</O>
			);
		},
		"de": (site_name) => {
			return (
				<O>
				<A href="/explore-crypto">Rohstoffe</A> sind unbearbeitete Grundstoffe aus der Natur, welche direkt verbraucht oder
				industriell weiterverarbeitet werden. Beispiele sind u.a. Silber und Öl, welche an der
				Börse und bei {site_name} gehandelt werden.
				</O>
			);
		},
	},

	"831cd51799b2": {
		"_context": "guide_a_6",
		"en": () => {
			return (
				<O>
				The stock price alone does not reveal whether a share is cheap or expensive. The P/E
				ratio (price-to-earnings ratio) is more helpful as it puts the stock price in relation
				to the expected profit (stock price / earnings per stock). The lower the P/E ratio, the
				cheaper the stock appears - however a very low figure can also indicate a low evaluation
				and pessimistic expectations for the stock’s future. The P/E ratio average varies from
				industry to industry. Therefore, you should always compare the P/E ratio of a stock with
				that of its competitors to assess whether the stock is over- or undervalued. By the way,
				the average P/E ratio of the DAX of the last 30 years is 19. Please note that the P/E
				ratio is only one of many parameters to evaluate a stock.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Aktiekursen alene fortæller ikke om en aktie er dyr eller billig. P/E ratio
				(price/earnings ratio) fortæller mere af sandheden da det sætter aktiekursen i relation
				til den forventede profit pr. aktie. Jo lavere P/E ratio jo billigere forekommer aktien
				at være. Dog kan en meget lav P/E ratio også indikere pessimistiske forventninger og lav
				valuering ift. aktien fremtid. P/E ratio varierer fra industri til industri, så derfor
				bør man altid sammenligne en akties P/E med konkurrenternes når man vurderer om en aktie
				er over- eller undervurderet. Bemærk venligst at P/E ratio kun er én af mange parametre
				man bruger til at evaluere en aktie.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				Aktiekursen avslöjar enbart om en aktie är billig eller dyr. P/E-förhållandet
				(pris/vinst-förhållande) är mer användbart eftersom det sätter aktiekursen i förhållande
				till det förväntade resultatet (aktiekurs/vinst per aktie). Ju lägre P/E-förhållande,
				desto billigare visas aktien - dock kan en mycket låg siffra också indikera en låg
				utvärdering och pessimistiska förväntningar på aktiens framtid. P/E-genomsnittet
				varierar från bransch till bransch. Därför bör du alltid jämföra P/E-förhållandet för en
				aktie med konkurrenterna för att bedöma om beståndet är över- eller undervärderad.
				Observera att P/E-förhållandet endast är en av många parametrar för att utvärdera en
				aktie.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Der Aktienkurs allein verrät nicht, ob eine Aktie günstig oder teuer ist. Nützlicher ist
				die Kennzahl KGV, das Kurs-Gewinn-Verhältnis, welche den Aktienkurs ins Verhältnis zum
				erwarteten Gewinn setzt (Aktienkurs / Gewinn pro Aktie). Je niedriger das KGV, desto
				preisgünstiger ist die Aktie - allerdings kann eine zu niedrige Zahl auch für eine
				niedrige Bewertung und pessimistische Zukunftserwartungen sprechen. Der KGV-Durchschnitt
				variiert von Branche zu Branche. Daher solltest du das KGV einer Aktie stets mit dem der
				Wettbewerber vergleichen, um abzuschätzen, ob die Aktie über- oder unterbewertet ist.
				Der durchschnittliche DAX-KGV der letzten 30 Jahre liegt übrigens bei 19. Bitte beachte,
				der KGV ist nur eine von vielen Kennzahlen, die zur Aktienbewertung benutzt werden kann.
				</O>
			);
		},
	},

	"6ab0cccad6ff": {
		"_context": "guide_a_7",
		"en": () => {
			return (
				<O>
				To keep the risk of stock market loss as low as possible, you should diversify your
				equity portfolio, i.e. invest in different stocks that, if possible, do not correlate -
				for example stocks from different industries. If the price of one stock falls, the loss
				can be, at least to some degree, compensated by other rising stock prices in your
				portfolio.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				For at holde risikoen for tab så lav som muligt bør man lave stor spredning
				(diversifikation) i sin portefølje, dvs. investere i aktier fra forskellige brancher.
				Hvis man f.eks kun har fokuseret på aktier fra olieindustrien får man tab i hele
				porteføljen hvis olieprisen falder. Hvis man er i mange forskellige brancher kan en
				forhåbentlig stigning i én branche kompensere for faldet i en anden.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				För att hålla risken för förlust på aktiemarknaden så låg som möjligt bör du
				diversifiera din aktieportfölj, d.v.s. investera i olika aktier som, om möjligt, inte
				korrelerar - till exempel aktie från olika industrier. Om priset på en aktie sjunker kan
				förlusten, åtminstone till viss del, kompenseras av andra stigande aktiekurser i din
				portfölj.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Um das Verlustrisiko möglichst gering zu halten, sollte man sein Aktienportfolio
				diversifizieren, d.h. in verschiedene Aktien investieren, welche nach Möglichkeit nicht
				korrelieren - beispielsweise Aktien verschiedener Branchen. Wenn der Kurs einer Aktie
				sinkt, kann dieser Verlust durch smarte Diversifikation mit anderen steigenden
				Aktienkursen ausgeglichen werden.
				</O>
			);
		},
	},

	"af11e1e7a7ce": {
		"_context": "guide_a_8",
		"en": () => {
			return (
				<O>
				A fund is like a pool of a variety of stocks. It is actively or passively (ETF-funds)
				managed for you, the investor. By buying a share of the fund, your risk for stock market
				loss is more diversified compared to buying individual stocks. However, you have no
				control over which stocks are traded in the fund. In addition to a lower risk of loss,
				funds can save administration time and money compared to trading individual stocks.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				En fond er en pulje af aktier. Det forvaltes aktiv eller passivt (ETF) for dig der er
				investor. Ved at købe en del af en fond, er din risiko for tab spredt mere ud, ift. at
				købe enkelte aktier. Du har dog ingen kontrol over hvilke aktier der handles i fonden,
				og i tillæg til en lavere risiko sparer du også tid (og bekymringer) ift. selv at stå
				for alle handler.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				En fond är som en pool av en mängd olika aktier. Det förvaltas aktivt eller passivt
				(ETF-fonder) för dig, investeraren. Genom att köpa en andel av fonden är din risk för
				aktiemarknadsförlust mer diversifierad jämfört med att köpa enskilda aktier. Du har dock
				ingen kontroll över vilka aktier som handlas i fonden. Förutom en lägre risk för förlust
				kan fonder spara administrationstid och pengar jämfört med handel med enskilda aktier.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Ein Fonds ist wie ein Korb, in dem breit gestreut verschiedene Wertpapiere gesammelt
				werden. Er wird aktiv oder passiv (ETF-Fonds) für den Anleger gemanagt, sprich gekauft
				und verkauft. Durch den Kauf eines Fondsanteils, ist das Risiko breiter gestreut als bei
				Einzelaktien. Du hast jedoch keinen Einfluss darauf, welche Aktien im Fonds gehandelt
				werden. Neben einem geringeren Verlustrisiko, sparen Fonds mitunter Verwaltungszeit und
				Geld (je nach Gebühren) ggü. dem Kauf und Verkauf einzelner Aktien.
				</O>
			);
		},
	},

	"967cbf96f047": {
		"_context": "guide_a_9",
		"en": () => {
			return (
				<O>
				The dividend is part of a company's profit that is distributed to stockholders. Example:
				The Lockheed Martin Corporation decided to pay a dividend of $2.20 per stock in the
				second quarter of 2019. If you own 100 Lockheed Martin stocks, you will receive a $220
				dividend. Note that the stock price usually drops immediately after the dividend is
				paid, as investors are often less willing to pay for the stock since they missed the
				dividend payment.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Udbytte er en del af et firmas profit, der distribueres til aktionærerne. Eksempel:
				The Lockheed Martin Corporation udbetalte et udbytte på 2,20$ pr. aktie i andet kvartal
				af 2019. Hvis du ejede 100 Lockheed Martin aktier på det tidspunkt, fik du udbetalt
				220$. Bemærk også at aktiekursen ofte falder efter udbetaling af udbytte &mdash; bl.a.
				fordi folk er mindre villige til at købe aktien hvis de missede udbytteudbetalingen.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				Utdelningen är en del av ett företags vinst som delas ut till aktieägare. Exempel:
				Lockheed Martin Corporation beslutade att betala en utdelning på 2,20$ per aktie under
				andra kvartalet 2019. Om du äger 100 Lockheed Martin-aktier, kommer du att få en
				utdelning på 220$. Observera att aktiekursen vanligtvis sjunker omedelbart efter
				utdelningen, eftersom investerare ofta är mindre villiga att betala för aktien eftersom
				de missade utdelningen.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Die Dividende ist ein Teil des Gewinns eines Unternehmens, der an die Aktionäre
				ausgeschüttet wird. Beispiel: The Lockheed Martin Corporation hat im 2. Quartal 2019
				eine Dividende von 2,20 USD je Aktie beschlossen. Wenn du 100 Lockheed-Martin-Aktien
				besitzt, erhältst du demnach 220 USD als Dividendenzahlung. Beachte, dass der Aktienkurs
				normalerweise direkt nach Auszahlung der Dividende fällt, da Anleger dann oft nur noch
				weniger für die Aktie zu zahlen bereit sind.
				</O>
			);
		},
	},

	"15f707ab0ef8": {
		"_context": "guide_a_10",
		"en": () => {
			return (
				<O>
				A tick is the smallest price unit by which the stock price can change.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Et tick er den mindste prisenhed med hvilken en aktiekurs kan ændre sig.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				Et tick är den minsta prisenhet med vilken aktiekursen kan förändras.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Ein Tick ist die kleinste Preiseinheit, um die sich der Wertpapierkurs verändern kann.
				</O>
			);
		},
	},

	"527228b755f4": {
		"_context": "guide_a_11",
		"en": () => {
			return (
				<O>
				In the event of a stock split, a company divides its existing stocks into several stocks
				in order to reduce the individual stock price. This is often done when a stock has
				become too expensive. The price cut makes the stock more attractive for potential
				investors and therefore easier to trade. Typically, a stock is split in the ratio 1:2 or
				1:3.  Thus, if you own 100 stocks worth $200 each &mdash; and a 1:2 stock split is
				executed &mdash; you will then own 200 shares worth $100 each. The company value
				remains unchanged in the event of a stock split.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Ved et aktiesplit deler et firma dets eksisterende aktiebeholdning i flere dele for at
				reducere aktiekursen. Det gøres ofte når en aktie har nået en høj pris. Den lavere
				aktiekurs efter splittet kan gøre aktien mere attraktiv for potentielle investorer og
				derfor nemmere at handle. Et typisk aktiesplit er 1:2 eller 1:3. Hvis man ejer 100 aktier
				med en aktiekurs på $200, og der sker et aktiesplit på 1:2, har man efterfølgende 200
				aktier der er $100 værd. Firmaets samlede værdi forbliver uændret ved et aktiesplit.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				I händelse av aktieuppdelning delar ett företag sina befintliga aktier i flera aktier
				för att sänka den enskilda aktiekursen. Detta görs ofta när en aktie har blivit för
				dyrt. Prissänkningen gör aktien mer attraktiv för potentiella investerare och därför
				lättare att handla.
				Typiskt delas en aktie i förhållandet 1: 2 eller 1: 3. Således, om du äger 100 aktier
				värda $200 vardera &mdash; och en 1: 2 aktiesplit utförs &mdash; kommer du sedan att äga
				200 aktier värda $100 vardera. Företagets värde förblir oförändrat vid aktiesplit.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Bei einem Aktiensplit teilt ein Unternehmen seine vorhandenen Aktien in mehrere Aktien
				auf, um den Einzelwert der Aktien zu senken. Dies wird oft getan, wenn eine Aktie zu
				teuer geworden ist. Durch die Preissenkung ist sie für potenzielle Anleger attraktiver
				und daher leichter handelbar. Normalerweise wird eine Aktie im Verhältnis 1:2 oder 1:3
				aufgeteilt. Wenn du also 100 Aktien im Wert von jeweils 200 US-Dollar besitzt – und ein
				1:2-Aktiensplit vorgenommen wird – besitzt du danach 200 Aktien im Wert von jeweils 100
				US-Dollar. Der Unternehmens- bzw. Börsenwert bleibt bei einem Aktiensplit unverändert.
				</O>
			);
		},
	},

	"e1fde4bd8552": {
		"_context": "guide_a_12",
		"en": () => {
			return (
				<O>
				A reverse stock split is the opposite of a stock split. Instead of dividing shares into
				more, the total number is reduced by merging shares. This is often done when a stock has
				become too cheap and companies want to optically increase its price. Suppose you own 100
				shares worth $2 each and a 5:1 reverse stock split happens, you will then have 20 stocks
				worth $10 each.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Et omvendt aktiesplit er det modsatte af en aktiesplit. Istedet for at dele de
				individuelle aktier i flere dele, samles flere aktier til én. Dette gøres ofte når en
				aktie har en lav pris og firmaet vil hæve aktiekursen. Hvis du ejer 100 aktier til en
				kurs på $2 stykket, vil du efter et 5:1 omvendt aktiesplit have 20 aktier til en
				aktiekurs på $10. Firmaets samlede værdi forbliver som ved et aktiesplit uændret.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				En omvänd aktiesplit är motsatsen till en aktiesplit. Istället för att dela upp aktier i
				mer, reduceras det totala antalet genom att fusionera aktier. Detta görs ofta när en
				aktie har blivit för billig och företag vill optiskt höja sitt pris. Anta att du äger
				100 aktier värda $2 vardera. Om en 5: 1 omvänd aktiesplit sker, kommer du att ha 20
				aktie värda $10 vardera.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Ein umgekehrter Aktiensplit ist das Gegenteil eines Aktiensplits. Anstatt Aktien zu
				teilen, wird die Gesamtanzahl reduziert, indem Aktien verschmolzen werden. Dies wird oft
				getan, wenn eine Aktie zu billig geworden ist und Firmen den Kurs optisch erhöhen
				wollen. Angenommen, du besitzt 100 Aktien im Wert von je 2 USD. Wenn ein umgekehrter
				Aktiensplit von 5:1 erfolgt, besitzt du dann 20 Aktien im Wert von jeweils 10 USD.
				</O>
			);
		},
	},

	"5a2de398abe6": {
		"_context": "guide_a_13",
		"en": () => {
			return (
				<O>
				Short sales (shorting) are stock transactions where investors want to make profits with
				falling stock prices. Example: Suppose you assume that Apple Inc.'s stock price will
				fall in the future. You ask a broker or, to simplify matters, a friend whether he wants
				to lend you his Apple stocks. Then you sell the stocks with the promise to buy them
				again at a later stage and give them back to your friend. Let's say you sell 100
				borrowed Apple stocks for $200 and get $20,000 for them. A bit later Apple's stock
				drops to $150 as hoped.  You then buy 100 Apple stocks for $15,000 and give them back
				to your friend. That way you made a $5,000 profit - minus borrowing fees and taxes.
				Sounds good? Not so quick. There is a potential risk of loss: if the stock price rises
				instead of the expected decrease, you have to buy the stocks back at a price higher than
				your original selling price and will thus potentially lose a lot of money.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Shortselling er aktietransaktioner hvor investorer investerer i at tjene penge på
				faldende aktiekurser. Lad os sige man tror på at Apple Inc.'s aktiekurs vil falde i
				fremtiden. Så spørger man en børsmægler, eller for at forenkle sagen, en ven om
				vedkommende vil låne dig sine Apple-aktier. Så sælger du straks alle aktierne
				med løftet om at levere dem tilbage senere. Lad os sige du sælger 100 lånte Apple-aktier
				til kurs $200 og dermed tjener $20.000. Noget tid senere er Apple's aktiekurs nede i
				$150, hvorefter du køber 100 Apple-aktier for $15.000 og giver dem tilbage til vennen.
				På den måde er fortjenesten på $5.000, eksl. lånegebyr, skatter, etc. Det lyder godt
				ikke? Klap hesten. Der er en potentiel risiko for et stort tab: hvis aktiekursen stiger
				istedet for at falde, skal du købe dem dyrere tilbage end det du fik for salget. Og der
				er ingen øvre grænse for hvor stort tabet kan blive.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				Blankning är aktietransaktioner där investerare vill tjäna vinster med fallande
				aktiekurser. Exempel: Anta att du antar att Apple Inc.s aktiekurs kommer att falla i
				framtiden. Du frågar en mäklare eller, för att förenkla saken, en vän om han vill låna
				ut sina Apple-aktier. Sedan säljer du aktierna med löfte att köpa dem igen i ett senare
				skede och ger dem tillbaka till din vän. Låt oss säga att du säljer 100 lånade
				Apple-aktier för 200$ och får 20 000 $ för dem. Lite senare sjunker Apples aktie
				till 150$ som du hoppats. Du köper sedan 100 Apple-aktier för 15 000$ och ger dem
				tillbaka till din vän. På så sätt gjorde du en vinst på 5 000$ - minus låneavgifter och
				skatter. Låter bra, eller hur? Det finns en potentiell risk för förlust: om aktiekursen
				stiger istället för den förväntade minskningen måste du köpa tillbaka aktierna till ett
				högre pris än ditt ursprungliga försäljningspris och därmed potentiellt förlora mycket
				pengar.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Leerverkäufe sind Wertpapiergeschäfte, bei denen Investoren Gewinne mit fallenden
				Aktienkursen machen wollen. Beispiel: Angenommen du vermutest, dass der Aktienkurs von
				Apple Inc. in Zukunft fallen wird. Du fragst einen Broker oder zur Vereinfachung einen
				Freund, ob dieser dir seine Apple-Aktien ausleihen möchte. Dann verkaufst du die Aktien
				mit dem Versprechen, sie später erneut zu kaufen und an deinen Freund zurückzugeben.
				Sagen wir, du verkaufst 100 geliehene Apple-Aktien zum Preis von 200 US-Dollar und
				erhältst dafür 20.000 USD. Einige Zeit später ist die Apple-Aktie wie gehofft auf 150 USD
				gefallen. Du kaufst also zu diesem Aktienkurs wieder 100 Apple-Aktien für 15.000 USD und
				gibst sie an deinen Freund zurück. So hast du 5.000 USD Gewinn gemacht - abzüglich der
				Leihgebühren und Steuern. Klingt gut? Nicht so schnell, denn es lauert ein potenzielles
				Verlustrisiko: Wenn der Aktienkurs anders als erwartet steigt statt zu fallen, musst du
				die Aktien zu einem höheren Kurs zurückkaufen als zu deinem ursprünglichen Verkaufspreis
				und verlierst potenziell viel Geld.
				</O>
			);
		},
	},

	"04b52a55ca92": {
		"_context": "guide_a_14",
		"en": () => {
			return (
				<O>
				An issuer is an institution such as a company, investment trusts or governments that
				issues securities/stocks to raise capital. The person who buys the security is called a
				shareholder or investor.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				En udsteder er en institution så som et firma, investeringsfond, bank eller regering som
				udsteder værdipapirer (så som aktier) for at rejse kapital. Den der køber disse
				værdipapirer kaldes en aktionær eller investor.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				En emittent är en institution som ett företag, investeringsfonder eller regeringar som
				emitterar värdepapper/aktier för att skaffa kapital. Den som köper säkerheten kallas
				aktieägare eller investerare.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Ein Emittent ist eine Institution wie z.B. ein Unternehmen, Kreditinstitut oder Staat,
				welche Wertpapiere herausgibt, um sich Kapital zu beschaffen. Die Person, die das
				Wertpapier kauft, heißt Aktionär.
				</O>
			);
		},
	},

	"1ab0f463a198": {
		"_context": "guide_q_14",
		"en": () => <O>What is an issuer?</O>,
		"da": () => <O>Hvad er en udsteder (emittent)?</O>,
		"sv": () => <O>Vad är en emittent?</O>,
		"de": () => <O>Was ist ein Emittent?</O>,
	},

	"ffcf41c0606a": {
		"_context": "guide_q_13",
		"en": () => <O>What is shorting?</O>,
		"da": () => <O>Hvad er shortselling (at shorte)?</O>,
		"sv": () => <O>Vad är blankning?</O>,
		"de": () => <O>Was sind Leerverkäufe (Short Selling)?</O>,
	},

	"4eaa3533b162": {
		"_context": "guide_q_12",
		"en": () => <O>What is a reverse stock split?</O>,
		"da": () => <O>Hvad er et omvendt aktiesplit</O>,
		"sv": () => <O>Vad är en omvänd aktiesplit?</O>,
		"de": () => <O>Was ist ein umgekehrter Aktiensplit?</O>,
	},

	"900f3fd5ee2c": {
		"_context": "guide_q_11",
		"en": () => <O>What is a stock split?</O>,
		"da": () => <O>Hvad er et aktiesplit?</O>,
		"sv": () => <O>Vad är en aktiesplit?</O>,
		"de": () => <O>Was ist ein Aktiensplit?</O>,
	},

	"3cc9c2c57db4": {
		"_context": "guide_q_10",
		"en": () => <O>What is a tick?</O>,
		"da": () => <O>Hvad er et tick?</O>,
		"sv": () => <O>Hvad är et tick?</O>,
		"de": () => <O>Was ist ein Tick?</O>,
	},

	"2fef7df6548b": {
		"_context": "guide_q_9",
		"en": () => <O>What is a dividend?</O>,
		"da": () => <O>Hvad er udbytte?</O>,
		"sv": () => <O>Vad är en utdelning?</O>,
		"de": () => <O>Was ist eine Dividende?</O>,
	},

	"a30350ae9a6e": {
		"_context": "guide_q_8",
		"en": () => <O>What is a fund?</O>,
		"da": () => <O>Hvad er en fond?</O>,
		"sv": () => <O>Vad är en fond?</O>,
		"de": () => <O>Was ist ein Fonds?</O>,
	},

	"c3ac4fc1373c": {
		"_context": "guide_q_7",
		"en": () => <O>What is diversification?</O>,
		"da": () => <O>Hvad er diversificering?</O>,
		"sv": () => <O>Vad är diversifiering?</O>,
		"de": () => <O>Was ist Diversifikation?</O>,
	},

	"efc96ce9f793": {
		"_context": "guide_q_6",
		"en": () => <O>How do I evaluate a stock?</O>,
		"da": () => <O>Hvordan vurderer jeg en aktie?</O>,
		"sv": () => <O>Hur utvärderar jag ett aktie?</O>,
		"de": () => <O>Wie bewerte ich eine Aktie?</O>,
	},

	"66cb0334a3af": {
		"_context": "guide_q_5",
		"en": () => <O>What are commodities?</O>,
		"da": () => <O>Hvad er råvarer?</O>,
		"sv": () => <O>Hvad er råvaror?</O>,
		"de": () => <O>Was sind Rohstoffe?</O>,
	},

	"9c9a620ec62b": {
		"_context": "guide_q_4",
		"en": () => <O>What are crypto currencies?</O>,
		"da": () => <O>Hvad er kryptovalutaer?</O>,
		"sv": () => <O>Vad är kryptovalutor?</O>,
		"de": () => <O>Was sind Kryptowährungen?</O>,
	},

	"7e54d48b8e17": {
		"_context": "guide_q_3",
		"en": () => <O>What are currencies?</O>,
		"da": () => <O>Hvad er valuta?</O>,
		"sv": () => <O>Vad är valutor?</O>,
		"de": () => <O>Was sind Währungen?</O>,
	},

	"1cd9b84ee993": {
		"_context": "guide_q_2",
		"en": () => <O>What is a portfolio?</O>,
		"da": () => <O>Hvad er en portefølje?</O>,
		"sv": () => <O>Vad är en portfölj?</O>,
		"de": () => <O>Was ist ein Portfolio?</O>,
	},

	"81eb7eb5de23": {
		"_context": "guide_q_1",
		"en": () => <O>What is a stock?</O>,
		"da": () => <O>Hvad er en aktie?</O>,
		"sv": () => <O>Vad är en aktie?</O>,
		"de": () => <O>Was ist eine Aktie?</O>,
	},

	"45e722802e14": {
		"en": () => <O>Vienna Stock Exchange</O>,
		"da": () => <O>Wiener Börse</O>,
		"sv": () => <O>Wiener Börse</O>,
		"de": () => <O>Wiener Börse</O>,
	},

	"a9824fb24f67": {
		"en": () => <O>ETF</O>,
		"da": () => <O>ETF</O>,
		"sv": () => <O>ETF</O>,
		"de": () => <O>ETF</O>,
	},

	"dccca8693c30": {
		"en": () => <O>Index</O>,
		"da": () => <O>Indeks</O>,
		"sv": () => <O>Index</O>,
		"de": () => <O>Index</O>,
	},

	"a063697c9ba3": {
		"en": () => <O>Stock</O>,
		"da": () => <O>Aktie</O>,
		"sv": () => <O>Stock</O>,
		"de": () => <O>Stock</O>,
	},

	"54304ff728c6": {
		"en": () => <O>Risk score</O>,
		"da": () => <O>Risikoscore</O>,
		"sv": () => <O>Riskpoäng</O>,
		"de": () => <O>Risikoscore</O>,
	},

	"fc414ae9be88": {
		"en": () => {
			return (
				<O>
				<p>The risk score is based on the beta value of the asset. <br /> Above 1 = higher fluctuation than the market <br /> Below 1 = lower volatility than the market</p>
				<p>Data is  from Morningstar</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				<p>Risikoscoren er baseret på aktivets betaværdi. <br /> Over 1 = højere udsving end markedet. <br /> Under 1 = lavere udsving end markedet</p>
				<p>Data hentet fra Morningstar</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				<p>Riskpoängen baseras på tillgångens betavärde. <br /> Över 1 = högre fluktuation än marknaden <br /> Under 1 = lägre volatilitet än marknaden</p>
				<p>Data hämtade från Morningstar.</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				<p>Die Risikobewertung basiert auf dem Beta-Wert des Vermögenswerts. <br /> Über 1 = höhere Schwankung als der Markt <br /> Unter 1 = geringere Volatilität als der Markt</p>
				<p>Die Daten stammen von Morningstar.</p>
				</O>
			);
		},
	},

	"007af08d430e": {
		"en": () => <O>If your game has risk-adjusted return enabled, this asset cannot be traded.</O>,
		"da": () => <O>Hvis dit spil har aktiveret risikojusteret afkast, kan dette aktiv ikke handles.</O>,
		"sv": () => <O>Om ditt spel har riskjusterad avkastning aktiverad kan denna tillgång inte handlas.</O>,
		"de": () => <O>Wenn in Ihrem Spiel die risikobereinigte Rendite aktiviert ist, kann dieser Vermögenswert nicht gehandelt werden.</O>,
	},

	"f489d39b2547": {
		"en": (name) => <O>This commodity follows the ETF – {name}, which follows the price of the commodity. There may therefore be a deviation.</O>,
		"da": (name) => <O>Denne råvarer følger ETF – {name}, som følger kursen på råvaren. Der kan derfor være en afvigelse.</O>,
		"sv": (name) => <O>Denna råvara följer ETF – {name}, som följer priset på råvaran. Det kan därför finnas en avvikelse.</O>,
		"de": (name) => <O>Dieser Rohstoff folgt dem ETF – {name}, der dem Preis des Rohstoffs folgt. Daher kann es zu Abweichungen kommen.</O>,
	},

	"814d0744bfaf": {
		"en": () => <O>Explore assets exclusively from Austria</O>,
		"da": () => <O>Udforsk værdipapirer eksklusivt fra Østrig</O>,
		"sv": () => <O>Utforska tillgångar exklusivt från Österrike</O>,
		"de": () => <O>Erkunde österreichische Wertpapiere</O>,
	},

	"9897ab397604": {
		"en": () => <O>Loading...</O>,
		"da": () => <O>Indlæser data...</O>,
		"sv": () => <O>Laddar data...</O>,
		"de": () => <O>Wird geladen...</O>,
	},

	"cb80e399bc9b": {
		"en": () => <O>Chart</O>,
		"da": () => <O>Diagram</O>,
		"sv": () => <O>Diagram</O>,
		"de": () => <O>Diagramm</O>,
	},

	"b150a9564f86": {
		"en": () => <O>Company Information</O>,
		"da": () => <O>Virksomhedsoplysninger</O>,
		"sv": () => <O>Företagsinformation</O>,
		"de": () => <O>Unternehmensinformationen</O>,
	},

	"6481e0a60223": {
		"en": () => <O>Financial Information</O>,
		"da": () => <O>Finansielle oplysninger</O>,
		"sv": () => <O>Finansiell information</O>,
		"de": () => <O>Finanzinformationen</O>,
	},

	"c7307642ba38": {
		"en": () => <O>Key Figures</O>,
		"da": () => <O>Nøgletal</O>,
		"sv": () => <O>Nyckeltal</O>,
		"de": () => <O>Kennzahlen</O>,
	},

	"2bcee5cf58f4": {
		"en": () => <O>BETA</O>,
		"da": () => <O>BETA</O>,
		"sv": () => <O>BETA</O>,
		"de": () => <O>Betafaktor</O>,
	},

	"b16af306aafb": {
		"en": (site_name) => {
			return (
				<O>
				  <p><strong>What is it?</strong> BETA measures how much the stock price moves in relation to the overall market. If BETA is 1, the stock moves in sync with the market. If BETA is greater than 1, the stock is more volatile (it swings more).</p>
				  <p><strong>Why is it important?</strong> It shows how risky the stock is compared to the market. As a shareholder, you can use BETA to assess whether the stock matches your risk tolerance.</p>
				  <p>Note: BETA is the factor we use in {site_name} when you compete to have the best balance.</p>
				</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>
				  <p><strong>Hvad er det?</strong> BETA måler, hvor meget aktiens pris bevæger sig i forhold til hele markedet. Hvis BETA er 1, bevæger aktien sig ligesom markedet. Hvis BETA er over 1, er aktien mere volatil (den svinger mere).</p>
                  <p><strong>Hvorfor er det vigtigt?</strong> Det viser, hvor risikofyldt aktien er i forhold til markedet. Som aktionær kan du bruge BETA til at vurdere, om aktien passer til din risikotolerance.</p>
				  <p>OBS: BETA er i øvrigt den faktor vi bruger her på {site_name}, når I dyster i at have den bedste balance.</p>
				</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>
				  <p><strong>Vad är det för något?</strong> BETA mäter hur mycket aktiekursen rör sig i förhållande till hela marknaden. Om BETA är 1 rör sig aktien som marknaden. Om BETA är över 1 är aktien mer volatil (den fluktuerar mer).</p>
                  <p><strong>Varför är detta viktigt?</strong> Det visar hur riskfylld aktien är jämfört med marknaden. Som aktieägare kan du använda BETA för att bedöma om aktien passar din risktolerans.</p>
				  <p>OBS: BETA är också den faktor vi använder här på {site_name} när ni tävlar om att ha den bästa balansen.</p>
				</O>
			);
		},
		"de": (site_name) => {
			return (
				<O>
				  <p><strong>Was ist damit gemeint?</strong> Der Betafaktor gibt an, wie stark sich der Aktienkurs im Verhältnis zum gesamten Markt bewegt. Wenn er bei 1 liegt, bewegt sich die Aktie im Takt mit dem Markt. Wenn er über 1 liegt, ist die Aktie volatiler (schwankt stärker).</p>
                  <p><strong>Warum ist das wichtig?</strong> Diese Information zeigt, wie riskant die Aktie im Verhältnis zu Markt ist. Als Aktionär kannst du den Betafaktor nutzen, um zu beurteilen, ob die Aktie deiner Risikotoleranz entspricht.</p>
				  <p>Bitte beachte: Der Betafaktor ist der Faktor, den wir hier bei {site_name} verwenden, wenn du das beste Gleichgewicht haben willst.</p>
				</O>
			);
		},
	},

	"d4352dd5c9ed": {
		"en": () => <O>Who is</O>,
		"da": () => <O>Hvem er</O>,
		"sv": () => <O>Vem är</O>,
		"de": () => <O>Über uns</O>,
	},

	"674e59c377bd": {
		"en": () => {
			return (
				<O><p><strong>What is it?</strong> It's the company's "bio" – a quick description of what they do and what they stand for. Think of it as the company's intro! If you want to know more, you can always check their website and dive into the interesting details.</p>
				<p><strong>Why is it important?</strong> If you're a shareholder, it's pretty smart to know what the company does. It helps you understand whether you believe in their mission and industry. Are they in a field that seems exciting and innovative? Or is there a risk that they're heading downhill?</p>
				<p>And remember: the CEO is like the team's captain! A strong leader with a good track record can make a big difference for the company’s future.</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O><p><strong>Hvad er det?</strong> Det er virksomhedens "bio" – en hurtig beskrivelse af, hvad de laver, og hvad de står for. Tænk på det som en virksomheds intro! Vil du vide mere, kan du altid tjekke deres hjemmeside og dykke ned i de spændende detaljer.</p>
				<p><strong>Hvorfor er det vigtigt?</strong> Hvis du er aktionær, er det ret smart at vide, hvad virksomheden laver. Det hjælper dig med at forstå, om du tror på deres mission og branche. Er de i en industri, der ser spændende og innovativ ud? Eller er der risiko for, at de er på vej ned ad bakke?</p>
				<p>Og husk: CEO'en er lidt som holdets kaptajn! En stærk leder med en god trackrecord kan gøre en stor forskel for virksomhedens fremtid.</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O><p><strong>Vad är det?</strong> Det här är företagets ”bio” - en snabb beskrivning av vad de gör och vad de står för. Vill du veta mer kan du alltid kika på deras hemsida för att dyka ner i alla spännande detaljer.</p>
				<p><strong>Varför är det viktigt?</strong> Om du är aktieägare är det ganska smart att veta vad företaget gör. Det hjälper dig att förstå om du tror på deras uppdrag och bransch. Är de i en bransch som ser spännande och innovativ ut? Eller finns det risk för att de är på väg utför?</p>
				<p>Och kom ihåg: VD:n är lite som lagkaptenen i ett lag! En stark ledare med ett bra track record kan göra stor skillnad för företagets framtid.</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O><p><strong>Was ist damit gemeint?</strong> Die „Biographie“ des Unternehmens – eine Kurzbeschreibung über die Tätigkeiten und Werte des Unternehmens. Quasi eine Art Einführung in das Unternehmen! Besuche die Homepage und erforsche die spannenden Details, um mehr über das Unternehmen zu erfahren.</p>
				<p><strong>Warum ist das wichtig?</strong> Als Aktionär empfiehlt es sich, darüber Bescheid zu wissen, was das Unternehmen herstellt. Das hilft dir dabei, das Unternehmen zu verstehen, wenn du an dessen Mission und Branche glaubst. Gehört das Unternehmen einer Industrie an, die spannend und innovativ erscheint? Oder besteht ein Risiko, dass das Unternehmen kurz vor dem Abstieg steht?</p>
				<p>Nicht vergessen: Der CEO ist sozusagen der Kapitän der Mannschaft. Eine starke Führungskraft mit einem soliden Hintergrund kann starken Einfluss auf die Zukunft eines Unternehmens nehmen.</p>
				</O>
			);
		},
	},

	"05a5f280e3e5": {
		"en": () => <O>Website</O>,
		"da": () => <O>Hjemmeside</O>,
		"sv": () => <O>Webbplats</O>,
		"de": () => <O>Webseite</O>,
	},

	"87c23d413290": {
		"en": () => <O>Head office</O>,
		"da": () => <O>Hovedkontor</O>,
		"sv": () => <O>Huvudkontor</O>,
		"de": () => <O>Hauptsitz</O>,
	},

	"9c6f28c8da2a": {
		"en": () => <O>The security</O>,
		"da": () => <O>Værdipapiret</O>,
		"sv": () => <O>Säkerheten</O>,
		"de": () => <O>Wertpapier</O>,
	},

	"2b8885ba1c80": {
		"en": () => {
			return (
				<O>
				  <p>This provides important information about the stock itself and its market status.</p>
				  <p>- <strong>Number of owners:</strong> Think of it as the stock's ‘fanbase’! If many people have it, it indicates that people trust the company.</p>
				  <p>- <strong>Number of Shares:</strong> This is the total number of shares the company has issued. It tells you how many ‘pieces’ of the company exist. It gives you an idea of how big the company is and how much of it can be owned by investors.</p>
				  <p>- <strong>Market capitalisation:</strong> This is the total value of all the company's shares (share price times number of shares). It tells you how much the company is worth on the stock market, which is important when assessing whether it's a good investment.’</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p>Her gives vigtige informationer om aktien selv og dens markedsstatus.</p>
				  <p>- <strong>Antal ejere:</strong> Tænk på det som aktiens "fanbase"! Hvis mange har den, tyder det på, at folk stoler på virksomheden.</p>
				  <p>- <strong>Antal Aktier:</strong> Det er det samlede antal aktier, virksomheden har udstedt. Det fortæller dig, hvor mange "stykker" af virksomheden der findes. Det giver en idé om, hvor stor virksomheden er, og hvor meget af den, der kan ejes af investorer.</p>
				  <p>- <strong>Børsværdi:</strong> Det er den samlede værdi af alle virksomhedens aktier (aktiekurs gange antal aktier). Det fortæller dig, hvor meget virksomheden er værd på aktiemarkedet, hvilket er vigtigt, når du vurderer, om den er en god investering.</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p>Här finns viktig information om själva aktien och dess marknadsstatus.</p>
				  <p>- <strong>Antal ägare:</strong> Tänk på det som aktiens ”fanbase”! Om många människor har den, indikerar det att människor litar på företaget.</p>
				  <p>- <strong>Antal aktier:</strong> Detta är det totala antalet aktier som företaget har utfärdat. Det berättar hur många ”bitar” av företaget som finns. Det ger dig en uppfattning om hur stort företaget är och hur stor del av det som kan ägas av investerare.</p>
				  <p>- <strong>Börsvärde:</strong> Detta är det totala värdet av alla bolagets aktier (aktiekurs gånger antal aktier). Det berättar hur mycket bolaget är värt på börsen, vilket är viktigt för att bedöma om det är en bra investering.”</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p>Hier findest du wichtige Informationen zur eigentlichen Aktie und zu dessen Marktstatus.</p>
				  <p>- <strong>Anzahl der Aktienbesitzer:</strong> Quasi eine Art „Fangemeinde“ der Aktie! Wenn viele Menschen die Aktie besitzen, deutet das darauf hin, dass viele Menschen dem Unternehmen vertrauen.</p>
				  <p>- <strong>Anzahl der Aktien:</strong> Die Gesamtanzahl der Aktien, die das Unternehmen ausgestellt hat. Diese Zahl sagt dir, wie viele Exemplare das Unternehmen hat. Sie gibt dir eine Vorstellung davon, wie groß das Unternehmen ist und wie groß der Teil des Unternehmens ist, der von Investoren gehalten werden kann.</p>
				  <p>- <strong>Börsenwert:</strong> Der Gesamtwert aller Aktien des Unternehmens (Aktienkurs multipliziert mit der Anzahl der Aktien). Dieser Wert zeigt dir, wie viel das Unternehmen auf dem Aktienmarkt wert ist – was wichtig ist, wenn du wissen möchtest, ob es sich um eine gute Investition handelt.</p>
				</O>
			);
		},
	},

	"ce031a3e3b99": {
		"en": () => <O>Number of owners</O>,
		"da": () => <O>Antal ejere</O>,
		"sv": () => <O>Antal ägare</O>,
		"de": () => <O>Anzahl der Aktienbesitzer</O>,
	},

	"0d7e6320fe03": {
		"en": () => <O>Number of Shares</O>,
		"da": () => <O>Antal Aktier</O>,
		"sv": () => <O>Antal Aktier</O>,
		"de": () => <O>Anzahl der Aktien</O>,
	},

	"0d417712024d": {
		"en": () => <O>Market capitalisation</O>,
		"da": () => <O>Børsværdi</O>,
		"sv": () => <O>Börsvärde</O>,
		"de": () => <O>Börsenwert</O>,
	},

	"db3b557c1dcc": {
		"en": () => <O>Which Market</O>,
		"da": () => <O>Hvilket marked</O>,
		"sv": () => <O>Vilken marknad</O>,
		"de": () => <O>Welcher Markt</O>,
	},

	"7df023433cf8": {
		"en": () => {
			return (
				<O>
				  <p>Here, you’ll get info on which sector, industry, and country the company operates in. It’s pretty smart to know this because it can help you diversify your portfolio and reduce risk.</p>
				  <p>• <strong>Sector:</strong> This is the broad category the company belongs to. It helps you understand which part of the economy affects the company. Some sectors are more stable than others, like healthcare, while others are more volatile, like technology.</p>
				  <p>• <strong>Industry:</strong> This is the more specific part of the sector the company operates in, like medical equipment within the healthcare sector. Industries have different growth rates and risks, and knowing this helps you compare the company with others in the same niche.</p>
				  <p>• <strong>Country:</strong> The country matters because taxes, regulations, and the overall economy can impact the company. As a shareholder, it’s smart to know if the company operates in a stable country or in a place with uncertain market conditions. This can be the difference between a safe investment or a wild rollercoaster ride!</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p>Her får du info om, hvilken sektor, branche og land virksomheden opererer i. Det er ret smart at vide, fordi det kan hjælpe dig med at sprede din portefølje og dermed mindske risikoen.</p>
				  <p>• <strong>Sektor:</strong> Det er den brede kategori, som virksomheden tilhører. Det hjælper dig med at forstå, hvilken del af økonomien virksomheden påvirkes af. Nogle sektorer er mere stabile end andre, fx sundhed, mens andre er mere volatile, fx teknologi.</p>
				  <p>• <strong>Branche:</strong> Det er den mere specifikke del af sektoren, virksomheden arbejder i, fx medicinsk udstyr inden for sundhedssektoren. Branchen kan have forskellig vækst og risiko, og det hjælper dig med at sammenligne virksomheden med andre i samme niche.</p>
				  <p>• <strong>Land:</strong> Landet er vigtigt, fordi skatter, regler og den generelle økonomi kan påvirke virksomheden. Som aktionær er det smart at vide, om virksomheden opererer i et stabilt land eller et sted med usikre markedsforhold. Det kan være forskellen mellem en sikker investering eller en vild rutsjebanetur!</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p>Här får du information om vilken sektor, bransch och land som bolaget är verksamt inom. Det är ganska bra att veta eftersom det kan hjälpa dig att diversifiera din portfölj och därmed minimera risken.</p>
				  <p>• <strong>Sektor:</strong> Detta är den breda kategori som företaget tillhör. Det hjälper dig att förstå vilken del av ekonomin som företaget påverkas av. Vissa sektorer är mer stabila än andra, t.ex. hälsovård, medan andra är mer volatila, t.ex. teknik.</p>
				  <p>• <strong>Bransch:</strong> Detta är den mer specifika delen av den sektor som företaget är verksamt inom, till exempel medicintekniska produkter inom hälso- och sjukvårdssektorn. Branschen kan ha olika tillväxt och risk, och det hjälper dig att jämföra företaget med andra i samma nisch.</p>
				  <p>• <strong>Land:</strong> Landet är viktigt eftersom skatter, regleringar och den allmänna ekonomin kan påverka bolaget. Som aktieägare är det smart att veta om bolaget verkar i ett stabilt land eller någonstans med osäkra marknadsförhållanden. Det kan vara skillnaden mellan en säker investering och en vild berg- och dalbana!”</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p>Hier erfährst du mehr darüber, in welchem Sektor, welcher Branche und welchem Land das Unternehmen tätig ist. Es empfiehlt sich, darüber Bescheid zu wissen, da diese Informationen dir dabei helfen können, dein Portfolio zu erweitern und entsprechende Risiken zu mindern.</p>
				  <p>• <strong>Sektor:</strong> Die weiter gefasste Kategorie, der das Unternehmen angehört. Diese Information hilft dir dabei, zu verstehen, von welchem Teil der Wirtschaft das Unternehmen beeinflusst wird. Einige Sektoren sind stabiler als andere, bspw. der Gesundheitssektor, während andere volatiler sind, bspw. der Technologiesektor.</p>
				  <p>• <strong>Branche:</strong> Hierbei handelt es sich um den spezifischeren Teil des Sektors, in dem das Unternehmen arbeitet, bspw. medizinische Ausrüstung innerhalb des Gesundheitssektors. Die Branche kann unterschiedliches Wachstum und unterschiedliche Risiken aufweisen und hilft dir dabei, das Unternehmen mit anderen Unternehmen in derselben Nische zu vergleichen.</p>
				  <p>• <strong>Land:</strong> Das Land ist wichtig, da sich Steuern, Regeln und die allgemeine Wirtschaft auf das Unternehmen auswirken können. Als Aktionär sollte man wissen, ob das Unternehmen in einem stabilen Land oder an einem Ort mit unsicheren Marktbedingungen tätig ist. Dies kann den Unterschied zwischen einer sicheren Investition und einer wilden Achterbahnfahrt ausmachen!</p>
				</O>
			);
		},
	},

	"61cd5f033f9b": {
		"en": () => <O>Sector:</O>,
		"da": () => <O>Sektor:</O>,
		"sv": () => <O>Sektor:</O>,
		"de": () => <O>Sektor:</O>,
	},

	"2b9ed7081ce7": {
		"en": () => <O>Risk Value</O>,
		"da": () => <O>Risikoværdi</O>,
		"sv": () => <O>Riskvärde</O>,
		"de": () => <O>Risikowert</O>,
	},

	"d9793892343f": {
		"en": () => <O>Expresses the stock's price sensitivity relative to the overall market.</O>,
		"da": () => <O>Udtrykker aktiens prissensitivitet i forhold til det samlede marked.</O>,
		"sv": () => <O>Uttrycker aktiens prisvolatilitet i förhållande till hela marknaden.</O>,
		"de": () => <O>Drückt die Preissensitivität der Aktie im Verhältnis zum Gesamtmarkt aus.</O>,
	},

	"c77930b00d26": {
		"en": () => <O>Industry:</O>,
		"da": () => <O>Branche:</O>,
		"sv": () => <O>Bransch:</O>,
		"de": () => <O>Branche:</O>,
	},

	"6a7fa16c9e9f": {
		"en": () => <O>Country:</O>,
		"da": () => <O>Land:</O>,
		"sv": () => <O>Land:</O>,
		"de": () => <O>Land:</O>,
	},

	"84b7046d8c0f": {
		"en": () => <O>ROE (Return on Equity)</O>,
		"da": () => <O>Egenkapitalens forrentning (ROE eller Return on Equity)</O>,
		"sv": () => <O>Avkastning på eget kapital (ROE eller Return on Equity)</O>,
		"de": () => <O>Eigenkapitalrendite (ROE oder Return on Equity)</O>,
	},

	"ea15b93e89fc": {
		"en": () => {
			return (
				<O>
				  <p><strong>What is it?</strong> It’s a ratio that shows how good the company is at generating profits from shareholders' investments.</p>
				  <p>Return on Equity = Net Income / Average Equity</p>
				  <p><strong>Why is it important?</strong> It helps you evaluate how efficiently the company uses your money as a shareholder.</p>
				  <p>Note: Average Equity = (Equity at the start of the period + Equity at the end of the period) / 2.</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p><strong>Hvad er det?</strong> Det er et tal, der viser, hvor god virksomheden er til at tjene penge på aktionærernes investering.</p>
				  <p>Egenkapitalens forrentning (Return on Equity): Indtjeningen / Den gennemsnitlige egenkapital</p>
				  <p><strong>Hvorfor er det vigtigt?</strong> Det hjælper med at vurdere, hvor effektivt virksomheden bruger dine penge som aktionær.</p>
				  <p>OBS: Gennemsnitlig egenkapital = (Egenkapital ved periodens start + Egenkapital ved periodens slut) / 2. Fx</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p><strong>Vad är det?</strong> Det är en siffra som visar hur bra företaget är på att tjäna pengar på aktieägarnas investeringar.</p>
				  <p>Avkastning på eget kapital (ROE): Vinst / Genomsnittligt eget kapital</p>
				  <p><strong>Varför är det viktigt?</strong> Det hjälper till att bedöma hur effektivt företaget använder dina (=aktieägarnas) pengar.</p>
				  <p>Anm: Genomsnittligt eget kapital = (Eget kapital i början av perioden + Eget kapital i slutet av perioden) / 2. T.ex.</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p><strong>Was ist damit gemeint?</strong> Diese Kennzahl zeigt, wie gut das Unternehmen mit der Investition der Aktionäre Geld verdient</p>
				  <p>Eigenkapitalrendite (Return on Equity): Einnahmen/Durchschnittliches Eigenkapital</p>
				  <p><strong>Warum ist das wichtig?</strong> Diese Kennzahl hilft bei der Beurteilung, wie effektiv das Unternehmen dein Geld als Aktionär einsetzt.</p>
				  <p>Bitte beachte: Durchschnittliches Eigenkapital = (Eigenkapital zu Beginn des Zeitraums + Eigenkapital am Ende des Zeitraums) / 2.</p>
				</O>
			);
		},
	},

	"f8dfdb3e3469": {
		"en": () => <O>P/E (Price/Earnings)</O>,
		"da": () => <O>P/E eller Price/Earnings</O>,
		"sv": () => <O>P/E eller Price/Earnings</O>,
		"de": () => <O>Kurs-Gewinn-Verhältnis (P/E oder Price/Earnings)</O>,
	},

	"900ae3960d4e": {
		"en": () => {
			return (
				<O>
				  <p><strong>What is it?</strong> It’s the ratio between the stock price and the company’s earnings per share.</p>
				  <p>P/E = Stock Price / Earnings per Share (EPS)</p>
				  <p><strong>Why is it important?</strong> It shows how much investors are willing to pay for each dollar the company earns. A high P/E ratio usually indicates that the market is paying more for earnings today because it expects the company to increase profits in the future. A low P/E ratio suggests the market expects lower growth in profits going forward.</p>
				  <p>Note: If EPS is negative, P/E cannot be calculated.</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p><strong>Hvad er det?</strong> Det er forholdet mellem aktiekursen og virksomhedens indtjening per aktie.</p>
				  <p>P/E = Aktiekurs (Price) / Indtjening per aktie (EPS)</p>
				  <p><strong>Hvorfor er det vigtigt?</strong>  Det viser, hvor meget investorerne er villige til at betale for hver krone, virksomheden tjener. Et højt P/E-forhold indikerer normalt, at markedet betaler mere for at få overskuddet i dag. Det skyldes bl.a., at markedet mener, at virksomheden vil øge fortjenesten i fremtiden. Et lavt P/E-forhold indikerer, at markedet har en lavere forventning til, at virksomhedens fortjeneste vil stige i fremtiden.</p>
				  <p>OBS.: Hvis EPS er negativ, kan man ikke regne P/E</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p><strong>Vad är det för något?</strong> Det är förhållandet mellan aktiekursen och bolagets vinst per aktie.</p>
				  <p>P/E = Aktiekurs (Price) / Vinst per aktie (EPS)</p>
				  <p><strong>Varför är det viktigt?</strong> Det visar hur mycket investerarna är villiga att betala för varje krona som företaget tjänar. Ett högt P/E-tal indikerar vanligtvis att marknaden är villig att betala mer för att få vinsten idag. Detta beror delvis på att marknaden tror att bolaget kommer att öka vinsten i framtiden. Ett lågt P/E-tal indikerar att marknaden har en lägre förväntan på att bolagets vinster kommer att öka i framtiden.</p>
				  <p>OBS: Om EPS är negativt kan du inte beräkna P/E</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p><strong>Was ist damit gemeint?</strong> Das Verhältnis zwischen dem Aktienkurs und dem Gewinn je Aktie des Unternehmens.</p>
				  <p>P/E = Aktienkurs (Preis) / Gewinn je Aktie (EPS)</p>
				  <p><strong>Warum ist das wichtig?</strong> Diese Kennzahl zeigt, inwieweit die Investoren gewillt sind, für jeden Cent zu zahlen, den das Unternehmen einnimmt. Ein hohes Kurs-Gewinn-Verhältnis deutet normalerweise darauf hin, dass der Markt zu viel zahlt, um heute einen Gewinn zu erzielen. Das liegt unter anderem daran, dass der Markt davon ausgeht, dass das Unternehmen die Einnahmen in Zukunft steigern wird. Ein geringes Kurs-Gewinn-Verhältnis deutet darauf hin, dass der Markt weniger davon ausgeht, dass das Unternehmen zukünftig seinen Gewinn steigern wird.</p>
				  <p>Bitte beachte: Wenn der EPS negativ ist, kann das Kurs-Gewinn-Verhältnis nicht berechnet werden.</p>
				</O>
			);
		},
	},

	"7eeddf29fc3d": {
		"en": () => <O>ROIC (Return on Invested Capital)</O>,
		"da": () => <O>Afkastningsgrad  - (ROIC eller Return on Invested Capital)</O>,
		"sv": () => <O>Avkastning på investeringar - (ROIC eller Return on Invested Capital)</O>,
		"de": () => <O>Gesamtkapitalrendite (ROIC/Return on Invested Capital)</O>,
	},

	"229eccc6e354": {
		"en": () => {
			return (
				<O>
				  <p><strong>What is it?</strong> ROIC measures how efficiently the company uses its assets to generate profits. It shows how well the company is making money from what it has invested in its business (equity and debt).</p>
				  <p>ROIC = Operating Profit x 100 / (Fixed Assets + Current Assets)</p>
				  <p><strong>Why is it important?</strong> It gives you, as a shareholder, an idea of how efficiently the company generates returns (profits) from what it uses to run the business. The higher the ROIC, the better the "bang for your buck"!</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p><strong>Hvad er det?</strong> Måler, hvor effektivt virksomheden bruger sine aktiver til at skabe overskud. Altså det viser, hvor godt virksomheden tjener penge på det den har investeret i sin forretning (egenkapital og gæld).</p>
				  <p>Afkastningsgrad = Resultat før renter x 100 / (Anlægsaktiver + omsætningsaktiver)</p>
				  <p><strong>Hvorfor er det vigtigt?</strong> Det giver dig som aktionær et billede af, hvor effektiv virksomheden er til at skabe afkast (tjene penge) på det, den bruger til at drive sin forretning. Jo højere afkastningsgrad, jo bedre "bang for the buck"!</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p><strong>Vad är det för något?</strong> Mäter hur effektivt företaget använder sina tillgångar för att generera vinst. Med andra ord visar det i hur hög grad företaget tjänar pengar på det som det har investerat i sin verksamhet (eget kapital och skulder).</p>
				  <p>Avkastningsgrad = Resultat före räntor x 100 / (Anläggningstillgångar + omsättningstillgångar).</p>
				  <p><strong>Varför är detta viktigt?</strong> Det ger dig som aktieägare en bild av hur effektivt företaget är på att generera avkastning (tjäna pengar) på det som det använder för att driva sin verksamhet. Ju högre avkastningen är, desto mer får man för pengarna!</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p><strong>Was ist damit gemeint?</strong> Diese Kennzahl zeigt, wie effektiv ein Unternehmen seine Vermögenswerte einsetzt, um einen Profit zu generieren. Sie zeigt also, wie gut ein Unternehmen darin ist, Geld an den Investitionen ins eigene Geschäft zu verdienen (Eigenkapital und Schulden).</p>
				  <p>Gesamtkapitalrendite = Ergebnis vor Zinsen x 100 / (Sachanlagen + Umlaufvermögen).</p>
				  <p><strong>Warum ist das wichtig?</strong> Diese Kennzahl gibt dir als Aktionär eine Vorstellung, wie effizient das Unternehmen beim Erzielen einer Rendite (Geldverdienen) für die Mittel ist, die es zur Führung seines Geschäfts verwendet. Je höher die Gesamtkapitalrendite, desto mehr gibt‘s fürs Geld!</p>
				</O>
			);
		},
	},

	"8c12dbf064e4": {
		"en": () => <O>Cash Flow from Financing</O>,
		"da": () => <O>Pengestrøm fra finansiering</O>,
		"sv": () => <O>Kassaflöde från finansiering</O>,
		"de": () => <O>Cashflow aus Finanzierung</O>,
	},

	"2476685a5155": {
		"en": () => <O>Cash Flow from Investments</O>,
		"da": () => <O>Pengestrøm fra investeringer</O>,
		"sv": () => <O>Kassaflöde från investeringar</O>,
		"de": () => <O>Cashflow aus Investitionen</O>,
	},

	"5d8afc0d0bcc": {
		"en": () => <O>Short-term Debt</O>,
		"da": () => <O>Kortfristet gæld</O>,
		"sv": () => <O>Kortfristiga skulder</O>,
		"de": () => <O>Kurzfristige Schulden</O>,
	},

	"892e4a07c04a": {
		"en": () => <O>Long-term Debt</O>,
		"da": () => <O>Langfristet gæld</O>,
		"sv": () => <O>Långfristiga skulder</O>,
		"de": () => <O>Langfristige Schulden</O>,
	},

	"abfda7d8e0e3": {
		"en": () => <O>Inventory</O>,
		"da": () => <O>Varelager</O>,
		"sv": () => <O>Varulager</O>,
		"de": () => <O>Warenlager</O>,
	},

	"18d615919a32": {
		"en": () => {
			return (
				<O>
				  <p><strong>What is it?</strong> It's the products that are waiting to be sold - like trainers on the shelf or pizzas that haven't been eaten yet. In other words, it's the company's ‘stock of goodies’ that hasn't gone out to customers yet.</p>
				  <p><strong>Why is this important?</strong> It shows how much money the company has tied up in things that have not yet been sold. As a shareholder, you want to know if they have too much lying around gathering dust, because it can be a bad deal if the goods don't sell fast enough. It's like having too much food in the fridge - you want to eat it before it gets boring.’</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p><strong>Hvad er det?</strong> Det er de produkter, der ligger og venter på at blive solgt – som sneakers på hylden eller pizzaer, der ikke er blevet spist endnu. Det er altså virksomhedens "lager af goodies", som ikke er røget afsted til kunderne endnu.</p>
				  <p><strong>Hvorfor er det vigtigt?</strong> Det viser, hvor mange penge virksomheden har bundet op i ting, der endnu ikke er solgt. Som aktionær vil du gerne vide, om de har for meget liggende og samle støv, for det kan være en dårlig deal, hvis varerne ikke bliver solgt hurtigt nok. Det er ligesom at have for meget mad i køleren – det skal helst blive spist, inden det bliver kedeligt.</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p><strong>Vad är ett varulager?</strong> Det är de produkter som väntar på att säljas - som sneakers på hyllan eller pizzor som inte har ätits upp än. Med andra ord är det företagets ”lager av goodies” som inte har skickats ut till kunderna ännu.</p>
				  <p><strong>Varför är detta viktigt?</strong> Det visar hur mycket pengar företaget har bundit upp i saker som ännu inte har sålts. Som aktieägare vill du veta om de har för mycket som ligger och samlar damm, eftersom det kan vara en dålig affär om varorna inte säljs tillräckligt snabbt. Det är som att ha för mycket mat i kylskåpet - man vill äta upp den innan den blir dålig.</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p><strong>Was ist damit gemeint?</strong> Die Produkte, die auf Lager sind und darauf warten, verkauft zu werden – so etwa wie Sneaker im Regal oder Pizzas, die noch nicht gegessen wurden. Damit ist als das Lager des Unternehmens mit den Goodies gemeint, die noch nicht an die Kunden verkauft wurden.</p>
				  <p><strong>Warum ist das wichtig?</strong> Diese Information zeigt, wie viele Gelder in den Dingen stecken, die noch nicht verkauft wurden. Als Aktionär möchtest du gerne wissen, ob das Unternehmen viele Artikel herumliegen hat, die Staub ansammeln – denn wenn sie nicht schnell genug weggehen, ist das auch für dich schlecht. In etwa so, als ob man zu viel Essen im Kühlschrank gelagert hat – das am besten gegessen werden sollte, bevor es verdirbt.</p>
				</O>
			);
		},
	},

	"3f88579495c4": {
		"en": () => <O>Total Equity</O>,
		"da": () => <O>Egenkapital</O>,
		"sv": () => <O>Eget kapital</O>,
		"de": () => <O>Eigenkapital</O>,
	},

	"47508c90692d": {
		"en": () => {
			return (
				<O>
				  <p><strong>What is it?</strong> It's the portion of the company's assets owned by the shareholders - that's you and all the other investors - after all the debt has been paid. Think of it as the company's ‘piggy bank’ - what's left if they sold everything, paid off their loans and still had something left.</p>
				  <p><strong>Why is it important?</strong> Equity represents the value that shareholders own in the company. When you, as a shareholder, own shares, you own a portion of that equity. Therefore, it is a direct reflection of your investment in the company.
				  High equity means that the company has more assets than debt, which is a good sign of financial stability. A company with plenty of equity can better handle bad times without having to borrow money. So it's a bit like having solid savings - it gives you peace of mind in case of unexpected bills.’</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p><strong>Hvad er det?</strong> Det er den del af virksomhedens værdier, som ejes af aktionærerne – altså dig og alle de andre investorer – efter alt gælden er betalt. Tænk på det som virksomhedens "sparegris" – det, der er tilbage, hvis de solgte alt, betalte deres lån og stadig havde noget tilbage.</p>
				  <p><strong>vorfor er det vigtigt?</strong> Egenkapitalen repræsenterer den værdi, som aktionærerne ejer i virksomheden. Når du som aktionær ejer aktier, ejer du en del af denne egenkapital. Derfor er det en direkte refleksion af din investering i virksomheden.
				  En høj egenkapital betyder, at virksomheden har flere aktiver end gæld, hvilket er et godt tegn på økonomisk stabilitet. En virksomhed med masser af egenkapital kan bedre håndtere dårlige tider uden at skulle låne penge. Så det er lidt ligesom at have en solid opsparing – det giver ro i maven, hvis der skulle komme uventede regninger.</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p><strong>Vad är det för något?</strong> Det är den del av bolagets tillgångar som ägs av aktieägarna - det vill säga du och alla andra investerare - efter att alla skulder har betalats. Tänk på det som företagets ”spargris” - det som finns kvar om de sålt allt, betalat av sina lån och ändå haft något kvar.</p>
				  <p><strong>Varför är det viktigt?</strong> Eget kapital representerar det värde som aktieägarna äger i företaget. När du som aktieägare äger aktier äger du en del av det egna kapitalet. Därför är det en direkt återspegling av din investering i företaget.
				  Ett högt eget kapital innebär att bolaget har mer tillgångar än skulder, vilket är ett gott tecken på finansiell stabilitet. Ett företag med mycket eget kapital kan bättre hantera dåliga tider utan att behöva låna pengar. Så det är lite som att ha ett bra sparande - det ger dig sinnesro om det skulle komma oväntade räkningar.</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p><strong>Was ist damit gemeint?</strong> Der Teil der Unternehmenswerte, der nach der Tilgung aller Schulden den Aktionären – also dir und allen anderen Investoren – gehört. Quasi das „Sparschwein“ des Unternehmens: Das, was übrigbliebe, wenn das Unternehmen alles verkaufen und die Kredite abbezahlen würde.</p>
				  <p><strong>Warum ist das wichtig?</strong> Eigenkapital stellt den Wert dar, den die Aktionäre am Unternehmen besitzen. Wenn du als Aktionär Aktien besitzt, besitzt du auch einen Teil dieses Eigenkapitals. Daher spiegelt es deine Investition in das Unternehmen auf direkte Weise wider.
				  Ein hohes Eigenkapital bedeutet, dass das Unternehmen mehr Vermögenswerte als Schulden hat, was ein gutes Zeichen für finanzielle Stabilität ist. Ein Unternehmen mit viel Eigenkapital kann schlechte Zeiten besser überstehen, ohne sich Geld leihen zu müssen. Es ist also ein bisschen so, als hätte man eine solide Ersparnis – die einem Seelenfrieden gibt, wenn unerwartete Rechnungen eintrudeln.</p>
				</O>
			);
		},
	},

	"7b5e1c2e1fe9": {
		"en": () => <O>Long-Term Debt</O>,
		"da": () => <O>Langfristet gæld</O>,
		"sv": () => <O>Långfristiga skulder</O>,
		"de": () => <O>Langfristige Schulden</O>,
	},

	"de82fa5c5e81": {
		"en": () => {
			return (
				<O>
				  <p><strong>What is it?</strong> It's the money the company has borrowed but doesn't have to pay back right away - only after a year or more.</p>
				  <p><strong>Why is it important?</strong> It shows how much debt the company has hanging over it in the long term. As a shareholder, it's important to know how much they've borrowed because it can affect how quickly the company can grow or weather difficult times. It's like having a student loan - how long and how will you pay it back?’</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p><strong>Hvad er det?</strong> Det er de penge, virksomheden har lånt, men som de ikke skal betale tilbage lige med det samme – først efter et år eller mere.</p>
				  <p><strong>Hvorfor er det vigtigt?</strong> Det viser, hvor meget gæld virksomheden har hængende over sig på længere sigt. Som aktionær er det vigtigt at vide, hvor meget de har lånt, fordi det kan påvirke, hvor hurtigt virksomheden kan vokse eller klare sig gennem svære tider. Det er ligesom at have et studielån – hvor længe og hvordan vil du betale det tilbage</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p><strong>Vad är det för något?</strong> Det är de pengar som företaget har lånat men inte behöver betala tillbaka direkt - utan först om ett år eller mer.</p>
				  <p><strong>Varför är det viktigt?</strong> Det visar hur mycket skuld företaget har på lång sikt. Som aktieägare är det viktigt att veta hur mycket de har lånat eftersom det kan påverka hur snabbt företaget kan växa eller klara svåra tider. Det är som att ha ett studielån - hur länge och hur ska du betala tillbaka?</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p><strong>Was ist damit gemeint?</strong> Dabei handelt es sich um das Geld, das sich das Unternehmen geliehen hat, das es jedoch nicht sofort, sondern erst nach einem Jahr oder später zurückzahlen muss.</p>
				  <p><strong>Warum ist das wichtig?</strong> Diese Information zeigt, wie hoch die Schulden des Unternehmens auf langfristige Sicht sind. Als Aktionär ist es wichtig, zu wissen, wie viel Kredit das Unternehmen aufgenommen hat, da dies Einfluss darauf haben kann, wie schnell es wachsen oder schwierige Zeiten überstehen kann. Langfristige Schulden sind mit einem Studienkredit vergleichbar.</p>
				</O>
			);
		},
	},

	"aa5de0086908": {
		"en": () => <O>Cashflow</O>,
		"da": () => <O>Cashflow</O>,
		"sv": () => <O>Kassaflöde</O>,
		"de": () => <O>Cashflow</O>,
	},

	"e558c7121b0f": {
		"en": () => {
			return (
				<O>
				  <p><strong>What is it?</strong> It shows how much money is coming in and going out. It's like keeping an eye on your own budget: what comes in from salaries and what goes out to bills?</p>
				  <p><strong>Why is it important?</strong> A healthy cash flow means that the company is in control of its money and can pay the bills. As a shareholder, you want the money to flow inwards and not outwards, so there's enough to grow the business.</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p><strong>Hvad er det?</strong> Cashflow er virksomhedens "pengeflow" – det viser, hvor mange penge der kommer ind og går ud. Det er som at holde øje med dit eget budget: Hvad kommer ind fra løn, og hvad ryger ud til regninger?</p>
				  <p><strong>Hvorfor er det vigtigt?</strong> Et sundt cashflow betyder, at virksomheden har styr på sine penge og kan betale regningerne. Som aktionær vil du gerne have, at pengene flyder indad og ikke udad, så der er nok til at vokse forretningen.</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p><strong>Vad är det för något?</strong> Cash flow är ett företags ”kassaflöde” - det visar hur mycket pengar som kommer in och går ut. Det är som att hålla koll på sin egen budget: vad kommer in i form av löner och vad går ut i form av räkningar?</p>
				  <p><strong>Varför är det viktigt?</strong> Ett sunt kassaflöde innebär att företaget har kontroll över sina pengar och kan betala räkningarna. Som aktieägare vill man att pengarna ska flöda in och inte ut, så att det finns tillräckligt med pengar för att utveckla verksamheten.</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p><strong>Was ist damit gemeint?</strong> Der Cashflow – oder Geldfluss – des Unternehmens, der zeigt, wie viele Gelder ein- und ausgehen. Hierbei geht es darum, das eigene Budget im Blick zu behalten: Welche Gelder werden durch Einnahmen generiert und wie viel davon geht für Rechnungen drauf?</p>
				  <p><strong>Warum ist das wichtig?</strong> Ein gesunder Cashflow bedeutet, dass das Unternehmen sein Geld im Blick hat und die Rechnungen bezahlen kann. Als Aktionär möchtest du gern, dass mehr Geld ein- als ausgeht, damit das Unternehmen ein Wachstum erlebt!</p>
				</O>
			);
		},
	},

	"4de150f34cee": {
		"en": () => <O>Current Assets</O>,
		"da": () => <O>Omsætningsaktiver</O>,
		"sv": () => <O>Omsättningstillgångar</O>,
		"de": () => <O>Umlaufvermögen</O>,
	},

	"d793b6693a32": {
		"en": () => <O>Current assets</O>,
		"da": () => <O>Omsætningsaktiver</O>,
		"sv": () => <O>Omsättningstillgångar</O>,
		"de": () => <O>Umlaufvermögen</O>,
	},

	"df74470e8d64": {
		"en": () => {
			return (
				<O>
				  <p><strong>What is it?</strong> It's the things the company has that can turn into money within a year. It could be inventory, money in the bank or money still owed by customers. Think of it as the company's ‘fast money’</p>
				  <p><strong>Why is this important?</strong> It shows how quickly the company can raise money to pay bills and keep the wheels turning. As a shareholder, it gives you an idea of how well the company is doing in the short term - whether they're on top of cash or risk running out before the end of the year.’</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p><strong>Hvad er det?</strong> Det er de ting, virksomheden har, som kan blive til penge inden for et år. Det kan være varer på lager, penge i banken eller penge, kunderne stadig skylder. Tænk på det som virksomhedens "hurtige penge"</p>
				  <p><strong>Hvorfor er det vigtigt?</strong> Det viser, hvor hurtigt virksomheden kan skaffe penge til at betale regninger og holde hjulene i gang. Som aktionær giver det dig en idé om, hvor godt virksomheden står på kort sigt – altså, om de har styr på kontanterne eller risikerer at løbe tør, før året er omme.</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p><strong>Vad är omsättningstillgångar?</strong> Det är de saker som företaget har och som kan bli pengar inom ett år. Det kan vara varor i lager, pengar på banken eller pengar som kunder fortfarande är skyldiga. Tänk på det som företagets ”snabba pengar”</p>
				  <p><strong>Varför är detta viktigt?</strong> Det visar hur snabbt företaget kan skaffa fram pengar för att betala räkningar och hålla hjulen igång. Som aktieägare får du en uppfattning om hur bra det går för företaget på kort sikt - om det har gott om pengar eller riskerar att få slut på dem före årets slut.</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p><strong>Was ist damit gemeint?</strong> Die Dinge, die das Unternehmen besitzt, und die innerhalb des Jahrs zu Geld gemacht werden können. Dabei kann es sich um Lagerwaren, eingezahlte Gelder oder um Gelder handeln, die die Kunden dem Unternehmen schulden. Quasi eine Art „Tageskonto“ des Unternehmens</p>
				  <p><strong>Warum ist das wichtig?</strong> Das Umlaufvermögen zeigt, wie rasch ein Unternehmen Geld beschaffen kann, um die Rechnungen zu zahlen und den Betrieb am Laufen zu halten. Als Aktionär gibt dir diese Information eine Vorstellung, wie gut das Unternehmen kurzfristig läuft – sprich, ob es die Finanzen im Blick hat oder Gefahr läuft, dass das Geld vor Ende des Jahres ausgeht.</p>
				</O>
			);
		},
	},

	"c0c908d76467": {
		"en": () => <O>Profit After Tax</O>,
		"da": () => <O>Overskud efter skat</O>,
		"sv": () => <O>Vinst efter skatt</O>,
		"de": () => <O>Gewinn nach Steuern</O>,
	},

	"96cfa6b3785a": {
		"en": () => <O>Profit Before Tax</O>,
		"da": () => <O>Overskud før skat</O>,
		"sv": () => <O>Vinst före skatt</O>,
		"de": () => <O>Gewinn vor Steuern</O>,
	},

	"6ada6d6c0ad4": {
		"en": () => <O>Profit before taxes (IBT)</O>,
		"da": () => <O>Resultat før skat</O>,
		"sv": () => <O>Resultat efter finansiella poster</O>,
		"de": () => <O>Ergebnis vor Steuern</O>,
	},

	"ec40a54a0f6b": {
		"en": () => {
			return (
				<O>
				  <p><strong>What is it?</strong> It's the profit after deducting financial costs such as interest. In other words, what does the company earn from its core business. It's the amount of profit the company makes before they have to cough up money to the taxman.</p>
				  <p><strong>Why is this important?</strong> It shows how much the company has actually earned before tax comes into play. It's an important indicator of how well the company is doing before they have to pay their ‘joint bill’ to the government.’</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p><strong>Hvad er det?</strong> Det er resultatet når der fratrækkes finansielle omkostninger såsom renter. Med andre ord, hvad tjener virksomheden på sin kerneforretning. Det er det beløb, virksomhedens overskud, før de skal hoste op med penge til skattefar.</p>
				  <p><strong>Hvorfor er det vigtigt?</strong> Det viser, hvor meget virksomheden egentlig har tjent, før skatten kommer i spil. Det er en vigtig indikator for, hvor godt virksomheden klarer sig, før de skal betale deres "fælles regning" til staten.</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p><strong>Vad är det?</strong> Det är vinsten efter avdrag för finansiella kostnader som till exempel ränta. Med andra ord, vad tjänar företaget på sin kärnverksamhet. Det är det belopp som företaget går med vinst på innan de måste hosta upp pengar till Skatteverket.</p>
				  <p><strong>Varför är detta viktigt?</strong> Det visar hur mycket företaget faktiskt har tjänat innan skatten kommer in i bilden. Det är en viktig indikator på hur bra det går för företaget innan de måste betala sin ”gemensamma räkning” till regeringen</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p><strong>Was ist damit gemeint?</strong> Das Ergebnis, nachdem Finanzkosten wie etwa Zinsen abgezogen wurden. Oder anders gesagt: Wie viel verdient das Unternehmen mit seinem Kerngeschäft? Der Gewinn des Unternehmens, bevor es Steuern an Vater Staat abdrücken muss.</p>
				  <p><strong>Warum ist das wichtig?</strong> Diese Information zeigt, wie viel ein Unternehmen eigentlich verdient hat, bevor Steuern ins Spiel kommen. Dies ist ein wichtiger Indikator dafür, wie gut ein Unternehmen abschneidet, bevor es seine Rechnung mit dem Staat begleichen muss.Diese Information zeigt, wie viel ein Unternehmen eigentlich verdient hat, bevor Steuern ins Spiel kommen. Dies ist ein wichtiger Indikator dafür, wie gut ein Unternehmen abschneidet, bevor es seine Rechnung mit dem Staat begleichen muss.</p>
				</O>
			);
		},
	},

	"54eb0aec76ac": {
		"en": () => <O>Net Income</O>,
		"da": () => <O>Resultat efter skat</O>,
		"sv": () => <O>Årets resultat</O>,
		"de": () => <O>Ergebnis nach Steuern</O>,
	},

	"bb0bb8047405": {
		"en": () => {
			return (
				<O>
				  <p><strong>What is it?</strong> It's the amount the company has left after all bills are paid - both costs and taxes. It's the final profit!</p>
				  <p><strong>Why is it important?</strong> It shows how much the company actually earns for its owners, the shareholders. It's like seeing how much you have left after a shopping trip when both the goods and VAT are paid - that's the money you can count on!’</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p><strong>Hvad er det?</strong> Det er det beløb, virksomheden har tilbage, når alle regninger er betalt – både omkostninger og skat. Det er det endelige overskud!</p>
				  <p><strong>Hvorfor er det vigtigt?</strong> Det viser, hvor meget virksomheden rent faktisk tjener til sine ejere, altså aktionærerne. Det er ligesom at se, hvor meget du har tilbage efter en shoppingtur, når både varen og momsen er betalt – det er de penge, du kan regne med!</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p><strong>Vad är det?</strong> Det är det belopp som företaget har kvar efter att alla räkningar är betalda - både kostnader och skatter. Det är den slutliga vinsten!</p>
				  <p><strong>Varför är det viktigt?</strong> Det visar hur mycket företaget faktiskt tjänar till sina ägare, alltså aktieägarna. Det är som att se hur mycket du har kvar efter en shoppingrunda när både varorna och momsen är betalda - det är de pengarna du kan räkna med!</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p><strong>Was ist damit gemeint?</strong> Der Betrag, der dem Unternehmen bleibt, nachdem es alle Rechnungen bezahlt hat – sowohl Kosten als auch Steuern. Dies ist der endgültige Gewinn des Unternehmens!</p>
				  <p><strong>Warum ist das wichtig?</strong> Diese Information zeigt, wie viel das Unternehmen seinen Eigentümern – sprich, den Aktionären – einbringt. In etwa so, als würde man schauen, wie viel Geld nach einer Shoppingtour übrigbleibt, nachdem Waren und Mehrwertsteuer bezahlt sind – also die Kosten, mit denen du in jedem Fall rechnen musst!</p>
				</O>
			);
		},
	},

	"16f99ec985e4": {
		"en": () => <O>Balance sheet</O>,
		"da": () => <O>Balance</O>,
		"sv": () => <O>Balansräkning </O>,
		"de": () => <O>Bilanz</O>,
	},

	"386f8bb2f956": {
		"en": () => {
			return (
				<O>
				  <p><strong>What is it?</strong> The balance sheet shows what the company owns (assets) and what they owe (debts), as well as what they have left as equity. It's like checking your bank account: how much do you have and how much do you owe?</p>
				  <p><strong>Why is this important?</strong> It gives you a quick idea of how solid the company is financially - how much value they have and how much they owe. As a shareholder, you want to see that the company has more value than debt!’</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p><strong>Hvad er det?</strong> Balancen viser, hvad virksomheden ejer (aktiver), og hvad de skylder (gæld), samt hvad de har tilbage som egenkapital. Det er ligesom at tjekke din konto: hvor meget har du, og hvor meget skylder du?</p>
				  <p><strong>Hvorfor er det vigtigt?</strong> Det giver dig en hurtig idé om, hvor solid virksomheden står økonomisk – hvor meget de har af værdi, og hvor meget de skylder. Som aktionær vil du gerne se, at virksomheden har flere værdier end gæld!</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p><strong>Vad är det för något?</strong> Balansräkningen visar vad företaget äger (tillgångar) och vad de är skyldiga (skulder), samt vad de har kvar som eget kapital. Det är som att kolla sitt bankkonto: hur mycket har man och hur mycket är man skyldig?</p>
				  <p><strong>Varför är detta viktigt?</strong> Det ger dig en snabb uppfattning om hur solitt företaget är finansiellt - hur mycket värde de har och hur mycket de är skyldiga. Som aktieägare vill du se att företaget har mer värde än skulder!</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p><strong>Was ist damit gemeint?</strong> Die Bilanz zeigt, wie viel das Unternehmen besitzt (Vermögenswerte), wie viel es schuldet (Schulden) und wie viel Eigenkapital am Ende bleibt. Etwa so, als würde man sein Konto prüfen: Wie viel Geld ist drauf und wie viele Schulden müssen noch bezahlt werden?</p>
				  <p><strong>Warum ist das wichtig?</strong> Diese Information gibt dir eine Vorstellung, wie robust ein Unternehmen in wirtschaftlicher Hinsicht ist – wie viele Vermögenswerte hat es und wie viele Schulden hat es? Als Aktionär ist es natürlich in deinem Interesse, dass das Unternehmen mehr Vermögenswerte als Schulden hat!</p>
				</O>
			);
		},
	},

	"85b2eeee378f": {
		"en": () => <O>Operating Profit</O>,
		"da": () => <O>Driftsoverskud</O>,
		"sv": () => <O>Rörelseresultat</O>,
		"de": () => <O>Betriebsgewinn</O>,
	},

	"675104adcc62": {
		"en": () => <O>Operating profit (EBIT)</O>,
		"da": () => <O>Driftsresultat</O>,
		"sv": () => <O>Rörelseresultat</O>,
		"de": () => <O>Betriebsergebnis</O>,
	},

	"7f96b2166df7": {
		"en": () => {
			return (
				<O>
				  <p><strong>What is it?</strong> It's the profit that remains after all operating costs have been deducted from revenue.</p>
				  <p><strong>Why is it important?</strong> It shows whether the company is actually making money on what they do every day. In other words, how good their core business is at generating profit. If the operating profit is good, then the business is going in the right direction.</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p><strong>Hvad er det?</strong> Det er det overskud, der er tilbage efter at alle driftsomkostninger er trukket fra omsætningen.</p>
				  <p><strong>Hvorfor er det vigtigt?</strong> Det viser, om virksomheden rent faktisk tjener penge på det, de laver til hverdag. Altså, hvor god deres kerneforretning er til at skabe profit. Hvis driftsresultatet er godt, så kører forretningen i den rigtige retning.</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p><strong>Vad är det för något?</strong> Det är det överskott som återstår efter att alla rörelsekostnader har dragits av från intäkterna.</p>
				  <p><strong>Varför är det viktigt?</strong> Det visar om företaget faktiskt tjänar pengar på det som de gör varje dag. Med andra ord, hur bra deras kärnverksamhet är på att generera vinst. Om rörelseresultatet är bra går verksamheten i rätt riktning.</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p><strong>Was ist damit gemeint?</strong> Der Gewinn, der übrigbleibt, nachdem alle Betriebskosten vom Umsatz abgezogen wurden.</p>
				  <p><strong>Warum ist das wichtig?</strong> Diese Information zeigt, ob ein Unternehmen faktisch gesehen an dem verdient, was es tagtäglich verkauft. Sprich, wie gut das Kerngeschäft für die Generierung eines Profits ausgelegt ist. Wenn das Betriebsergebnis gut ist, bewegt sich das Unternehmen in die richtige Richtung!</p>
				</O>
			);
		},
	},

	"878d8530d529": {
		"en": () => <O>Operating Costs</O>,
		"da": () => <O>Vareforbrug og produktionsomkostninger</O>,
		"sv": () => <O>Kostnad sålda varor och tillverkningskostnader</O>,
		"de": () => <O>Warenverbrauch und Produktionskosten</O>,
	},

	"c0076ceac9ae": {
		"en": () => <O>Operating Expenses</O>,
		"da": () => <O>Driftsomkostninger:</O>,
		"sv": () => <O>Rörelsekostnader</O>,
		"de": () => <O>Betriebskosten</O>,
	},

	"67172f084796": {
		"en": () => {
			return (
				<O>
				  <p><strong>What is it?</strong> It's all the money the company spends to keep the store running. Think of things like employee salaries and rent. All the things that keep the business running day after day.</p>
				  <p><strong>Why is this important?</strong> It tells you how effective the business is at managing its expenses. If they can keep operating costs down, they can keep more money and make more money.</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p><strong>Hvad er det?</strong> Det er alle de penge, virksomheden bruger for at holde butikken kørende. Tænk på ting som løn til medarbejdere og husleje. Alt det, der gør, at forretningen kan køre rundt dag efter dag.</p>
				  <p><strong>Hvorfor er det vigtigt?</strong> Det fortæller, hvor effektiv virksomheden er til at styre sine faste udgifter. Hvis de kan holde driftsomkostningerne nede, kan de beholde flere penge og tjene bedre.</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p><strong>Vad är det?</strong> Det är alla pengar som företaget spenderar för att hålla butiken igång. Tänk på saker som anställdas löner och hyra. Alla de saker som håller verksamheten igång dag efter dag.</p>
				  <p><strong>Varför är det här viktigt?</strong> Det berättar hur effektiv verksamheten är på att hantera sina omkostnader. Om de kan hålla nere driftskostnaderna kan de behålla mer pengar och tjäna mer.</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p><strong>Was ist damit gemeint?</strong> Die Geldsumme, die das Unternehmen benötigt, um das Geschäft am Laufen zu halten. Darunter fallen Gehälter für Mitarbeiter und Mietkosten – also alle Kosten, die für den täglichen Betrieb des Geschäfts erforderlich sind.</p>
				  <p><strong>Warum ist das wichtig?</strong> Diese Information zeigt dir, wie effektiv ein Unternehmen im Umgang mit den eigenen Fixkosten ist. Wenn das Unternehmen die Betriebskosten gering halten kann, hat es am Ende mehr Geld übrig und verdient mehr.</p>
				</O>
			);
		},
	},

	"645a07d0d4bd": {
		"en": () => {
			return (
				<O>
				  <p><strong>What is it?</strong> It's the variable costs a company incurs in the production and sale of its goods - like raw materials and production costs. Think of it like shopping for your lemonade stand: what ingredients do you need to make something your neighbours will buy?</p>
				  <p><strong>Why is this important?</strong> It's all about keeping costs under control! If your business spends too much here, it can quickly eat into your profits. Just like with the lemonade stand: If you buy expensive ingredients but sell too cheap, you end up losing money. A business needs to be smart and balance their expenses so they have the best opportunities to make a good profit!’</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p><strong>Hvad er det?</strong> Det er de variable omkostninger, virksomheden har i forbindelse med produktionen og salget af sine varer – som råvarer og produktionsomkostninger. Tænk på det som at købe ind til din lemonadestand: Hvilke ingredienser skal der til for at lave noget, naboerne vil købe?</p>
				  <p><strong>Hvorfor er det vigtigt?</strong> Det handler om at holde styr på omkostningerne! Hvis virksomheden bruger for meget her, kan det hurtigt spise en stor del af deres profit. Ligesom med lemonadestanden: Hvis du køber dyre ingredienser, men sælger for billigt, ender du med at miste penge. En virksomhed skal være smart og balancere sine udgifter, så de har de bedste muligheder for at tjene godt!</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p><strong>Vad är det?</strong> Det är de rörliga kostnader som ett företag ådrar sig för att producera och sälja sina varor - som råvaror och produktionskostnader. Tänk på det som att handla till ditt lemonadstånd: vilka ingredienser behöver du för att göra något som dina grannar kommer att köpa?</p>
				  <p><strong>Varför är detta viktigt?</strong> Det handlar om att hålla kostnaderna under kontroll! Om ditt företag spenderar för mycket här kan det snabbt äta sig in i din vinst. Precis som med lemonadståndet: Om du köper dyra ingredienser men säljer för billigt, förlorar du pengar i slutändan. Ett företag måste vara smart och balansera sina utgifter så att de har de bästa möjligheterna att göra en bra vinst!</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p><strong>Was ist damit gemeint?</strong> Damit sind die variablen Kosten gemeint, die dem Unternehmen im Rahmen der Produktion und des Verkaufs seiner Waren entstehen – bspw. Rohstoff- und Produktionskosten. Das ist in etwa so, als würde man für seinen Limonadestand einkaufen: Welche Zutaten braucht man, um etwas herzustellen, das die Nachbarn gern kaufen möchten?</p>
				  <p><strong>Warum ist das wichtig?</strong> Hierbei geht es darum, die eigenen Kosten unter Kontrolle zu halten. Wenn ein Unternehmen zu viel ausgibt, schluckt das schnell mal einen großen Teil des Gewinns. Genauso wie am Limonadestand: Wenn du teure Zutaten einkaufst, die Limonade jedoch günstig anbietest, verlierst du Geld. Ein Unternehmen muss clever handeln und seine Ausgaben so handhaben, dass es so gut wie möglich dazu in der Lage ist, viel Geld zu verdienen!</p>
				</O>
			);
		},
	},

	"18cf6847b85c": {
		"en": () => <O>Gross Profit</O>,
		"da": () => <O>Bruttoresultat</O>,
		"sv": () => <O>Bruttoresultat</O>,
		"de": () => <O>Bruttoergebnis</O>,
	},

	"23fc83477b1f": {
		"en": () => {
			return (
				<O>
				  <p><strong>What is it?</strong> It's the difference between revenue and the cost of products sold.</p>
				  <p><strong>Why is it important?</strong> It shows how much the company earns after they have paid to make or purchase their products. For example, when you've bought your new trainers - the more you have left, the better!</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p><strong>Hvad er det?</strong> Det er forskellen mellem omsætningen og omkostningerne til produkterne, der sælges. </p>
				  <p><strong>Hvorfor er det vigtigt?</strong> Det viser, hvor meget virksomheden tjener, efter de har betalt for at lave eller indkøbe deres produkter. Fx når du har købt dine nye sneakers – jo mere du har til overs, jo bedre!</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p><strong>Vad är det för något?</strong> Det är skillnaden mellan intäkterna och kostnaden för sålda produkter.</p>
				  <p><strong>Varför är det viktigt?</strong> Det visar hur mycket företaget tjänar efter att de har betalat för att tillverka eller köpa sina produkter. Till exempel när du har köpt dina nya gympaskor - ju mer du har kvar, desto bättre</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p><strong>Was ist damit gemeint?</strong> Es besteht ein Unterschied zwischen Umsatz und den Kosten für die Produkte, die verkauft werden.</p>
				  <p><strong>Warum ist das wichtig?</strong> Diese Zahl zeigt, wie viel ein Unternehmen verdient, nachdem es die Kosten für die Herstellung oder den Einkauf der eigenen Produkte beglichen hat. So in etwa, als wenn du dir neue Sneaker kaufst – je mehr Geld du am Ende übrig hast, desto besser!</p>
				</O>
			);
		},
	},

	"6048b06f5511": {
		"en": () => <O>Costs</O>,
		"da": () => <O>Omkostninger</O>,
		"sv": () => <O>Kostnader</O>,
		"de": () => <O>Kosten</O>,
	},

	"4a3ef7a38ce3": {
		"en": () => <O>Financial Statements</O>,
		"da": () => <O>Regnskabstal</O>,
		"sv": () => <O>Finansiell översikt</O>,
		"de": () => <O>Buchhaltungszahlen</O>,
	},

	"89dbf5c71d7a": {
		"en": () => <O>Annual Report</O>,
		"da": () => <O>Årsregnskab</O>,
		"sv": () => <O>Resultaträkning </O>,
		"de": () => <O>Jahresabschluss</O>,
	},

	"2d93a46e8c61": {
		"en": () => {
			return (
				<O>
				  <p><strong>What is it?</strong> The annual report shows how the company has performed financially over the year. Think of it as their report card for finances – here you can see if they’ve aced it or just barely scraped by.</p>
				  <p><strong>Why is it important?</strong> It gives you a clear picture of the company’s financial performance – how much money they’ve made, what they’ve spent, and what they have left.</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p><strong>Hvad er det?</strong> Årsregnskabet er hvordan de har klaret sig økonomisk i løbet af året. Tænk på det som deres karakterbog for økonomi – her kan du se, om de har bestået med topkarakterer eller lige netop skrabet igennem.</p>
				  <p><strong>Hvorfor er det vigtigt?</strong> Det giver dig et klart billede af virksomhedens økonomiske præstation – hvor mange penge de har tjent, hvad de har brugt, og hvad de har tilbage.</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p><strong>Vad är det för något?</strong> Bokslutet är en sammanställning av hur företaget har klarat sig ekonomiskt under året. Tänk på det som deras finansiella betyg - här kan du se om de klarade sig med beröm godkänt eller bara nätt och jämnt klarade sig.</p>
				  <p><strong>Varför är detta viktigt?</strong> Det ger dig en tydlig bild av företagets ekonomiska resultat - hur mycket pengar de har tjänat, vad de har spenderat och vad de har kvar.</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p><strong>Was ist damit gemeint?</strong> Der Jahresabschluss zeigt, wie gut das Unternehmen über das Jahr hinweg abgeschnitten hat. Quasi eine Art Notenbuch für die Wirtschaftlichkeit des Unternehmens – es zeigt, ob das Unternehmen mit Bestnoten bestanden hat oder es gerade so durchgekommen ist.</p>
				  <p><strong>Warum ist das wichtig?</strong> Der Jahresabschluss liefert ein klares Bild der wirtschaftlichen Leistung des Unternehmens – wie viel Geld hat es verdient? Wie viel hat es ausgegeben? Was ist am Ende übriggeblieben? </p>
				</O>
			);
		},
	},

	"fd4031621d6f": {
		"en": () => <O>Year</O>,
		"da": () => <O>År</O>,
		"sv": () => <O>År</O>,
		"de": () => <O>Jahr</O>,
	},

	"643331a22e94": {
		"en": () => <O>Operating Cash Flow</O>,
		"da": () => <O>Cash flow fra driften</O>,
		"sv": () => <O>Kassaflöde från löpande verksamheten</O>,
		"de": () => <O>Cashflow aus dem laufenden Betrieb</O>,
	},

	"e5b2a2556fa9": {
		"en": () => {
			return (
				<O>
				  <p><strong>What is it?</strong> It's the money the company earns or spends on its daily activities, like selling products and paying bills.</p>
				  <p><strong>Why is it important?</strong> It shows whether the company is good at generating profits from its core business. As a shareholder, you want to see that the company has a healthy cash flow as it indicates stability - it gives you peace of mind.</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p><strong>Hvad er det?</strong> Det er de penge, virksomheden tjener eller bruger på sine daglige aktiviteter, som at sælge produkter og betale regninger.</p>
				  <p><strong>Hvorfor er det vigtigt?</strong> Det viser, om virksomheden er god til at skabe overskud fra sin kerneforretning. Som aktionær vil du gerne se, at virksomheden har et sundt cash flow, da det indikerer stabilitet. – det giver ro i maven.</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p><strong>Vad är det för något?</strong> Det är de pengar som företaget tjänar eller spenderar på sin dagliga verksamhet, som att sälja produkter och betala räkningar.</p>
				  <p><strong>Varför är det viktigt?</strong> Det visar om företaget är bra på att generera vinster från sin kärnverksamhet. Som aktieägare vill du se att företaget har ett sunt kassaflöde eftersom det indikerar stabilitet - det ger dig sinnesfrid.</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p><strong>Was ist damit gemeint?</strong> Hierbei handelt es sich um das Geld, das das Unternehmen für seine alltäglichen Aktivitäten verdient oder ausgibt, beispielsweise für den Verkauf von Produkten und das Bezahlen von Rechnungen.</p>
				  <p><strong>Warum ist das wichtig?</strong> Diese Information zeigt, ob das Unternehmen gut darin ist, einen Gewinn aus seinem Kerngeschäft zu generieren. Als Aktionär ist es in deinem Interesse, dass das Unternehmen einen gesunden Cashflow hat, da dies von Stabilität zeugt – und für Seelenfrieden sorgt!</p>
				</O>
			);
		},
	},

	"0e0d8ff98065": {
		"en": () => <O>Investing Cash Flow</O>,
		"da": () => <O>Cashflow fra investering</O>,
		"sv": () => <O>Kassaflöde från investeringsverksamheten</O>,
		"de": () => <O>Cashflow aus Investitionstätigkeit</O>,
	},

	"85f6c652314c": {
		"en": () => {
			return (
				<O>
				  <p><strong>What is it?</strong> It's the money the company spends or earns on investments, such as buying new machines or selling assets.</p>
				  <p><strong>Why is it important?</strong> It shows whether the company is investing in future growth or selling assets to raise money. As a shareholder, you can see if the company is actively growing. Like a PC, there are upgrades coming all the time, but are you investing in making it perform better?</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p><strong>Hvad er det?</strong> Det er de penge, virksomheden bruger eller tjener på investeringer, fx køb af nye maskiner eller salg af aktiver.</p>
				  <p><strong>Hvorfor er det vigtigt?</strong> Det viser, om virksomheden investerer i fremtidig vækst eller sælger aktiver for at skaffe penge. Som aktionær kan du se, om virksomheden er aktivt på vej til at vokse. Ligesom med en PC, der kommer hele tiden upgrades, men investerer du i at få den til at performe bedre?</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p><strong>Vad är det för något?</strong> Det är de pengar som företaget spenderar eller tjänar på investeringar, till exempel att köpa nya maskiner eller sälja tillgångar.</p>
				  <p><strong>Varför är det viktigt?</strong> Det visar om företaget investerar i framtida tillväxt eller säljer tillgångar för att få in pengar. Som aktieägare kan du se om företaget växer aktivt. Precis som med en PC kommer det uppgraderingar hela tiden, men investerar du i att få den att prestera bättre?</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p><strong>Was ist damit gemeint?</strong> Das Geld, das das Unternehmen für Investitionen aufwendet oder an diesen verdient, bspw. beim Kauf von Maschinen oder beim Verkauf von Vermögenswerten. </p>
				  <p><strong>Warum ist das wichtig?</strong> Diese Information zeigt, ob das Unternehmen in das zukünftige Wachstum investiert oder Vermögenswerte verkauft, um Geld zu beschaffen. Als Aktionär kannst du einsehen, ob das Unternehmen aktives Wachstum erlebt. Genauso wie bei einem PC, der ständig mit den neuesten Upgrades bespielt wird und in den du somit investierst, damit er besser wird.</p>
				</O>
			);
		},
	},

	"3f2c464c1073": {
		"en": () => <O>Financing Cash Flow</O>,
		"da": () => <O>Cashflow fra finansieringer</O>,
		"sv": () => <O>Kassaflöde från finansieringsverksamheten</O>,
		"de": () => <O>Cashflow aus Finanzierungstätigkeit</O>,
	},

	"b3ab738f88df": {
		"en": () => {
			return (
				<O>
				  <p><strong>What is it?</strong> It's the money the company receives or pays back through loans or by issuing shares.</p>
				  <p><strong>Why is it important?</strong> It shows how the company finances its activities, either through loans or by raising new money from investors. As a shareholder, it's important to know if the company is taking many loans or diluting the value of your shares by issuing more. Borrowing from a friend or selling off their stuff to get money.</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p><strong>Hvad er det?</strong> Det er de penge, virksomheden modtager eller betaler tilbage gennem lån eller ved at udstede aktier.</p>
				  <p><strong>Hvorfor er det vigtigt?</strong> Det viser, hvordan virksomheden finansierer sine aktiviteter, enten gennem lån eller ved at skaffe nye penge fra investorer. Som aktionær er det vigtigt at vide, om virksomheden tager mange lån eller udvander værdien af dine aktier ved at udstede flere. Låner fra en ven eller sælger ud af deres ting for at få penge</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p><strong>Vad är det för något?</strong> Det är de pengar som företaget får eller betalar tillbaka genom lån eller genom att emittera aktier.</p>
				  <p><strong>Varför är det viktigt?</strong> Det visar hur bolaget finansierar sin verksamhet, antingen genom lån eller genom att ta in nya pengar från investerare. Som aktieägare är det viktigt att veta om företaget tar många lån eller späder ut värdet på dina aktier genom att emittera fler. Lånar av en vän eller säljer av sina saker för att få pengar.</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p><strong>Was ist damit gemeint?</strong> Hierbei handelt es sich um das Geld, das das Unternehmen durch Kredite oder die Ausstellung von Aktien erhält oder zurückzahlt.</p>
				  <p><strong>Warum ist das wichtig?</strong> Diese Information zeigt, wie das Unternehmen seine Aktivitäten finanziert, entweder durch Kredite oder durch die Beschaffung neuer Gelder von Investoren. Als Aktionär ist es wichtig, zu wissen, ob das Unternehmen viele Kredite aufnimmt oder den Wert deiner Aktien durch die Ausstellung weiterer Aktien verwässert. So etwa, als würde man sich Geld von einem Freund leihen und dann seine Sachen verkaufen, um Geld zu beschaffen.</p>
				</O>
			);
		},
	},

	"1a49d16edf7c": {
		"en": () => <O>Cash Flow from Operations</O>,
		"da": () => <O>Pengestrøm fra driften</O>,
		"sv": () => <O>Kassaflöde från verksamheten</O>,
		"de": () => <O>Cashflow aus dem operativen Geschäft</O>,
	},

	"7b94513c7e4f": {
		"en": () => <O>Fixed Assets</O>,
		"da": () => <O>Anlægsaktiver</O>,
		"sv": () => <O>Anläggningstillgångar</O>,
		"de": () => <O>Sachanlagen</O>,
	},

	"93f7dbf75ebc": {
		"en": () => {
			return (
				<O>
				  <p><strong>What is it?</strong> Think of it as the company's expensive gadgets and tools that don't wear out after a day or two. For example, buildings, machinery or patents.</p>
				  <p><strong>Why is it important?</strong> Fixed assets show how much the company has invested in long-term things that can help make money in the future. For you as a shareholder, it's a sign of how solid the company is and whether it's well equipped to succeed in the long run. ’</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p><strong>Hvad er det?</strong> Tænk på det som virksomhedens dyre gadgets og værktøjer, der ikke lige slides op efter en dag eller to. fx bygninger, maskiner eller patenter.</p>
				  <p><strong>Hvorfor er det vigtigt?</strong> Anlægsaktiver viser, hvor meget virksomheden har investeret i langsigtede ting, der kan hjælpe med at tjene penge i fremtiden. For dig som aktionær er det et tegn på, hvor solid virksomheden er, og om den er godt rustet til at klare sig på den lange bane.</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p><strong>Vad är det för något?</strong> Tänk på det som företagets dyra prylar och verktyg som inte bara slits ut efter en dag eller två. Det kan till exempel vara byggnader, maskiner eller patent.</p>
				  <p><strong>Varför är det viktigt?</strong> Anläggningstillgångar visar hur mycket företaget har investerat i långsiktiga saker som kan hjälpa till att tjäna pengar i framtiden. För dig som aktieägare är det ett tecken på hur solitt företaget är och om det är väl rustat för att lyckas på lång sikt.</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p><strong>Was ist damit gemeint?</strong> Sozusagen die teuren Gadgets und Werkzeuge eines Unternehmens, die nicht direkt nach einem oder zwei Tagen verbraucht sind, bspw. Gebäude, Maschinen und Patente.</p>
				  <p><strong>Warum ist das wichtig?</strong> Sachanlagen zeigen, wie viel ein Unternehmen in langfristige Werte investiert hat, die in Zukunft beim Geldverdienen helfen können. Für dich als Aktionär sind sie ein Zeichen dafür, wie solide das Unternehmen und ob es gut für die Zukunft gerüstet ist.</p>
				</O>
			);
		},
	},

	"9192776641a1": {
		"en": () => <O>Revenue</O>,
		"da": () => <O>Omsætning</O>,
		"sv": () => <O>Nettoomsättning</O>,
		"de": () => <O>Umsatz</O>,
	},

	"23fa06fb721e": {
		"en": () => {
			return (
				<O>
				  <p><strong>What is it?</strong> It’s the money rolling in every time the company sells something. It’s the company’s total "income" from selling goods and services.</p>
				  <p><strong>Why is it important?</strong> Revenue shows how good the company is at getting its products out the door (sold) and into customers' hands. It’s like keeping track of how many goals your favorite team scores – and you definitely want a lot of goals!</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <p><strong>Hvad er det?</strong> Det er de penge, der ruller ind, hver gang virksomheden sælger noget Det er virksomhedens samlede "indtjening" fra salg af varer og tjenester.</p>
				  <p><strong>Hvorfor er det vigtigt?</strong> Omsætning viser, hvor godt virksomheden er til at få sine produkter over disken (solgt) og ud til kunderne. Det er ligesom at følge med i, hvor mange mål dit yndlingshold scorer. Der vil du gerne have rigtig mange mål!</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <p><strong>Vad är det för något?</strong> Det är de pengar som rullar in varje gång företaget säljer något Det är företagets totala ”intäkter” från försäljning av varor och tjänster.</p>
				  <p><strong>Varför är det viktigt?</strong> Intäkterna visar hur bra företaget är på att få ut sina produkter på marknaden (sälja) och ut till kunderna. Det är som att hålla koll på hur många mål ditt favoritlag gör. Du vill ha många mål!</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <p><strong>Was ist damit gemeint?</strong> Die Gelder, die eingehen, wenn das Unternehmen etwas verkauft – die gesamten „Einnahmen“ aus dem Verkauf von Waren und Dienstleistungen.</p>
				  <p><strong>Warum ist das wichtig?</strong> Der Umsatz zeigt, wie gut das Unternehmen es schafft, die eigenen Produkte loszuwerden und an die Kunden zu verkaufen. Das ist in etwa so, als würde man mitverfolgen, wie viele Tore die Lieblingsmannschaft schießt. Und da möchte man natürlich, dass möglichst viele Tore geschossen werden!</p>
				</O>
			);
		},
	},

	"4a911cea86a7": {
		"en": () => <O>DAX 30</O>,
		"da": () => <O>DAX 30</O>,
		"sv": () => <O>DAX 30</O>,
		"de": () => <O>DAX 30</O>,
	},

	"05f29ec8dabc": {
		"en": () => <O>German cars</O>,
		"da": () => <O>Tyske biler</O>,
		"sv": () => <O>Tyska bilar</O>,
		"de": () => <O>Deutsche Autos</O>,
	},

	"5bd4085bf699": {
		"en": () => <O>German collections</O>,
		"da": () => <O>Tyske lister</O>,
		"sv": () => <O>Tyska kollektioner</O>,
		"de": () => <O>Deutsche Listen</O>,
	},

	"47ed1c88f13d": {
		"en": () => <O>Selected german stocks</O>,
		"da": () => <O>Udvalgte tyske aktier</O>,
		"sv": () => <O>Utvalda tyska aktier</O>,
		"de": () => <O>Ausgewählte deutsche Aktien</O>,
	},

	"78d55e675de9": {
		"en": () => <O>Random german stocks</O>,
		"da": () => <O>Tilfældige tyske aktier</O>,
		"sv": () => <O>Slumpmässiga tyska aktier</O>,
		"de": () => <O>Zufällige deutsche Aktien</O>,
	},

	"ceb55b41d8f4": {
		"en": () => <O>Search German assets</O>,
		"da": () => <O>Søg tyske værdipapirer</O>,
		"sv": () => <O>Sök tyska värdepapper</O>,
		"de": () => <O>Nach deutschen Aktien suchen</O>,
	},

	"aad4fb9baa30": {
		"en": () => <O>Germany</O>,
		"da": () => <O>Tyskland</O>,
		"sv": () => <O>Tyskland</O>,
		"de": () => <O>Deutschland</O>,
	},

	"ef6f803910fb": {
		"en": () => <O>Explore and Discover</O>,
		"da": () => <O>Gå på opdagelse</O>,
		"sv": () => <O>Gå på upptäckt</O>,
		"de": () => <O>Gehe auf Entdeckungstour</O>,
	},

	"db1eed635f24": {
		"en": (site_name) => {
			return (
				<O>
				Here at {site_name}, we take the stock market seriously, but we also believe that
				learning can be fun and exciting. That's why we have created a universe where you
				can explore and learn about stocks risk-free through our gaming environment. Go on
				a journey in our world and experience how enjoyable and educational it can be to
				invest in stocks.
				</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>
				Her på {site_name} tager vi aktiemarkedet alvorligt, men vi tror også på at læring kan være sjovt
				og spændende. Derfor har vi skabt et univers, hvor du kan udforske og lære om aktier risikofrit
				gennem vores spilunivers. Gå på opdagelse i vores verden og oplev, hvor sjovt og lærerigt det kan
				være at investere i aktier.
				</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>
				Här på {site_name} tar vi aktiemarknaden på allvar, men vi tror också att lärande kan vara roligt
				och spännande. Därför har vi skapat ett universum där du kan utforska och lära dig om aktier
				riskfritt genom vårt speluniversum. Utforska vår värld och upptäck hur roligt och lärorikt det kan
				vara att investera i aktier.
				</O>
			);
		},
		"de": (site_name) => {
			return (
				<O>
				Hier bei {site_name} nehmen wir die Börse ernst, aber wir glauben auch, dass Lernen Spaß machen
				und spannend sein kann. Deshalb haben wir ein Spieluniversum geschaffen, in dem du risikofrei
				Aktien entdecken und über diese lernen kannst. Erkunde unsere Welt und entdecke, wie unterhaltsam
				und lehrreich das Investieren in Aktien sein kann.
				</O>
			);
		},
	},

	"73fc239cf44e": {
		"en": () => <O>Start or Join Game</O>,
		"da": () => <O>Start eller deltag i spil</O>,
		"sv": () => <O>Starta eller gå med i spel</O>,
		"de": () => <O>Spiel starten oder beitreten</O>,
	},

	"1c9da13c55ab": {
		"en": (A) => {
			return (
				<O>
				Who is the best investor among your friends or colleagues?
				Create a <A className="ad_blue_link" href="/new_game">private duel</A>,
				invite your friends, and challenge each other. You can also
				participate in our ongoing <A href="/games">prize games</A> (reward games), where
				everyone can join for free.
				</O>
			);
		},
		"da": (A) => {
			return (
				<O>
				Hvem er den bedste investor blandt dine venner eller kollegaer? Opret en
				<A className="ad_blue_link" href="/new_game">privat dyst</A>, inviter dine venner og udfordr
				hinanden. Du kan også deltage i vores løbende <A href="/games">præmiespil</A>, hvor alle over 18
				år kan være med helt gratis.
				</O>
			);
		},
		"sv": (A) => {
			return (
				<O>
				Vem är den bästa investeraren bland dina vänner eller kollegor? Skapa en
				<A className="ad_blue_link" href="/new_game">privat tävling</A>, bjud in dina vänner och utmana
				varandra. Du kan också delta i våra pågående <A href="/games">prisspel</A>, där alla kan delta
				gratis.
				</O>
			);
		},
		"de": (A) => {
			return (
				<O>
				Wer ist der beste Investor unter deinen Freunden oder Kollegen? Erstelle ein
				<A className="ad_blue_link" href="/new_game">privates Spiel</A>, lade deine Freunde ein und
				fordert euch gegenseitig heraus. Du kannst auch an unseren laufenden <A href="/games">Börsen
				Battles</A> teilnehmen, bei denen jeder kostenlos mitmachen kann.
				</O>
			);
		},
	},

	"cf8d784a6612": {
		"en": (site_name) => <O>Education with {site_name}</O>,
		"da": (site_name) => <O>Undervisning med {site_name}</O>,
		"sv": (site_name) => <O>Undervisning med {site_name}</O>,
		"de": (site_name) => <O>Unterrichten mit {site_name}</O>,
	},

	"3fd59aac69c9": {
		"en": (site_name) => {
			return (
				<O>
				{site_name} is currently used by a wide range of teachers as part
				of educational programs in various subjects and at different
				levels. From 7th grade to university level, the game is used for
				edutainment, motivation, and introduction to various subjects such
				as economics, mathematics, and social sciences.
				</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>
				{site_name} bruges i dag af en lang række lærere som en del af undervisningsforløb i forskellige
				fag og på mange forskellige niveauer. Helt fra 7. klasse til universitetsniveau bruges spillet som
				edutainment, motivation og indføring i mange forskellige fag, f.eks. økonomi, matematik og
				samfundsfag. Besøg vores <a alt='undervisningsside' href='https://undervisning.aktiedysten.dk/'>undervisningsside her</a>.
				</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>
				{site_name} används för närvarande av många lärare som del av undervisningsprogrammen i olika
				ämnen och på många nivåer. Från sjunde klass till universitetsnivå används spelet som edutainment,
				motivation och introduktion till en rad olika ämnen, t.ex. ekonomi, matematik och samhällskunskap.
				</O>
			);
		},
		"de": (site_name) => {
			return (
				<O>
				Heutzutage wird {site_name} von vielen Lehrkräften als Teil des Unterrichts in verschiedenen
				Fächern und Klassenstufen eingesetzt. Von der 7. Klasse bis zur Universität dient das Spiel als
				Edutainment, Motivation und Einführung in verschiedene Fächer wie Wirtschaft, Mathematik und
				Sozialkunde.
				</O>
			);
		},
	},

	"2334eeabea32": {
		"en": () => <O>Partnerships &amp; Branded Games</O>,
		"da": () => <O>Partnerskaber og skinnet spil</O>,
		"sv": () => <O>Partnerskap och varumärkesanpassade spel</O>,
		"de": () => <O>Partnerschaften und gebrandete Spiele</O>,
	},

	"4a4591adb121": {
		"en": (site_name) => {
			return (
				<O>
				In a partnership(cooperation) agreement with {site_name}, we offer
				branded games, branding on our materials, social media, and within
				our game, as well as a range of activations where you become the
				focal point.
				- Read more <a alt='partnership site' href='https://together.traderr.com/become-a-partner/'>here</a>.
				</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>
				I en samarbejdsaftale med {site_name} kan vi tilbyde skinnet spil, branding på vores materiale,
				sociale medier og i vores spil samt en række aktiveringer, hvor I bliver omdrejningspunktet. - Læs
				ere <a alt='partnerside' href='https://sammen.aktiedysten.dk/'>her</a>.
				</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>
				I ett samarbetsavtal med {site_name} kan vi erbjuda varumärkesanpassade spel, branding i vårt
				material, i sociala medier och i våra spel samt ett antal aktiveringar där ni står i centrum för
				uppmärksamheten. - Läs mer <a alt='partnerside' href='https://samman.aktiekampen.se/bli-partner/'>här</a>.
				</O>
			);
		},
		"de": (site_name) => {
			return (
				<O>
				Im Rahmen einer Zusammenarbeit mit {site_name} bieten wir gebrandete Börsenspiele an. Hier steht
				eure Marke im Mittelpunkt: im Spiel, in unserem Marketingmaterial, in den sozialen Medien sowie in
				einer Reihe von Aktivierungen. – Jetzt
				<a alt='koorporation' href='https://kooperation.investspiel.de/werde-partner/'>mehr erfahren</a>.
				</O>
			);
		},
	},

	"51841d8bbad7": {
		"en": () => <O>Get Started and Tutorials</O>,
		"da": () => <O>Kom i gang og tutorials</O>,
		"sv": () => <O>Komma igång och handledning</O>,
		"de": () => <O>Erste Schritte und Tutorials</O>,
	},

	"271372781eb9": {
		"en": (A, site_name) => {
			return (
				<O>
				We want to ensure that you have the best experience when using
				{site_name}, and that's why we have gathered everything you need to
				know to use the platform in our tutorials. Read more in our <A href='/faq'>FAQ</A>.
				</O>
			);
		},
		"da": (A, site_name) => {
			return (
				<O>
				Vi vil sikre, at du har den bedste oplevelse, når du er på {site_name} og derfor har vi samlet
				alt, hvad du behøver at for at kunne bruge platformen i vores tutorials - Læs <a href='https://inspiration.aktiedysten.dk/category/vejledninger/'
				alt='tutorials'>mere her</a>.
				</O>
			);
		},
		"sv": (A, site_name) => {
			return (
				<O>
				Vi vill säkra att du får den bästa upplevelsen när du är på {site_name} och därför har vi samlat
				allt du behöver för att kunna använda plattformen i våra handledningar - Se
				<a target="_blank" rel="noopener noreferrer" alt='tutorial' href='https://vimeo.com/848298269/0a16fc94e8'>mer här</a>.
				</O>
			);
		},
		"de": (A, site_name) => {
			return (
				<O>
				Wir möchten sicherstellen, dass du auf {site_name} die besten Erfahrungen machst. Deshalb haben
				wir in unseren Tutorials alles zusammengetragen, was du für die Nutzung der Plattform brauchst.
				Finde deine Antworten in den <A href='/faq'>FAQ</A>.
				</O>
			);
		},
	},

	"19dbea236453": {
		"en": () => <O>Getting Started</O>,
		"da": () => <O>Sådan kommer du i gang</O>,
		"sv": () => <O>Hur du kommer igång</O>,
		"de": () => <O>So legst du los</O>,
	},

	"e781c4bd77f3": {
		"en": () => <O>Explore assets exclusively from Germany</O>,
		"da": () => <O>Udforsk værdipapirer eksklusivt fra Tyskland</O>,
		"sv": () => <O>Utforska tillgångar exklusivt från Tyskland</O>,
		"de": () => <O>Erkunde deutsche Wertpapiere</O>,
	},

	"fbaefea0a880": {
		"en": () => <O>You're unsubscribing from the emails that show you how your games are developing. Sure you want to risk forgetting to trade when something happens?</O>,
		"da": () => <O>Du er ved at framelde dig emails der fortæller dig, hvordan det går med dine spil. Sikker på at du vil risikere ikke at handle i tide, hvis det er påkrævet?</O>,
		"sv": () => <O>Du avanmäler dig från e-mail som visar hur det går med dina spel. Är du säker på att du vill riskera att glömma bort att inte handla i tid när någonting har hänt?</O>,
		"de": () => <O>Willst du dich wirklich von den E-Mails abmelden, die deinen Spielfortschritt anzeigen? Du riskierst etwas wichtiges zu verpassen und zu spät zu reagieren.</O>,
	},

	"eaa77808874a": {
		"en": () => <O>You're unsubscribing from the email that tell you when a game you're in, starts. Sure you want to risk missing the start of a game?</O>,
		"da": () => <O>Du er ved at framelde dig emails der fortæller dig, når et spil du er tilmeldt, starter. Sikker på at du vil risikere at misse starten af et spil?</O>,
		"sv": () => <O>Du avanmäler dig från e-mail som påminner dig om när ett spel startar som du är delaktig i. Är du säker på att du vill riskera att missa starten av ett spel?</O>,
		"de": () => <O>Willst du dich wirklich von den E-Mails abmelden, die Bescheid geben, wenn eines deiner Spiele beginnt? Dadurch verpasst du eventuell den Start eines Spiels.</O>,
	},

	"04fd5ad0f8ae": {
		"en": (site_name) => <O>You're unsubscribing from a service email from {site_name}.</O>,
		"da": (site_name) => <O>Du er ved at framelde dig en service email fra {site_name}.</O>,
		"sv": (site_name) => <O>Du avanmäler dig från service email från {site_name}.</O>,
		"de": (site_name) => <O>Du meldest dich von Service-E-Mails von {site_name} ab.</O>,
	},

	"d21fa24c4ce3": {
		"en": () => <O>You can also see and update your email preferences here</O>,
		"da": () => <O>Du kan også se og ændre dine e-mailindstillinger her</O>,
		"sv": () => <O>Du kan också se och uppdatera dina inställningar för din email här</O>,
		"de": () => <O>Hier kannst du deine E-Mail Einstellungen sehen und ändern.</O>,
	},

	"d462610b59ce": {
		"en": () => <O>Unsubscribe</O>,
		"da": () => <O>Frameld</O>,
		"sv": () => <O>Avanmäl</O>,
		"de": () => <O>Abmelden</O>,
	},

	"5bcb55a852a9": {
		"en": (PrefLink) => <O>The unsubscribe link is no longer valid, but you can always <PrefLink>log in and update your email preferences</PrefLink></O>,
		"da": (PrefLink) => <O>Linket er ikke længere gyldigt, men du kan altid <PrefLink>logge ind og ændre dine e-mailindstillinger</PrefLink></O>,
		"sv": (PrefLink) => <O>Länken fungerar inte längre, men du kan alltid <PrefLink>logga in och uppdatera dina email-inställningar</PrefLink></O>,
		"de": (PrefLink) => <O>Der Link zur Abmeldung ist nicht mehr gültig. Du kannst dich jedoch jederzeit <PrefLink>einloggen und deine E-Mail-Einstellungen ändern</PrefLink></O>,
	},

	"e13acc646160": {
		"en": () => <O>You have been unsubscribed; you will no longer receive this type of email.</O>,
		"da": () => <O>Du er nu afmeldt og vil ikke modtage denne type e-mail fremover.</O>,
		"sv": () => <O>Du har avregistrerats och kommer inte längre få denna typ av e-mail framöver</O>,
		"de": () => <O>Du hast dich erfolgreich abgemeldet. Du wirst von nun an keine E-Mails dieser Art mehr erhalten</O>,
	},

	"4607d9a132e0": {
		"en": () => <O>Swedish collections</O>,
		"da": () => <O>Svenske lister</O>,
		"sv": () => <O>Svenska kollektioner</O>,
		"de": () => <O>Schwedische Liste</O>,
	},

	"16db4cc5821c": {
		"en": () => <O>Selected Swedish stocks</O>,
		"da": () => <O>Udvalgte svenske aktier</O>,
		"sv": () => <O>Utvalda svenska aktier</O>,
		"de": () => <O>Ausgewählte schwedische Aktien</O>,
	},

	"6bc53e790a3e": {
		"en": () => <O>Random Swedish stocks</O>,
		"da": () => <O>Tilfældige svenske værdipapirer</O>,
		"sv": () => <O>Slumpässigt utvalda svenska aktier</O>,
		"de": () => <O>Zufällige schwedische Aktien</O>,
	},

	"91b424a0fb95": {
		"en": () => <O>Search Swedish assets</O>,
		"da": () => <O>Søg svenske værdipapirer</O>,
		"sv": () => <O>Sök på svenska tillgångar</O>,
		"de": () => <O>Schwedische Wertpapiere suchen</O>,
	},

	"353d9b174e45": {
		"en": () => <O>Explore assets exclusively from Sweden</O>,
		"da": () => <O>Udforsk værdipapirer eksklusivt fra Sverige</O>,
		"sv": () => <O>Utforska tillgångar exklusivt från Sverige</O>,
		"de": () => <O>Erkunde schwedische Wertpapiere</O>,
	},

	"1ab3ec765285": {
		"en": () => <O>Your email has been verified!</O>,
		"da": () => <O>Din e-mail adresse er blevet verificeret!</O>,
		"sv": () => <O>Din e-mail adress har blivit verifierad!</O>,
		"de": () => <O>Ihre E-Mail-Adresse ist bestätigt!</O>,
	},

	"556b5511507e": {
		"en": () => <O>You can safely close this window</O>,
		"da": () => <O>Du kan roligt lukke dette vindue</O>,
		"sv": () => <O>Du kan med säkerhet stänga detta fönster</O>,
		"de": () => <O>Du kannst dieses Fenster jetzt schließen.</O>,
	},

	"cf8968c9ca21": {
		"en": (A) => <O>You can find out more about us at our <A className="ad_blue_link" href="/about">about</A> page.</O>,
		"da": (A) => <O>Du kan finde ud af mere om os <A className="ad_blue_link" href="/about">her</A>.</O>,
		"sv": (A) => <O>Du kan få reda på mer om oss på våran <A className="ad_blue_link" href="/about">Om</A> page.</O>,
		"de": (A) => <O>Erfahre auf der Seite <A className="ad_blue_link" href="/about">Über Uns</A> mehr über Investspiel.</O>,
	},

	"0c1d4aafe516": {
		"en": (A) => <O>Curious about what markets we support? Have a look at our <A className="ad_blue_link" href="/explore">explore</A> section.</O>,
		"da": (A) => <O>Nysgerrig i forhold til hvilke markeder vi understøtter? Kig på vores <A className="ad_blue_link" href="/explore">udforsk</A> sektion.</O>,
		"sv": (A) => <O>Nyfiken på vilka marknader vi stödjer? Kika på våran <A className="ad_blue_link" href="/explore">Utforska</A> section.</O>,
		"de": (A) => <O>Neugierig, welche Märkte wir unterstützen? Hier findest du eine vollständige <A className="ad_blue_link" href="/explore">Liste</A>.</O>,
	},

	"bae8096f16b1": {
		"en": (A) => <O>We also have some <A className="ad_blue_link" href="/faq">frequently asked questions</A>.</O>,
		"da": (A) => <O>Vi har også nogle <A className="ad_blue_link" href="/faq">ofte stillede spørgsmål</A>.</O>,
		"sv": (A) => <O>Vi har också några <A className="ad_blue_link" href="/faq">Vanliga frågor</A>.</O>,
		"de": (A) => <O>Noch Fragen? Wir haben <A className="ad_blue_link" href="/faq">hier</A> einige FAQs für dich gesammelt.</O>,
	},

	"fb2212e60b7a": {
		"en": () => <O>Verifying email... please wait.</O>,
		"da": () => <O>Verificerer e-mail... vent venligst.</O>,
		"sv": () => <O>Verifierar e-mail... vänligen vänta.</O>,
		"de": () => <O>Verifizierung der E-Mail... Bitte einen Moment warten.</O>,
	},

	"e784e9e60bc1": {
		"_context": "privacy_policy",
		"en": (site_base) => {
			return (
				<O>
				<h1 className="ad_privacy_policy">Privacy Policy</h1>

				<p>Last updated: May 5th, 2022</p>

				<p>Aktiedysten ApS, VAT No. 38 96 22 56, ("us", "we", or "our") is responsible for
				the https://{site_base} website (the "Service").</p>

				<p>This page informs you of our policies regarding the collection, use, and
				disclosure of personal data when you use our Service and the choices you have
				associated with that data.</p>

				<p>We use your data to provide and improve the Service. By using the Service,
				you agree to the collection and use of information in accordance with this
				policy. Unless otherwise defined in this Privacy Policy, terms used in this
				Privacy Policy have the same meanings as in our Terms and Conditions,
				accessible from https://{site_base}</p>

				<h2 className="ad_privacy_policy pt-3">Purpose of collecting and processing your personal data</h2>

				<p>We collect several different types of information for various purposes to
				provide and improve our Service to you. You can read the purpose, and what
				information we collect and process about you, below.</p>

				<h3 className="ad_privacy_policy">Types of Data Collected</h3>

				<h4 className="ad_privacy_policy">Personal Data</h4>

				<p>While using our Service, we may ask you to provide us with certain
				personally identifiable information that can be used to contact or identify you
				("Personal Data"). Personally identifiable information may include, but is not
				limited to:</p>

				<ul>
					<li>E-mail</li>
					<li>Screen Name</li>
					<li>Cookies</li>
					<li>Usage Data</li>
				</ul>

				<h4 className="ad_privacy_policy">Usage Data</h4>

				<p>We may also collect information how the Service is accessed and used ("Usage
				Data"). This Usage Data may include information such as:</p>

				<ul>
					<li>your computer's internet protocol address (e.g. IP address)</li>
					<li>browser type</li>
					<li>browser version</li>
					<li>the pages of our Service that you visit</li>
					<li>the time and date of your visit</li>
					<li>the time spent on those pages, unique device identifiers and other diagnostic data.</li>
				</ul>

				<h2 className="ad_privacy_policy pt-3">Legal Basis</h2>
				<p>
				The legal basis for us processing your information is to fulfill an agreement based on you
				creating a user account, cf. databeskyttelsesforordningens artikel 6, stk.1, litra b, and
				our legitimate interest in delivering a good Service and be as relevant in our marketing of said
				Service, cf. databeskyttelsesforordningens artikel 6, stk. 1, litra f.</p>

				<h2 className="ad_privacy_policy pt-3">Purpose</h2>

				<p>Aktiedysten ApS uses the collected data for various purposes:</p>
				<ul>
					<li>To provide and maintain the Service</li>
					<li>To notify you about changes to our Service</li>
					<li>To allow you to participate in interactive features of our Service when you choose to do so</li>
					<li>To provide customer care and support</li>
					<li>To provide analysis or valuable information so that we can improve the Service</li>
					<li>To monitor the usage of the Service</li>
					<li>To detect, prevent and address technical issues</li>
				</ul>


				<h2 className="ad_privacy_policy pt-3">Transfer Of Data</h2>
				<p>Your information, including Personal Data, may be transferred to — and
				maintained on — computers located outside of your state, province, country or
				other governmental jurisdiction where the data protection laws may differ than
				those from your jurisdiction.</p>

				<p>If you are located outside Denmark and choose to provide
				information to us, please note that we transfer the data,
				including Personal Data, to Denmark and process it there.</p>

				<p>Your consent to this Privacy Policy followed by your submission of such
				information represents your agreement to that transfer.</p>

				<p>Aktiedysten ApS will take all steps reasonably necessary to ensure that
				your data is treated securely and in accordance with this Privacy Policy and
				no transfer of your Personal Data will take place to an organization or a
				country unless there are adequate controls in place including the security of
				your data and other personal information.</p>

				<p>Your personal information will not be transferred or handled in any way to
				data processors or data responsible outside of the EU/EØS.</p>

				<h2 className="ad_privacy_policy pt-3">Disclosure Of Data</h2>

				<h3 className="ad_privacy_policy">Legal Requirements</h3>
				<p>Aktiedysten ApS may disclose your Personal Data in the good faith belief
				that such action is necessary to:</p>
				<ul>
					<li>To comply with a legal obligation.</li>
					<li>To protect and defend the rights or property of Aktiedysten ApS.</li>
					<li>To prevent or investigate possible wrongdoing in connection with
					the Service.</li>
					<li>To protect the personal safety of users of the Service or the
					public.</li>
					<li>To protect against legal liability.</li>
				</ul>

				<h2 className="ad_privacy_policy pt-3">Security Of Data</h2>
				<p>The security of your data is important to us, but remember that no method of
				transmission over the Internet, or method of electronic storage is 100% secure.
				While we strive to use commercially acceptable means to protect your Personal
				Data, we cannot guarantee its absolute security.</p>

				<h2 className="ad_privacy_policy pt-3">Removal Of Data</h2>
				<p>You can always get your account and all data deleted, by clicking "preferences"
				followed by the "DELETE ACCOUNT" button. This deletes your account and data permanently
				and irrevocably.</p>

				<h2 className="ad_privacy_policy pt-3">Service Providers</h2>
				<p>We may employ third party companies and individuals to facilitate our
				Service ("Service Providers"), to provide the Service on our behalf, to perform
				Service-related services or to assist us in analyzing how our Service is
				used.</p> <p>These third parties have access to your Personal Data only to
				perform these tasks on our behalf and are obligated not to disclose or use it
				for any other purpose.</p>

				<h2 className="ad_privacy_policy pt-3">Links To Other Sites</h2>
				<p>Our Service may contain links to other sites that are not operated by us. If
				you click on a third party link, you will be directed to that third party's
				site. We strongly advise you to review the Privacy Policy of every site you
				visit.</p>
				<p>We have no control over and assume no responsibility for the
				content, privacy policies or practices of any third party sites or
				services.</p>

				<h2 className="ad_privacy_policy pt-3">Children's Privacy</h2>
				<p>Our Service does not address anyone under the age of 18 ("Children").</p>
				<p>We do not knowingly collect personally identifiable information from anyone
				under the age of 18. If you are a parent or guardian and you are aware that
				your Children has provided us with Personal Data, please contact us. If we
				become aware that we have collected Personal Data from children without
				verification of parental consent, we take steps to remove that information from
				our servers.</p>

				<h2 className="ad_privacy_policy pt-3">Tracking &amp; Cookies Data</h2>
				<p><A href="/cookies" className="ad_blue_link">About cookies</A></p>

				<h2 className="ad_privacy_policy pt-3">Your rights</h2>
				<p>
				<ul>
					<li>You have the right to gain insight into your personal information.</li>
					<li>You can object to collection and processing of your personal information.</li>
					<li>You have the right to correction and/or deletion of your personal information.</li>
					<li>You have the right to request a limitation of your stored personal information.</li>
					<li>You have the right to revoke your consent.</li>
					<li>Under certain circumstances can you request a copy of your personal information,
					and request a transfer of said information to another data controller (data portability).</li>
				</ul>
				</p>


				<h2 className="ad_privacy_policy pt-3">Contact and complaint</h2>
				<p>If you have any questions about this Privacy Policy or would like to complain about the way
				we treat your personal information, you're welcome to contact us:</p>
				<ul>
					<li>By email: info@{site_base}</li>
					<li>By phone number: +45 53539791</li>
				</ul>
				<p>
				If you want to file an official complaint, please send it to:
				</p>
				<p>
				Datatilsynet<br />
				Borgergade 28, 5.<br />
				DK-1300 København K<br />
				Denmark<br />
				Phone: +45 3319 3200<br />
				E-mail: dt@datatilsynet.dk<br />
				</p>

				<h2 className="ad_privacy_policy pt-3">Changes To This Privacy Policy</h2>
				<p>
				We reserve the right to make changes to this Privacy Policy from time to time. When
				a change has happened, the data in the top of this policy will be updated. The at any time
				applicable Privacy Policy can always be found at https://{site_base}/privacy.
				</p>

				</O>
			);
		},
		"da": (site_base) => {
			return (
				<O>
				<h1 className="ad_privacy_policy">Privatlivspolitik</h1>

				<p>Sidst opdateret: 5. maj, 2022</p>

				<p>Aktiedysten ApS, CVR-nr. 38962256, ("os", "vi", eller "vores") er
				ansvarlige for hjemmesiden https://{site_base} ("Tjenesten").</p>

				<p>Denne side informerer dig om vores politikker vedrørende indsamling,
				brug og videregivelse af personlige data, når du bruger vores Tjeneste og
				de valg du har forbundet med de data.</p>

				<p>Vi bruger dine data til at levere og forbedre Tjenesten. Ved at bruge Tjenesten
				accepterer du indsamlingen og brugen af oplysninger i overensstemmelse med denne
				politik. Medmindre andet fremgår i denne Privatlivspolitik, har betingelserne i
				denne Privatlivspolitik samme betydning som i vores Brugsbetingelser
				tilgængelig på https://{site_base}</p>

				<h2 className="ad_privacy_policy pt-3">Formålet med indsamlingen og behandling
				af dine personoplysninger</h2>

				<p>Vi indsamler flere forskellige typer oplysninger til forskellige formål for at
				levere og forbedre vores Tjeneste til dig. Nedenfor kan du læse formål, hvilke
				oplysninger vi indsamler og behandler om dig. </p>

				<h3 className="ad_privacy_policy">Typer af Data Indsamlet</h3>

				<h4 className="ad_privacy_policy">Personoplysninger</h4>

				<p>Når du bruger vores Tjeneste, kan vi spørge, om du ønsker at give os dine
				personlige identificerbare oplysninger, som vi kan bruge til at kontakte eller
				identificere dig ("Personoplysninger"). Personlige identificerbare oplysninger
				kan indeholde, men er ikke begrænset til:</p>
				<ul>
					<li>E-mail</li>
					<li>Skærmnavn</li>
					<li>Cookies</li>
					<li>Brugsdata</li>
				</ul>

				<h4 className="ad_privacy_policy">Brugsdata</h4>

				<p>Vi kan indsamle oplysninger, om hvordan Tjenesten er tilgået og brugt
				("Brugsdata"). Denne brugsdata kan inkludere oplysninger såsom:</p>
				<ul>
					<li>din computers Internetprotokol (IP-adresse),</li>
					<li>browser type,</li>
					<li>browser version,</li>
					<li>de sider på Tjenesten du besøger,</li>
					<li>tidspunkt og dato på dit besøg,</li>
					<li>tiden brugt på de sider, entydige enhedsidentifikatorer og anden
					diagnostisk data.</li>
				</ul>

				<h2 className="ad_privacy_policy pt-3">Retsgrundlaget</h2>
				<p>
				Retsgrundlaget for vores behandling af dine oplysninger sker af hensyn til
				opfyldelse af en aftale truffet på baggrund af din henvendelse til os ved
				oprettelse af en profil, jf. databeskyttelsesforordningens artikel 6, stk.1,
				litra b, og vores legitime interesse i at levere en god Tjeneste til dig og
				være så relevant i vores markedsføring i Tjenesten til dig som muligt, jf.
				databeskyttelsesforordningens artikel 6, stk. 1, litra f.
				</p>

				<h2 className="ad_privacy_policy pt-3">Formål</h2>

				<p>Aktiedysten ApS behandler den indsamlede data til nedenstående formål:</p>
				<ul>
					<li>Til at levere og opretholde Tjenesten.</li>
					<li>Til at give dig besked om ændringer på vores Tjeneste.</li>
					<li>Til at give dig adgang til forskellige interaktive funktioner på vores Tjeneste, når du vælger at gøre det.</li>
					<li>Til at yde kundepleje og -service.</li>
					<li>Til at analysere oplysninger så vi kan forbedre Tjenesten.</li>
					<li>Til at overvåge brugen af Tjenesten.</li>
					<li>Til at opdage, forhindre og håndtere tekniske problemer.</li>
				</ul>

				<h2 className="ad_privacy_policy pt-3">Overførsel Af Data</h2>
				<p>Dine oplysninger, herunder Personoplysninger, kan blive overført til - og
				bevaret på - computere placeret uden for din stat, region, land eller anden
				stats jurisdiktion, hvor databeskyttelsesloven er anderledes end fra din
				jurisdiktion.</p>
				<p>Hvis du befinder dig uden for Danmark og vælger at give
				oplysninger til os, vær da opmærksom på, at vi overfører dataene, herunder
				Personoplysninger, til Danmark og behandler det der.</p>
				<p>Dit samtykke til denne Privatlivspolitik efterfulgt af din indsendelse
				af de oplysninger repræsenterer din enighed af den overførsel.</p>
				<p>Aktiedysten ApS tager alle rimelige nødvendige trin for at forsikre, at
				din data bliver behandlet sikkert og i overensstemmelse med denne
				Privatlivspolitik, og ingen overførsel af dine Personoplysninger vil
				blive videresendt til en organisation eller land medmindre, der er
				tilstrækkelig sikkerhed herunder beskyttelse af dine data eller andre personlige
				oplysninger.</p>

				<h2 className="ad_privacy_policy pt-3">Videregivelse Af Data</h2>

				<h3 className="ad_privacy_policy">Lovlige Bestemmelser</h3>
				<p>Aktiedysten ApS kan videregive dine Personoplysninger, hvis vi i god tro mener,
				det er nødvendigt for at:</p>
				<ul>
					<li>Overholde gældende love</li>
					<li>Beskytte og forsvare Aktiedysten ApS’s rettigheder eller ejendom</li>
					<li>Forhindre eller undersøge mulige ulovligheder i forbindelse med Tjenesten</li>
					<li>Beskytte personlig sikkerhed af brugere af Tjenesten eller offentligheden</li>
					<li>Beskytte mod retsansvar</li>
				</ul>

				<h2 className="ad_privacy_policy pt-3">Beskyttelse Af Data</h2>
				<p>Sikkerheden af dine oplysninger er vigtig for os, men husk at intet system til
				overførsel over internettet eller system til elektronisk opbevaring er 100%
				sikker. Selvom vi bestræber os efter at gøre en kommerciel rimelig indsats for
				at beskytte dine Personoplysninger, kan vi ikke garantere fuldstændig
				sikkerhed.</p>

				<h2 className="ad_privacy_policy pt-3">Sletning Af Data</h2>
				<p>Du kan altid slette konto og alle data, ved at klikke "indstillinger", efterfulgt
				af "SLET DIN KONTO". Dette sletter din konto og alle data permanent og irreversibelt.</p>

				<h2 className="ad_privacy_policy pt-3">Tjenesteudbydere</h2>
				<p>Vi kan ansætte tredjepartsfirmaer og -individer til at lette vores Tjeneste
				("Tjenesteudbydere"), til at levere Tjenesten på vegne af os, til at udføre
				opgaver relateret til Tjenesten eller hjælpe os med at analysere på, hvordan
				vores Tjeneste bliver brugt.</p>
				<p>Disse tredjeparter har adgang til dine Personoplysninger udelukkende
				for at udføre disse opgaver på vegne af os og er forpligtet til ikke at
				videregive eller bruge dem til andre formål.</p>

				<h2 className="ad_privacy_policy pt-3">Links Til Andre Sider</h2>
				<p>Vores Tjeneste kan indeholde links til andre sider, som ikke er styret af os.
				Hvis du klikker på et tredjepartslink, vil du blive sendt videre til den
				tredjeparts hjemmeside. Vi anbefaler kraftigt, at du gennemgår
				Privatlivspolitikken på hver side, du besøger.</p>
				<p>Vi har ingen kontrol over og tager ikke ansvar for indholdet,
				privatlivspolitikker eller fremgangsmåder på nogen tredjepartssider eller
				-tjenester.</p>

				<h2 className="ad_privacy_policy pt-3">Sporing &amp; Cookies Data</h2>
				<p><A href="/cookies" className="ad_blue_link">Om cookies</A></p>

				<h2 className="ad_privacy_policy pt-3">Dine rettigheder</h2>
				<ul>
					<li>Du har ret til at få indsigt i dine personoplysninger.</li>
					<li>Du kan gøre indsigelse mod indsamling og videre behandling af dine personoplysninger.</li>
					<li>Du har du ret til berigtigelse eller sletning af dine personoplysninger.</li>
					<li>Du har ret til at anmode om begrænsning af dine personoplysninger.</li>
					<li>Du har altid ret til at tilbagekalde dit samtykke.</li>
					<li>Under visse omstændigheder kan du også anmode om at modtage en
					kopi af dine personoplysninger, samt anmode om transmission af dine
					personoplysninger til en anden dataansvarlig (dataportabilitet).</li>
				</ul>

				<h2 className="ad_privacy_policy pt-3">Kontakt og klage</h2>
				<p>Hvis du har nogen spørgsmål til denne Privatlivspolitik, eller ønsker at
				klage over den måde vi behandler dine personoplysninger på, er du velkommen
				til at kontakte os:</p>
				<ul>
					<li>På email: info@{site_base}</li>
					<li>På telefon: +45 53539791</li>
				</ul>
				<p>Hvis du ønsker at indgive en klage, kan denne sendes til:</p>
				<p>
				Datatilsynet<br />
				Borgergade 28, 5. Sal<br />
				1300 København K<br />
				Telefon: 3319 3200<br />
				E-mail: dt@datatilsynet.dk<br />
				</p>

				<h2 className="ad_privacy_policy pt-3">Ændringer af Privatlivspolitikken</h2>
				<p>Vi forbeholder os retten til at foretage ændringer af denne
				Privatlivspolitik fra tid til anden. Ved ændringer vil datoen øverst i
				privatlivspolitikken blive ændret. Den til enhver tid gældende
				Privatlivspolitik vil være tilgængelig på https://{site_base}/privacy.</p>
				</O>
			);
		},
		"sv": (site_base) => {
                        return (
                                <O>
                                <h1 className="ad_privacy_policy">Integritetspolicy</h1>

                                <p>Ikraftträdandedatum: 5 maj 2022</p>

                                <p>Aktiedysten ApS ("oss", "vi", eller "våran") styr hemsidan https://aktiekampen.se
                                ("Tjänsten").</p>

                                <p>Denna sida informerar dig om våra policies angående samlandet, användandet och avslöjandet av personlig data när du använder dig av vår Tjänst samt de val du har i anslutning med denna data.</p>

                                <p>Vi använder din data för att leverera och förbättra vår Tjänst. Vid användning av vår Tjänst accepterar du insamlingen och användandet av data i överrensstämmelse med denna policy.
                                Om inte annat framgår i denna Integritetspolicy så är villkoren i denna Integritetspolicy samma som villkoren angående användandet av Tjänsten som du kommer åt här
                               https://aktiekampen.se</p>

                                <h2 className="ad_privacy_policy pt-3">Insamling och användande av information</h2>

                                <p>Vi samlar in flera typer av information av olika anledningar för att förbättra vår Tjänst för dig.</p>

                                <h3 className="ad_privacy_policy">Olika typer av data som vi samlar in</h3>

                                <h4 className="ad_privacy_policy">Personlig data och information</h4>

                                <p>När du använder vår Tjänst så kan det hända att vi frågar dig efter en viss typ av personligt identifierbar information som kan användas för att kontakta och identifiera dig ("Personlig data"). Personlig identifierbar information kan inkludera, men är inte bundet till:</p>
                                <ul>
                                        <li>Email adress</li>
                                        <li>Cookies</li>
                                        <li>Användardata</li>
                                </ul>

                                <h4 className="ad_privacy_policy">Användardata</h4>

                                <p>Vi kan samla in information om hur Tjänsten nås och används ("Användardata"). Användardata kan inkludera information som din dators IP-adress, typ av webbläsare, webbläsarversion, vilka sidor du besöker på vår hemsida, tidpunkten och datumet av ditt besök, tiden du spenderat på dessa sidor, unika enhetsidentifierare och annan diagnostiska data.</p>

                                <h2 className="ad_privacy_policy pt-3">Användning av Data</h2>

                                <p>Aktiedysten ApS använder data som samlas in för olika ändamål:</p>
                                <ul>
                                        <li>Till att leverera och upprätthålla Tjänsten</li>
                                        <li>Till att ge dig besked om ändringar som sker i Tjänsten</li>
                                        <li>Till att ge dig möjlighet att ta del av interaktiva funktioner på vår Tjänst, när du väljer att göra det.</li>
                                        <li>Till att tillhandahålla kundvård och service</li>
                                        <li>Till att analysera information så att vi kan förbättra tjänsten</li>
                                        <li>För att övervaka användningen av tjänsten</li>
                                        <li>Till att upptäcka, förebygga och hantera tekniska problem.</li>
                                </ul>

                                <h2 className="ad_privacy_policy pt-3">Överföring av Data</h2>
                                <p>Din information, som inkluderar Personlig Data, kan bli överförd till - och underhållen på - datorer som är lokerade utanför din stat, region, land eller annan statlig jurisdiktion, där dataskyddslagen skiljer sig från din jurisdiktion.</p>
                                <p>Om du befinner dig utanför Danmark och väljer att ge information till oss, var uppmärksam på att vi överför data, inklusive Personlig information, till Danmark och behandlar det där.</p>
                                <p>Ditt samtycke till denna Integritetspolicy följt av ditt inlämnande av sådan information innebär ditt godkännande för en sådan överföring .</p>
                                <p>Aktiedysten ApS tar alla nödvändiga steg för att försäkra att
                                din data blir behandlat säkert och i överensstämmelse med denna
                                Integritetspolicy, och ingen överföring av din Personliga information vidarebefordras till en organisation eller land om det inte finns adekvata kontroller och säkerhet som beskyddar din Data och Personliga information.</p>

                                <h2 className="ad_privacy_policy pt-3">Offentliggörande Av Data</h2>

                                <h3 className="ad_privacy_policy">Rättsliga Bestämmelser</h3>
                                <p>Aktiedysten ApS kan offentliggöra din Personliga Data i god tro att en sådan handling är nödvändig för att:</p>
                                <ul>
                                        <li>Följa gällande lagar</li>
                                        <li>Beskydda och försvare Aktiedysten ApS’s rättigheter eller egendom</li>
                                        <li>Förhindra eller undersöka möjliga oegentligheter i samband med Tjänsten</li>
                                        <li>Beskydda den personliga säkerheten av användare av Tjänsten eller offentligheten</li>
                                        <li>Beskydda mot juridiskt ansvar</li>
                                </ul>

                                <h2 className="ad_privacy_policy pt-3">Dataskydd</h2>
                                <p>Säkerheten av din information och data är viktig för oss, men kom ihåg att inget system för överföring av information via Internet eller system för elektronisk lagring är 100% säkert. Medans vi strävar efter att använda oss av kommerciellt rimliga insatser för att skydda sin Personliga data, så kan vi inte garantera din säkerhet.</p>

				<h2 className="ad_privacy_policy pt-3">Radering Av Data</h2>
				<p>Du kan alltid få ditt konto och all data raderad genom att klicka på "Inställningar"
				följt av knappen "RADERA KONTO". Detta raderar ditt konto och dina data permanent
				och oåterkalleligt.</p>

                                <h2 className="ad_privacy_policy pt-3">Tjänsteleverantörer</h2>
                                <p>Vi kan anställa tredjepartsföretag och individer för att underlätta vår Tjänst
                                ("Tjänsteleverantörer"), att tillhandahålla tjänsten på våra vägnar, att utföra uppgifter relaterade till Tjänsten eller hjälper oss att analysera hur vår Tjänst används.</p>
                                <p>Dessa tredjeparter har åtgång till din Personliga information endast
                                för at udföra dessa uppgifter å våra vägnar och de är förpliktade till att inte
                                avslöja eller använda dem till andra ändamål.</p>

                                <h2 className="ad_privacy_policy pt-3">Länkar Till Andra Sidor</h2>
                                <p>Våran Tjänst kan innehålla länkar till andra sidor som inte styrs av oss.
                                Om du klickar på en tredjepartslänk så blir du riktad till den tredjepartssidan. Vi rekommenderar starkt att du granskar Integritetspolicyn på varje sida du besöker.</p>
                                <p>Vi har ingen kontroll över och tar inget ansvar för innnehållet,
                                Integritetspolicyn eller metoder på någen tredjepartssida eller
                                -tjänst.</p>

                                <h2 className="ad_privacy_policy pt-3">Barns Integritet</h2>
                                <p>Våran Tjänst vänder sig inte till någon under 18 år ("Barn").</p>
                                <p>Vi samlar inte medvetet in personlig information från personer under 18 år.
                                Om du är en förälder eller förmyndare och är medveten om, att dina Barn har gett oss Personlig Information, vänligen kontakta oss. Om vi upptäcker att vi har
                                samlat in Personlig information från barn utan samtycke från föräldrar,
så tar vi steg för att ta bort informationen från våra servrar.</p>


				<h2 className="ad_privacy_policy pt-3">Spårnings- och cookiesdata</h2>
				<p><A href="/cookies" className="ad_blue_link">Om cookies</A></p>

				<h2 className="ad_privacy_policy pt-3">Dina rättigheter</h2>

				<ul>
					<li>Du har rätt att få tillgång till dina personuppgifter</li>
					<li>Du kan invända mot insamling och vidarebehandling av dina personuppgifter.</li>
					<li>Du har rätt att rätta eller rader dina personuppgifter</li>
					<li>Du har rätt att begära begränsning av dina personuppgifter</li>
					<li>Du har alltid rätt att återkalla ditt samtycke</li>
					<li>Under vissa omständigheter kan du också begära att få en kopia av din personliga information,
					samt begära överföring av dina personuppgifter till en annan personuppgiftsansvarig
					(dataportabilitet)</li>
				</ul>
				<h2 className="ad_privacy_policy pt-3">Kontakt och klagomål</h2>
				<p>Om du har några frågor till denna Integritetspolicy, eller vill klaga på hur vi behandlar dina
				personuppgifter så är du välkommen att kontakta oss:</p>

				<ul>
					<li>På email: info@{site_base}</li>
					<li>På telefon +45 53539791</li>
				</ul>
				<p>Om du vill lämna in ett klagomål så kan det skickas till</p>
				<p>
				Datatilsynet<br />
				Borgergade 28, 5. Sal<br />
				1300 København K<br />
				Telefon: 3319 3200<br />
				E-mail: dt@datatilsynet.dk<br />
				</p>

				<h2 className="ad_privacy_policy">Ändringar av Integritetspolicyn</h2>
				<p>Vi förbehåller oss rätten att göra ändringar av denna Integritetspolicy. Vid eventuella ändringar kommer
				datumet överst i Integritetspolicyn att ändras. Den aktuella integritetspolicyn kommer att finnas
				tillgänglig på https://{site_base}/privacy</p>

                                <p>Du rekommenderas att granska denna Integritetspolicy med jämna mellanrum för eventuella ändringar. Ändringar till denna Integritetspolicy träder i kraft när de är publicerade på denna sida.</p>

                                </O>
                        );
                },
		"de": (site_base) => {
                        return (
                                <O>
                                <h1 className="ad_privacy_policy">Datenschutzerklärung</h1>

                                <p>Letzte Aktualisierung: 20. April 2020</p>

				<p>Aktiedysten ApS, USt-Nr. 38 96 22 56, ("uns", "wir" oder "unser") ist verantwortlich
				für die Website https://{site_base} (der "Service").</p>

				<p>Auf dieser Seite informieren wir Sie über unsere Richtlinien in Bezug auf die
				Erfassung, Verwendung und Weitergabe von personenbezogenen Daten bei der Nutzung unseres
				Dienstes sowie über die Auswahlmöglichkeiten, die Sie im Zusammenhang mit diesen Daten
				haben.</p>

				<p>Wir verwenden Ihre Daten zur Bereitstellung und Verbesserung des Dienstes. Durch die
				Nutzung des Dienstes erklären Sie sich mit der Erfassung und Nutzung von Informationen
				in Übereinstimmung mit dieser Richtlinie einverstanden. Sofern in dieser
				Datenschutzrichtlinie nicht anders definiert, haben Begriffe, die in dieser
				Datenschutzrichtlinie verwendet werden, die gleiche Bedeutung wie in unseren Allgemeinen
				Geschäftsbedingungen, abrufbar unter: https://{site_base}).</p>

                                <h2 className="ad_privacy_policy pt-3">Zweck der Erfassung und Verarbeitung Ihrer persönlichen Daten</h2>

				<p>Wir sammeln verschiedene Arten von Informationen für verschiedene Zwecke, um Ihnen
				unseren Service anbieten und diesen weiter verbessern zu können. Im Folgenden können Sie
				nachlesen, zu welchem Zweck wir welche Informationen über Sie sammeln und
				verarbeiten.</p>

                                <h3 className="ad_privacy_policy">Arten der gesammelten Daten</h3>

                                <h4 className="ad_privacy_policy">Persönliche Daten</h4>

				<p>Während der Nutzung unseres Dienstes werden Sie möglicherweise gebeten, uns bestimmte
				personenbezogene Daten zur Verfügung zu stellen, mit denen wir Sie kontaktieren oder
				identifizieren können ("Persönliche Daten"). Persönlich identifizierbare Informationen
				können unter anderem Folgendes umfassen:</p>

                                <ul>
                                        <li>E-Mail</li>
                                        <li>Benutzername</li>
                                        <li>Cookies</li>
                                        <li>Nutzungsdaten</li>
                                </ul>

                                <h4 className="ad_privacy_policy">Nutzungsdaten</h4>

				<p>Wir erfassen möglicherweise auch Informationen darüber, wie auf den Dienst
				zugegriffen wird und wie er genutzt wird ("Nutzungsdaten"). Diese Nutzungsdaten können
				Informationen enthalten, wie zum Beispiel:</p>

				<ul>
					<li>die Internet-Protokoll-Adresse Ihres Computers (IP-Adresse)</li>
					<li>den Browser-Typ</li>
					<li>die Browser-Version</li>
					<li>die Seiten unseres Dienstes, die Sie besuchen sowie die Uhrzeit und das Datum Ihres Besuchs</li>
					<li>die auf diesen Seiten verbrachte Zeit, eindeutige Gerätekennungen und andere Diagnosedaten</li>
				</ul>

                                <h2 className="ad_privacy_policy">Rechtliche Grundlage</h2>
				<p>Die rechtliche Grundlage für die Verarbeitung Ihrer Daten durch uns ist die
				Ausführung einer Vereinbarung, die auf der Einrichtung eines Benutzerkontos durch Sie
				beruht, vgl. Artikel 6 (1) (b) der Datenschutzverordnung, sowie unser legitimes
				Interesse an der Bereitstellung eines hervorragenden Dienstes sowie an der Vermarktung
				dieses Dienstes, vgl. Artikel 6 Absatz 2 der Datenschutzverordnung 1, Punkt f.</p>

                                <h2 className="ad_privacy_policy">Verwendungszweck</h2>

                                <p>Aktiedysten ApS verwendet die gesammelten Daten für verschiedene Zwecke:</p>

				<ul>
					<li>Um unseren Dienst bereitzustellen und aufrechtzuerhalten.</li>
					<li>Um Sie über Änderungen an unserem Service zu informieren.</li>
					<li>Um Ihnen die Teilnahme an interaktiven Funktionen unseres Dienstes zu ermöglichen, falls Sie sich dafür entscheiden.</li>
					<li>Um Kundenservice und Support zu bieten.</li>
					<li>Um Analysen oder wertvolle Informationen bereitzustellen, damit wir unseren Dienst verbessern können.</li>
					<li>Um die Nutzung des Dienstes zu überwachen.</li>
					<li>Um technische Probleme zu erkennen, zu verhindern und zu beheben.</li>
				</ul>


                                <h2 className="ad_privacy_policy">Datenübertragung</h2>
				<p>Ihre Informationen, einschließlich personenbezogener Daten, können an Computer
				außerhalb Ihres Staates, Ihrer Provinz, Ihres Landes oder einer anderen staatlichen
				Gerichtsbarkeit, in der die Datenschutzgesetze möglicherweise von denen Ihrer
				Gerichtsbarkeit abweichen, übertragen und auf diesen Computern verwaltet werden.</p>

				<p>Wenn Sie sich außerhalb Dänemarks befinden und sich dafür entscheiden, uns
				Informationen zur Verfügung zu stellen, beachten Sie bitte, dass wir die Daten,
				einschließlich personenbezogener Daten, nach Dänemark übertragen und dort
				verarbeiten.</p>

				<p>Ihre Zustimmung zu dieser Datenschutzrichtlinie, gefolgt von der Übermittlung dieser
				Informationen, stellt Ihre Zustimmung zu dieser Übertragung dar.</p>

				<p>Aktiedysten ApS wird alle vernünftigerweise notwendigen Schritte unternehmen, um
				sicherzustellen, dass Ihre Daten sicher und in Übereinstimmung mit dieser
				Datenschutzrichtlinie behandelt werden und keine Übertragung Ihrer persönlichen Daten an
				eine Organisation oder ein Land erfolgt, es sei denn, es gibt angemessene
				Kontrollmaßnahmen, einschließlich der Sicherheit Ihrer Daten und anderer persönlicher
				Informationen.</p>

				<p>Ihre persönlichen Daten werden in keiner Weise an Datenverarbeiter oder
				Datenverantwortliche außerhalb der EU/EWR übertragen oder behandelt.</p>

                                <h2 className="ad_privacy_policy">Veröffentlichung von Daten</h2>

                                <h3 className="ad_privacy_policy">Rechtliche Anforderungen</h3>

				<p>Aktiedysten ApS kann Ihre persönlichen Daten im guten Glauben offen legen, wenn eine 
				solche Maßnahme notwendig ist, um:</p>

				<ul>
					<li>einer rechtlichen Verpflichtung nachzukommen.</li>
					<li>das Recht oder Eigentum von Aktiedysten ApS zu schützen und zu verteidigen.</li>
					<li>mögliches Fehlverhalten im Zusammenhang mit dem Dienst zu verhindern oder zu untersuchen.</li>
					<li>die persönliche Sicherheit der Benutzer des Dienstes oder der Öffentlichkeit zu schützen.</li>
					<li>gegen rechtliche Haftung geschützt zu sein.</li>

				</ul>

                                <h2 className="ad_privacy_policy">Datensicherheit</h2>
                                <p>Die Sicherheit Ihrer Daten ist uns wichtig, doch bedenken Sie, dass keine Methode der
				Übertragung über das Internet oder der elektronischen Speicherung zu 100 % sicher ist.
				Obwohl wir bestrebt sind, kommerziell akzeptable Mittel zum Schutz Ihrer persönlichen
				Daten einzusetzen, können wir ihre absolute Sicherheit nicht garantieren.</p>

                                <h2 className="ad_privacy_policy">Dienstanbieter</h2>
                                <p>Wir können Drittfirmen und Einzelpersonen damit beauftragen, unseren Dienst zu
				unterstützen ("Dienstanbieter"), den Dienst in unserem Namen zu erbringen,
				dienstleistungsbezogene Dienstleistungen zu erbringen oder uns bei der Analyse der
				Nutzung unseres Dienstes zu unterstützen.</p>

                                <p>Diese Drittanbieter haben nur Zugang zu Ihren persönlichen Daten, um diese Aufgaben
				in unserem Namen durchzuführen, und dürfen diese nicht offenlegen oder für andere Zwecke
				verwenden.</p>

                                <h2 className="ad_privacy_policy">Links zu anderen Websites</h2>
                                <p>Unser Service kann Links zu anderen Websites enthalten, die nicht von uns betrieben
				werden. Wenn Sie auf den Link eines Drittanbieters klicken, werden Sie auf die Website
				dieses Drittanbieters weitergeleitet. Wir empfehlen Ihnen dringend, die
				Datenschutzrichtlinie jeder von Ihnen besuchten Website zu überprüfen.</p>

                                <p>Wir haben keine Kontrolle über und übernehmen keine Verantwortung für den Inhalt, die
				Datenschutzrichtlinien oder die Praktiken von Websites oder Diensten Dritter.</p>

                                <h2 className="ad_privacy_policy">Datenschutz für Kinder</h2>
                                <p>Unser Dienst richtet sich nicht an Personen unter 18 Jahren ("Kinder").</p>

				<p>Wir erfassen keine persönlich identifizierbare Informationen von Personen unter 18 Jahren.
				Falls Sie ein Elternteil oder ein Erziehungsberechtigter sind und Sie
				wissen, dass Ihre Kinder uns persönliche Daten zur Verfügung gestellt haben,
				kontaktieren Sie uns bitte. Sollten wir Kenntnis davon erlangen, dass wir persönliche
				Daten von Kindern ohne Überprüfung der elterlichen Zustimmung gesammelt haben, ergreifen
				wir Maßnahmen, um diese Daten von unseren Servern zu entfernen.</p>

                                <h2 className="ad_privacy_policy">Tracking &amp; Cookie-Daten</h2>
				<p><A href="/cookies" className="ad_blue_link">About cookies</A></p>

				<h2 className="ad_privacy_policy">Ihre Rechte</h2>

				<ul>
					<li>Sie haben das Recht, Einsicht in Ihre persönlichen Daten zu erhalten.</li>
					<li>Sie können der Erfassung und Verarbeitung Ihrer persönlichen Daten widersprechen.</li>
					<li>Sie haben das Recht auf Richtigstellung und/oder Löschung Ihrer persönlichen Daten.</li>
					<li>Sie haben das Recht, eine Beschränkung Ihrer gespeicherten persönlichen Informationen zu verlangen.</li>
					<li>Sie haben das Recht, Ihre Zustimmung zu widerrufen.</li>
					<li>Unter bestimmten Umständen können Sie eine Kopie Ihrer persönlichen
					Informationen anfordern und eine Übertragung dieser Informationen an einen
					anderen für die Datenverarbeitung Verantwortlichen verlangen
					(Datenportabilität).</li>
				</ul>

				<h2 className="ad_privacy_policy">Kontakt und Beschwerde</h2>
				<p>Sollten Sie Fragen zu dieser Datenschutzrichtlinie haben oder sich über die Art und
				Weise beschweren wollen, wie wir Ihre persönlichen Daten behandeln, kontaktieren Sie uns
				bitte:</p>

				<ul>
					<li>Per E-Mail: info@{site_base}</li>
					<li>Per Telefon: +45 53539791</li>
				</ul>

				<p>Falls Sie eine offizielle Beschwerde einreichen möchten, senden Sie diese bitte
				an:</p>

				<p>Datatilsynet<br />
				Borgergade 28, 5.<br />
				DK-1300 København K<br />
				Dänemark<br />
				Telefon: +45 3319 3200<br />
				E-Mail: dt@datatilsynet.dk</p>

				<h2 className="ad_privacy_policy">Änderungen an dieser Datenschutzerklärung</h2>
				<p>Wir behalten uns das Recht vor, diese Datenschutzerklärung jederzeit zu ändern.
				Wenn eine Änderung eingetreten ist, wird das Datum im oberen Teil dieser Richtlinie aktualisiert.
				Die jederzeit geltende Datenschutzrichtlinie ist immer unter https://{site_base}/privacy einsehbar.</p>

                                </O>
                        );
                },
	},

	"6ff5747321cd": {
		"en": () => <O>Frequently Asked Questions</O>,
		"da": () => <O>Ofte stillede spørgsmål</O>,
		"sv": () => <O>Vanliga frågor</O>,
		"de": () => <O>Häufig gestellte Fragen</O>,
	},

	"db4555324d99": {
		"en": () => <O>See more questions and answers</O>,
		"da": () => <O>Se flere spørgsmål og svar</O>,
		"sv": () => <O>Se fler frågor och svar</O>,
		"de": () => <O>Sieh dir mehr Fragen und Antworten an</O>,
	},

	"c7ba1ee086cc": {
		"en": () => {
			return (
				<O>
					Here you will find answers to the most important questions about the
					investment game and an introduction on how to get started. If you want
					to refresh your stock knowledge, please visit our <A href="/equity-abc">Investment ABC</A>.<br />
					Please <A href="/contact">contact us</A> if you have any further questions.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
					Her finder du svar på de mest stillede spørgsmål om spillet, samt
					en introduktion til at komme i gang. Hvis du vil dykke dybere ned i
					investeringsaspekter, er du velkommen til at læse vores <A href="/equity-abc">Investerings-ABC</A>.<br />
					<A href="/contact">Kontakt os</A> venligst, hvis du har yderligere spørgsmål.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
					Här hittar du svar på de viktigaste frågorna om investeringsspelet och en
					introduktion till hur du kommer igång. Om du vill uppdatera din aktiekunskap,
					besök vår <A href="/equity-abc">Kapital ABC</A>.
					<br /><A href="/contact">Kontakta oss</A> om du har ytterligare frågor.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
					Hier bekommst du Antworten auf die wichtigsten Fragen zum Börsenspiel und eine
					Einführung wie du loslegen kannst. Wenn du dein Börsenwissen auffrischen
					möchtest, besuche unser <A href="/equity-abc">Börsenlexikon</A>.<br />
					<A href="/contact">Kontaktiere uns </A> gerne, falls du weitere Fragen hast.
				</O>
			);
		},
	},

	"8460e62cc672": {
		"_context": "faq_a_1",
		"en": (site_name) => {
			return (
				<O>
					Let's address this right away - we do not sell user data, so you can safely play on {site_name}.
					However, {site_name} does engage in exciting activations with external partners
					where users can have the opportunity to play and learn together with them. In such cases,
					user consent will always be required if there is an exchange of emails with the partner.
				</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>
					Lad os bare tage den med det samme, vi sælger ikke brugernes data, så du kan trygt spille
					på {site_name}. {site_name} laver dog en masse spændende aktiveringer sammen med eksterne
					partnere, hvor brugerne kan få lov til at lege og lære sammen med dem. Her vil det altid
					være samtykke baseret, hvis der udveksles mails med partneren.
				</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>
					Låt oss gå rakt på sak, vi säljer inte användarnas uppgifter, så du kan spela på
					{site_name} med förtroende. {site_name} gör dock många spännande aktiveringar tillsammans
					med externa partners, där användarna kan spela och lära sig tillsammans med dem. Här
					kommer möjliga e-postutbyten med partners alltid vara samtyckesbaserat.
				</O>
			);
		},
		"de": (site_name) => {
			return (
				<O>
					Um es gleich vorweg zu nehmen: Wir verkaufen keine Nutzerdaten, du kannst also bedenkenlos
					auf {site_name} üben. Stattdessen führt {site_name} viele spannende bezahlte Aktionen mit
					externen Partnern durch, bei denen du kostenlos mitspielen und lernen kannst. Nur wenn du
					deine Zustimmung gibst, können unsere Partner dir Mails schicken.
				</O>
			);
		},
	},

	"f6241867d1ad": {
		"_context": "faq_q_1",
		"en": (site_name) => <O>How does {site_name} earn money?</O>,
		"da": (site_name) => <O>Hvordan tjener {site_name} penge?</O>,
		"sv": (site_name) => <O>Hur tjänar {site_name} pengar?</O>,
		"de": (site_name) => <O>Womit verdient {site_name} Geld?</O>,
	},

	"5a478b4b600c": {
		"_context": "faq_a_2",
		"en": (site_name) => {
			return (
				<O>
				We want you to have fun and learn how to trade stocks in a playful way. Unlike
				other stock games, with {site_name} you can freely decide when, how long and how
				many games you want to play and invite your friends to do so instead of playing
				against strangers. The player earning the highest profit by smartly buying and
				selling his stocks wins. Fun and community, does that sound good?
				</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>
				Vi ønsker du har det sjovt mens du leger med værdipapirhandel. Modsat de fleste
				andre investeringsspil, kan du med {site_name} selv beslutte hvornår, hvor længe,
				mod hvem og hvor mange spil du vil spille. Den der er bedste til at investere,
				vinder.
				</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>
				Vi vill att du ska ha kul och lära dig att handla aktier på ett lekfullt sätt. Till
				skillnad från andra aktiespel, kan du med {site_name} fritt bestämma när, hur länge och
				hur många spel du vill spela och bjuda in dina vänner istället för att spela mot
				främlingar. Spelaren som tjänar den högsta vinsten genom att smart köpa och sälja sina
				aktier vinner. Kul och bra gemenskap, låter det bra?
				</O>
			);
		},
		"de": (site_name) => {
			return (
				<O>
				Wir möchten, dass du Spaß hast und spielerisch das Handeln mit Aktien lernst. Anders als
				andere Börsenspiele, kannst du bei {site_name} frei entscheiden, wann, wie lange und wie
				viele Spiele du spielen möchtest und dazu deine Freunde einladen, statt gegen Fremde zu
				spielen. Derjenige, der mit klugem Kauf und Verkauf seiner Aktien am meisten Gewinn
				macht, gewinnt. Spaß und Community, klingt das gut?
				</O>
			);
		},
	},

	"725639d40ecf": {
		"_context": "faq_a_3",
		"en": (site_name) => {
			return (
				<O>
				Yes! {site_name} was developed by two friends out of their love for trading. We
				want to pass this joy on to as many people as possible, that’s why {site_name} is
				100% free. By the way: participation in our community <A href="/games">prize games</A> is also
				free - and you can even win cool prizes!
				</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>
				Ja! {site_name} blev udviklet fordi vi ønsker så mange som muligt kan opleve
				glæden ved at investere uden risiko. Det er også gratis at deltage i vores <A href="/games">præmiespil</A>.
				</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>
				Ja! {site_name} utvecklades av två vänner på grund av deras kärlek till aktiehandel. Vi
				vill överföra denna glädje till så många människor som möjligt, det är därför
				{site_name} är 100% gratis. Förresten: deltagande i våra <A href="/games">utlottningar</A> är också gratis -
				och du kan till och med vinna coola priser!
				</O>
			);
		},
		"de": (site_name) => {
			return (
				<O>
				Ja! {site_name} wurde von zwei Freunden aus der Freude am Investieren heraus entwickelt
				- es ist also ein Herzensprojekt. Diese Freude wollen wir an möglichst viele Personen
				weitergeben, daher ist {site_name} 100% kostenlos. Übrigens: Auch die Teilnahme
				an den <A href="/games">Börsen Battles</A>, unseren Community-Gewinnspielen, ist kostenlos - und du kannst sogar
				coole Preise gewinnen!
				</O>
			);
		},
	},

	"2e695f59058e": {
		"_context": "faq_a_4",
		"en": (site_name) => {
			return (
				<O>
				<ol>
				<li>Create game: Click on "Start your first game" and fill in the game data. We
				recommend that your game should run for at least 1-2 weeks in order to
				experience the volatility of the stock prices and thus have the most realistic
				stock exchange experience. Options such as the broker commission are designed to
				make your game as realistic as possible.</li>
				<li>Invite friends: Once you have created the game, you can use the share button
				below the game chat to invite your friends to the game - this is still possible
				after the game has started.</li>
				<li>Buy stocks: Once the game has started, you can start buying stocks. Do so
				by searching in the search field or in <A href="/explore">"Explore"</A> for stocks you
				might like. The shorter your game time, the more you should look for stocks that
				potentially promise a quick stock price raise rather than falling. Analyze the stock
				chart with the prices of the past weeks and months, google the company's PE ratio (see
				stock evaluation in the <A href="/equity-abc">Investment ABC</A>) and follow the news.
				On the chart page you can find the trade button to buy stocks at the bottom left. An
				example of the chart page (Apple Inc.) can be found <A href="/ticker/NASDAQ/AAPL">here</A>.
				</li>
				<li>Diversification: As in real trading on the stock exchange, a broad
				diversification is key, i.e. you should invest in different stocks in order to
				compensate for a possibly falling stock price with other stocks.</li>
				<li>Purchase amount: Since {site_name} is a game, there is no financial risk for
				you. Therefore, you could go all in and invest all your money in different
				stocks to see what happens. You could also start with a small investment amount
				to try things out and adjust the amount later on. There is no right or wrong.</li>
				<li>Winning: Now you just need to smartly trade your stocks to end with a higher
				profit than your fellow players. In the end, the total amount matters, meaning
				both your cash and stock value. Hence you do not need to sell all your stocks
				before the game ends. Good luck!</li>
				</ol>
				</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>
				<ol>
				<li>Lav et spil: klik på "Start dit første aktiespil" og udfyld data om spillet.
				Vi anbefaler at spillet skal køre i mindst 2 uger, for at få den mest realistiske
				oplevelse. Valgmuligheder så som "Kurtage" er designet til at lave spillet så
				realistisk som muligt.</li>
				<li>Invitér dine venner: når du har lavet spillet, kan du lige under spilchatten
				finde mulighed for at dele et invitationslink med dine venner. Det er stadig muligt
				efter spillet har startet.</li>
				<li>Køb aktier: når spillet er started, kan du begynde at handle. Én mulighed
				for at finde noget at købe er enten at bruge søgefeltet øverst på siden, en anden
				er at klikke ind på vores <A href="/explore">"Udforsk"</A>-side hvor man kan udforske de forskellige markeder.
				Hvis spillet kører i kort tid, kan det betale sig at kigge efter aktier der tegner til
				at stige hurtigt. Analysér kursgrafen, ift. prisen de sidste uger og måneder, slå
				firmaets PE-værdi op (se mere i vores <A href="/equity-abc">Investerings-ABC</A> sektion) og følg nyhederne om firmaet.
				Selve købs-handlingen foregår ved at klikke på "Handl"-knappen, inde på kursgraf-siden.
				Ét eksempel på et firmas kursgraf-side (Apple Inc.) finder du <A href="/ticker/NASDAQ/AAPL">her</A>.
				</li>
				<li>Diversificering: som i virkeligheden er en diversificering godt til at sprede
				risikoen. Investér i flere forskellige brancher, for at kompensere for kursfald i aktier
				i andre brancher.</li>
				<li>Indkøbsbeløb: eftersom {site_name} er et spil, er der ingen risiko ved at
				handle. Derfor kan du gå "all in" og investere det hele, bare for at se hvad der
				sker. Du kan også starte med et lille beløb (et frimærke) til at starte med, og så
				justere efterhånden som kursen udvikler sig. Der findes ikke rigtig og forkert.</li>
				<li>Vinde: nu skal du "bare" handle dine aktier på en måde, så du slutter med højere
				profit end dine medspillere. Til slut er det det totale beløb der betyder noget, altså
				både dine kontanter og værdien af dine værdipapirer. Så du behøver ikke sælge alle dine
				værdipapirer før spillet slutter. Held og lykke!</li>
				</ol>
				</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>
				<ol>
				<li>Skapa ett spel: Klicka på "Starta ditt första spel" och fyll i speldata. Vi
				rekommenderar att ditt spel ska köras i minst 1–2 veckor för att uppleva aktiekursernas
				volatilitet och därmed ha den mest realistiska börsupplevelsen. Alternativ som
				mäklarkommissionen är utformade för att göra ditt spel så realistiskt som möjligt.</li>
				<li>Bjud in vänner: När du har skapat spelet kan du använda delningsknappen under
				spelchatten för att bjuda in dina vänner till spelet - detta är fortfarande möjligt
				efter att spelet har startat.</li>
				<li>Köp aktier: När spelet har börjat kan du börja köpa aktier. Gör det genom att söka i
				sökfältet eller i <A href="/explore">"Utforska"</A> efter aktier du kanske gillar. Ju kortare din speltid,
				desto mer bör du leta efter aktier som potentiellt lovar en snabb aktiehöjning snarare
				än att falla.  Analysera aktiekartan med priserna för de senaste veckorna och månaderna,
				googla företagets PE-kvot (se aktieutvärdering i <A href="/equity-abc">Kapital ABC</A>)
				och följ nyheterna. På kartsidan kan du hitta handelsknappen för att köpa aktier längst
				ner till vänster. Ett exempel på kartsidan (Apple Inc.) kan hittas <A href="/ticker/NASDAQ/AAPL">här</A>.
				</li>
				<li>Diversifiering: Liksom vid verklig handel på börsen är en bred diversifiering a och
				o, dvs. du bör investera i olika aktier för att kompensera för ett eventuellt fallande
				aktiekurs med andra aktier.</li>
				<li>Inköpsbelopp: Eftersom {site_name} är ett spel finns det ingen ekonomisk risk för dig.
				Därför kan du gå in och investera alla dina pengar i olika aktie för att se vad som
				händer. Du kan också börja med ett litet investeringsbelopp för att prova saker och
				justera beloppet senare. Det finns inget rätt eller fel svar.</li>
				<li>Vinna: Nu behöver du bara handla dina aktier för att sluta med en högre vinst än dina
				medspelare. I slutändan är det totala beloppet viktigt, vilket innebär både ditt
				kontantvärde och aktievärdet. Därför behöver du inte sälja alla dina aktier innan spelet
				är slut. Lycka till!</li>
				</ol>
				</O>
			);
		},
		"de": (site_name) => {
			return (
				<O>
				<ol>
				<li>Spiel erstellen: Klicke auf "Erstes Spiel starten" und fülle die Spieldaten aus.
				Wir empfehlen, dass dein Spiel mindestens 1-2 Wochen laufen sollte, um die Schwankungen
				(Volatilität) der Aktienkurse im Spiel zu erleben und somit ein möglichst reales
				Börsen-Erlebnis zu haben. Optionen wie die Vermittlungsgebühr für deine Aktienkäufe
				dienen dazu, dein Spiel so realitätsnah wie möglich zu gestalten.</li>
				<li>Freunde einladen: Wenn du das Spiel erstellt hast, kannst du unter dem Spielchat
				deine Freunde per Link zum Spiel einladen - dies funktioniert übrigens auch noch nach
				Spielstart.</li>
				<li>Aktien kaufen: Sobald das Spiel startet, kannst du mit dem Aktienkauf beginnen.
				Suche dazu im Suchfeld oder unter <A href="/explore">"Erkunden"</A> nach Aktien, die dir gefallen. Je kürzer deine
				Spieldauer, desto mehr solltest du nach Aktien schauen, die potenziell schnelle
				Kurssteigerungen versprechen, statt zunächst zu fallen. Analysiere dazu den Aktienchart
				mit den Kursen der letzten Wochen und Monate (klicke dazu auf eine Aktie), google den
				KGV des Unternehmens (siehe Aktienbewertung im <A href="/equity-abc">Börsenlexikon</A>) und verfolge die
				Nachrichten. Auf der Chart-Seite findest du unten links den Handelbutton, über den du
				die Aktie kaufen kannst. Ein Beispiel der Chart-Seite (Apple Inc.) kannst du <A href="/ticker/NASDAQ/AAPL">hier</A> sehen.
				</li>
				<li>Diversifikation: Eine breite Streuung ist wie beim echten Handeln an der Börse Key,
				d.h. du solltest in verschiedene Aktien investieren, um einen eventuell fallenden
				Aktienkurs mit den anderen Aktien ausgleichen zu können.</li>
				<li>Kaufsumme: Da es sich bei {site_name} um ein Spiel handelt, gibt es kein finanzielles
				Verlustrisiko für dich. Daher könntest du bei deinen Aktienkäufen "all in" gehen und
				dein gesamtes Geld in verschiedene Aktien investieren und sehen was passiert. Du kannst
				auch erstmal mit einer geringen Kaufsumme anfangen und dich "rantasten" und ggf.
				nachkaufen. Es gibt kein richtig oder falsch.</li>
				<li>Gewinnen: Jetzt heisst es mit klugem Kaufen und Verkaufen deiner Aktien mehr Gewinn zu
				erzielen als deine Mitspieler. Am Ende zählt dein totaler Betrag, d.h. sowohl dein Cash
				auf dem Bankkonto als auch deine Aktienwerte. Du musst also vor Spielende nicht alle
				Aktien verkaufen. Viel Erfolg!</li>
				</ol>
				</O>
			);
		},
	},

	"6d0f137d6788": {
		"_context": "faq_a_5",
		"en": (site_name) => {
			return (
				<O>
				You can also use {site_name} to track stock developments or portfolios.
				</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>
				Du kan også bruge {site_name} til at følge aktieudviklingen gennem porteføljer du selv
				sammensætter.
				</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>
				Du kan också använda {site_name} för att spåra aktieutvecklingar eller portföljer.
				</O>
			);
		},
		"de": (site_name) => {
			return (
				<O>
				Du kannst {site_name} auch zum Verfolgen deiner Aktien oder Portfolios verwenden.
				</O>
			);
		},
	},

	"9d4e39a83189": {
		"_context": "faq_q_2",
		"en": (site_name) => <O>What is the goal of {site_name}?</O>,
		"da": (site_name) => <O>Hvad er formålet med {site_name}?</O>,
		"sv": (site_name) => <O>Vad är målet med {site_name}?</O>,
		"de": (site_name) => <O>Was ist das Ziel von {site_name}?</O>,
	},

	"38ed9da6a9f1": {
		"_context": "faq_a_6",
		"en": () => {
			return (
				<O>
				We open the markets 20 minutes after their real opening. This results in the
				following opening hours (CET) from Monday to Friday:
				<ul>
				<li>German stock exchange: 9:20 a.m. to 5:15 p.m. (<a target="_blank" rel="noopener noreferrer"
				href="https://www.xetra.com/xetra-en/newsroom/trading-calendar">holidays</a>)</li>
				<li>Danish stock exchange: 9:20 a.m. to 4:45 p.m. (<a target="_blank" rel="noopener noreferrer"
				href="http://www.nasdaqomxnordic.com/tradinghours">holidays</a>)</li>
				<li>Swedish stock exchange: 9:20 a.m. to 5:30 p.m. (<a target="_blank" rel="noopener noreferrer"
				href="http://www.nasdaqomxnordic.com/tradinghours">holidays</a>)</li>
				<li>Austrian stock exchange: 9:30 a.m. to 5:15 p.m. (<a target="_blank" rel="noopener noreferrer"
				href="https://www.wienerborse.at/en/trading/trading-information/trading-calendar/">holidays</a>)</li>
				<li>Icelandic stock exchange: 9:50 a.m. to 3:30 p.m. (<a target="_blank" rel="noopener noreferrer"
				href="http://www.nasdaqomxnordic.com/tradinghours">holidays</a>)</li>
				<li>Finnish stock exchange: 10:20 a.m. to 6:30 p.m. (<a target="_blank" rel="noopener noreferrer"
				href="http://www.nasdaqomxnordic.com/tradinghours">holidays</a>)</li>
				<li>American stock exchanges: 3:50 p.m. to 9:45 p.m. (<a target="_blank" rel="noopener noreferrer"
				href="https://www.nyse.com/markets/hours-calendars">holidays</a>)</li>
				</ul>
				Commodities, currencies and cryptocurrencies can be traded 24/7 &mdash; even on weekends.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Vi åbner markederne 20 minutter efter deres åbning i virkeligheden. Det giver følgende
				åbningstider (CET) fra mandag til fredag:
				<ul>
				<li>Tyske børs: 9:20 til 17:15 (<a target="_blank" rel="noopener noreferrer"
				href="https://www.xetra.com/xetra-en/newsroom/trading-calendar">helligdage</a>)</li>
				<li>Danske børs: 9:20 til 16:45 (<a target="_blank" rel="noopener noreferrer"
				href="http://www.nasdaqomxnordic.com/tradinghours">helligdage</a>)</li>
				<li>Svenske børs: 9:20 til 17:30 (<a target="_blank" rel="noopener noreferrer"
				href="http://www.nasdaqomxnordic.com/tradinghours">helligdage</a>)</li>
				<li>Østrigske børs: 9:30 til 17:15 (<a target="_blank" rel="noopener noreferrer"
				href="https://www.wienerborse.at/en/trading/trading-information/trading-calendar/">helligdage</a>)</li>
				<li>Islandske børs: 9:50 til 15:30 (<a target="_blank" rel="noopener noreferrer"
				href="http://www.nasdaqomxnordic.com/tradinghours">helligdage</a>)</li>
				<li>Finske børs: 10:20 til 18:30 (<a target="_blank" rel="noopener noreferrer"
				href="http://www.nasdaqomxnordic.com/tradinghours">helligdage</a>)</li>
				<li>Amerikanske børser: 15:50 til 21:45 (<a target="_blank" rel="noopener noreferrer"
				href="https://www.nyse.com/markets/hours-calendars">helligdage</a>)</li>
				</ul>
				Råvarer, valuta og kryptovaluta kan handles døgnet rundt &mdash; også i weekenden.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				Vi öppnar marknaderna 20 minuter efter deras verkliga öppning. Detta resulterar i
				följande öppettider (CET) från måndag till fredag:
				<ul>
				<li>Tyska börsen: 9:20 till 17:15 (<a target="_blank" rel="noopener noreferrer"
				href="https://www.xetra.com/xetra-en/newsroom/trading-calendar">helgdagar</a>)</li>
				<li>Danska börsen: 9:20 till 16.45 (<a target="_blank" rel="noopener noreferrer"
				href="http://www.nasdaqomxnordic.com/tradinghours">helgdagar</a>)</li>
				<li>Svenska börsen: 9:20 till 17:30 (<a target="_blank" rel="noopener noreferrer"
				href="http://www.nasdaqomxnordic.com/tradinghours">helgdagar</a>)</li>
				<li>Österrikiska börsen: 9:30 till 17:15 (<a target="_blank" rel="noopener noreferrer"
				href="https://www.wienerborse.at/en/trading/trading-information/trading-calendar/">helgdagar</a>)</li>
				<li>Isländska börsen: 09:50 till 15:30 (<a target="_blank" rel="noopener noreferrer"
				href="http://www.nasdaqomxnordic.com/tradinghours">helgdagar</a>)</li>
				<li>Finska börsen: 10:20 till 18:30 (<a target="_blank" rel="noopener noreferrer"
				href="http://www.nasdaqomxnordic.com/tradinghours">helgdagar</a>)</li>
				<li>Amerikanska börser: 15:50 till 21.45 (<a target="_blank" rel="noopener noreferrer"
				href="https://www.nyse.com/markets/hours-calendars">helgdagar</a>)</li>
				</ul>
				Varor, valutor och kryptokurser kan handlas dygnet runt, även på helger.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Wir öffnen die Märkte 20 Minuten nach deren realer Öffnung. Daraus ergeben sich folgende
				Öffnungszeiten (MEZ) von Montag bis Freitag:
				<ul>
				<li>Deutsche Börse: 9:20 bis 17:15 Uhr (<a target="_blank" rel="noopener noreferrer"
				href="https://www.xetra.com/xetra-de/newsroom/handelskalender">Feiertage</a>)</li>
				<li>Dänische Börse: 9:20 bis 16:45 Uhr (<a target="_blank" rel="noopener noreferrer"
				href="http://www.nasdaqomxnordic.com/tradinghours">Feiertage</a>)</li>
				<li>Schwedische Börse: 9:20 bis 17:30 Uhr (<a target="_blank" rel="noopener noreferrer"
				href="http://www.nasdaqomxnordic.com/tradinghours">Feiertage</a>)</li>
				<li>Österreichische Börse: 9:30 bis 17:15 Uhr (<a target="_blank" rel="noopener noreferrer"
				href="https://www.wienerborse.at/handel/handelsinformationen/handelskalender/">Feiertage</a>)</li>
				<li>Isländische Börse: 9:50 bis 15:30 Uhr (<a target="_blank" rel="noopener noreferrer"
				href="http://www.nasdaqomxnordic.com/tradinghours">Feiertage</a>)</li>
				<li>Finnische Börse: 10:20 bis 18:30 Uhr (<a target="_blank" rel="noopener noreferrer"
				href="http://www.nasdaqomxnordic.com/tradinghours">Feiertage</a>)</li>
				<li>Amerikanische Börsen: 15:50 bis 21:45 Uhr (<a target="_blank" rel="noopener noreferrer"
				href="https://www.nyse.com/markets/hours-calendars">Feiertage</a>)</li>
				</ul>
				Rohstoffe, Währungen und Kryptowährungen können übrigens rund um die Uhr gehandelt
				werden, auch am Wochenende.
				</O>
			);
		},
	},

	"d315049a7d8d": {
		"_context": "faq_a_7",
		"en": () => {
			return (
				<O>
				We display real-time prices with a delay to limit the benefit of knowing and
				tracking real-time stock prices. This gives everyone a fair chance.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				For at eliminere potentielle fordele ved at kende til realtidskursen på en aktie, har vi
				besluttet at indlægge en forsinkelse der passer til forsinkelsen i vores kurser ift. realtid.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				För att eliminera potentiella fördelar med att veta realtidskursen på aktier, har vi
				beslutat att lägga in en fördröjelse som matchar fördröjelsen med vår data jämfört med
				realtidskursen.
				</O>
			);
		},
		"de": () => {
			return (
				<O> Wir zeigen die Echtzeitkurse verzögert an, um den Vorteil zu limitieren, der von dem
				Kennen und Verfolgen der Echtzeitkurse von Aktien stammen. Dadurch haben alle eine faire
				Chance
				</O>
			);
		},
	},

	"96c309f1b1d5": {
		"_context": "faq_a_15",
		"en": () => {
			return (
				<O>
				In general, when you press the “Request cancelation” button, your order will still go through
				and not be canceled. We want to avoid that players that have access to real-time stock prices
				cancel an order they know will change to their disadvantage. There are some exceptions,
				however: If you place an order outside of stock exchange operating hours, you can cancel the
				order until the stock exchange opens. If an order does not go through after the 15-minute
				trade delay due to technical or data issues, you can cancel the order. When trading a
				low-volume stock in a volume game, the order may split up according to the actual volumes
				traded. In this case, you can cancel the upcoming trades.<br />
				Please note: There is a 15 min delay on cancelations.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Som hovedregel vil din handel stadig gå igennem selvom du beder om at annullere handlen, da
				du har anmodet om annullering efter du anmodede om handlen. Dette er for at undgå at folk der
				kender realtidskurser, kan annullere handler alt efter hvad der sker i virkeligheden, som
				{site_name} er 15 minutter forsinket ift. Der er dog undtagelser: Hvis du har sat handlen i
				kø udenfor et aktiemarkeds åbningstid, kan du annullere den inden markedet åbner. Hvis en
				ordre ikke går igennem pga. tekniske problemer med aktiens data (“handelen hænger”) kan du
				også annullere den, ligesom du også kan hvis det er en ordre i en aktie med lav volumen, der
				bliver splittet op i flere handler, og ordren derfor tager lang tid om at gå igennem.<br />
				Kort sagt; alt hvad der stadig ligger i kø 15 minutter efter der er anmodet om annullering,
				kan annulleres.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				I allmänhet, när du trycker på knappen "Begär avbokning" kommer din beställning
				fortfarande att gå igenom och inte annulleras. Vi vill undvika att spelare som har
				tillgång till aktiekurser i realtid avbryter en order som de vet kommer att ändras till
				deras nackdel. Det finns dock några undantag: Om du lägger en beställning utanför
				börsens öppettider kan du avbryta beställningen tills börsen öppnar. Om en beställning
				inte går igenom efter 15 minuters handelsfördröjning på grund av tekniska eller
				dataproblem, kan du avbryta beställningen. Vid handel med en aktie med låg volym i ett
				volymspel kan ordern delas upp efter de faktiska omsatta volymerna. I det här fallet kan
				du avbryta de kommande affärerna.<br />
				Observera: Det finns en fördröjning på 15 minuter vid avbokningar.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Wenn du auf den Button "Abbruchen" klickst, wird deine Order generell trotzdem
				ausgeführt und nicht storniert. Wir möchten damit vermeiden, dass Spieler mit Zugang zu
				Echtzeit-Aktienkursen eine Order stornieren, von der sie wissen, dass sie sich zu ihrem
				Nachteil verändert. Es gibt einige Ausnahmen: Wenn du eine Order außerhalb der
				Börsenöffnungszeiten aufgibst, kannst du diese stornieren, bis die Börse öffnet. Wenn eine
				Order nach der 15-minütigen Handelsverzögerung aufgrund von technischen oder Datenproblemen
				nicht ausgeführt wird, kannst du sie stornieren. Wenn du eine Aktie mit geringem Volumen in
				einem Spiel mit aktiviertem Handelsvolumen handelst, kann es sein, dass die Order auf mehrere
				Handel aufgeteilt wird. In dem Fall kannst du die anstehenden Trades stornieren.<br />
				Bitte beachte: Es gibt eine 15-minütige Verzögerung beim Abbruch einer Order.
				</O>
			);
		},
	},

	"c5057179d310": {
		"_context": "faq_a_17",
		"en": (site_name) => {
			return (
				<O>
					There are plenty of good reasons to use {site_name} even if you're not actively participating
					in a game. {site_name} tracks real stock prices, making it a great tool to keep an eye on
					market developments. Additionally, we regularly publish articles on our inspiration page,
					host webinars, podcasts, and much more to expand your knowledge.
				</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>
					Der er masser af gode grunde til at bruge {site_name}, også selvom du ikke er aktiv i et
					spil. {site_name} følger nemlig rigtige aktiekurser, så det er et godt værktøj til at
					holde øje med udviklingen på markedet. Foruden det, laver vi løbende artikler på
					inspirationssiden, webinarer, podcast og en masse mere, hvor du kan udvide din viden.
				</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>
					Det finns många goda skäl att använda {site_name} även om du inte är aktiv i ett spel.
					{site_name} följer verkliga aktiekurser, det är därför ett utmärkt verktyg för att hålla
					ett öga på marknadsutvecklingen. Dessutom publicerar vi regelbundet artiklar på
					inspirationssidan, webbseminarier, poddar och mycket annat där du kan utöka dina
					kunskaper.
				</O>
			);
		},
		"de": (site_name) => {
			return (
				<O>
					Es gibt viele gute Gründe, {site_name} zu nutzen, selbst wenn du nicht an einem Spiel
					teilnimmst. {site_name} nutzt reale Aktienkurse und ist damit ein großartiges Werkzeug, um
					die Marktentwicklung im Auge zu behalten. Außerdem veröffentlichen wir regelmäßig Artikel
					auf der Inspirationsseite, Webinare, Podcasts und vieles mehr, mit denen du dein Wissen
					erweitern kannst.
				</O>
			);
		},
	},

	"478f4a2c510e": {
		"_context": "faq_a_18",
		"en": (site_base) => {
			return (
				<O>
					If you don't receive a confirmation email, please send the name and email address
					associated with the user account to info@{site_base}.
				</O>
			);
		},
		"da": (site_base) => {
			return (
				<O>
					Hvis du ikke modtager en bekræftelsesmail, skal du sende navn og e-mail på den bruger, der
					er taler om til info@{site_base}.
				</O>
			);
		},
		"sv": (site_base) => {
			return (
				<O>
					Om du inte får något bekräftelsemejl, skicka namn och e-postadress på användaren i fråga
					till info@{site_base}.
				</O>
			);
		},
		"de": (site_base) => {
			return (
				<O>
					Wenn du keine Bestätigungs-E-Mail erhalten hast, sende den Namen und die E-Mail-Adresse
					des betreffenden Nutzers an info@{site_base}.

				</O>
			);
		},
	},

	"6a27c8395400": {
		"_context": "faq_q_18",
		"en": () => <O>I'm not receiving my confirmation email?</O>,
		"da": () => <O>Jeg har ikke modtaget min bekræftelsesmail?</O>,
		"sv": () => <O>Jag får inget bekräftelsemejl?</O>,
		"de": () => <O>Ich habe keine Bestätigungs-E-Mail erhalten</O>,
	},

	"9b5c104e509e": {
		"_context": "faq_q_19",
		"en": () => <O>Can I create a company game for my organization or target audience?</O>,
		"da": () => <O>Kan jeg lave et firmaspil for mit firma eller målgruppe?</O>,
		"sv": () => <O>Kan jag skapa ett företagsspel för mitt företag?</O>,
		"de": () => <O>Kann ich ein Firmenspiel für mein Unternehmen erstellen?</O>,
	},

	"732ee8899364": {
		"_context": "faq_a_20",
		"en": (site_base) => {
			return (
				<O>
					It could be due to a genuine significant increase in the stock's value. However, if your
					friend suddenly surpasses you by 1,000% in your stock duel, it could also be due to a
					reverse stock split. If you suspect a stock has been split, please feel free to contact us
					at info@{site_base}.
				</O>
			);
		},
		"da": (site_base) => {
			return (
				<O>
					Det kan skyldes at aktien reelt er steget markant og stigningen er reel, men er din ven ud
					af det blå kommet 1.000% foran dig, i jeres aktiedyst, kan det også skyldes et omvendt
					aktiesplit? Hvis du mistænker en aktie for at være splittet, må du meget gerne kontakte os
					på info@{site_base}.
				</O>
			);
		},
		"sv": (site_base) => {
			return (
				<O>
					Det kan vara så att aktien faktiskt har stigit kraftigt och att ökningen är verklig, men
					om din vän helt plötsligt ligger 1 000 % före dig i din aktietävling, kan det också bero
					på en omvänd aktiesplit? Om du misstänker att en aktie har delats upp, vänligen kontakta
					oss på info@{site_base}.
				</O>
			);
		},
		"de": (site_base) => {
			return (
				<O>
					Das kann daran liegen, dass die Aktie tatsächlich stark gestiegen ist und der Anstieg real
					ist. Aber wenn dein Freund in eurem Börsenspiel plötzlich mit 1.000% vor dir liegt,
					könnte das auch auf einen umgekehrten Aktiensplit hindeuten. Wenn du den Verdacht hast,
					dass eine Aktie gesplittet wurde, kontaktiere uns bitte unter info@{site_base}.
				</O>
			);
		},
	},

	"3c32852b08da": {
		"_context": "faq_q_20",
		"en": () => <O>A stock has increased significantly, what could be the reason?</O>,
		"da": () => <O>En aktie er steget markant, hvad kan det skyldes?</O>,
		"sv": () => <O>En aktie har stigit kraftigt, vad kan det bero på?</O>,
		"de": () => <O>Eine Aktie ist stark gestiegen, was könnte der Grund dafür sein?</O>,
	},

	"6949f5922a2b": {
		"_context": "faq_a_21",
		"en": () => {
			return (
				<O>
					Shorting stocks involves significant risks and can be challenging for beginners to
					understand. Therefore, we currently do not offer shorting on the platform.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
					Shorting er behæftet med en meget stor risiko og kan for nybegynderen være svær at forstå.
					Derfor har vi nuværende ikke shorting på platformen.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
					Shorting är mycket riskabelt och kan vara svårt för nybörjare att förstå. Därför finns
					f.n. inte blankning på plattformen.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
					Leerverkäufe sind sehr riskant und für Anfänger schwer zu verstehen. Aus diesem Grund
					bieten wir derzeit keine Leerverkäufe auf der Plattform an.
				</O>
			);
		},
	},

	"274d2c507773": {
		"_context": "faq_a_22",
		"en": () => {
			return (
				<O>
					Once a game has started, you cannot remove a player from it.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
					Du kan ikke fjerne en spiller fra din dyst efter den er startet.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
					Du kan inte avlägsna en spelare från din tävling efter att den har börjat.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
					Du kannst keinen Spieler aus deinem Börsenspiel entfernen, nachdem es begonnen hat.
				</O>
			);
		},
	},

	"04db110d236d": {
		"_context": "faq_a_23",
		"en": () => {
			return (
				<O>
					Currency exchange is always possible in banks, so it remains also an option on {site_name}.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
					Man kan altid veksle penge i banken, derfor er det også altid en mulighed her på {site_name}.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
					Du kan alltid växla pengar på banken, och därför finns den möjligheten alltid tillgänglig här på {site_name}.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
					Du kannst jederzeit Geld bei der Bank umtauschen, deshalb ist es auch bei {site_name} immer eine Option.
				</O>
			);
		},
	},

	"a3683cd37ccf": {
		"_context": "faq_a_24",
		"en": () => {
			return (
				<O>
					Unfortunately, we do not currently handle dividends on stocks, and we understand that
					stocks tend to drop after dividend payouts. However, this applies to everyone equally.
					Therefore, it is currently advisable to keep an eye on dividend payout dates. That way,
					you have the opportunity to sell before the payout and buy back at a lower price the next
					day.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
					Vi håndterer desværre ikke udbytter på aktier pt. og vi ved godt at aktier falder efter en
					udbytteudbetaling, men det er ens for alle. Derfor er det lige nu at holde øje med
					hvornår, der udbetales udbytte. Så har du muligheden for at sælge inden, og så købe
					tilbage billigere dagen efter.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
					Tyvärr hanteras inte utdelningar på aktier f.n. och vi vet mycket väl att aktier faller
					efter en utdelning, men det är lika för alla.  Därför gäller det att hålla ett öga på när
					utdelningar sker. Då har du möjlighet att sälja innan och sedan köpa tillbaka billigare
					dagen efter.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
					Leider zahlen wir derzeit keine Dividenden auf Aktien aus. Wir wissen, dass Aktienkurse
					nach einer Dividendenzahlung fallen, aber das ist für alle Spieler gleich. Deshalb
					solltest du ein Auge darauf werfen, wann Dividenden gezahlt werden. Das gibt dir die
					Möglichkeit, sie vorher zu verkaufen und am nächsten Tag günstiger zurückzukaufen.
				</O>
			);
		},
	},

	"f8e2c78094ed": {
		"_context": "faq_q_24",
		"en": () => <O>How do you handle dividends on stocks?</O>,
		"da": () => <O>Hvordan håndteres udbytte fra værdipapirer?</O>,
		"sv": () => <O>Hur hanterar ni utdelning på aktier?</O>,
		"de": () => <O>Wie handhabt ihr Dividenden auf Aktien?</O>,
	},

	"b8be126a36c2": {
		"_context": "faq_q_23",
		"en": () => <O>Why can't I disable currencies in my game?</O>,
		"da": () => <O>Hvorfor kan jeg ikke slå valuta fra i mit spil?</O>,
		"sv": () => <O>Varför går inte valuta att avaktivera i mitt spel?</O>,
		"de": () => <O>Warum kann ich keine Währungen aus meinem Spiel entfernen?</O>,
	},

	"f498322f7b07": {
		"_context": "faq_q_22",
		"en": () => <O>Is it possible to remove a person from a game?</O>,
		"da": () => <O>Kan man fjerne en spiller fra et spil?</O>,
		"sv": () => <O>Är det möjligt att avlägsna en person från ett aktiespel?</O>,
		"de": () => <O>Ist es möglich, eine Person aus einem Börsenspiel zu entfernen?</O>,
	},

	"e8f595f43a4d": {
		"_context": "faq_q_21",
		"en": () => <O>Is it possible to short stocks?</O>,
		"da": () => <O>Kan man shorte aktier?</O>,
		"sv": () => <O>Är det möjligt att blanka aktier i Aktiekampen</O>,
		"de": () => <O>Ist es möglich, eine Aktie zu shorten?</O>,
	},

	"e52c42f872c4": {
		"_context": "faq_a_19",
		"en": () => {
			return (
				<O>
					A stock game is a fun and engaging way to involve your target audience. In the game,
					participants can trade stocks with fictional money, but with real-time stock updates. This
					provides an opportunity for an enjoyable competition with dialogue, learning, and play -
					regardless of whether the market goes up or down. So if you want to create an engaging and
					relevant experience for your target audience, a branded stock game is a fantastic
					opportunity to reach out, strengthen relationships, and activate your colleagues, network
					group, or meet the target audience.
					Read more here about how we can customize the game to align with your brand identity.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
					Et aktiespil er en sjov og inddragende måde at engagere sin målgruppe på,. I spillet kan
					deltagerne handle aktier for fiktive penge, men med de rigtige liveopdaterede aktiekurser.
					Det giver mulighed for en sjov dyst med dialog, læring og leg - uanset om markedet går op
					eller ned. Så hvis man ønsker at skabe en engagerende og vedkommende oplevelse for sin
					målgruppe, er et brandet aktiespil en fantastisk mulighed for at nå ud til, styrke
					relationen og aktivere sine kollegaer, netværksgruppen eller til at møde målgruppen.
					Læs mere her, om hvordan vi kan gøre spillet til jeres og i overensstemmelse med jeres
					brandidentitet
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
					Ett aktiespel är ett roligt och involverande sätt att engagera din målgrupp. I spelet kan
					deltagarna handla med aktier för fiktiva pengar, men med verkliga uppdaterade aktiekurser.
					Detta möjliggör en rolig tävling med dialog, lärande och lek - oavsett om marknaden går
					upp eller ner. Så om du vill skapa en engagerande och relevant upplevelse för din målgrupp
					är ett aktiespel med varumärke ett utmärkt tillfälle att nå ut, stärka relationen och
					aktivera dina kollegor, din nätverksgrupp eller att träffa målgruppen.
					Läs mer här om hur vi kan göra spelet till ditt och i linje med din varumärkesidentitet.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
					Ein Börsenspiel ist eine unterhaltsame Möglichkeit, um deine Zielgruppe einzubinden. Im
					Spiel können die Teilnehmer Aktien mit fiktivem Geld, aber zu realen, stets aktuellen
					Aktienkursen handeln. Das ermöglicht einen unterhaltsamen Wettbewerb mit Fokus auf Dialog,
					Lernen und Spielen – unabhängig davon, ob der Markt gerade steigt oder fällt. Wenn du also
					ein fesselndes und relevantes Erlebnis für deine Zielgruppe schaffen willst, ist ein
					gebrandetes Börsenspiel eine großartige Möglichkeit, um deine Kollegen, deine
					Networking-Gruppe oder deine Zielgruppe zu erreichen, sie zu aktivieren und eure
					Beziehungen zu stärken.
					Erfahre jetzt mehr dazu, wie wir das Spiel zu deinem Eigenen machen und mit deiner
					Markenidentität in Einklang bringen können.
				</O>
			);
		},
	},

	"7c307ec492c3": {
		"_context": "faq_q_15",
		"en": () => <O>Why can I not cancel my trade?</O>,
		"da": () => <O>Hvorfor kan jeg ikke annullere min ordre?</O>,
		"sv": () => <O>Varför kan jag inte avbryta min affär?</O>,
		"de": () => <O>Warum kann ich meine Order nicht löschen?</O>,
	},

	"8700218a4b15": {
		"_context": "faq_a_8",
		"en": () => {
			return (
				<O>
				If you buy or sell stocks while the stock exchange is closed, you will find
				your order in your trading queue. It can be found on your <A href="/">starting page</A> between
				your game graph and the game chat.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Hvis du køber og sælger aktier mens markedet er lukket, kan du finde din planlagte
				handler i din handelskø. Den er på <A href="/">startsiden</A> mellem grafen og spil-chatten.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				Om du köper eller säljer aktier medan börsen är stängd, hittar du din beställning i din
				kö. Den kan hittas på din <A href="/">startsida</A> mellan din spelgrafik och spelchatten.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Wenn du Aktien kaufst oder verkaufst, während die Börse geschlossen ist, findest du
				deine Order in der Warteschleife. Diese befindet sich auf deiner <A href="/">Startseite</A>, zwischen
				deinem Spielgraph und dem Spielchat.
				</O>
			);
		},
	},

	"e84c41c2e3e9": {
		"_context": "faq_a_9",
		"en": () => {
			return (
				<O>
				Yes. To do so, click the "Edit" button in your game. You can edit the game name,
				the maximum amount in one equity, the broker commission and the markets (the
				latter only if no stocks from that market have been traded in the game yet).
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Ja. Klik på knappen "Rediger" i dit spil. Du kan redigere navnet, max. beløb i hvert
				værdipapir, kurtage og markederne (det sidste dog kun hvis ingen aktier fra markedet har
				været handlet i spillet endnu).
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				Ja. För att göra det, klicka på knappen "Redigera" i ditt spel. Du kan redigera
				spelnamnet, det maximala beloppet i ett eget kapital, mäklarkommissionen och marknaderna
				(det senare endast om inga aktier från den marknaden har handlats i spelet ännu).
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Ja. Klicke dazu auf den "Bearbeiten"-Button in deinem Spiel. Du kannst den Spielnamen,
				den maximalen Betrag pro Anlage, die Vermittlungsgebühr und die Märkte bearbeiten
				(letztere nur, falls in dem Spiel noch keine Aktien des Marktes gekauft wurden).
				</O>
			);
		},
	},

	"9b6e45828fbb": {
		"_context": "faq_a_10",
		"en": () => {
			return (
				<O>
				You can add stocks that you would like to track but might not want to trade yet
				to your shortlist. That way you can easily view them at the bottom of your <A href="/">starting
				page</A>. To do so, tick the box "Added to shortlist" below the stock's
				trade button. The numbers display the change in stock price today compared to 1
				month ago.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Du kan tilføje aktier du vil følge, men ikke nødvendigvis handle endnu, til shortlisten.
				På den måde kan du nemt finde dem nederst på din <A href="/">startside</A>. Ving checkboksen "Tilføjet
				til shortlist" af. Den findes lige under handelsknappen. Tallene viser prisen i dag,
				sammenlignet med for 1 måned siden.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				Du kan lägga till aktier som du vill spåra men som kanske inte vill handla ännu till din
				kortlista. På så sätt kan du enkelt se dem längst ner på <A href="/">startsidan</A>. För att göra det,
				kryssa i rutan "Tillagd till shortlist" under aktiens handelsknapp. Siffrorna visar
				förändringen i aktiekursen idag jämfört med för en månad sedan.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Du kannst Aktien, die du gerne verfolgen aber nicht sofort kaufen möchtest, auf deine
				Shortlist setzen. So hast du sie unten auf deiner <A href="/">Startseite</A> immer im Blick. Aktiviere
				dazu unter dem Handelbutton der Aktie die Box "Zur Shortlist hinzugefügt". Die
				Kennzahlen in der Shortlist zeigen die Kursveränderung von heute im Vergleich zu vor 1
				Monat an.
				</O>
			);
		},
	},

	"7dfbbc9ad53c": {
		"_context": "faq_a_11",
		"en": () => {
			return (
				<O>
				On your starting page you can click on the name of your fellow player below game
				participants to see their portfolio. If you are wondering why the portfolio is
				empty, but the player has a lot of money in the account, that is because he or
				she sold all the stocks and has only cash left.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Du kan klikke på navnet på medspilleren på spilsiden for at se deres portefølje. Hvis du
				spekulerer over hvorfor listen er tom, men spilleren har mange penge på kontoen, er det
				fordi de har solgt alt og kun har kontanter.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				På din startsida kan du klicka på namnet på din medspelare under speldeltagarna för att
				se deras portfölj. Om du undrar varför portföljen är tom, men spelaren har mycket pengar
				på kontot, beror det på att han eller hon sålde alla aktier och bara har kontanter kvar.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Auf deiner Startseite kannst du unter Spielteilnehmer auf den gewünschten Namen deines
				Mitspielers klicken, um dessen Portfolio zu sehen. Wenn du dich fragst weshalb das
				Portfolio leer ist, dein Mitspieler aber viel Geld auf dem Konto hat, liegt das daran,
				dass er oder sie alle Aktien verkauft hat und nur noch Bargeld übrig hat.
				</O>
			);
		},
	},

	"a3447285eed0": {
		"_context": "faq_q_12",
		"en": () => <O>What are <i>achievements</i>?</O>,
		"da": () => <O>Hvad er <i>bedrifter</i>?</O>,
		"sv": () => <O>Vad är <i>bedrifter</i>?</O>,
		"de": () => <O>Was sind <i>Erfolge</i>?</O>,
	},

	"8d3d66a27b77": {
		"_context": "faq_q_11",
		"en": () => <O>Where can I see the stocks the other game players have bought?</O>,
		"da": () => <O>Hvor kan jeg se de aktier, de andre spillere har købt?</O>,
		"sv": () => <O>Var kan jag se de aktier som de andra spelarna har köpt?</O>,
		"de": () => <O>Wo sehe ich, welche Aktien meine Mitspieler gekauft haben?</O>,
	},

	"8eb224898b3b": {
		"_context": "faq_q_10",
		"en": () => <O>What do I use the shortlist for?</O>,
		"da": () => <O>Hvad bruger jeg shortlisten til?</O>,
		"sv": () => <O>Vad använder jag shortlistan för?</O>,
		"de": () => <O>Wofür nutze ich die Shortlist?</O>,
	},

	"4a60483acaed": {
		"_context": "faq_q_9",
		"en": () => <O>Can I edit a game?</O>,
		"da": () => <O>Kan jeg redigere et spil?</O>,
		"sv": () => <O>Kan jag redigera ett spel?</O>,
		"de": () => <O>Kann ich ein Spiel nachträglich bearbeiten?</O>,
	},

	"6c49e76f8f3c": {
		"_context": "faq_q_8",
		"en": () => <O>Where can I see my planned stock transactions?</O>,
		"da": () => <O>Hvor kan jeg se mine planlagte handler?</O>,
		"sv": () => <O>Var kan jag se mina planerade aktietransaktioner?</O>,
		"de": () => <O>Wo sehe ich meine geplanten Aktiengeschäfte?</O>,
	},

	"d9f84ed76411": {
		"_context": "faq_q_7",
		"en": () => <O>Why is there a 15 minutes delay on trades with stocks?</O>,
		"da": () => <O>Hvorfor er der 15 minutters forsinkelse på handel med aktier?</O>,
		"sv": () => <O>Varför är det en 15 minuters fördröjning på handel med aktier?</O>,
		"de": () => <O>Warum ist der Handel mit Aktien um 15 Minuten verzögert?</O>,
	},

	"e49949714334": {
		"_context": "faq_q_6",
		"en": () => <O>When are the stock exchanges open?</O>,
		"da": () => <O>Hvornår er aktiemarkederne åbne?</O>,
		"sv": () => <O>When are the stock exchanges open?</O>,
		"de": () => <O>Wann sind die Börsen geöffnet?</O>,
	},

	"a5e608556ca2": {
		"_context": "faq_q_5",
		"en": () => <O>Don’t want to play a game?</O>,
		"da": () => <O>Vil du ikke spille et spil?</O>,
		"sv": () => <O>Vill du inte spela ett spel?</O>,
		"de": () => <O>Keine Lust auf ein Spiel?</O>,
	},

	"3982bca2b80d": {
		"_context": "faq_q_4",
		"en": () => <O>How do I get started?</O>,
		"da": () => <O>Hvordan kommer jeg i gang?</O>,
		"sv": () => <O>Hur kommer jag igång?</O>,
		"de": () => <O>Wie lege ich los?</O>,
	},

	"d67cc89d48a8": {
		"_context": "faq_q_3",
		"en": () => <O>Is the game really free?</O>,
		"da": () => <O>Er spillet virkelig gratis?</O>,
		"sv": () => <O>Är spelet verkligen gratis?</O>,
		"de": () => <O>Ist das Spiel wirklich kostenlos?</O>,
	},

	"4462fa8cf6c7": {
		"_context": "faq_a_12",
		"en": () => {
			return (
				<O>
				<A href="/achievements">Achievements</A> are like medals that you can earn by reaching a certain goal. For
				example winning a game with at least 5 people. The achievements are there to
				motivate you and we will continuously add new ones.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				<A href="/achievements">Bedrifter</A> er en slags medaljer du optjener ved at nå et bestemt mål. F.eks. at vinde et
				spil med mindst 5 deltagere. Bedrifterne er til for at motivere dig, og vi vil løbende
				tilføje flere.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				<A href="/achievements">Bedrifter</A> är som medaljer som du kan tjäna genom att nå ett visst mål. Till exempel
				att vinna ett spel med minst 5 personer. Bedrifterna är där för att motivera dig och
				vi kommer kontinuerligt att lägga till nya.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				<A href="/achievements">Erfolge</A> sind eine Art Medaille, die du durch das Erreichen eines bestimmten Ziels
				erlangst. Das kann zum Beispiel der Gewinn eines Spieles mit mindestens 5 Personen sein.
				Die Erfolgsseite ist für dich als Spielmotivation gedacht und wir werden sie laufend um
				neue Erfolge ergänzen.
				</O>
			);
		},
	},

	"ac2d200682fe": {
		"_context": "faq_q_13",
		"en": () => <O>Can you create a corporate version for my company?</O>,
		"da": () => <O>Kan jeg lave et firmaspil for mit firma?</O>,
		"sv": () => <O>Kan jag skapa en företagsversion för mitt företag?</O>,
		"de": () => <O>Könnt ihr eine Corporate-Version für meine Firma erstellen?</O>,
	},

	"82a8d30b2341": {
		"_context": "faq_a_16",
		"en": () => {
			return (
				<O>
					An investment game is a simulation of real stock market events. In a
					fictitious portfolio you can practice trading stocks with fictitious
					money. That way you can gain your first trading experience and try out
					investment strategies. Unlike on the real stock exchange, you have no
					financial risk in a stock game.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
					Et investeringsspil er en simulation af rigtige aktiemarkedshandligner.
					Du kan øve dig i at investere i en fiktiv portefølje med fiktive penge.
					Dermed får du handelserfaring og kan prøve investeringsstrategier. Og
					ulig det rigtige marked, har du ingen finansiel risiko.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
					Ett investeringsspel är en simulering av verkliga aktiemarknadshändelser. I en
					fiktiv portfölj kan du öva på att handla aktier med fiktiva pengar. På så sätt
					kan du få din första handelsupplevelse och prova investeringsstrategier. Till
					skillnad från på den verkliga börsen har du ingen ekonomisk risk i ett
					aktiespel.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
					Ein Börsenspiel ist eine Simulation des realen Börsengeschehens. In einem fiktiven Depot
					kannst du das Handeln von Aktien mit fiktivem Geld üben. Dadurch sammelst du erste
					Börsenerfahrung und kannst Investmentstrategien ausprobieren. Anders als an der echten
					Börse, hast du hier kein finanzielles Risiko.
				</O>
			);
		},
	},

	"8b9f2af0733c": {
		"_context": "faq_q_16",
		"en": () => <O>What is an investment game?</O>,
		"da": () => <O>Hvad er et investeringsspil?</O>,
		"sv": () => <O>Vad är ett investeringsspel?</O>,
		"de": () => <O>Was ist ein Börsenspiel?</O>,
	},

	"cedddbcf0a12": {
		"_context": "faq_q_17",
		"en": (site_name) => <O>Can I use {site_name} if I'm not part of a game?</O>,
		"da": (site_name) => <O>Kan jeg bruge {site_name} selvom jeg ikke er del af et spil?</O>,
		"sv": (site_name) => <O>Kan jag använda {site_name} även om jag inte deltar i ett spel?</O>,
		"de": (site_name) => <O>Kann ich {site_name} auch nutzen, wenn ich an keinem Spiel teilnehme? </O>,
	},

	"5587e7dfc094": {
		"_context": "faq_a_14",
		"en": () => {
			return (
				<O>
				When creating a game you can select real trade volume. The trade volume shows how many
				stocks (volume) are currently tradable on the real stock market. If you select the
				feature, you can only buy the available amount of stocks. If you want to buy more stocks
				than that, the order will appear in your trading queue and will only go through, once
				more stocks are available on the real market. This feature makes your investment game
				even more realistic!
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Når du laver et spil kan du vælge realistisk handelsvolumen. Handelsvolumenen viser
				hvor mange aktier (volumen) der for nuværende kan handles på det rigtige aktiemarked.
				Hvis du spiller med handelsvolumen, kan der kun købes den tilsvarende mængde aktier i
				spillet, som handles i virkeligheden. Hvis du har lagt en ordre på flere aktier end der
				er aktier tilgængeligt i virkeligheden, vil ordren gå igennem af flere omgange,
				efterhånden som der bliver aktier tilgængelig i virkeligheden. Denne feature gør dit spil
				endnu mere realistisk!
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				När du skapar ett spel kan du välja verklig handelsvolym. Handelsvolymen visar hur
				många aktier (volym) som för närvarande kan handlas på den verkliga aktiemarknaden. Om
				du väljer funktionen kan du bara köpa den tillgängliga mängden aktier. Om du vill köpa
				fler aktier än så kommer ordern att dyka upp i vår handelskö och går bara igenom när
				fler aktier finns tillgängliga på den riktiga marknaden. Den här funktionen gör ditt
				investeringsspel ännu mer realistiskt!
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Du kannst ein Spiel mit oder ohne realem Handelsvolumen erstellen. Das
				Handelsvolumen beschreibt, wie viele Aktien (Volumen) derzeit am realen Aktienmarkt
				handelbar sind, also von dir gekauft werden können. Wenn du die Funktion auswählst,
				kannst du maximal die verfügbare Menge an Aktien kaufen. Wenn du mehr Aktien handelst
				als verfügbar sind, wird die Order in deine Warteschleife gestellt, bis am realen Markt
				weitere Aktien verfügbar sind. Mit dieser Funktion wird dein Börsenspiel noch
				realistischer!
				</O>
			);
		},
	},

	"72bb801fe702": {
		"_context": "faq_q_14",
		"en": () => <O>What does playing with real trade volume mean?</O>,
		"da": () => <O>Hvad betyder det at spille med handelsvolumen?</O>,
		"sv": () => <O>Vad innebär att spela med verklig handelsvolym?</O>,
		"de": () => <O>Was bedeutet es, mit realem Handelsvolumen zu spielen?</O>,
	},

	"4dd1829cd7ea": {
		"_context": "faq_a_13",
		"en": () => {
			return (
				<O>
				Yes! Stock games are an excellent way to motivate your own employees, customers
				or followers, create team spirit, reward them with competitions and expose them
				to your brand. We would be happy to develop a corporate version for you with
				your logo, corporate colors and your product information. Please <A href="/contact">contact us</A> for
				more information.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Ja! Aktiespil er en fremragende måde at motivere dine ansatte, kunder eller følgere,
				skabe holdånd, belønne dem med præmier alt imends du eksponerer dem til dit brand. Vi
				vil hjertens gave lave et firmaspil til dig med dit logo, farver, produktinformationer,
				m.m. <A href="/contact">Kontakt os</A> venligst for at høre mere.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				Ja! Aktiespel är ett utmärkt sätt att motivera dina anställda, kunder eller följare,
				skapa laganda, belöna dem med tävlingar och lansera ditt varumärke. Vi hjälper dig gärna
				att utveckla en företagsversion för dig med din logotyp, företagsfärger och din
				produktinformation. Vänligen <A href="/contact">kontakta oss</A> för mer information.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Ja, schön, dass du fragst! Börsenspiele sind eine hervorragende Möglichkeit, um die
				eigenen Mitarbeiter, Kunden oder Follower zu motivieren, Teamspirit zu entfachen, mit
				Gewinnspielen zu belohnen und mit deiner Marke in Berührung zu bringen. Wir erstellen
				gerne eine Corporate-Version für dich, mit deinem Logo, deinen Firmenfarben und
				Produktinformationen. <A href="/contact">Kontaktiere</A> uns für mehr Infos.
				</O>
			);
		},
	},

	"483f7c3f8ace": {
		"en": () => <O>FAQ - Contents</O>,
		"da": () => <O>FAQ - Indhold</O>,
		"sv": () => <O>FAQ - Innehåll</O>,
		"de": () => <O>FAQ - Inhalt</O>,
	},

	"e560faec3f93": {
		"en": () => <O>Won a game with only Crypto-currencies (UNIMPLEMENTED)</O>,
		"da": () => <O>Vandt et spil kun med kryptovalutahandler (UNIMPLEMENTED)</O>,
		"sv": () => <O>Vann ett spel endast med kryptovalutor (UNIMPLEMENTED)</O>,
		"de": () => <O>Du hast das Spiel mit ausschließlich Kryptowährung gewonnen. (NICHT IMPLEMENTIERT)</O>,
	},

	"c6851993dcc0": {
		"en": () => <O>A stock has fallen more than 20% and you used the chance to double your amount of it (UNIMPLEMENTED)</O>,
		"da": () => <O>Et værdipapir faldte mere end 20% og du brugte chancen til at fordoble din mængde (UNIMPLEMENTED)</O>,
		"sv": () => <O>En aktie har sjunkit mer än 20% och du använde din chans att dubbla din mängd av den (UNIMPLEMENTED)</O>,
		"de": () => <O>Für eine Aktie, die um mehr als 20% gefallen ist, hast du den Betrag verdoppelt. (NICHT IMPLEMENTIERT)</O>,
	},

	"b5f801fba8ac": {
		"en": () => <O>You lost more than 50% value of an asset (UNIMPLEMENTED)</O>,
		"da": () => <O>Du mistede mere end 50% af værdien af et værdipapir (UNIMPLEMENTED)</O>,
		"sv": () => <O>Du tappade mer än 50% av värdet av en aktie (UNIMPLEMENTED)</O>,
		"de": () => <O>Eine deiner Anlagen hat um mehr als 50% an Wert verloren. (NICHT IMPLEMENTIERT)</O>,
	},

	"2598503413ca": {
		"en": () => <O>Lost half or more</O>,
		"da": () => <O>Tabte mere end 50%</O>,
		"sv": () => <O>Tappade halva eller mer</O>,
		"de": () => <O>Mehr als die Hälfte verloren</O>,
	},

	"fee3423f1210": {
		"en": () => <O>When in trouble then double</O>,
		"da": () => <O>Problemer? Fordobling!</O>,
		"sv": () => <O>Problem? Dubbla!</O>,
		"de": () => <O>Schwierigkeiten? Verdoppeln!</O>,
	},

	"2512388dec9f": {
		"en": () => <O>Cryptographer</O>,
		"da": () => <O>Kryptograf</O>,
		"sv": () => <O>Kryptograf</O>,
		"de": () => <O>Kryptograph</O>,
	},

	"d77d5cc613e1": {
		"en": () => <O>Got it right the first time</O>,
		"da": () => <O>Rigtigt i første forsøg</O>,
		"sv": () => <O>Fick det rätt på första försöket</O>,
		"de": () => <O>Beim ersten Mal gleich richtig</O>,
	},

	"aea4d1a24651": {
		"en": () => <O>Pump'n'dump</O>,
		"da": () => <O>Pump'n'dump</O>,
		"sv": () => <O>Pump'n'dump</O>,
		"de": () => <O>Pump'n'dump</O>,
	},

	"ae11ac69f7f3": {
		"en": () => <O>You won a game with at least 5 players! Plain and simple!</O>,
		"da": () => <O>Du vandt et spil med mindst 5 spillere! Kort og godt!</O>,
		"sv": () => <O>Du vann ett spel med minst 5 spelare! Kort och gott!</O>,
		"de": () => <O>Du hast ein Spiel mit mindestens 5 Spielern gewonnen. Gratulation!</O>,
	},

	"17666a8a247e": {
		"en": () => <O>You lost a game with at least 25 players. Not as easy as it sounds.</O>,
		"da": () => <O>Du tabte et spil med mindst 25 spillere. Ikke så nemt som det lyder.</O>,
		"sv": () => <O>Du förlorade ett spel med minst 25 personer. Inte så lätt som det låter.</O>,
		"de": () => <O>Du hast ein Spiel mit mindestens 25 Spielern verloren. Nicht so einfach wie es klingt.</O>,
	},

	"97ceeb3ef989": {
		"en": () => <O>Lost a game</O>,
		"da": () => <O>Tabte et spil</O>,
		"sv": () => <O>Förlorade ett spel</O>,
		"de": () => <O>Ein Spiel verloren</O>,
	},

	"419a6544f397": {
		"en": () => <O>Won a game</O>,
		"da": () => <O>Vandt et spil</O>,
		"sv": () => <O>Vann ett spel</O>,
		"de": () => <O>Ein Spiel gewonnen</O>,
	},

	"1789c4efe40a": {
		"_context": "1h 2m",
		"en": (seconds) => <O>{format_duration({d:'d',h:'h',m:'min',s:'s'}, seconds)}</O>,
		"da": (seconds) => <O>{format_duration({d:'d',h:'t',m:'min',s:'s'}, seconds)}</O>,
		"sv": (seconds) => <O>{format_duration({d:'d',h:'t',m:'min',s:'s'}, seconds)}</O>,
		"de": (seconds) => <O>{format_duration({d:'d',h:'h',m:'min',s:'s'}, seconds)}</O>,
	},

	"9f38ea14748d": {
		"_context": "123,456.78",
		"en": (n_decimals, val) => <O>{format_number_en(val, n_decimals)}</O>,
		"da": (n_decimals, val) => <O>{format_number_da(val, n_decimals)}</O>,
		"sv": (n_decimals, val) => <O>{format_number_sv(val, n_decimals)}</O>,
		"de": (n_decimals, val) => <O>{format_number_de(val, n_decimals)}</O>,
	},

	"31fef541ffbe": {
		"_context": "DKK 123,456.78",
		"en": (n_decimals, unit, val) => {
			switch (unit) {
			case "USD": return <O>${format_number_en(val, n_decimals)}</O>;
			case "EUR": return <O>€{format_number_en(val, n_decimals)}</O>;
			default:    return <O>{unit} {format_number_en(val, n_decimals)}</O>;
			}
		},
		"da": (n_decimals, unit, val) => {
			switch (unit) {
			case "USD": return <O>${format_number_da(val, n_decimals)}</O>;
			case "EUR": return <O>€{format_number_da(val, n_decimals)}</O>;
			case "DKK": return <O>{format_number_da(val, n_decimals)} kr.</O>;
			default:    return <O>{unit} {format_number_da(val, n_decimals)}</O>;
			}
		},
		"sv": (n_decimals, unit, val) => {
			switch (unit) {
			case "USD": return <O>${format_number_sv(val, n_decimals)}</O>;
			case "EUR": return <O>€{format_number_sv(val, n_decimals)}</O>;
			case "SEK": return <O>{format_number_sv(val, n_decimals)} kr</O>;
			default:    return <O>{unit} {format_number_sv(val, n_decimals)}</O>;
			}
		},
		"de": (n_decimals, unit, val) => {
			switch (unit) {
			case "USD": return <O>${format_number_de(val, n_decimals)}</O>;
			case "EUR": return <O>€{format_number_de(val, n_decimals)}</O>;
			default:    return <O>{unit} {format_number_de(val, n_decimals)}</O>;
			}
		},
	},

	"7de72a232de7": {
		"_context": "Mon",
		"en": (weekday_bewteen_0_and_6_monday_to_sunday) => <O>{weekday_short.en[(weekday_bewteen_0_and_6_monday_to_sunday+1)%7]}</O>,
		"da": (weekday_bewteen_0_and_6_monday_to_sunday) => <O>{weekday_short.da[(weekday_bewteen_0_and_6_monday_to_sunday+1)%7]}</O>,
		"sv": (weekday_bewteen_0_and_6_monday_to_sunday) => <O>{weekday_short.sv[(weekday_bewteen_0_and_6_monday_to_sunday+1)%7]}</O>,
		"de": (weekday_bewteen_0_and_6_monday_to_sunday) => <O>{weekday_short.de[(weekday_bewteen_0_and_6_monday_to_sunday+1)%7]}</O>,
	},

	"89ef4092463f": {
		"_context": "January",
		"en": (month_between_0_and_11) => <O>{months.en[month_between_0_and_11]}</O>,
		"da": (month_between_0_and_11) => <O>{months.da[month_between_0_and_11]}</O>,
		"sv": (month_between_0_and_11) => <O>{months.sv[month_between_0_and_11]}</O>,
		"de": (month_between_0_and_11) => <O>{months.de[month_between_0_and_11]}</O>,
	},

	"473b562fa7fc": {
		"_context": "1 January 2018",
		"en": (timestamp) => {
			const d = ts2date(timestamp);
			const dd = d.getDate();
			const mm = d.getMonth();
			const yyyy = d.getFullYear();
			return <O>{dd} {months.en[mm]} {yyyy}</O>;
		},
		"da": (timestamp) => {
			const d = ts2date(timestamp);
			const dd = d.getDate();
			const mm = d.getMonth();
			const yyyy = d.getFullYear();
			return <O>{dd}. {months.da[mm]} {yyyy}</O>;
		},
		"sv": (timestamp) => {
			const d = ts2date(timestamp);
			const dd = d.getDate();
			const mm = d.getMonth();
			const yyyy = d.getFullYear();
			return <O>{dd} {months.sv[mm]} {yyyy}</O>;
		},
		"de": (timestamp) => {
			const d = ts2date(timestamp);
			const dd = d.getDate();
			const mm = d.getMonth();
			const yyyy = d.getFullYear();
			return <O>{dd}. {months.de[mm]} {yyyy}</O>;
		},
	},

	"a1fc00dbd506": {
		"_context": "1/1/18",
		"en": (timestamp) => {
			const date = ts2date(timestamp);
			const m = date.getMonth()+1;
			const d = date.getDate();
			const yy = date.getYear() - 100;
			return <O>{m}/{d}/{yy}</O>
		},
		"da": (timestamp) => {
			const date = ts2date(timestamp);
			const m = date.getMonth()+1;
			const d = date.getDate();
			const yy = date.getYear() - 100;
			return <O>{d}/{m}.{yy}</O>
		},
		"sv": (timestamp) => {
			const date = ts2date(timestamp);
			const m = date.getMonth()+1;
			const d = date.getDate();
			const yy = date.getYear() - 100;
			return <O>{d}/{m}.{yy}</O>
		},
		"de": (timestamp) => {
			const date = ts2date(timestamp);
			const m = date.getMonth()+1;
			const d = date.getDate();
			const yy = date.getYear() - 100;
			return <O>{d}.{m}.{yy}</O>
		},
	},

	"2496a8470234": {
		"_context": "Fri, Jan 1 4:20 PM",
		"en": (timestamp) => {
			const d = ts2date(timestamp);
			const ww = d.getDay();
			const mm = d.getMonth();
			const dd = d.getDate();
			const hrs = d.getHours();
			const hrs12 = ((hrs+11)%12)+1;
			const ampm = hrs >= 12 ? "PM" : "AM";
			const mins = d.getMinutes();
			return <O>{weekday_short.en[ww]}, {months_short.en[mm]} {dd} {hrs12}:{npad(mins,2)} {ampm}</O>;
		},
		"da": (timestamp) => {
			const d = ts2date(timestamp);
			const ww = d.getDay();
			const mm = d.getMonth();
			const dd = d.getDate();
			const hrs = d.getHours();
			const mins = d.getMinutes();
			return <O>{weekday_short.da[ww]} d. {dd}. {months_short.da[mm]} {hrs}:{npad(mins,2)}</O>;
		},
		"sv": (timestamp) => {
			const d = ts2date(timestamp);
			const ww = d.getDay();
			const mm = d.getMonth();
			const dd = d.getDate();
			const hrs = d.getHours();
			const mins = d.getMinutes();
			return <O>{weekday_short.sv[ww]} d. {dd}. {months_short.sv[mm]} {hrs}:{npad(mins,2)}</O>;
		},
		"de": (timestamp) => {
			const d = ts2date(timestamp);
			const ww = d.getDay();
			const mm = d.getMonth();
			const dd = d.getDate();
			const hrs = d.getHours();
			const mins = d.getMinutes();
			return <O>{weekday_short.de[ww]} d. {dd}. {months_short.de[mm]} {hrs}:{npad(mins,2)}</O>;
		},
	},

	"1f3397b36afe": {
		"en": () => <O>unfollow</O>,
		"da": () => <O>følg <i>ikke</i></O>,
		"sv": () => <O>Sluta <i>följa</i></O>,
		"de": () => <O>entfolgen</O>,
	},

	"54d88000cf98": {
		"en": () => <O>follow</O>,
		"da": () => <O>følg</O>,
		"sv": () => <O>följ</O>,
		"de": () => <O>folgen</O>,
	},

	"5fde683fe55d": {
		"en": () => <O>All stocks from Iceland</O>,
		"da": () => <O>Alle islandske aktier</O>,
		"sv": () => <O>Alla isländska aktier</O>,
		"de": () => <O>Alle isländischen Aktien</O>,
	},

	"007b4350f608": {
		"en": () => {
			return (
				<O>
				The Icelandic stock market isn't very large, and the volume pretty low. So you can
				see all stocks from Iceland here.</O>
			);
		},
		"da": () => {
			return (
				<O>
				Det islandske aktiemarked er ikke så stort, og volumen er ret lav. Så du kan se alle
				aktier fra Island her.</O>
			);
		},
		"sv": () => {
                        return (
                                <O>
                                Den isländska marknaden är inte så stor och volymen är låg. Så du kan se alla aktier från Island här.</O>
                        );
                },
		"de": () => {
                        return (
                                <O>
                                Die isländische Börse ist nicht groß und das Handelsvolumen auch sehr gering. Deswegen siehst
				du hier alle isländischen Aktien auf einen Blick.</O>
                        );
                },
	},

	"b36c7ab47a4b": {
		"en": () => <O>Search stocks in Iceland</O>,
		"da": () => <O>Søg islandske værdipapirer</O>,
		"sv": () => <O>Sök isländska aktier</O>,
		"de": () => <O>Nach isländischen Aktien suchen</O>,
	},

	"30032cb8ac50": {
		"en": () => <O>Icelandic stocks</O>,
		"da": () => <O>Islandske aktier</O>,
		"sv": () => <O>Isländska aktier</O>,
		"de": () => <O>Isländische Aktien</O>,
	},

	"52b8b0b0838a": {
		"en": () => <O>Explore stocks from Iceland</O>,
		"da": () => <O>Udforsk værdipapirer fra Island</O>,
		"sv": () => <O>Utforska aktier från Island</O>,
		"de": () => <O>Entdecke Aktien aus Island</O>,
	},

	"c71f20b76f75": {
		"en": () => <O>All currencies</O>,
		"da": () => <O>Alle valutaer</O>,
		"sv": () => <O>Alla valutor</O>,
		"de": () => <O>Alle Währungen</O>,
	},

	"0eca98c4153b": {
		"en": () => <O>Random currencies</O>,
		"da": () => <O>Tilfældige valutaer</O>,
		"sv": () => <O>Slumpässiga valutor</O>,
		"de": () => <O>Zufällige Währungen</O>,
	},

	"2cf4ab0fdf56": {
		"en": () => <O>Search currencies</O>,
		"da": () => <O>Søg valutaer</O>,
		"sv": () => <O>Sök valutor</O>,
		"de": () => <O>Währungen durchsuchen</O>,
	},

	"5de938ed8472": {
		"en": () => <O>All commodities</O>,
		"da": () => <O>Alle råvarer</O>,
		"sv": () => <O>Alla handelsvaror</O>,
		"de": () => <O>Alle Rohstoffe</O>,
	},

	"74fedb969aa2": {
		"en": () => <O>Explore commodities</O>,
		"da": () => <O>Udforsk råvarer</O>,
		"sv": () => <O>Utforska handelsvaror</O>,
		"de": () => <O>Erkunde unsere unterstützten Rohstoffe</O>,
	},

	"f14290777462": {
		"en": () => <O>All Funds and Bonds</O>,
		"da": () => <O>Alle fonde og obligationer</O>,
		"sv": () => <O>Alla Fonder och obligationer</O>,
		"de": () => <O>Alle Fonds und Anleihen</O>,
	},

	"a72cf850f143": {
		"en": () => <O>Funds and Bonds</O>,
		"da": () => <O>Fonde og obligationer</O>,
		"sv": () => <O>Fonder och obligationer</O>,
		"de": () => <O>Fonds und Anleihen</O>,
	},

	"06ed7e0006bb": {
		"en": () => <O>(Mutual fund or exchange-traded fund)</O>,
		"da": () => <O>(Investeringsfond eller børshandlet fond)</O>,
		"sv": () => <O>(Fond eller börshandlad fond)</O>,
		"de": () => <O>(Investmentfonds oder börsengehandelter Fonds)</O>,
	},

	"4c1d2531eb1a": {
		"en": () => <O>Explore Funds and Bonds</O>,
		"da": () => <O>Udforsk fonde og obligationer</O>,
		"sv": () => <O>Utforska Fonder och obligationer</O>,
		"de": () => <O>Erkunde Fonds und Anleihen</O>,
	},

	"d454f6425de0": {
		"en": () => <O>All ETFs (Exchange-Traded Funds)</O>,
		"da": () => <O>Alle EFT'er</O>,
		"sv": () => <O>Alla ETFs (Exchange-Traded Funds)</O>,
		"de": () => <O>Alle ETFs (Exchange-Traded Funds)</O>,
	},

	"e07e1066810a": {
		"en": () => <O>ETFs (Exchange-Traded Funds)</O>,
		"da": () => <O>EFT'er</O>,
		"sv": () => <O>ETF (Exchange-Traded Funds)</O>,
		"de": () => <O>ETFs (Exchange-Traded Funds)</O>,
	},

	"ba74bfbe235a": {
		"en": () => <O>Explore ETFs</O>,
		"da": () => <O>Udforsk EFT'er</O>,
		"sv": () => <O>Utforska ETF</O>,
		"de": () => <O>Erkunde ETFs</O>,
	},

	"74894e95373f": {
		"en": () => <O>Search securities</O>,
		"da": () => <O>Søg værdipapirer</O>,
		"sv": () => <O>Sök värdepapper</O>,
		"de": () => <O>Wertpapiere suchen</O>,
	},

	"0668f04135d3": {
		"en": () => <O>US Collections</O>,
		"da": () => <O>Amerikanske lister</O>,
		"sv": () => <O>Amerikanska listor</O>,
		"de": () => <O>Amerikanische Listen</O>,
	},

	"1408c0aa9482": {
		"en": () => <O>Selected US stocks</O>,
		"da": () => <O>Udvalgte amerikanske aktier</O>,
		"sv": () => <O>Utvalda amerikanska aktier</O>,
		"de": () => <O>Amerikanische Aktien auswählen</O>,
	},

	"dcaa1849b28c": {
		"en": () => <O>Random US stocks</O>,
		"da": () => <O>Tilfældige amerikanske aktier</O>,
		"sv": () => <O>Slumpmässiga amerikanska aktier</O>,
		"de": () => <O>Zufällige amerikanische Aktien</O>,
	},

	"1de01b6ad55b": {
		"en": () => <O>Search US assets</O>,
		"da": () => <O>Søg i USA papirer</O>,
		"sv": () => <O>Sök tillgångar i USA</O>,
		"de": () => <O>Amerikanische Anlagen durchsuchen</O>,
	},

	"f6c43c7da1b5": {
		"en": () => <O>United States of America</O>,
		"da": () => <O>United States of America</O>,
		"sv": () => <O>United States of America</O>,
		"de": () => <O>Vereinigte Staaten von Amerika</O>,
	},

	"28472c115bea": {
		"en": () => <O>Explore assets exclusively from the US</O>,
		"da": () => <O>Udforsk værdipapirer eksklusivt fra USA</O>,
		"sv": () => <O>Utforska tillgångar exklusivt från USA</O>,
		"de": () => <O>Entdecke amerikanische Aktien</O>,
	},

	"8e83ffa3c180": {
		"_context": "1/1 4:20 PM",
		"en": (timestamp) => {
			const date = ts2date(timestamp);
			const m = date.getMonth()+1;
			const d = date.getDate();
			const hrs = date.getHours();
			const hrs12 = ((hrs+11)%12)+1;
			const ampm = hrs >= 12 ? "PM" : "AM";
			const mins = date.getMinutes();
			return <O>{m}/{d} {hrs12}:{npad(mins,2)} {ampm}</O>;
		},
		"da": (timestamp) => {
			const date = ts2date(timestamp);
			const m = date.getMonth()+1;
			const d = date.getDate();
			const hrs24 = date.getHours();
			const mins = date.getMinutes();
			return <O>{d}/{m} {hrs24}:{npad(mins,2)}</O>;
		},
		"sv": (timestamp) => {
			const date = ts2date(timestamp);
			const m = date.getMonth()+1;
			const d = date.getDate();
			const hrs24 = date.getHours();
			const mins = date.getMinutes();
			return <O>{d}/{m} {hrs24}:{npad(mins,2)}</O>;
		},
		"de": (timestamp) => {
			const date = ts2date(timestamp);
			const m = date.getMonth()+1;
			const d = date.getDate();
			const hrs24 = date.getHours();
			const mins = date.getMinutes();
			return <O>{d}.{m}. {hrs24}:{npad(mins,2)}</O>;
		},
	},

	"cb6ad2e7239a": {
		"en": (site_name) => {
			return (
				<O>
				We are passionate about making {site_name} as user-friendly as possible, and an
				entertaining game for you and your friends.
				</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>
				Vi brænder for at gøre {site_name} så brugervenlig som muligt, og et underholdende spil til dig og dine venner.
				</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>
				Vi brinner för att göra {site_name} så användarvänligt som möjligt, och ett underhållande spel för dig och dina vänner.
				</O>
			);
		},
		"de": (site_name) => {
			return (
				<O>
				Wir sind leidenschaftlich darum bemüht, {site_name} so benutzerfreundlich wie möglich zu gestalten
				und ein unterhaltsames Spiel für dich und deine Freunde zu bieten.
				</O>
			);
		},
	},

	"cfa4752b8d6a": {
		"en": (site_base) => {
			return (
				<O>
				There's a lot on the drawing board, but we'd love to hear from you if you have a
				great idea. So please send feedback, praise, and good ideas to <b className='ad_main_green'>info@{site_base}</b>.
				</O>
			);
		},
		"da": (site_base) => {
			return (
				<O>
				Der er en masse på tegnebrættet, men vi hører gerne fra dig, hvis du har en god idé. Så
				send endelig ris, ros og gode ideer til <b className='ad_main_green'>info@{site_base}</b>.
				</O>
			);
		},
		"sv": (site_base) => {
			return (
				<O>
				Det finns mycket på ritbordet, men vi hör gärna från dig om du har en bra idé. Så skicka gärna
				feedback, beröm och bra idéer till <b className='ad_main_green'>info@{site_base}</b>.
				</O>
			);
		},
		"de": (site_base) => {
			return (
				<O>
				Es gibt viele Pläne, aber wir würden uns freuen von Ihnen zu hören, wenn Sie eine gute Idee haben.
				Bitte senden Sie Feedback, Lob und gute Ideen an <b className='ad_main_green'>info@{site_base}</b>.
				</O>
			);
		},
	},

	"b2b3c0c8e8dc": {
		"en": () => <O>Game starts in&nbsp;</O>,
		"da": () => <O>Spillet starter om &nbsp;</O>,
		"sv": () => <O>Spelet startar om &nbsp;</O>,
		"de": () => <O>Das Spiel beginnt in&nbsp;</O>,
	},

	"b9c52d3f63f5": {
		"en": (duration_days) => {
			return duration_days === 1 ? <O>{duration_days} day</O> : <O>{duration_days} days</O>
		},
		"da": (duration_days) => {
			return duration_days === 1 ? <O>{duration_days} dag</O> : <O>{duration_days} dage</O>
		},
		"sv": (duration_days) => {
			return duration_days === 1 ? <O>{duration_days} dag</O> : <O>{duration_days} dagar</O>
		},
		"de": (duration_days) => {
			return duration_days === 1 ? <O>{duration_days} Tag</O> : <O>{duration_days} Tage</O>
		},
	},

	"ef20660816d6": {
		"en": () => <O>Duration</O>,
		"da": () => <O>Varighed</O>,
		"sv": () => <O>Varaktighet</O>,
		"de": () => <O>Dauer</O>,
	},

	"052f154e810f": {
		"en": () => <O>Game ends in&nbsp;</O>,
		"da": () => <O>Spillet slutter om&nbsp;</O>,
		"sv": () => <O>Spelet slutar om&nbsp;</O>,
		"de": () => <O>Das Spiel ist vorbei in&nbsp;</O>,
	},

	"840459202711": {
		"en": () => <O>Users</O>,
		"da": () => <O>Brugere</O>,
		"sv": () => <O>Användare</O>,
		"de": () => <O>Benutzer</O>,
	},

	"27c2ad08f32a": {
		"en": () => <O>Games</O>,
		"da": () => <O>Spil</O>,
		"sv": () => <O>Spel</O>,
		"de": () => <O>Spiele</O>,
	},

	"642f7a3fdd1d": {
		"en": () => <O>Trades</O>,
		"da": () => <O>Handler</O>,
		"sv": () => <O>Handlingar</O>,
		"de": () => <O>Handel</O>,
	},

	"199a3541df83": {
		"en": () => <O>Game has ended</O>,
		"da": () => <O>Spillet er slut</O>,
		"sv": () => <O>Spelet är slut</O>,
		"de": () => <O>Das Spiel ist beendet</O>,
	},

	"c9ff15cc8bcd": {
		"en": () => <O>Active</O>,
		"da": () => <O>Aktiv</O>,
		"sv": () => <O>Aktiv</O>,
		"de": () => <O>Aktiv</O>,
	},

	"558a003bd9aa": {
		"en": () => <O><b>PERMANENTLY</b> delete game?</O>,
		"da": () => <O>Slet spil <b>PERMANENT</b>?</O>,
		"sv": () => <O>Ta bort spelet <b>PERMANENT</b>?</O>,
		"de": () => <O>Spiel <b>DAUERHAFT</b> löschen?</O>,
	},

	"62a6afe22b4d": {
		"en": () => <O>Oops! Seems we're having technical problems or doing some quick maintenance. Try reloading the page. We would like to hear from you if the problem persists after a few minutes.</O>,
		"da": () => <O>Oops! Det lader til at vi har tekniske problemer eller laver hurtig vedligeholdelse. Prøv at genindlæse siden. Hvis problemet fortsat består efter nogle minutter, hører vi gerne fra dig.</O>,
		"sv": () => <O>Oops! Det verkar som att vi har tekniska problem eller att vi underhåller sidan. Prova att uppdatera sidan. Om problemet kvarstår om några minuter så hör vi gärna från dig.</O>,
		"de": () => <O>Uups! Wir scheinen technische Probleme zu haben oder gerade Wartungen durchzuführen. Versuchen die Seite neu zu laden. Bitte melde dich bei uns, sollte das Problem nach ein paar Minuten weiter bestehen.</O>,
	},

	"e441eb0bb0e7": {
		"en": () => <O>Please verify your email</O>,
		"da": () => <O>Venligst verificér din email</O>,
		"sv": () => <O>Vänligen bekräfta din email</O>,
		"de": () => <O>Bitte verifiziere deine E-Mail-Adresse</O>,
	},

	"548d8fd62600": {
		"en": (current_email, mailtime) => <O>We've sent you a verification email to <tt>{current_email}</tt> at {mailtime}.</O>,
		"da": (current_email, mailtime) => <O>Vi har sendt dig en verificerings-email til <tt>{current_email}</tt> ({mailtime}).</O>,
		"sv": (current_email, mailtime) => <O>Vi har skickat dig ett bekräftelsemail till <tt>{current_email}</tt> ({mailtime}).</O>,
		"de": (current_email, mailtime) => <O>Wir senden eine E-Mail zu Verifizierung an <tt>{current_email}</tt> ({mailtime}).</O>,
	},

	"f99e7723f69b": {
		"en": (current_email) => <O>We've sent you a verification email to <tt>{current_email}</tt>.</O>,
		"da": (current_email) => <O>Vi har sendt dig en verificerings-email til <tt>{current_email}</tt>.</O>,
		"sv": (current_email) => <O>Vi har skickat dig ett bekräftelsemail till <tt>{current_email}</tt>.</O>,
		"de": (current_email) => <O>Wir haben eine E-Mail zur Verifizierung an <tt>{current_email}</tt> gesendet.</O>,
	},

	"cc2cfe67b45d": {
		"en": () => <O>Our partners</O>,
		"da": () => <O>Vores samarbejdspartnere</O>,
		"sv": () => <O>Våra samarbetspartners</O>,
		"de": () => <O>Unsere Partner</O>,
	},

	"badecffac849": {
		"en": () => {
			return (
				<O>
				Have a look at our awesome partners here. Great companies we really enjoy working with.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Du kan kigge nærmere på vores fantastiske samarbejdspartnere her. Gode virksomheder vi virkelig nyder at arbejde sammen med.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				Här kan du se vilka fantastiska samarbetspartners vi arbetar med. Det är bra företag som vi verkligen uppskattar att arbete med.
				</O>
			);
		},
		"de": () => {
                        return (
                                <O>
                                Hier siehst du eine Übersicht unserer Partner: großartige Unternehmen, mit denen wir gerne zusammenarbeiten.
                                </O>
                        );
                },
	},

	"6fbfdbebf863": {
		"en": () => {
			return (
				<O>
					IEX Group
				</O>
			);
		},
		"da": () => {
			return (
				<O>
					IEX Group
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
					IEX Group
				</O>
			);
		},
		"de": () => {
			return (
				<O>
					IEX Group
				</O>
			);
		},
	},

	"044b8d908d4b": {
		"en": () => {
			return (
				<O>
					IEX Group operates the Investors Exchange (IEX), a stock exchange for U.S. equities
					that is built for investors and companies.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
					IEX Group driver Investors Exchange (IEX), en børs for amerikanske aktier, der er
					bygget for investorer og virksomheder.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
					IEX Group driver Investors Exchange (IEX), en börs för amerikanska aktier som är
					byggt för investerare och företag.
				</O>
			);
		},
		"de": () => {
                        return (
                                <O>
                                        Die IEX Group betreibt die Investors Exchange (IEX), eine Börse für US-Aktien, die für Investoren und Unternehmen gemacht wurde.
                                </O>
                        );
                },
	},

	"0fa653f345f2": {
		"en": () => <O>Number of players</O>,
		"da": () => <O>Antal spillere</O>,
		"sv": () => <O>Antal spelare</O>,
		"de": () => <O>Anzahl der Spieler</O>,
	},

	"a84e5d443703": {
		"en": () => <O>Number of participants</O>,
		"da": () => <O>Antal deltagere</O>,
		"sv": () => <O>Antal deltagare</O>,
		"de": () => <O>Anzahl der Teilnehmer</O>,
	},

	"6f74c5e7c7e2": {
		"en": () => <O>Number of months</O>,
		"da": () => <O>Antal måneder</O>,
		"sv": () => <O>Antal månader</O>,
		"de": () => <O>Anzahl der Monate</O>,
	},

	"17af476c1750": {
		"en": () => <O>Start date</O>,
		"da": () => <O>Startdato</O>,
		"sv": () => <O>Startdatum</O>,
		"de": () => <O>Startdatum</O>,
	},

	"ea9acb37efbf": {
		"en": () => <O>Upgrade an ongoing game</O>,
		"da": () => <O>Opgrader et igangværende spil</O>,
		"sv": () => <O>Uppgradera ett pågående spel</O>,
		"de": () => <O>Ein laufendes Spiel upgraden</O>,
	},

	"4fd82d87f096": {
		"en": () => <O>Total Price:</O>,
		"da": () => <O>Total Pris:</O>,
		"sv": () => <O>Totalpris:</O>,
		"de": () => <O>Gesamtpreis:</O>,
	},

	"5a450ce21c8a": {
		"en": () => <O>you save</O>,
		"da": () => <O>du sparer</O>,
		"sv": () => <O>du sparar</O>,
		"de": () => <O>du sparst</O>,
	},

	"e4a259c3ffa7": {
		"en": () => <O>Discount:</O>,
		"da": () => <O>Rabat:</O>,
		"sv": () => <O>Rabatt:</O>,
		"de": () => <O>Rabatt:</O>,
	},

	"3a1236851c36": {
		"en": () => <O>Add to Cart</O>,
		"da": () => <O>Tilføj til kurv</O>,
		"sv": () => <O>Lägg i varukorg</O>,
		"de": () => <O>In den Warenkorb</O>,
	},

	"08f0084a5e25": {
		"en": () => <O>Update Cart</O>,
		"da": () => <O>Opdater kurv</O>,
		"sv": () => <O>Uppdatera varukorg</O>,
		"de": () => <O>Warenkorb aktualisieren</O>,
	},

	"d6a4e81874bc": {
		"en": () => <O>You can play with up to 30 participants for free</O>,
		"da": () => <O>Du kan spille med op til 30 deltagere gratis</O>,
		"sv": () => <O>Du kan spela med upp till 30 deltagare gratis</O>,
		"de": () => <O>Wie viele Spieler werden das Spiel spielen</O>,
	},

	"df1a7d2ee5d3": {
		"en": (RadiosAndCustom, state_invalid_input, this_parent) => {
			return (
				<O><RadiosAndCustom
						name="number_of_players"
						parent={this_parent}
						defaults={[
							{"value": 10, "text": "10 players"},
							{"value": 20, "text": "20 players"},
				                                        {"value": 30, "text": "Under 30"},
						]}
						custom_text="Other"
						custom_placeholder="players"
						custom_error={state_invalid_input === "number_of_players"}
						min={1}
						max={100000}
					/></O>
			);
		},
		"da": (RadiosAndCustom, state_invalid_input, this_parent) => {
			return (
				<O><RadiosAndCustom
						name="number_of_players"
						parent={this_parent}
						defaults={[
							{"value": 10, "text": "10 spillere"},
							{"value": 20, "text": "20 spillere"},
				                                        {"value": 30, "text": "Under 30"},
						]}
						custom_text="Andet"
						custom_placeholder="spillere"
						custom_error={state_invalid_input === "number_of_players"}
						min={1}
						max={100000}
					/></O>
			);
		},
		"sv": (RadiosAndCustom, state_invalid_input, this_parent) => {
			return (
				<O><RadiosAndCustom
						name="number_of_players"
						parent={this_parent}
						defaults={[
							{"value": 10, "text": "10 spelare"},
							{"value": 20, "text": "20 spelare"},
				                                        {"value": 30, "text": "Under 30"},
						]}
						custom_text="Övrigt"
						custom_placeholder="spelare"
						custom_error={state_invalid_input === "number_of_players"}
						min={1}
						max={100000}
					/></O>
			);
		},
		"de": (RadiosAndCustom, state_invalid_input, this_parent) => {
			return (
				<O><RadiosAndCustom
						name="number_of_players"
						parent={this_parent}
						defaults={[
							{"value": 10, "text": "10 Spieler"},
							{"value": 20, "text": "20 Spieler"},
				                                        {"value": 30, "text": "Unter 30"},
						]}
						custom_text="Andere"
						custom_placeholder="Spieler"
						custom_error={state_invalid_input === "number_of_players"}
						min={1}
						max={100000}
					/></O>
			);
		},
	},

	"57e919705312": {
		"en": () => <O>Your position</O>,
		"da": () => <O>Din placering</O>,
		"sv": () => <O>Din placering</O>,
		"de": () => <O>Deine Position</O>,
	},

	"6c513dd5f763": {
		"en": () => <O>Your development</O>,
		"da": () => <O>Din udvikling</O>,
		"sv": () => <O>Din utveckling</O>,
		"de": () => <O>Deine Entwicklung</O>,
	},

	"32a2a8d47ede": {
		"en": () => <O>Go to game</O>,
		"da": () => <O>Gå til spil</O>,
		"sv": () => <O>Gå till spelet</O>,
		"de": () => <O>Zum Spiel gehen</O>,
	},

	"39ff0c4fc201": {
		"en": () => <O>Added to shortlist</O>,
		"da": () => <O>Tilføjet til shortlist</O>,
		"sv": () => <O>Tillagd till shortlist</O>,
		"de": () => <O>Zur Shortlist hinzugefügt</O>,
	},

	"358872caedf1": {
		"en": () => <O>Miscellaneous equities</O>,
		"da": () => <O>Diverse værdipapirer</O>,
		"sv": () => <O>Diverse värdepapper</O>,
		"de": () => <O>Verschiedene Wertpapiere</O>,
	},

	"1dbcad3f241a": {
		"en": () => <O>Explore assets</O>,
		"da": () => <O>Gå på opdagelse i værdipapirer</O>,
		"sv": () => <O>Utforska tillgångar</O>,
		"de": () => <O>Anlaged erkunden</O>,
	},

	"8b14e1b4c91c": {
		"en": () => {
			return (
				<O>Welcome to our explore assets section. This is where we collect things, that we believe
				is interesting. Feel free to reach out, if you have something you believe would be of interest here!</O>
			);
		},
		"da": () => {
			return (
				<O>Velkommen til vores "gå på opdagelse"-sektion. Det er her vi samler de ting, som vi mener kan være
				af interesse. Du er mere end velkommen til at tage fat i os, hvis du har noget du mener kunne være interessant her!</O>
			);
		},
		"sv": () => {
                        return (
				<O>Välkommen till våran utforsknings-sektion. Här samlar vi saker som vi tror kan vara av intresse.
				Du är mer än välkommen att höra av dig till oss om du tror dig veta något som skulle kunna vara intressant här!</O>
                        );
                },
		"de": () => {
                        return (
                                <O>Willkommen in unserem Bereich zum Erkunden von Anlagen. Hier sammeln wir Dinge, die wir für interessant halten. Sie können sich gerne bei uns melden, wenn Sie etwas haben, von dem Sie glauben, dass es für diese Sektion interessant ist!</O>
                        );
                },
	},

	"605e5382aa94": {
		"en": () => <O>Welcome to our explore markets section. Here you can delve deeper into the five main market areas we support (more will follow in the future), and look at current boomers across the markets.</O>,
		"da": () => <O>Velkommen til vores "udforsk markeder"-sektion. Her kan du fordybe dig yderligere i de markeder, vi understøtter i spillet (flere vil følge i fremtiden), samt kigge på højdespringere fra de forskellige markeder.</O>,
		"sv": () => <O>Välkommen till våran sektion för att utforska marknader. Här kan du fördjupa dig ytterligare i de marknader som vi stöttar i spelet (fler kommer dyka upp i framtiden), samt se vilka höjdare det finns i de olika marknaderna.</O>,
		"de": () => <O>Hier kannst du tiefer in die verschiedenen Wertpapiermärkte eintauchen, an denen du in deinen Börsenspielen handeln kannst. Lass dich auch von den aktuell stärksten Anlagen aller Märkte inspirieren.</O>,
	},

	"35f59181ebe5": {
		"en": () => <O>Search:</O>,
		"da": () => <O>Søgning:</O>,
		"sv": () => <O>Sökning:</O>,
		"de": () => <O>Suche:</O>,
	},

	"1f95331b1f14": {
		"en": () => <O>American Market</O>,
		"da": () => <O>Amerikanske marked</O>,
		"sv": () => <O>Amerikanska marknaden</O>,
		"de": () => <O>Amerikanischer Markt</O>,
	},

	"42f9581ab00b": {
		"en": () => <O>The <a target="_blank" rel="noopener noreferrer" href="https://en.wikipedia.org/wiki/New_York_Stock_Exchange">New York Stock Exchange (NYSE)</a> and the <a target="_blank" rel="noopener noreferrer" href="https://en.wikipedia.org/wiki/NASDAQ">Nasdaq Stock Market</a> were founded in 1792 and 1971, respectively. NYSE is by far the largest stock exchange in the world by market capitalization.</O>,
		"da": () => <O><a target="_blank" rel="noopener noreferrer" href="https://da.wikipedia.org/wiki/New_York_Stock_Exchange">New York Stock Exchange (NYSE)</a> og <a target="_blank" rel="noopener noreferrer" href="https://da.wikipedia.org/wiki/NASDAQ">Nasdaq aktiemarkedet</a> - stiftet i 1792 og 1971. NYSE er verdens klart største aktiemarked, målt på markedsværdi.</O>,
		"sv": () => <O><a target="_blank" rel="noopener noreferrer" href="https://sv.wikipedia.org/wiki/New_York_Stock_Exchange">New York Stock Exchange (NYSE)</a> och <a target="_blank" rel="noopener noreferrer" href="https://sv.wikipedia.org/wiki/Nasdaq">Nasdaq aktiemarknaden</a> - stiftat 1792 och 1971. NYSE är världens största aktiemarknad, mätt efter marknadsvärde.</O>,
		"de": () => <O>Die <a target="_blank" rel="noopener noreferrer" href="https://en.wikipedia.org/wiki/New_York_Stock_Exchange">New York Stock Exchange (NYSE)</a> und der <a target="_blank" rel="noopener noreferrer" href="https://en.wikipedia.org/wiki/NASDAQ">Nasdaq Stock Market</a> wurden 1792, beziehungsweise 1971 gegründet. NYSE ist, gemessen an der Marktkapitalisierung, die mit Abstand größte Börse der Welt.</O>,
	},

	"34b3aaebfe24": {
		"en": () => <O>Danish Market</O>,
		"da": () => <O>Danske marked</O>,
		"sv": () => <O>Danska marknaden</O>,
		"de": () => <O>Dänischer Markt</O>,
	},

	"ce71aee668f1": {
		"en": () => <O>The <a target="_blank" rel="noopener noreferrer" href="https://en.wikipedia.org/wiki/Copenhagen_Stock_Exchange">Copenhagen Stock Exchange (Nasdaq Copenhagen since 2014)</a> was founded in 1625 and was located in the <a target="_blank" rel="noopener noreferrer" href="https://en.wikipedia.org/wiki/B%C3%B8rsen">Børsen building (image above)</a> until 1974.</O>,
		"da": () => <O><a target="_blank" rel="noopener noreferrer" href="https://da.wikipedia.org/wiki/K%C3%B8benhavns_Fondsb%C3%B8rs">Københavns Fondsbørs (Nasdaq Copenhagen siden 2014)</a> blev stiftet 1625 og holdt til i <a target="_blank" rel="noopener noreferrer" href="https://da.wikipedia.org/wiki/B%C3%B8rsen">Børsen (billedet ovenfor)</a> indtil 1974.</O>,
		"sv": () => <O><a target="_blank" rel="noopener noreferrer" href="https://sv.wikipedia.org/wiki/Copenhagen_Stock_Exchange">Köpenhamns börs (Nasdaq Copenhagen sedan 2014)</a> grundades 1625 och låg i <a target="_blank" rel="noopener noreferrer" href="https://sv.wikipedia.org/wiki/B%C3%B6rsen,_K%C3%B6penhamn">Börsen (bilden ovanför)</a> fram till 1974.</O>,
		"de": () => <O>Die <a target="_blank" rel="noopener noreferrer" href="https://en.wikipedia.org/wiki/Copenhagen_Stock_Exchange">Kopenhagener Börse (seit 2014 Nasdaq Copenhagen)</a> wurde 1625 gegründet und war bis 1974 im <a target="_blank" rel="noopener noreferrer" href="https://en.wikipedia.org/wiki/B%C3%B8rsen">Børsen Gebäude (siehe obiges Bild)</a>.</O>,
	},

	"6927bec8375d": {
		"en": () => <O>Swedish Market</O>,
		"da": () => <O>Svenske marked</O>,
		"sv": () => <O>Svenska marknaden</O>,
		"de": () => <O>Schwedischer Markt</O>,
	},

	"87470e238217": {
		"en": () => <O>The <a target="_blank" rel="noopener noreferrer" href="https://en.wikipedia.org/wiki/Stockholm_Stock_Exchange">Stockholm Stock Exchange (Nasdaq Stockholm since 2008)</a> was founded in 1863 and was located in the <a target="_blank" rel="noopener noreferrer" href="https://en.wikipedia.org/wiki/Stockholm_Stock_Exchange_Building">Börshuset building</a> until 1998.</O>,
		"da": () => <O><a target="_blank" rel="noopener noreferrer" href="https://da.wikipedia.org/wiki/Stockholmsb%C3%B6rsen">Stockholmsbörsen (Nasdaq Stockholm since 2008)</a> blev stiftet i 1863 og havde til huse i Börshuset, Stockholm indtil 1998.</O>,
		"sv": () => <O><a target="_blank" rel="noopener noreferrer" href="https://sv.wikipedia.org/wiki/Stockholmsb%C3%B6rsen">Stockholmsbörsen (Nasdaq Stockholm sedan 2008)</a> stiftades 1863 och låg brevid <a target="_blank" rel="noopener noreferrer" href="https://sv.wikipedia.org/wiki/B%C3%B6rshuset">Börshuset</a> fram till 1998.</O>,
		"de": () => <O>Die <a target="_blank" rel="noopener noreferrer" href="https://en.wikipedia.org/wiki/Stockholm_Stock_Exchange">Stockholmer Börse (seit 2008 Nasdaq Stockholm)</a> wurde 1863 eingeweiht und war bis 1998 in dem <a target="_blank" rel="noopener noreferrer" href="https://de.wikipedia.org/wiki/B%C3%B6rse_Stockholm">Börshuset</a> untergebracht.</O>,
	},

	"12e6aef54ea9": {
		"en": () => <O>Finnish Market</O>,
		"da": () => <O>Finske marked</O>,
		"sv": () => <O>Finska marknaden</O>,
		"de": () => <O>Finnischer Markt</O>,
	},

	"a0ffe9ef5132": {
		"en": () => <O>The <a target="_blank" rel="noopener noreferrer" href="https://en.wikipedia.org/wiki/Helsinki_Stock_Exchange">Helsinki Stock Exchange (Nasdaq Helsinki since 2008)</a> was founded in 1912.</O>,
		"da": () => <O><a target="_blank" rel="noopener noreferrer" href="https://en.wikipedia.org/wiki/Helsinki_Stock_Exchange">Børsen i Helsinki (Nasdaq Helsinki siden 2008)</a> blev stiftet i 1912.</O>,
		"sv": () => <O><a target="_blank" rel="noopener noreferrer" href="https://en.wikipedia.org/wiki/Helsinki_Stock_Exchange">Börsen i Helsingfors (Nasdaq Helsingfors sedan 2008)</a> stiftades 1912.</O>,
		"de": () => <O>Die <a target="_blank" rel="noopener noreferrer" href="https://de.wikipedia.org/wiki/B%C3%B6rse_Helsinki">Börse in Helsinki (seit 2008 Nasdaq Helsinki)</a> wurde 1912 gegründet.</O>,
	},

	"8f0dd5e66f9e": {
		"en": () => <O>German Market</O>,
		"da": () => <O>Tyske marked</O>,
		"sv": () => <O>Tyska marknaden</O>,
		"de": () => <O>Deutscher Markt</O>,
	},

	"780ceaa04f3f": {
		"en": () => <O>The <a target="_blank" rel="noopener noreferrer" href="https://en.wikipedia.org/wiki/Frankfurt_Stock_Exchange">Frankfurt Stock Exchange</a> is owned and operated by Deutsche Börse AG. There's actually seven regional exchanges in Germany, with the one in Frankfurt being the main one. Also known as XETRA.</O>,
		"da": () => <O><a target="_blank" rel="noopener noreferrer" href="https://da.wikipedia.org/wiki/Frankfurter_Wertpapierbörse">Børsen i Frankfurt</a> er ejet af Deutsche Börse AG. Ud af de syv regionelle børser i Tyskland, er den i Frankfurt den primære. Også kendt som XETRA.</O>,
		"sv": () => <O><a target="_blank" rel="noopener noreferrer" href="https://sv.wikipedia.org/wiki/Frankfurter_Wertpapierbörse">Frankfurtbörsen</a> ägs och drivs av Deutsche Börse AG. Det finns sju regionala börser i Tyskland och just den i Frankfurt räknas som den huvudsakliga (även känd som XETRA).</O>,
		"de": () => <O><a target="_blank" rel="noopener noreferrer" href="https://en.wikipedia.org/wiki/Frankfurt_Stock_Exchange">Die Frankfurter Wertpapierbörse</a> (XETRA) ist eine von acht regionalen Börsen in Deutschland und gleichzeitig Deutschlands wichtigste. Sie gehört der Deutschen Börse AG an und wird von dieser betrieben.</O>,
	},

	"491d190478c2": {
		"en": () => <O>Icelandic Market</O>,
		"da": () => <O>Islandske marked</O>,
		"sv": () => <O>Isländska marknaden</O>,
		"de": () => <O>Isländischer Markt</O>,
	},

	"bc59f02ca05f": {
		"en": () => <O>The <a target="_blank" rel="noopener noreferrer" href="https://en.wikipedia.org/wiki/Iceland_Stock_Exchange">Iceland Stock Exchange (Nasdaq Iceland since 2008)</a> began trading in 1986.</O>,
		"da": () => <O><a target="_blank" rel="noopener noreferrer" href="https://en.wikipedia.org/wiki/Iceland_Stock_Exchange">Børsen i Island (Nasdaq Iceland siden 2008)</a> begyndte sin handel i 1986.</O>,
		"sv": () => <O><a target="_blank" rel="noopener noreferrer" href="https://en.wikipedia.org/wiki/Iceland_Stock_Exchange">Börsen i Island (Nasdaq Island sedan 2008)</a> började sin handel 1986.</O>,
		"de": () => <O>An der <a target="_blank" rel="noopener noreferrer" href="https://de.wikipedia.org/wiki/Isl%C3%A4ndische_B%C3%B6rse">isländischen Börse (seit 2008 Nasdaq Iceland)</a> wird seit 1986 gehandelt.</O>,
	},

	"b39446e218e5": {
		"en": () => <O>Commodity Market</O>,
		"da": () => <O>Råvaremarkedet</O>,
		"sv": () => <O>Råvarumarknaden</O>,
		"de": () => <O>Rohstoffmarkt</O>,
	},

	"566ba21e770d": {
		"en": () => <O>We have several metals and oil in our commodity section.</O>,
		"da": () => <O>Vi har forskellige metaller og olie i vores råvare-sektion.</O>,
		"sv": () => <O>Vi har flera metaller och olja i våran sektion för handelsvaror.</O>,
		"de": () => <O>Wir haben verschiedene Metalle und Öl in unserer Rohstoffauswahl.</O>,
	},

	"42358bab7813": {
		"en": () => <O>Currency Market</O>,
		"da": () => <O>Valutamarkedet</O>,
		"sv": () => <O>Valutamarknaden</O>,
		"de": () => <O>Währungsmarkt</O>,
	},

	"53a639e4a95b": {
		"en": () => <O>We support the purchasing of a number of currencies. The exchange rates follow the rates from the <a target="_blank" rel="noopener noreferrer" href="https://en.wikipedia.org/wiki/Danmarks_Nationalbank">National Bank of Denmark</a>.</O>,
		"da": () => <O>Vi understøtter handel med valutaer. Valutakurserne følger dem fra <a target="_blank" rel="noopener noreferrer" href="https://da.wikipedia.org/wiki/Danmarks_Nationalbank">Danmarks Nationalbank</a>.</O>,
		"sv": () => <O>Vi stödjer handel med valutor. Valutakurserna följer kurser från <a target="_blank" rel="noopener noreferrer" href="https://sv.wikipedia.org/wiki/Danmarks_Nationalbank">Danmarks Nationalbank</a>.</O>,
		"de": () => <O>Wir unterstützen den Kauf einer Reihe von Währungen. Die Wechselkurse folgen den Kursen der <a target="_blank" rel="noopener noreferrer" href="https://de.wikipedia.org/wiki/D%C3%A4nische_Nationalbank">Nationalbank Dänemarks</a>.</O>,
	},

	"880541ca7f26": {
		"en": () => <O>Crypto Market</O>,
		"da": () => <O>Kryptomarkedet</O>,
		"sv": () => <O>Kryptomarknaden</O>,
		"de": () => <O>Kryptomarkt</O>,
	},

	"4caaa145d799": {
		"en": (A, T) => {
			return (
				<O>
				<a className="ad_footer_link" href="https://undervisning.aktiedysten.dk/om-aktiedysten/">
				  <T>About Aktiedysten</T>
				</a>
				</O>
			);
		},
		"da": (A, T) => {
			return (
				<O>
				<a className="ad_footer_link" href="https://undervisning.aktiedysten.dk/om-aktiedysten/">
				  <T>About Aktiedysten</T>
				</a>
				</O>
			);
		},
		"sv": (A, T) => {
			return (
				<O>
				<a className="ad_footer_link" href="https://undervisning.aktiekampen.se/om-aktiekampen">
				  <T>About Aktiedysten</T>
				</a>
				</O>
			);
		},
		"de": (A, T) => {
			return (
				<O>
				<a className="ad_footer_link" href="https://undervisning.aktiedysten.dk/om-aktiedysten/">
				  <T>About Aktiedysten</T>
				</a>
				</O>
			);
		},
	},

	"9c87a994e0b4": {
		"en": () => <O>We mirror the crypto currencies traded on the crypto exchanges. The different currencies will be listed and de-listed based on the volume traded. A certain volume is needed before you can trade it in our game.</O>,
		"da": () => <O>Vi spejler kryptovalutaer der handles på kryptobørser. Det er nødvendigt med en vis handelsvolumen, så de forskellige valutaer bliver noteret og afnoteret i spillet, baseret på handelsvolumen i virkeligheden.</O>,
		"sv": () => <O>Vi speglar kryptovalutorna som handlats med från kryptobörser. Viss handelsvolym behövs så att de olika valutorna listas och avlistas i spelet, baserat på den faktiska handelsvolymeni.</O>,
		"de": () => <O>Wir spiegeln die Kryptowährungen wider, die an den Kryptobörsen gehandelt werden. Die verschiedenen Währungen werden basierend auf dem gehandelten Volumen gelistet und ggf. von der Liste gestrichen. Kryptowährungen benötigen ein bestimmtes Volumen, bevor sie in unserem Spiel handelbar sind.</O>,
	},

	"641799a8e2ca": {
		"en": () => <O>ETFs</O>,
		"da": () => <O>ETF'er</O>,
		"sv": () => <O>ETF</O>,
		"de": () => <O>ETFs</O>,
	},

	"5e2e5e0dd169": {
		"en": () => <O>An exchange-traded fund (ETF) is a pooled investment security that can be bought and sold like an individual stock. ETFs can be structured to track anything from the price of a commodity to a large and diverse collection of securities.</O>,
		"da": () => <O>En børshandlet fond (ETF) er en samlet investeringssikkerhed, der kan købes og sælges som en individuel aktie. ETF'er kan struktureres til at spore alt fra prisen på en råvare til en stor og forskelligartet samling af værdipapirer.</O>,
		"sv": () => <O>En börshandlad fond (ETF) är ett samlat investeringsskydd som kan köpas och säljas som en enskild aktie. ETF:er kan struktureras för att spåra allt från priset på en vara till en stor och mångsidig samling värdepapper.</O>,
		"de": () => <O>Ein börsengehandelter Fonds (ETF) ist ein gepooltes Anlagepapier, das wie eine einzelne Aktie gekauft und verkauft werden kann. ETFs können so strukturiert werden, dass sie alles vom Preis eines Rohstoffs bis hin zu einer großen und vielfältigen Sammlung von Wertpapieren abbilden.</O>,
	},

	"97a6108ee0a4": {
		"en": () => <O>(EFT - Exchange-Traded Funds)</O>,
		"da": () => <O>(EFT - Exchange-Traded Funds)</O>,
		"sv": () => <O>(EFT - Exchange-Traded Funds)</O>,
		"de": () => <O>(EFT - Exchange-Traded Funds)</O>,
	},

	"0b8ea0792165": {
		"en": () => <O>Explore markets</O>,
		"da": () => <O>Udforsk markeder</O>,
		"sv": () => <O>Udforsk marknader</O>,
		"de": () => <O>Märkte erkunden</O>,
	},

	"20a44780cf28": {
		"en": () => <O>Search crypto-currencies</O>,
		"da": () => <O>Søg kryptovalutaer</O>,
		"sv": () => <O>Sök kryptovalutor</O>,
		"de": () => <O>Nach Kryptowährungen suchen</O>,
	},

	"81223aa0d660": {
		"en": () => <O>Crypto currencies</O>,
		"da": () => <O>Kryptovalutaer</O>,
		"sv": () => <O>Kryptovalutor</O>,
		"de": () => <O>Kryptowährungen</O>,
	},

	"20b4c6e37690": {
		"en": () => <O>Explore various crypto currencies</O>,
		"da": () => <O>Udforsk forskellige kryptovalutaer</O>,
		"sv": () => <O>Utforska olika kryptovalutor</O>,
		"de": () => <O>Verschiedene Kryptowährungen entdecken</O>,
	},

	"b35854ca5dc3": {
		"en": () => <O>Danish banks</O>,
		"da": () => <O>Danske banker</O>,
		"sv": () => <O>Danska banker</O>,
		"de": () => <O>Dänische Banken</O>,
	},

	"c5d31751a319": {
		"en": () => <O>Danish soccer clubs</O>,
		"da": () => <O>Danske fodboldklubber</O>,
		"sv": () => <O>Danska fotbollsklubbar</O>,
		"de": () => <O>Dänische Fußballvereine</O>,
	},

	"2a3b2b023619": {
		"en": () => <O>Danish collections</O>,
		"da": () => <O>Danske lister</O>,
		"sv": () => <O>Danska listor</O>,
		"de": () => <O>Dänische Listen</O>,
	},

	"8ab3362c2b3f": {
		"en": () => <O>Selected danish stocks</O>,
		"da": () => <O>Udvalgte danske aktier</O>,
		"sv": () => <O>Utvalda danska aktier</O>,
		"de": () => <O>Ausgewählte dänische Aktien</O>,
	},

	"39088ec7cd5f": {
		"en": () => <O>Random danish stocks</O>,
		"da": () => <O>Tilfældige danske aktier</O>,
		"sv": () => <O>Slumpmässiga danska aktier</O>,
		"de": () => <O>Zufällige dänische Aktien</O>,
	},

	"9f97a0f2c4d7": {
		"en": () => <O>Search danish assets</O>,
		"da": () => <O>Søg danske værdipapirer</O>,
		"sv": () => <O>Sök danska värdepapper</O>,
		"de": () => <O>Nach dänischen Aktien suchen</O>,
	},

	"65c242e152d2": {
		"en": () => <O>Copy to clipboard</O>,
		"da": () => <O>Kopiér til clipboard</O>,
		"sv": () => <O>Kopiera till urklipp</O>,
		"de": () => <O>In die Zwischenablage kopieren</O>,
	},

	"112ab7b60e88": {
		"en": () => <O>Copied!</O>,
		"da": () => <O>Kopieret!</O>,
		"sv": () => <O>Kopierat!</O>,
		"de": () => <O>Kopiert!</O>,
	},

	"e439ab935f55": {
		"en": () => <O>Copy link</O>,
		"da": () => <O>Kopiér link</O>,
		"sv": () => <O>Kopiera link</O>,
		"de": () => <O>Link kopieren</O>,
	},

	"f383476df935": {
		"en": () => <O>Let us play a game!</O>,
		"da": () => <O>Lad os dyste!</O>,
		"sv": () => <O>Låt oss spela ett spel!</O>,
		"de": () => <O>Lass uns ein Investmentspiel spielen!</O>,
	},

	"2a3bf248378f": {
		"en": () => <O>Link to the game: </O>,
		"da": () => <O>Link til spillet: </O>,
		"sv": () => <O>Länk till spelet: </O>,
		"de": () => <O>Klicke hier, um dem Spiel beizutreten: </O>,
	},

	"288cef8a26d3": {
		"en": () => <O>Share</O>,
		"da": () => <O>Dél</O>,
		"sv": () => <O>Dela</O>,
		"de": () => <O>Teilen</O>,
	},

	"6cab0819d525": {
		"en": () => <O>Message</O>,
		"da": () => <O>Besked</O>,
		"sv": () => <O>Meddelande</O>,
		"de": () => <O>Nachricht</O>,
	},

	"cdda11bf911a": {
		"en": () => <O>Drag to show development</O>,
		"da": () => <O>Træk for at vise udvikling</O>,
		"sv": () => <O>Dra för att visa utveckling</O>,
		"de": () => <O>Ziehen, um die Entwicklung zu sehen</O>,
	},

	"836aa875758b": {
		"en": () => <O>no chart data yet</O>,
		"da": () => <O>ingen data endnu</O>,
		"sv": () => <O>ingen data ännu</O>,
		"de": () => <O>Noch keine Diagrammdaten</O>,
	},

	"866308f8ecca": {
		"en": () => <O>5y</O>,
		"da": () => <O>5å</O>,
		"sv": () => <O>5å</O>,
		"de": () => <O>5J</O>,
	},

	"9580fc35fb0b": {
		"en": (volume) => <O>Volume: {volume}</O>,
		"da": (volume) => <O>Volume: {volume}</O>,
		"sv": (volume) => <O>Volume: {volume}</O>,
		"de": (volume) => <O>Volumen: {volume}</O>,
	},

	"eafc402e73a3": {
		"en": () => <O>1y</O>,
		"da": () => <O>1å</O>,
		"sv": () => <O>1å</O>,
		"de": () => <O>1J</O>,
	},

	"9f96d8b09923": {
		"en": () => <O>3m</O>,
		"da": () => <O>3m</O>,
		"sv": () => <O>3m</O>,
		"de": () => <O>3M</O>,
	},

	"e51a0b2f093b": {
		"en": () => <O>1m</O>,
		"da": () => <O>1m</O>,
		"sv": () => <O>1m</O>,
		"de": () => <O>1M</O>,
	},

	"95be6ad92a60": {
		"en": () => <O>7d</O>,
		"da": () => <O>7d</O>,
		"sv": () => <O>7d</O>,
		"de": () => <O>7T</O>,
	},

	"034dbc82aee7": {
		"en": () => <O>24h</O>,
		"da": () => <O>24t</O>,
		"sv": () => <O>24t</O>,
		"de": () => <O>24&nbsp;Std.</O>,
	},

	"f0371bab0e06": {
		"en": () => <O>Notifications</O>,
		"da": () => <O>Notifikationer</O>,
		"sv": () => <O>Notifikationer</O>,
		"de": () => <O>Benachrichtigungen</O>,
	},

	"3c7312341c6d": {
		"en": () => <O>Achievements</O>,
		"da": () => <O>Bedrifter</O>,
		"sv": () => <O>Bedrifter</O>,
		"de": () => <O>Erfolge</O>,
	},

	"287183d326e6": {
		"en": () => <O>Trade history</O>,
		"da": () => <O>Handelshistorik</O>,
		"sv": () => <O>Handelshistorik</O>,
		"de": () => <O>Handelsverlauf</O>,
	},

	"986f59e83a30": {
		"en": () => <O>Achievements can be won both<br/>in official and own games</O>,
		"da": () => <O>Bedrifter kan opnås både<br/>i officielle og egne spil</O>,
		"sv": () => <O>Bedrifter kan uppnås både<br/>i officiella och egna spel</O>,
		"de": () => <O>Erfolge können sowohl in offiziellen als auch <br/> in privaten Spielen erzielt werden</O>,
	},

	"892c12b1cb5b": {
		"en": () => <O>Preferences</O>,
		"da": () => <O>Indstillinger</O>,
		"sv": () => <O>Inställningar</O>,
		"de": () => <O>Einstellungen</O>,
	},

	"b9a6b5ee2d97": {
		"en": () => <O>Your status:</O>,
		"da": () => <O>Din status:</O>,
		"sv": () => <O>Din status:</O>,
		"de": () => <O>Ihr Status:</O>,
	},

	"0962c54c291d": {
		"en": () => <O>Go to games</O>,
		"da": () => <O>Gå til spil</O>,
		"sv": () => <O>Gå till spel</O>,
		"de": () => <O>Zu den Spielen</O>,
	},

	"682595ceac59": {
		"en": () => <O>Upgrade</O>,
		"da": () => <O>Opgrader</O>,
		"sv": () => <O>Uppgradera</O>,
		"de": () => <O>Upgrade</O>,
	},

	"085ca9a7389b": {
		"en": (site_name) => <O>Try {site_name} for free</O>,
		"da": (site_name) => <O>Prøv {site_name} Gratis</O>,
		"sv": (site_name) => <O>Prova {site_name} gratis</O>,
		"de": (site_name) => <O>Teste {site_name} kostenlos</O>,
	},

	"ce4a52611eb0": {
		"en": () => <O>Start free game</O>,
		"da": () => <O>Start gratis spil</O>,
		"sv": () => <O>Starta gratisspel</O>,
		"de": () => <O>Gratis-Spiel starten</O>,
	},

	"d3b97d6bd3cd": {
		"en": () => <O>Premium from 6kr. per participant</O>,
		"da": () => <O>Premium fra 6 kr. pr. deltager</O>,
		"sv": () => <O>Premium från 6 kr per deltagare</O>,
		"de": () => <O>Premium ab 6 kr pro Teilnehmer</O>,
	},

	"3943acf7d715": {
		"en": (availableKeys, invitedUsers) => <O>*Upgrading the game will use {invitedUsers} invites. You currently have {availableKeys} invites available.</O>,
		"da": (availableKeys, invitedUsers) => <O>*Upgrading the game will use {invitedUsers} invites. You currently have {availableKeys} invites available.</O>,
		"sv": (availableKeys, invitedUsers) => <O>*Upgrading the game will use {invitedUsers} invites. You currently have {availableKeys} invites available.</O>,
		"de": (availableKeys, invitedUsers) => <O>*Upgrading the game will use {invitedUsers} invites. You currently have {availableKeys} invites available.</O>,
	},

	"d894715491de": {
		"en": () => <O>Telephone support</O>,
		"da": () => <O>Telefonisk support</O>,
		"sv": () => <O>Support via telefon</O>,
		"de": () => <O>Telefonische Unterstützung</O>,
	},

	"266922258c91": {
		"_deleted": true, // FIXME translation has no references; delete this entire section if you no longer need it
		"en": () => <O>Submit</O>,
		"da": () => <O>Indsend</O>,
		"sv": () => <O>Skicka in</O>,
		"de": () => <O>Einreichen</O>,
	},

	"53337e7fc6db": {
		"en": () => {
			return (
				<O>
				<h2>FAQ</h2>
				<p><strong>Can I use Aktiedysten commercially?</strong></p>
				<p>No, you may not use Aktiedysten commercially without first entering into an agreement with Aktiedysten. Commercial means the use of Aktiedysten for recruitment and/or outreach to non-existing customers/users. For commercial use, an additional fee of DKK 10,000 excluding VAT will be charged.</p>
				
				<p><strong>Am I the only one who becomes premium?</strong></p>
				<p>When you upgrade to premium, you will have access to the benefits that come with it. Likewise, the players you invite to your games will have premium benefits on the games they are invited to by you.<br />
				The invitations are not personalised, which means that you can transfer a key from one user to another if you wish. However, you can only have the number of Invitations in use at the same time that you have purchased.</p>
				
				<p><strong>How long is my premium valid for?</strong></p>
				<p>If you have chosen a monthly subscription, your Premium status will automatically renew until you cancel the subscription. If you have chosen a 1-year subscription, we will contact you one month before expiry.<br />
				When you cancel your subscription, it will stop at the end of the month you cancel your subscription.</p>
				
				<p><strong>What does a premium invitation cover?</strong></p>
				<p>An invitation gives you access to Aktiedysten's premium content, including more markets, key figures and visualisation of risk.<br />
				You as an administrator decide who should have premium access. The invitations are not personalised, which means that you can transfer a key from one user to another if you wish. However, you can only have the number of Invitations in use at the same time that you have purchased.</p>
				
				<p><strong>What if I have started a game and am no longer premium?</strong></p>
				<p>If you have started a game but cancel your subscription, your game will automatically be cancelled at the end of the premium period.<br />
				The users you have invited will no longer have premium benefits.</p>
				
				<p><strong>Who can I share my premium invitations with?</strong></p>
				<p>You as the administrator decide who should have premium access. The invitations are not personalised, which means you can transfer a key from one user to another if you wish. However, you can only have the number of invitations in use at the same time that you have purchased</p>
				
				<p><strong>What is the maximum amount of premium invitations I can include in my package?</strong></p>
				<p>You decide how many Invitations you want to open up. However, you can only have the number of Invitations in use that you have purchased.</p>
				
				<p><strong>How can I keep track of how many Invitations I have?</strong></p>
				<p>You can always contact us at <a href="mailto:info@aktiedysten.dk">info@aktiedysten.dk</a> to find out how many Invitations you have left and how long your subscription runs for. <br />
				You as the administrator decide who should have premium access. The invitations are not personalised, which means that you can transfer a key from one user to another if you wish. However, you can only have the number of Invitations in use at the same time that you have purchased.<br />
				For example, you can subscribe to 50 invitations for a year (at a discount) - and play for six months with 50 people and for the next six months with 50 other people. You choose who is in the game.</p>
				
				<p><strong>Can I buy single games</strong></p>
				<p>When you upgrade to premium, it's a subscription you start. However, you can cancel your subscription at any time at the end of the month.</p>
				
				<p><strong>How am I charged for premium invitations?</strong></p>
				<p>You can choose between paying per month at 24 kr. per invitation/month or you can choose to pay annually, where you get half price and pay 12 kr. per invitation/month billed annually.</p>
				
				<p><strong>When can I cancel my subscription?</strong></p>
				<p>You can cancel your subscription at the end of the subscription period. A subscription is valid for 1 year, so you can cancel it at the end of your subscription period.<br />
				When you cancel your subscription, it will stop at the end of the month you cancel your subscription.</p>
				
				<p><strong>What happens to my unused invitations?</strong></p>
				<p>Unused invitations cannot be transferred to the next subscription or game. It is therefore important to use your invitations so you don't miss the opportunity to activate them.</p>
				
				<p><strong>Is there a limit to how many games I can create with the premium invitations?</strong></p>
				<p>No, as premium you can create an unlimited number of games, however, you can only have the number of invitations active that you have purchased. This means that if you have purchased 10 invitations, you can have, for example, 2 games with 5 on each, but you cannot have 2 games with 10 on each.</p>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				<h2>FAQ</h2>
				<p><strong>Kan jeg bruge Aktiedysten kommercielt?</strong></p>
				<p>Nej, du må ikke bruge Aktiedysten kommercielt uden først at indgå en aftale med Aktiedysten. Med kommercielt menes brugen af Aktiedysten til rekruttering og/eller outreach til ikke-eksisterende kunder/bruger. Ved kommerciel brug pålægges der et ekstra gebyr på 10.000 kr. ekskl. Moms.</p>
				
				<p><strong>Er det kun mig der bliver premium?</strong></p>
				<p>Når man opgraderet til premium vil du have adgang til de fordele der følger med. Ligeledes vil de spillere du inviterer med til dine spil have premiumfordele på de spil de er inviteret med til af dig.<br />
				Invitationerne er ikke personlige, hvilket betyder, at du kan overføre en nøgle fra en bruger til en anden, hvis du ønsker det. Du kan dog kun have det antal Invitationer i brug samtidig, som du har købt.</p>
				
				<p><strong>Hvor længe gælder min premium?</strong></p>
				<p>Hvis du har valgt månedsabonnement fornyes din Premium-status automatisk, indtil du opsiger abonnementet. Hvis du har valgt 1 års abonnement kontakter vi dig en måned før udløb.<br />
				Når du opsiger dit abonnement, vil det stoppe med udgangen af den måned du opsiger dit abonnement.</p>
				
				<p><strong>Hvad dækker en premium invitation over?</strong></p>
				<p>En invitation giver adgang til Aktiedystens premium-indhold blandt andet flere markeder, nøgletal og synliggørelse af risiko.<br />
				Du som administrator bestemmer, hvem der skal have premium-adgang. Invitationerne er ikke personlige, hvilket betyder, at du kan overføre en nøgle fra en bruger til en anden, hvis du ønsker det. Du kan dog kun have det antal Invitationer i brug samtidig, som du har købt.</p>
				
				<p><strong>Hvad hvis jeg har startet et spil og ikke længere er premium?</strong></p>
				<p>Hvis du har startet et spil, men opsiger dit abonnement, vil dit spil automatisk blive afsluttet ved udløb af premium perioden.<br />
				De brugere som du har inviteret har herfra ikke længere Premium-fordele.</p>
				
				<p><strong>Hvem kan jeg dele mine premium-invitationer med?</strong></p>
				<p>Du som administrator bestemmer, hvem der skal have premium-adgang. Invitationerne er ikke personlige, hvilket betyder, at du kan overføre en nøgle fra en bruger til en anden, hvis du ønsker det. Du kan dog kun have det antal invitationer i brug samtidig, som du har købt</p>
				
				<p><strong>Hvad er den maksimale mængde premium-invitationer jeg kan inkludere i min pakke?</strong></p>
				<p>Du bestemmer selv, hvor mange Invitationer du ønsker at åbne op for. Du kan dog kun have det antal Invitationer i brug, som du har købt.</p>
				
				<p><strong>Hvordan kan jeg holde styr på hvor mange Invitationer jeg har?</strong></p>
				<p>Du kan til hver en tid kontakte os på <a href="mailto:info@aktiedysten.dk">info@aktiedysten.dk</a>, for at få oplyst, hvor mange Invitationer du har tilbage og hvor lang tid dit abonnement løber over. <br />
				Du som administrator bestemmer, hvem der skal have premium-adgang. Invitationerne er ikke personlige, hvilket betyder, at du kan overføre en nøgle fra en bruger til en anden, hvis du ønsker det. Du kan dog kun have det antal Invitationer i brug samtidig, som du har købt.<br />
				Du kan f.eks. abonnere på 50 invitationer i et år (med rabat) - og spille i seks måneder med 50 personer og i de næste seks måneder med 50 andre personer. Det er dig, der vælger, hvem der er med i spillet.</p>
				
				<p><strong>Kan jeg købe enkelt spil</strong></p>
				<p>Når man opgraderer til premium, så er det et abonnement du starter. Men du kan til hver en tid opsige dit abonnement, der løber måneden ud.</p>
				
				<p><strong>Hvordan bliver jeg opkrævet for premium-Invitationer?</strong></p>
				<p>Du kan vælge mellem at betale pr. Måned til 24 kr. pr. Invitation /måned eller du kan vælge at betale årligt, hvor at du opnår halv pris og dermed skal betale 12 kr. pr. Invitation /måned faktureret årligt.</p>
				
				<p><strong>Hvornår kan jeg annullere mit abonnement?</strong></p>
				<p>Du kan annullere dit abonnement ved udløb af abonnementsperioden. Et abonnement gælder i 1 år, så du kan opsige det ved udgangen af din abonnementsperiode.<br />
				Når du opsiger dit abonnement, vil det stoppe med udgangen af den måned du opsiger dit abonnement.</p>
				
				<p><strong>Hvad sker der med mine ubrugte invitationer?</strong></p>
				<p>Ubrugte invitationer kan ikke overføres til næste abonnement eller spil. Det er derfor vigtigt at bruge dine invitationer, så du ikke går glip af muligheden for at aktivere dem.</p>
				
				<p><strong>Er der en grænse for, hvor mange spil jeg kan oprette med premium-invitationerne?</strong></p>
				<p>Nej, som premium kan du oprette et ubegrænset antal spil, du kan dog kun have det antal invitationer aktive, som du har købt. Dvs. har du købt 10 inviationer, kan du godt have fx 2 spil med 5 på hver, men du kan ikke have 2 spil med 10 på hver.</p>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				<h2>FAQ</h2>
				<p><strong>Kan jag använda Aktiedysten kommersiellt?</strong></p>
				<p>Nej, du får inte använda Aktiedysten kommersiellt utan att först ha ingått ett avtal med Aktiedysten. Med kommersiellt avses användning av Aktiedysten för rekrytering och/eller uppsökande verksamhet till icke befintliga kunder/användare. För kommersiell användning debiteras en extra avgift på 10 000 DKK exklusive moms.</p>
				
				<p><strong>Är jag den enda som blir premium?</strong></p>
				<p>När du uppgraderar till premium får du tillgång till de fördelar som följer med det. På samma sätt kommer de spelare som du bjuder in till dina spel att ha premiumförmåner på de spel som du bjuder in dem till. <br />
				Inbjudningarna är inte personliga, vilket innebär att du kan överföra en nyckel från en användare till en annan om du vill. Du kan dock bara ha det antal inbjudningar i bruk samtidigt som du har köpt.</p>
				
				<p><strong>Hur länge är min premium giltig?</strong></p>
				<p>Om du har valt en månadsprenumeration kommer din Premium-status att förnyas automatiskt tills du säger upp prenumerationen. Om du har valt en 1-årsprenumeration kontaktar vi dig en månad innan den löper ut.<br />
				När du säger upp din prenumeration upphör den att gälla i slutet av den månad du säger upp prenumerationen.</p>
				
				<p><strong>Vad omfattar en premiuminbjudan?</strong></p>
				<p>En inbjudan ger dig tillgång till Aktiedystens premiuminnehåll, bland annat fler marknader, nyckeltal och visualisering av risk.<br />
				Du som administratör bestämmer vem som ska ha premiumåtkomst. Inbjudningarna är inte personaliserade, vilket innebär att du kan överföra en nyckel från en användare till en annan om du vill. Du kan dock bara ha det antal Invitationer i bruk samtidigt som du har köpt.</p>
				
				<p><strong>Vad händer om jag har startat ett spel och inte längre är premium?</strong></p>
				<p>Om du har startat ett spel men avbryter din prenumeration kommer ditt spel automatiskt att avbrytas i slutet av premiumperioden.<br />
				De användare som du har bjudit in kommer inte längre att ha premiumförmåner.</p>
				
				<p><strong>Vem kan jag dela mina premiuminbjudningar med?</strong></p>
				<p>Du som administratör bestämmer vem som ska ha premiumåtkomst. Inbjudningarna är inte personaliserade, vilket innebär att du kan överföra en nyckel från en användare till en annan om du vill. Du kan dock bara ha det antal inbjudningar i bruk samtidigt som du har köpt</p>
				
				<p><strong>Vad är det högsta antalet premiuminbjudningar som jag kan inkludera i mitt paket?</strong></p>
				<p>Du bestämmer själv hur många inbjudningar du vill öppna upp. Du kan dock bara ha det antal Inbjudningar i bruk som du har köpt.</p>
				
				<p><strong>Hur kan jag hålla koll på hur många Invitationer jag har?</strong></p>
				<p>Du kan alltid kontakta oss på <a href="mailto:info@aktiedysten.dk">info@aktiedysten.dk</a> för att få reda på hur många Inbjudningar du har kvar och hur länge ditt abonnemang löper.<br />
				Det är du som administratör som bestämmer vem som ska ha premiumåtkomst. Inbjudningarna är inte personaliserade, vilket innebär att du kan överföra en nyckel från en användare till en annan om du vill. Du kan dock bara ha det antal Invitationer i bruk samtidigt som du har köpt.<br />
				Du kan t.ex. prenumerera på 50 inbjudningar under ett år (med rabatt) - och spela i sex månader med 50 personer och i nästa sex månader med 50 andra personer. Du väljer själv vilka som är med i spelet.</p>
				
				<p><strong>Kan jag köpa enstaka spel</strong></p>
				<p>När du uppgraderar till premium är det en prenumeration du startar. Du kan dock säga upp din prenumeration när som helst i slutet av månaden. </p>
				
				<p><strong>Hur debiteras jag för premiuminbjudningar?</strong></p>
				<p>Du kan välja mellan att betala per månad á 24 kr per inbjudan/månad eller så kan du välja att betala årsvis, då får du halva priset och betalar 12 kr per inbjudan/månad fakturerat årsvis.</p>
				
				<p><strong>När kan jag säga upp min prenumeration?</strong></p>
				<p>Du kan säga upp din prenumeration i slutet av prenumerationsperioden. En prenumeration är giltig i 1 år, så du kan säga upp den i slutet av din prenumerationsperiod.<br />
				När du säger upp din prenumeration kommer den att upphöra i slutet av den månad du säger upp prenumerationen.</p>
				
				<p><strong>Vad händer med mina oanvända inbjudningar?</strong></p>
				<p>Oanvända inbjudningar kan inte överföras till nästa prenumeration eller spel. Det är därför viktigt att du använder dina inbjudningar så att du inte missar möjligheten att aktivera dem.</p>
				
				<p><strong>Finns det en gräns för hur många spel jag kan skapa med premiuminbjudningarna?</strong></p>
				<p>Nej, som premium kan du skapa ett obegränsat antal spel, men du kan bara ha det antal inbjudningar aktiva som du har köpt. Det innebär att om du har köpt 10 inbjudningar kan du till exempel ha 2 spel med 5 inbjudningar vardera, men du kan inte ha 2 spel med 10 inbjudningar vardera.</p>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				<h2>FAQ</h2>
				<p><strong>Kann ich Aktiedysten gewerblich nutzen?</strong></p>
				<p>Nein, Sie dürfen Aktiedysten nicht kommerziell nutzen, ohne vorher eine Vereinbarung mit Aktiedysten getroffen zu haben. Unter kommerzieller Nutzung versteht man die Nutzung der Aktiedysten zur Anwerbung und/oder Ansprache von nicht vorhandenen Kunden/Nutzern. Für die kommerzielle Nutzung wird eine zusätzliche Gebühr von 10.000 DKK ohne Mehrwertsteuer erhoben.</p>
				
				<p><strong>Bin ich der Einzige, der Premium wird?</strong></p>
				<p>Wenn Sie ein Premium-Upgrade durchführen, haben Sie Zugang zu den damit verbundenen Vorteilen. Auch die Spieler, die Sie zu Ihren Spielen einladen, erhalten Premium-Vorteile für die Spiele, zu denen sie von Ihnen eingeladen werden.<br />
				Die Einladungen sind nicht personalisiert, d. h. Sie können einen Schlüssel von einem Nutzer auf einen anderen übertragen, wenn Sie dies wünschen. Allerdings können Sie nur so viele Einladungen gleichzeitig nutzen, wie Sie erworben haben.</p>
				
				<p><strong>Wie lange ist meine Prämie gültig?</strong></p>
				<p>Wenn Sie ein Monatsabonnement gewählt haben, verlängert sich Ihr Premium-Status automatisch, bis Sie das Abonnement kündigen. Wenn Sie sich für ein 1-Jahres-Abonnement entschieden haben, werden Sie einen Monat vor Ablauf von uns benachrichtigt.<br />
				Wenn Sie Ihr Abonnement kündigen, endet es am Ende des Monats, in dem Sie Ihr Abonnement gekündigt haben.</p>
				
				<p><strong>Was beinhaltet eine Premium-Einladung?</strong></p>
				<p>Mit einer Einladung erhalten Sie Zugang zu den Premium-Inhalten von Aktiedysten, einschließlich weiterer Märkte, Kennzahlen und Risikovisualisierungen.<br />
				Sie als Administrator entscheiden, wer Premium-Zugang haben soll. Die Einladungen sind nicht personalisiert, d.h. Sie können einen Schlüssel von einem Nutzer auf einen anderen übertragen, wenn Sie dies wünschen. Allerdings können Sie nur so viele Einladungen gleichzeitig nutzen, wie Sie erworben haben.</p>
				
				<p><strong>Was ist, wenn ich ein Spiel begonnen habe und nicht mehr Premium bin?</strong></p>
				<p>Wenn Sie ein Spiel begonnen haben, aber Ihr Abonnement kündigen, wird Ihr Spiel am Ende des Premiumzeitraums automatisch beendet.<br />
				Die Nutzer, die Sie eingeladen haben, haben dann keine Premium-Vorteile mehr.</p>
				
				<p><strong>Mit wem kann ich meine Premium-Einladungen teilen?</strong></p>
				<p>Sie als Administrator entscheiden, wer Premium-Zugang haben soll. Die Einladungen sind nicht personalisiert, d. h. Sie können einen Schlüssel von einem Nutzer auf einen anderen übertragen, wenn Sie dies wünschen. Allerdings können Sie nur so viele Einladungen gleichzeitig nutzen, wie Sie erworben haben</p>
				
				<p><strong>Wie viele Premium-Einladungen kann ich maximal in mein Paket aufnehmen?</strong></p>
				<p>Sie entscheiden, wie viele Einladungen Sie öffnen möchten. Sie können jedoch nur so viele Einladungen verwenden, wie Sie gekauft haben.</p>
				
				<p><strong>Wie kann ich verfolgen, wie viele Einladungen ich habe?</strong></p>
				<p>Sie können uns jederzeit unter  <a href="mailto:info@aktiedysten.dk">info@aktiedysten.dk</a> kontaktieren, um herauszufinden, wie viele Einladungen Sie noch haben und wie lange Ihr Abonnement läuft.<br />
				Sie als Administrator entscheiden, wer Premium-Zugang haben soll. Die Einladungen sind nicht personalisiert, d. h. Sie können einen Schlüssel von einem Nutzer auf einen anderen übertragen, wenn Sie dies wünschen. Allerdings können Sie nur so viele Einladungen gleichzeitig nutzen, wie Sie erworben haben.<br />
				Sie können z. B. 50 Einladungen für ein Jahr abonnieren (mit einem Rabatt) - und sechs Monate lang mit 50 Personen und die nächsten sechs Monate mit 50 anderen Personen spielen. Sie entscheiden, wer mitspielt.</p>
				
				<p><strong>Kann ich einzelne Spiele kaufen?</strong></p>
				<p>Wenn Sie auf Premium upgraden, beginnen Sie ein Abonnement. Sie können Ihr Abonnement jedoch jederzeit zum Ende des Monats kündigen.</p>
				
				<p><strong>Wie werden die Premium-Einladungen berechnet?</strong></p>
				<p>Sie haben die Wahl zwischen einer monatlichen Zahlung von 24 kr. pro Einladung/Monat oder einer jährlichen Zahlung, bei der Sie die Hälfte des Preises erhalten und 12 kr. pro Einladung/Monat zahlen, die jährlich abgerechnet werden.</p>
				
				<p><strong>Wann kann ich mein Abonnement kündigen?</strong></p>
				<p>Sie können Ihr Abonnement zum Ende des Abonnementzeitraums kündigen. Ein Abonnement hat eine Laufzeit von 1 Jahr, Sie können es also zum Ende des Abonnementzeitraums kündigen.<br />
				Wenn Sie Ihr Abonnement kündigen, endet es am Ende des Monats, in dem Sie Ihr Abonnement gekündigt haben.</p>
				
				<p><strong>Was passiert mit meinen ungenutzten Einladungen?</strong></p>
				<p>Ungenutzte Einladungen können nicht auf das nächste Abonnement oder Spiel übertragen werden. Es ist daher wichtig, dass Sie Ihre Einladungen nutzen, damit Sie nicht die Gelegenheit verpassen, sie zu aktivieren.</p>
				
				<p><strong>Gibt es eine Begrenzung, wie viele Spiele ich mit den Premium-Einladungen erstellen kann?</strong></p>
				<p>Nein, als Premium-Mitglied können Sie eine unbegrenzte Anzahl von Spielen erstellen, allerdings können Sie nur so viele Einladungen aktiv haben, wie Sie gekauft haben. Das heißt, wenn Sie 10 Einladungen gekauft haben, können Sie z. B. 2 Spiele mit je 5 Einladungen erstellen, aber nicht 2 Spiele mit je 10 Einladungen.</p>
				</O>
			);
		},
	},

	"8f71f145a85d": {
		"en": () => <O>1 month</O>,
		"da": () => <O>1 måned</O>,
		"sv": () => <O>1 månad</O>,
		"de": () => <O>1 Monat</O>,
	},

	"4cd9c4033c62": {
		"en": () => <O>3 months</O>,
		"da": () => <O>3 måneder</O>,
		"sv": () => <O>3 månader</O>,
		"de": () => <O>3 Monate</O>,
	},

	"faf18891ea12": {
		"en": () => <O>6 months</O>,
		"da": () => <O>6 måneder</O>,
		"sv": () => <O>6 månader</O>,
		"de": () => <O>6 Monate</O>,
	},

	"8a1b72e95707": {
		"en": () => <O>12 months</O>,
		"da": () => <O>12 måneder</O>,
		"sv": () => <O>12 månader</O>,
		"de": () => <O>12 Monate</O>,
	},

	"22ccc4476da2": {
		"en": () => <O>18 kr.</O>,
		"da": () => <O>18 kr.</O>,
		"sv": () => <O>27 kr</O>,
		"de": () => <O>2,4 €</O>,
	},

	"3e21108ab55d": {
		"en": () => <O>3 months save 33%</O>,
		"da": () => <O>3 måneder spar 33%</O>,
		"sv": () => <O>3 månader spara 33%</O>,
		"de": () => <O>3 Monate sparen 33%</O>,
	},

	"c07d75e3f1de": {
		"en": () => <O>12 kr.</O>,
		"da": () => <O>12 kr.</O>,
		"sv": () => <O>18 kr</O>,
		"de": () => <O>1,6 €</O>,
	},

	"20ba48e9a5f1": {
		"en": () => <O>36 kr.</O>,
		"da": () => <O>36 kr.</O>,
		"sv": () => <O>54 kr</O>,
		"de": () => <O>4,8 €</O>,
	},

	"3cfa186d3c38": {
		"en": () => <O>54 kr.</O>,
		"da": () => <O>54 kr.</O>,
		"sv": () => <O>84 kr</O>,
		"de": () => <O>7,2 €</O>,
	},

	"dd8a049947c8": {
		"en": () => <O>108 kr.</O>,
		"da": () => <O>108 kr.</O>,
		"sv": () => <O>162 kr</O>,
		"de": () => <O>14,4 €</O>,
	},

	"90fb1532e6bf": {
		"en": () => <O>12 months save 66%</O>,
		"da": () => <O>12 måneder spar 66%</O>,
		"sv": () => <O>12 månader spara 66%</O>,
		"de": () => <O>12 Monate sparen 66%</O>,
	},

	"b2fda578df6b": {
		"en": () => <O>6 kr.</O>,
		"da": () => <O>6 kr.</O>,
		"sv": () => <O>9 kr</O>,
		"de": () => <O>0,8 €</O>,
	},

	"05cd6fcd5c52": {
		"en": () => <O>72 kr.</O>,
		"da": () => <O>72 kr.</O>,
		"sv": () => <O>108 kr</O>,
		"de": () => <O>9,6 €</O>,
	},

	"062f9b73ef96": {
		"en": () => <O>216 kr.</O>,
		"da": () => <O>216 kr.</O>,
		"sv": () => <O>324 kr</O>,
		"de": () => <O>28,8 €</O>,
	},

	"ffb4e555dacf": {
		"en": () => <O>6 months save 50%</O>,
		"da": () => <O>6 måneder spar 50%</O>,
		"sv": () => <O>6 månader spara 50%</O>,
		"de": () => <O>6 Monate sparen 50%</O>,
	},

	"9ad71d096aa4": {
		"en": () => <O>9 kr.</O>,
		"da": () => <O>9 kr.</O>,
		"sv": () => <O>14 kr</O>,
		"de": () => <O>1,2 €</O>,
	},

	"7f5f74f4d3de": {
		"en": () => <O>Not Premium</O>,
		"da": () => <O>Ikke Premium</O>,
		"sv": () => <O>Inte Premium</O>,
		"de": () => <O>Nicht Premium</O>,
	},

	"fbfb5abbc43c": {
		"en": () => <O>Premium</O>,
		"da": () => <O>Premium</O>,
		"sv": () => <O>Premie</O>,
		"de": () => <O>Prämie</O>,
	},

	"f8562d68de3b": {
		"en": () => <O>https://undervisning.aktiedysten.dk/faq-premium/</O>,
		"da": () => <O>https://undervisning.aktiedysten.dk/faq-premium/</O>,
		"sv": () => <O>https://undervisning.aktiekampen.se/faq-premium/</O>,
		"de": () => <O>https://bildung.investspiel.de/faq-premium/</O>,
	},

	"6cfebaa48128": {
		"en": () => <O>Remove unwanted participants</O>,
		"da": () => <O>Fjern uønskede deltagere</O>,
		"sv": () => <O>Ta bort oönskade deltagare</O>,
		"de": () => <O>Entfernen Sie unerwünschte Teilnehmer</O>,
	},

	"2d801bb9e317": {
		"en": () => <O>Stock price development, financial reports, descriptions, risk scores, and industry classification</O>,
		"da": () => <O>Kursudvikling på aktier og regnskaber, beskrivelser, risikoscore samt brancheindikation</O>,
		"sv": () => <O>Aktiekursutveckling, finansiella rapporter, beskrivningar, riskscore och branschklassificering</O>,
		"de": () => <O>Aktienkursentwicklung, Finanzberichte, Beschreibungen, Risikobewertungen und Branchenklassifizierung</O>,
	},

	"930d5d7d3674": {
		"en": () => <O>Winner based on risk-adjusted returns</O>,
		"da": () => <O>Vinder på risikovægtet afkast</O>,
		"sv": () => <O>Vinnare baserat på riskjusterad avkastning</O>,
		"de": () => <O>Gewinner basierend auf risikoadjustierter Rendite</O>,
	},

	"0c153f1786a1": {
		"en": () => <O>Stocks, bonds, ETFs, funds, and indices from the Danish, Swedish, US, German, Finnish, and Icelandic markets, as well as selected commodities</O>,
		"da": () => <O>Aktier, obligationer, ETF, Fonde og Index på det Danske, Svenske, USA, Tyske, Finske og Islandske marked samt udvalgte råvarer</O>,
		"sv": () => <O>Aktier, obligationer, ETF:er, fonder och index från de danska, svenska, amerikanska, tyska, finska och isländska marknaderna samt utvalda råvaror</O>,
		"de": () => <O>Aktien, Anleihen, ETFs, Fonds und Indizes aus den dänischen, schwedischen, US-amerikanischen, deutschen, finnischen und isländischen Märkten sowie ausgewählte Rohstoffe</O>,
	},

	"de1bedc78940": {
		"en": () => <O>You decide how many participants to invite</O>,
		"da": () => <O>Du bestemmer selv, hvor mange du vil invitere</O>,
		"sv": () => <O>Du bestämmer hur många deltagare du vill bjuda in</O>,
		"de": () => <O>Sie entscheiden, wie viele Teilnehmer Sie einladen möchten</O>,
	},

	"37f768a97ad1": {
		"en": (YTab, do_select_tab, items, tab_index) => {
			return (
				<O><YTab
					selected={tab_index}
					items={items}
					add_classes={["mb-3"]}
					onSelect={do_select_tab}
				/></O>
			);
		},
		"da": (YTab, do_select_tab, items, tab_index) => {
			return (
				<O><YTab
					selected={tab_index}
					items={items}
					add_classes={["mb-3"]}
					onSelect={do_select_tab}
				/></O>
			);
		},
		"sv": (YTab, do_select_tab, items, tab_index) => {
			return (
				<O><YTab
					selected={tab_index}
					items={items}
					add_classes={["mb-3"]}
					onSelect={do_select_tab}
				/></O>
			);
		},
		"de": (YTab, do_select_tab, items, tab_index) => {
			return (
				<O><YTab
					selected={tab_index}
					items={items}
					add_classes={["mb-3"]}
					onSelect={do_select_tab}
				/></O>
			);
		},
	},

	"d2d0e6f5eb7a": {
		"en": () => <O>Logout</O>,
		"da": () => <O>Log ud</O>,
		"sv": () => <O>Logga ut</O>,
		"de": () => <O>Ausloggen</O>,
	},

	"7316fa5b0cad": {
		"en": () => <O>You need to be logged in to checkout</O>,
		"da": () => <O>Du skal være logget ind for at gå til kassen</O>,
		"sv": () => <O>Du behöver vara inloggad för att gå till kassan</O>,
		"de": () => <O>Sie müssen eingeloggt sein, um zur Kasse zu gehen</O>,
	},

	"690806e5fb4e": {
		"en": () => <O>Login</O>,
		"da": () => <O>Log ind</O>,
		"sv": () => <O>Login</O>,
		"de": () => <O>Login</O>,
	},

	"b4224c177519": {
		"en": () => <O>Or login with</O>,
		"da": () => <O>Eller login med</O>,
		"sv": () => <O>Eller logga in med</O>,
		"de": () => <O>Oder Login mit:</O>,
	},

	"877d2a19e04e": {
		"en": () => <O>or</O>,
		"da": () => <O>eller</O>,
		"sv": () => <O>eller</O>,
		"de": () => <O>oder</O>,
	},

	"e62645cbfc7d": {
		"en": () => <O>Forgot your password?</O>,
		"da": () => <O>Glemt dit password?</O>,
		"sv": () => <O>Glömt ditt lösenord?</O>,
		"de": () => <O>Passwort vergessen?</O>,
	},

	"cbb3fadcd8a2": {
		"_deleted": true, // FIXME translation has no references; delete this entire section if you no longer need it
		"en": () => <O>Explore</O>,
		"da": () => <O>Udforsk</O>,
		"sv": () => <O>Utforska</O>,
		"de": () => <O>Erkunden</O>,
	},

	"83c783fd10e6": {
		"en": (site_name) => <O>More with {site_name}</O>,
		"da": (site_name) => <O>Mere med {site_name}</O>,
		"sv": (site_name) => <O>Mer med {site_name}</O>,
		"de": (site_name) => <O>More with {site_name}</O>,
	},

	"6d21f54873eb": {
		"en": (site_name) => {
			return (
				<O>
				  <a className="ad_footer_link" target="_blank" rel="noopener noreferrer" href="https://sammen.aktiedysten.dk/bliv-partner/">
				    Cooperation with {site_name}
				  </a>
				</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>
				  <a className="ad_footer_link" target="_blank" rel="noopener noreferrer" href="https://sammen.aktiedysten.dk/bliv-partner/">
				    Samarbejde med {site_name}
				  </a>
				</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>
				  <a className="ad_footer_link" target="_blank" rel="noopener noreferrer" href="https://undervisning.aktiekampen.se/samarbeta-med-aktiekampen/">
				    Samarbete med {site_name}
				  </a>
				</O>
			);
		},
		"de": (site_name) => {
			return null;
		},
	},

	"db8658fcbdef": {
		"en": () => {
			return (
				<O>
				  <a className='ad_footer_link' target="_blank" rel="noopener noreferrer" href="https://sammen.aktiedysten.dk/bliv-partner-ny/">
				    For business: Create your own game
				  </a>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <a className='ad_footer_link' target="_blank" rel="noopener noreferrer" href="https://sammen.aktiedysten.dk/bliv-partner-ny/">
				    Til erhverv: Skab dit eget spil
				  </a>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <a className='ad_footer_link' target="_blank" rel="noopener noreferrer" href="https://undervisning.aktiekampen.se/foretag-aktivering-och-sponsring/">
				    För företag: Skapa ditt eget spel
				  </a>
				</O>
			);
		},
		"de": () => {
			return null;
		},
	},

	"efb4a488cfad": {
		"_new": true, // FIXME remove this line when translation is done
		"_deleted": true, // FIXME translation has no references; delete this entire section if you no longer need it
		"en": () => <O>Create your own game</O>,
		"da": () => <O>Skab dit eget spil</O>,
		"sv": () => <O>Create your own game</O>,
		"de": () => <O>Create your own game</O>,
	},

	"719580468627": {
		"_deleted": true, // FIXME translation has no references; delete this entire section if you no longer need it
		"en": () => <O>Inspiration &amp; news</O>,
		"da": () => <O>Inspiration &amp; nyheder</O>,
		"sv": () => <O>Inspiration &amp; nyheter</O>,
		"de": () => <O>Inspiration &amp; Neuigkeiten</O>,
	},

	"9ec6b4e0fda8": {
		"_deleted": true, // FIXME translation has no references; delete this entire section if you no longer need it
		"en": (site_name) => <O>Cooperate with {site_name}</O>,
		"da": (site_name) => <O>Samarbejd med {site_name}</O>,
		"sv": (site_name) => <O>Partnerskap med {site_name}</O>,
		"de": (site_name) => <O>Partnerschaften mit {site_name}</O>,
	},

	"df24f3da5144": {
		"en": (site_name) => <O>Learn with {site_name}</O>,
		"da": (site_name) => <O>Lær med {site_name}</O>,
		"sv": (site_name) => <O>Lär med {site_name}</O>,
		"de": (site_name) => <O>Learn with {site_name}</O>,
	},

	"909f04e91e1c": {
		"en": () => {
			return (
				<O>
				  <a className="ad_footer_link" target="_blank" rel="noopener noreferrer" href="https://inspiration.aktiedysten.dk/">
				    Inspiration
				  </a>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <a className="ad_footer_link" target="_blank" rel="noopener noreferrer" href="https://inspiration.aktiedysten.dk/">
				    Inspiration
				  </a>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <a className="ad_footer_link" target="_blank" rel="noopener noreferrer" href="https://undervisning.aktiekampen.se/oka-din-kunskap/">
				    Inspiration och nyheter
				  </a>
				</O>
			)
		},
		"de": () => {
			return null;
		},
	},

	"e8a35fee6997": {
		"en": () => {
			return (
				<O>
				  <a className="ad_footer_link" target="_blank" rel="noopener noreferrer" href="https://undervisning.aktiedysten.dk/">
				    Education
				  </a>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <a className="ad_footer_link" target="_blank" rel="noopener noreferrer" href="https://undervisning.aktiedysten.dk/">
				    Undervisning
				  </a>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <a className="ad_footer_link" target="_blank" rel="noopener noreferrer" href="https://undervisning.aktiekampen.se/">
				    Undervisning
				  </a>
				</O>
			);
		},
		"de": () => {
			return null;
		},
	},

	"0ec1db231453": {
		"en": () => <O>Wrong password</O>,
		"da": () => <O>Forkert password</O>,
		"sv": () => <O>Fel lösenord</O>,
		"de": () => <O>Falsches Passwort</O>,
	},

	"9cfc229c8e56": {
		"en": () => <O>You must enter the password twice</O>,
		"da": () => <O>Du skal indtaste password to gange</O>,
		"sv": () => <O>Du måste skriva lösenordet två gånger</O>,
		"de": () => <O>Du musst das Passwort zweimal eingeben</O>,
	},

	"d0f9de934079": {
		"en": () => <O>The passwords do not match</O>,
		"da": () => <O>Passwords er ikke ens</O>,
		"sv": () => <O>Lösenorden överensstämmer inte</O>,
		"de": () => <O>Die Passwörter stimmen nicht überein</O>,
	},

	"b2834eb5f7dd": {
		"en": () => <O>Type in your current password</O>,
		"da": () => <O>Indtast dit nuværende password</O>,
		"sv": () => <O>Skriv in ditt nuvarande lösenord</O>,
		"de": () => <O>Gib dein aktuelles Passwort ein</O>,
	},

	"2f38b2e5eaa5": {
		"en": (delete_account_chkstr) => <O>Type in <span className="ad_yes_delete_me">{delete_account_chkstr}</span> in all caps, exactly as written</O>,
		"da": (delete_account_chkstr) => <O>Indtast <span className="ad_yes_delete_me">{delete_account_chkstr}</span> med store bogstaver, nøjagtigt som det står skrevet</O>,
		"sv": (delete_account_chkstr) => <O>Skriv in <span className="ad_yes_delete_me">{delete_account_chkstr}</span> med stora bokstäver, precis som det står skrivet</O>,
		"de": (delete_account_chkstr) => <O>Gib <span className="ad_yes_delete_me">{delete_account_chkstr}</span> in Großbuchstaben ein, so wie angegeben</O>,
	},

	"af3258ee4811": {
		"en": () => <O>YES, DELETE ACCOUNT</O>,
		"da": () => <O>JA, SLET MIN KONTO</O>,
		"sv": () => <O>JA, TA BORT MITT KONTO</O>,
		"de": () => <O>JA, KONTO LÖSCHEN</O>,
	},

	"016293dd94b2": {
		"en": (platform_name) => <O>To enable {platform_name} login, please enter your current password:</O>,
		"da": (platform_name) => <O>Indtast venligst dit password for at aktivere {platform_name} login:</O>,
		"sv": (platform_name) => <O>Vänligen skriv in ditt lösenord för att aktivera din {platform_name} inloggning:</O>,
		"de": (platform_name) => <O>Gib dein aktuelles Passwort ein, um die Anmeldung durch {platform_name} zu aktivieren:</O>,
	},

	"ef97abfda5c3": {
		"en": (platform_name) => <O>To disable {platform_name} login, please enter your current password:</O>,
		"da": (platform_name) => <O>Indtast venligst dit password for at deaktivere {platform_name} login:</O>,
		"sv": (platform_name) => <O>Vänligen skriv in ditt lösenord för att avaktivera din {platform_name} inloggning:</O>,
		"de": (platform_name) => <O>Gib dein aktuelles Passwort ein, um die Anmeldung durch {platform_name} zu deaktivieren:</O>,
	},

	"f4b5909591eb": {
		"en": (platform_name) => <O>Enable {platform_name} login</O>,
		"da": (platform_name) => <O>Aktivér {platform_name} login</O>,
		"sv": (platform_name) => <O>Aktivera {platform_name} inloggning</O>,
		"de": (platform_name) => <O>Login mit {platform_name} aktivieren</O>,
	},

	"f06d4c21e849": {
		"en": (platform_name) => <O>Disable {platform_name} login</O>,
		"da": (platform_name) => <O>Deaktivér {platform_name} login</O>,
		"sv": (platform_name) => <O>Avaktivera {platform_name} inloggning</O>,
		"de": (platform_name) => <O>Login mit {platform_name} deaktivieren</O>,
	},

	"6f6747575f96": {
		"en": () => <O>You cannot remove your email (you can remove it after enabling Facebook or Twitter login)</O>,
		"da": () => <O>Du kan ikke fjerne din email (du kan fjerne den efter du har aktiveret Facebook eller Twitter login)</O>,
		"sv": () => <O>Du kan inte ta bort din email (du kan ta bort den efter att du har akiverat inloggning med Facebook eller Twitter)</O>,
		"de": () => <O>Du kannst deine E-Mail-Adresse nicht löschen. (Du kannst diese erst entfernen, nachdem du den Login mit Facebook oder Twitter aktiviert hast).</O>,
	},

	"b57c53f5606b": {
		"en": () => <O>The email address is being used by another account</O>,
		"da": () => <O>Email-adressen bruges af en anden konto</O>,
		"sv": () => <O>Email-adressen används redan av ett annat konto</O>,
		"de": () => <O>Diese E-Mail-Adresse wird bereits von einem anderen Konto verwendet</O>,
	},

	"40e4dc497005": {
		"en": () => <O>You must enter a valid email address</O>,
		"da": () => <O>Du skal indtaste en gyldig email-adresse</O>,
		"sv": () => <O>Du måste skriva in en giltig email-adress</O>,
		"de": () => <O>Du musst eine gültige E-Mail-Adresse angeben</O>,
	},

	"de1dca537a12": {
		"en": () => <O>Click here for more info</O>,
		"da": () => <O>Klik her for mere info</O>,
		"sv": () => <O>Klicka här för mer information</O>,
		"de": () => <O>Klicken Sie hier für mehr Informationen</O>,
	},

	"0915d84ea7cc": {
		"en": () => <O>Gender:</O>,
		"da": () => <O>Køn:</O>,
		"sv": () => <O>Kön:</O>,
		"de": () => <O>Geschlecht:</O>,
	},

	"3b74df961974": {
		"en": () => <O>Female</O>,
		"da": () => <O>Kvinde</O>,
		"sv": () => <O>Kvinna</O>,
		"de": () => <O>Weiblich</O>,
	},

	"301c01e91b51": {
		"en": () => <O>Male</O>,
		"da": () => <O>Mand</O>,
		"sv": () => <O>Man</O>,
		"de": () => <O>Männlich</O>,
	},

	"e365399e063a": {
		"en": () => {
				return (
					<O>
					Enter your email to finish registration, so we
					can inform you about your games and other features.
					You can always unsubscribe.
					</O>
				);
			},
		"da": () => {
				return (
					<O>
					For at færdiggøre din tilmelding indtast din email,
					så vi kan holde dig orienteret om dine spil samt andre
					features. Du kan altid framelde dig igen.
					</O>
				);
			},
		"sv": () => {
				return (
					<O>
					För att bli klar med registreringen vänligen skriv in
					din email så att vi kan informera dig om dina spel och andra
					funktioner. Du kan alltid avprenumerera.
					</O>
				);
			},
		"de": () => {
				return (
					<O>
					Gib deine E-Mail-Adresse ein, um die Registrierung abzuschließen.
					Wir nutzen diese um dich über deine Spiele und andere Funktionen informieren zu können.
					Du kannst die Benachrichtigungen jederzeit abmelden.
					</O>
				);
			},
	},

	"3c32c4c81d3f": {
		"en": () => <O>Submit email</O>,
		"da": () => <O>Indsend email</O>,
		"sv": () => <O>Skicka in email</O>,
		"de": () => <O>E-Mail senden</O>,
	},

	"413d0092bf94": {
		"en": () => <O>Select month</O>,
		"da": () => <O>Vælg måned</O>,
		"sv": () => <O>Välj månad</O>,
		"de": () => <O>Monat auswählen</O>,
	},

	"3b5a4995b2ce": {
		"en": () => <O>Select day</O>,
		"da": () => <O>Vælg dag</O>,
		"sv": () => <O>Välj dag</O>,
		"de": () => <O>Tag auswählen</O>,
	},

	"a60b40f4cc62": {
		"en": () => <O>Year of birth (e.g. 1997):</O>,
		"da": () => <O>Fødselsår (f.eks. 1997):</O>,
		"sv": () => <O>Födelseår (t.ex. 1997):</O>,
		"de": () => <O>Geburtsjahr (z.B. 1997):</O>,
	},

	"288b951b03ec": {
		"en": () => <O>About You</O>,
		"da": () => <O>Om dig</O>,
		"sv": () => <O>Om dig</O>,
		"de": () => <O>Über dich</O>,
	},

	"635e5422f2ec": {
		"en": () => <O>Month:</O>,
		"da": () => <O>Måned:</O>,
		"sv": () => <O>Månad:</O>,
		"de": () => <O>Monat:</O>,
	},

	"0ce159aa22c9": {
		"en": () => <O>Day:</O>,
		"da": () => <O>Dag:</O>,
		"sv": () => <O>Dag:</O>,
		"de": () => <O>Tag:</O>,
	},

	"337cd9b35479": {
		"en": (site_name) => {
			return (
				<O>
				In order to comply with Danish law, we need to know your age.<br />
				This is <i>only</i> used to determine what content can be shown on {site_name}, and will <i>never</i> be
				shared with anyone, except with your explicit consent.
				</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>
				For at overholde dansk lovgivning, skal vi kende din alder.<br />
				Dette bruges <i>kun</i> til at afgøre, hvilket indhold der kan vises på {site_name}, og vil <i>aldrig</i> blive delt med
				nogen, undtagen med dit udtrykkelige samtykke.
				</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>
				För att följa dansk lag måste vi veta din ålder. <br />
				Detta används <i>endast</i> för att bestämma vilket innehåll som kan visas på {site_name} och kommer <i>aldrig</i> att delas med
				någon, förutom med ditt uttryckliga samtycke.
				</O>
			);
		},
		"de": (site_name) => {
			return (
				<O>
				Um den dänischen Gesetzen zu entsprechen, müssen wir dein Alter kennen.<br />
				Dieses nutzen wir lediglich dazu, um festzustellen, welcher Inhalt dir auf {site_name} angezeigt
				werden darf. Dein Alter wird <i>niemals</i> ohne deine ausdrückliche Zustimmung an andere
				weitergegeben.
				</O>
			);
		},
	},

	"370288f2ebdc": {
		"en": () => <O>Save birthdate</O>,
		"da": () => <O>Gem fødselsdato</O>,
		"sv": () => <O>Spara födelsedag</O>,
		"de": () => <O>Geburtsdatum speichern</O>,
	},

	"3c896158dd17": {
		"en": () => <O>Continue</O>,
		"da": () => <O>Fortsæt</O>,
		"sv": () => <O>Fortsätta</O>,
		"de": () => <O>Weiter</O>,
	},

	"064865abefc9": {
		"en": () => <O>Login with email</O>,
		"da": () => <O>Login med email</O>,
		"sv": () => <O>Logga in med email</O>,
		"de": () => <O>Logge dich mit deiner E-Mail-Adresse ein</O>,
	},

	"591f03280d60": {
		"en": () => <O>We're missing your e-mail</O>,
		"da": () => <O>Vi mangler din e-mail</O>,
		"sv": () => <O>Vi saknar din e-mail</O>,
		"de": () => <O>Wir haben keine E-Mail-Adresse von Ihnen</O>,
	},

	"5954b7edd70c": {
		"en": () => {
				return (
					<O>We need your e-mail to send you updates about your games,
					platform enhancements, tips &amp; tricks and more.
					</O>
				);
			},
		"da": () => {
				return (
					<O>Vi skal bruge din e-mail til at sende dig opdateringer om
					dine spil, platform-forbedringer, m.m.
					</O>
				);
			},
		"sv": () => {
				return (
					<O>Vi behöver din e-mail för att skicka dig uppdateringar om
					dina spel, information om nyheter och förbättringar samt tips och tricks m.m.
					</O>
				);
			},
		"de": () => {
				return (
					<O> Wir benötigen Ihre E-Mail-Adresse, um Ihnen Updates zu Ihren Spielen,
					Plattformverbesserungen, Tricks und weiteres zu senden.
					</O>
				);
			},
	},

	"85e53d66f996": {
		"en": (A) => <O>You can always change you email settings <A className="ad_blue_link" href="/preferences">here</A>.</O>,
		"da": (A) => <O>Du kan altid ændre dine email indstillinger <A className="ad_blue_link" href="/preferences">her</A>.</O>,
		"sv": (A) => <O>Du kan alltid ändra dina email inställningar. <A className="ad_blue_link" href="/preferences">her</A>.</O>,
		"de": (A) => <O>Du kannst deine E-Mail-Einstellungen jederzeit <A className="ad_blue_link" href="/preferences">hier</A> ändern.</O>,
	},

	"d9bbed4e9778": {
		"en": () => <O>E-MAIL ADDRESS:</O>,
		"da": () => <O>E-MAIL ADDRESSE:</O>,
		"sv": () => <O>E-MAIL ADRESS:</O>,
		"de": () => <O>E-MAIL-ADRESSE:</O>,
	},

	"f1435f8af48f": {
		"en": () => <O>Add e-mail</O>,
		"da": () => <O>Tilføj e-mail</O>,
		"sv": () => <O>Lägg till e-mail</O>,
		"de": () => <O>E-Mail-Adresse hinzufügen</O>,
	},

	"356ed1c9c6b2": {
		"en": () => <O>No thanks!</O>,
		"da": () => <O>Nej tak</O>,
		"sv": () => <O>Nej tack</O>,
		"de": () => <O>Nein Danke!</O>,
	},

	"8f8fe06dda53": {
		"en": () => <O>EMAIL</O>,
		"da": () => <O>EMAIL</O>,
		"sv": () => <O>EMAIL</O>,
		"de": () => <O>EMAIL</O>,
	},

	"32225da086ab": {
		"en": () => <O>Enter your current password to change your email:</O>,
		"da": () => <O>Indtast dit nuværende password for at ændre din email:</O>,
		"sv": () => <O>Skriv in ditt nuvarande lösenord för att ändra din email:</O>,
		"de": () => <O>Gib dein aktuelles Passwort ein, um deine E-Mail-Adresse zu ändern:</O>,
	},

	"35b5220c8c5c": {
		"en": () => <O>Change login/email and send email</O>,
		"da": () => <O>Skift login/email og send email</O>,
		"sv": () => <O>Ändra inloggning/email och skicka email</O>,
		"de": () => <O>Login/E-Mail ändern und E-Mail senden</O>,
	},

	"b30189f6a258": {
		"en": () => <O>Resend email</O>,
		"da": () => <O>Send email igen</O>,
		"sv": () => <O>Skicka email igen</O>,
		"de": () => <O>E-Mail erneut senden</O>,
	},

	"5135dc3374ea": {
		"en": () => <O>It contains a link you must click in order to keep your account active.</O>,
		"da": () => <O>Den indeholder et link du skal klikke for at verificere din email.</O>,
		"sv": () => <O>Den innehåller en länk som du behöver klicka för att hålla ditt konto aktivt.</O>,
		"de": () => <O>Die E-Mail enthält einen Link, auf den du klicken musst, um dein Konto aktiv zu behalten.</O>,
	},

	"e472ccae9081": {
		"en": () => <O>It contains a link you must click to verify your email.</O>,
		"da": () => <O>Den indeholder et link du skal klikke for at verificere din email.</O>,
		"sv": () => <O>Den innehåller en länk som du behöver klicka för att verifiera din email.</O>,
		"de": () => <O>Die E-Mail enthält einen Link, auf den du klicken musst, um deine E-Mail-Adresse zu bestätigen.</O>,
	},

	"f2f88b4cbc44": {
		"en": () => <O>LOGIN / EMAIL:</O>,
		"da": () => <O>LOGIN / EMAIL:</O>,
		"sv": () => <O>LOGGA IN / EMAIL:</O>,
		"de": () => <O>EINLOGGEN / EMAIL:</O>,
	},

	"5385c1972a65": {
		"en": () => <O>About {site_name}</O>,
		"da": () => <O>Om {site_name}</O>,
		"sv": () => <O>Om {site_name}</O>,
		"de": () => <O>Über {site_name}</O>,
	},

	"2935c3969d3f": {
		"en": () => <O>Privacy policy</O>,
		"da": () => <O>Privatlivspolitik</O>,
		"sv": () => <O>Integritetspolicy</O>,
		"de": () => <O>Datenschutzerklärung</O>,
	},

	"8f0db1d15536": {
		"en": () => <O>About cookies</O>,
		"da": () => <O>Om cookies</O>,
		"sv": () => <O>About cookies</O>,
		"de": () => <O>About cookies</O>,
	},

	"a46011adbf80": {
		"en": () => <O>Terms of use</O>,
		"da": () => <O>Brugsbetingelser</O>,
		"sv": () => <O>Villkor</O>,
		"de": () => <O>Geschäftsbedingungen</O>,
	},

	"66528c922600": {
		"en": () => {
			return (
				<O>
				  <a className="ad_footer_link" href="https://undervisning.aktiedysten.dk/gdpr/">
				    GDPR
				  </a>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				  <a className="ad_footer_link" href="https://undervisning.aktiedysten.dk/gdpr/">
				    GDPR
				  </a>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				  <a className="ad_footer_link" href="https://undervisning.aktiedysten.dk/gdpr/">
				    GDPR
				  </a>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				  <a className="ad_footer_link" href="https://undervisning.aktiedysten.dk/gdpr/">
				    GDPR
				  </a>
				</O>
			);
		},
	},

	"8943eff1a78e": {
		"en": () => <O>Contact us</O>,
		"da": () => <O>Kontakt os</O>,
		"sv": () => <O>Kontakta oss</O>,
		"de": () => <O>Kontakt</O>,
	},

	"8ae141db9302": {
		"en": () => <O>Imprint</O>,
		"da": () => <O>Imprint</O>,
		"sv": () => <O>Imprint</O>,
		"de": () => <O>Impressum</O>,
	},

	"2ebd13cd2f74": {
		"en": () => <O>Information according to &sect;5 German Teleservices Act</O>,
		"da": () => <O>Information according to &sect;5 German Teleservices Act</O>,
		"sv": () => <O>Information according to &sect;5 German Teleservices Act</O>,
		"de": () => <O>Angaben gemäß &sect;5 Telemediengesetz</O>,
	},

	"259c6c3ca64a": {
		"en": () => <O>Contact information</O>,
		"da": () => <O>Kontakt</O>,
		"sv": () => <O>Kontakt</O>,
		"de": () => <O>Kontakt</O>,
	},

	"65e32d8eac10": {
		"en": () => <O>Responsible for content</O>,
		"da": () => <O>Responsible for content</O>,
		"sv": () => <O>Responsible for content</O>,
		"de": () => <O>Verantwortlich für den Inhalt</O>,
	},

	"61bc4da27406": {
		"en": () => <O>Registry</O>,
		"da": () => <O>Registry</O>,
		"sv": () => <O>Registry</O>,
		"de": () => <O>Registereintrag</O>,
	},

	"63ea9cb37268": {
		"en": () => <O>Commercial registries: <a href="https://cvr.dk"rel="noopener noreferrer" target="_blank">Central Business Register Denmark</a></O>,
		"da": () => <O>Commercial registries: Central Business Register Denmark</O>,
		"sv": () => <O>Commercial registries: Central Business Register Denmark</O>,
		"de": () => <O>Register: <a href="https://cvr.dk"rel="noopener noreferrer" target="_blank">Das zentrale Unternehmensregister Dänemark</a></O>,
	},

	"b75ad077ed0b": {
		"en": () => <O>Registration number:</O>,
		"da": () => <O>Registration number:</O>,
		"sv": () => <O>Registration number:</O>,
		"de": () => <O>Registernummer:</O>,
	},

	"4ee689071789": {
		"en": () => <O>Imprint / Contact</O>,
		"da": () => <O>Imprint / Contact</O>,
		"sv": () => <O>Imprint / Contact</O>,
		"de": () => <O>Impressum / Kontakt</O>,
	},

	"04ba98a6635a": {
		"en": () => <O>Choose game</O>,
		"da": () => <O>Vælg spil</O>,
		"sv": () => <O>Välj spel</O>,
		"de": () => <O>Spiel auswählen</O>,
	},

	"3e863b8ce897": {
		"en": (I18N, popts) => <O>There's a maximum limit of {I18N.floatval_unit(popts.MaxAmountPerStock, 2, popts.GameCurrency)} per equity in this game</O>,
		"da": (I18N, popts) => <O>Der er en grænse på {I18N.floatval_unit(popts.MaxAmountPerStock, 2, popts.GameCurrency)} pr. værdipapir i dette spil</O>,
		"sv": (I18N, popts) => <O>Det är en gräns på {I18N.floatval_unit(popts.MaxAmountPerStock, 2, popts.GameCurrency)} per värdepapper i detta spel</O>,
		"de": (I18N, popts) => <O>In diesem Spiel gibt es ein maximales Limit von {I18N.floatval_unit(popts.MaxAmountPerStock, 2, popts.GameCurrency)} pro Anlage.</O>,
	},

	"7639549c6bf2": {
		"en": () => <O>Available funds:</O>,
		"da": () => <O>Tilgængeligt beløb:</O>,
		"sv": () => <O>Tillgängligt belopp:</O>,
		"de": () => <O>Verfügbares Vermögen:</O>,
	},

	"8f71ba9c81e8": {
		"en": () => <O>Stocks in game:</O>,
		"da": () => <O>Værdipapirer i spillet:</O>,
		"sv": () => <O>Aktier i spelet:</O>,
		"de": () => <O>Aktien im Spiel:</O>,
	},

	"43ebad5b570d": {
		"en": () => <O>BUY</O>,
		"da": () => <O>KØB</O>,
		"sv": () => <O>KÖP</O>,
		"de": () => <O>KAUFEN</O>,
	},

	"e04829cc6c02": {
		"en": () => <O>SELL</O>,
		"da": () => <O>SÆLG</O>,
		"sv": () => <O>SÄLJ</O>,
		"de": () => <O>VERKAUFEN</O>,
	},

	"9ebdd215da59": {
		"en": (ActionLink, on_read_more_here, trade_delay_minutes) => {
				return (
					<O>Be aware that this trade will go through in {trade_delay_minutes} minutes with the quote at
					that time. This is to avoid the difference between live data and game data. <ActionLink on_click={on_read_more_here}>Read more here.</ActionLink>
					</O>
				);
			},
		"da": (ActionLink, on_read_more_here, trade_delay_minutes) => {
				return (
					<O>Vær opmærksom på at handlen bliver
					gennemført om {trade_delay_minutes} minutter, til kursen på det
					tidspunkt, for at undgå en difference mellem
					live-kurser og spillets kurser. <ActionLink on_click={on_read_more_here}>Læs mere her.</ActionLink>
					</O>
				);
			},
		"sv": (ActionLink, on_read_more_here, trade_delay_minutes) => {
				return (
					<O>Notera att denna handel genomförs om {trade_delay_minutes} minuter, till kursen på den tidpunkten
					för att undvika en skillnad mellan live-kurser och spelets kurser. <ActionLink on_click={on_read_more_here}>Läs mer här.</ActionLink>
					</O>
				);
			},
		"de": (ActionLink, on_read_more_here, trade_delay_minutes) => {
				return (
					<O>Bitte beachte, dass dieser Handel in {trade_delay_minutes} Minuten mit dem dann
					aktuellen Preis abgeschlossen wird. Dies soll den Unterschied zwischen Live-Daten und
					Spieldaten vermeiden. <ActionLink on_click={on_read_more_here}>Mehr darüber
					lesen.</ActionLink>
					</O>
				);
			},
	},

	"af0a9eb1437a": {
		"en": (expected_ipo_date_element) => {
				return (
					<O>The stock is scheduled for IPO on {expected_ipo_date_element};
					the trade will execute as soon as we
					have the initial data.</O>
				);
			},
		"da": (expected_ipo_date_element) => {
				return (
					<O>Værdipapiret forventes børsnoteret
					{expected_ipo_date_element}.  Handlen vil gå
					igennem så snart vi har de første data.</O>
				);
			},
		"sv": (expected_ipo_date_element) => {
				return (
					<O>Aktien förväntas börsnoteras
					{expected_ipo_date_element}. Handeln går igenom
					så snart vi har den första datan.</O>
				);
			},
		"de": (expected_ipo_date_element) => {
				return (
					<O>Die Aktie ist für den Börsengang am {expected_ipo_date_element} vorgesehen. Der
					Handel wird ausgeführt, sobald wir die Anfangsdaten haben.</O>
				);
			},
	},

	"bd3e84b8b481": {
		"en": () => {
				return (
					<O>The stock is not currently available
					for sale due to missing data. The trade
					will execute as soon as we have data.</O>
				);
			},
		"da": () => {
				return (
					<O>Pga. manglende data kan værdipapiret ikke
					handles på nuværende tidspunkt. Handlen vil gå
					igennem så snart vi har data.</O>
				);
			},
		"sv": () => {
				return (
					<O>Pga saknad data kan aktien inte handlas med för tillfället. Handeln kommer gå igenom så snart vi har data.</O>
				);
			},
		"de": () => {
				return (
					<O>Die Aktie steht derzeit aufgrund fehlender Daten nicht zum Verkauf. Der Handel wird
					ausgeführt, sobald wir die nötigen Daten haben.
					</O>
				);
			},
	},

	"ac2dcf640e73": {
		"en": () => <O>Enqueue</O>,
		"da": () => <O>Sæt&nbsp;i&nbsp;kø</O>,
		"sv": () => <O>Sätt&nbsp;i&nbsp;kö</O>,
		"de": () => <O>Handeln</O>,
	},

	"81a1e4d8f11f": {
		"en": (date, exchange) => {
				return (
					<O>*The exchange ({exchange}) is not
					currently open, but you can enqueue a
					sale request which will be executed
					when the exchange opens ({date}). The
					quantity you choose to sell and the
					stock rate at the time the exchange
					opens will determine the amount you
					receive.</O>
				);
			},
		"da": (date, exchange) => {
				return (
					<O>*Markedet ({exchange}) er pt. ikke åbent.
					Du kan dog sætte din ordre i kø, så den
					bliver udført, når markedet åbner ({date}).
					Antallet af værdipapirer du vil sælge, samt
					kursen, når markedet åbner, afgør det beløb du
					får.</O>
				);
			},
		"sv": (date, exchange) => {
				return (
					<O>*Marknaden ({exchange}) är inte öppen för
					tillfället. Du kan dock sätta din order i kö
					som då blir utfört när marknaden öppnar ({date}).
					Antalet värdepapper du väljer att sälja samt
					aktiekursen vid tillfället när marknaden öppnar
					avgör vilket belopp du får.</O>
				);
			},
		"de": (date, exchange) => {
				return (
					<O>*Die Börse ({exchange}) ist derzeit nicht geöffnet, aber du kannst eine Handelsanfrage
					in die Warteschleife stellen, die ausgeführt wird, sobald die Börse geöffnet wird ({date}).
					Der Betrag, den du handeln möchtest, und der Aktienkurs zum Zeitpunkt der Eröffnung der
					Börse bestimmen die Menge, die du erhälst.
					</O>
				);
			},
	},

	"c8c9a50b3f34": {
		"en": () => {
				return (
					<O>*The stock cannot currently
					be traded, but you can enqueue
					a sale request which will be
					executed when trading is
					resumed. The quantity you
					choose to sell and the stock
					rate at the time trading
					resumes will determine the
					amount you receive.</O>
				);
			},
		"da": () => {
				return (
					<O>*Værdipapiret kan ikke handles på nuværende
					tidspunkt. Du kan dog sætte din ordre i kø, så
					den bliver udført, når handelen genoptages.
					Antallet af værdipapirer du vil sælge, samt
					kursen, når handelen genoptages, afgør det
					beløb du får.</O>
				);
			},
		"sv": () => {
				return (
				       <O>*Aktien kan för närvarande inte handlas med,
				       men du kan sätta din order i kö som då kommer
				       genomföras när handeln är igång igen. Antalet
				       värdepapper du väljer att sälja samt aktiekursen
				       vid tillfället när handeln börjar igen kommer
				       avgöra vilket belopp du får.</O>
				);
			},
		"de": () => {
				return (
					<O>*Die Aktie kann derzeit nicht gehandelt werden, aber du kannst eine Handelsanfrage
					in die Warteschleife stellen, die ausgeführt wird, sobald der Handel wieder aufgennem
					wird. Der Betrag, den du handeln möchtest, und der Aktienkurs zum Zeitpunkt der Eröffnung
					der Börse bestimmen die Menge, die du erhälst.</O>
				);
			},
	},

	"ec6fecd3849e": {
		"en": (date, exchange) => {
				return (
					<O>*The exchange ({exchange}) is not
					currently open, but you can enqueue a
					purchase request which will be executed
					when the exchange opens ({date}). The
					amount you choose to spend and the
					stock rate at the time the exchange
					opens will determine the quantity you
					receive.</O>
				);
			},
		"da": (date, exchange) => {
				return (
					<O>*Markedet ({exchange}) er pt. ikke åbent.
					Du kan dog sætte din handel i kø, så den
					bliver udført, når markedet åbner ({date}).
					Summen, du vil bruge, samt kursen, når markedet
					åbner, afgør antallet af aktier, som du får.</O>
				);
			},
		"sv": (date, exchange) => {
				return (
				       <O>*Marknaden ({exchange}) är inte öppen.  Du
				       kan dock sätta din handel i kö, så blir den
				       utförd när marknaden öppnar. ({date}).
				       Summan du vill använda, samt kursen när
				       marknaden öppnar, avgör antalet aktier du
				       får.</O>
				);
			},
		"de": (date, exchange) => {
				return (
					<O>*Die Börse ({exchange}) ist derzeit nicht geöffnet, aber du kannst eine
					Handelsanfrage in die Warteschleife stellen, die ausgeführt wird, sobald die Börse
					geöffnet wird ({date}). Der Betrag, den du handeln möchtest, und der Aktienkurs zum
					Zeitpunkt der Eröffnung der Börse bestimmen die Menge, die du erhälst.</O>
				);
			},
	},

	"c798a80c9c71": {
		"en": () => {
				return (
					<O>*The stock cannot currently
					be traded, but you can enqueue
					a purchase request which will
					be executed when trading is
					resumed. The amount you choose
					to spend and the stock rate at
					the time trading resumes will
					determine the quantity you
					receive.</O>
				);
			},
		"da": () => {
				return (
					<O>*Værdipapiret kan ikke handles på nuværende
					tidspunkt. Du kan dog sætte din ordre i kø, så
					den bliver udført, når handelen genoptages.
					Summen, du vil bruge, samt kursen, når handelen
					genoptages, afgør antallet af aktier, som du
					får.</O>
				);
			},
		"sv": () => {
				return (
					<O>*Aktien kan inte handlas med för närvarande,
					men du kan dock sätta din order i kö så blir
					den utförd när handeln sätter igång igen.
					Summan du vill använda och kursen när handeln
					återupptas avgör antalet aktier som du
					får.</O>
				);
			},
		"de": () => {
				return (
					<O>*Die Aktie kann derzeit nicht gehandelt werden, aber du kannst eine Handelsanfrage
					in die Warteschleife stellen, die ausgeführt wird, sobald der Handel wieder aufgennem
					wird. Der Betrag, den du handeln möchtest, und der Aktienkurs zum Zeitpunkt der Eröffnung
					der Börse bestimmen die Menge, die du erhälst.</O>
				);
			},
	},

	"a8d5e7033813": {
		"en": () => <O>Buy</O>,
		"da": () => <O>Køb</O>,
		"sv": () => <O>Köp</O>,
		"de": () => <O>Kaufen</O>,
	},

	"a559f5ffb310": {
		"en": () => <O>Total</O>,
		"da": () => <O>Total</O>,
		"sv": () => <O>Total</O>,
		"de": () => <O>Gesamt</O>,
	},

	"8701899115c8": {
		"en": () => <O>Quantity to sell</O>,
		"da": () => <O>Antal at sælge</O>,
		"sv": () => <O>Antal att sälja</O>,
		"de": () => <O>Menge zu verkaufen</O>,
	},

	"fa911abe4610": {
		"en": () => <O>Amount to receive</O>,
		"da": () => <O>Sum du modtager</O>,
		"sv": () => <O>Summan du mottar</O>,
		"de": () => <O>Zu empfangender Betrag</O>,
	},

	"3950e2b91a92": {
		"en": () => <O>Amount to spend</O>,
		"da": () => <O>Sum du vil bruge</O>,
		"sv": () => <O>Summan du spenderar</O>,
		"de": () => <O>Auszugebener Betrag</O>,
	},

	"5faf12bfe4d8": {
		"en": () => <O>Quantity to buy</O>,
		"da": () => <O>Antal at købe</O>,
		"sv": () => <O>Antal att köpa</O>,
		"de": () => <O>Kaufmenge </O>,
	},

	"b27c13bd6bae": {
		"en": () => <O>Sell</O>,
		"da": () => <O>Sælg</O>,
		"sv": () => <O>Sälj</O>,
		"de": () => <O>Verkaufen</O>,
	},

	"78ef74805bd8": {
		"en": () => <O>your portfolio</O>,
		"da": () => <O>din portefølje</O>,
		"sv": () => <O>din portfölj</O>,
		"de": () => <O>Ihr Portfolio</O>,
	},

	"6e67c7f73138": {
		"en": () => <O>Quantity</O>,
		"da": () => <O>Antal</O>,
		"sv": () => <O>Antal</O>,
		"de": () => <O>Menge</O>,
	},

	"4d58b0095a5d": {
		"en": () => <O>Security</O>,
		"da": () => <O>Værdipapir</O>,
		"sv": () => <O>Värdepapper</O>,
		"de": () => <O>Wertpapiere</O>,
	},

	"78383879e5e4": {
		"en": () => <O>Latest</O>,
		"da": () => <O>Senest</O>,
		"sv": () => <O>Senaste</O>,
		"de": () => <O>Angebot</O>,
	},

	"42e1ccbfb2dd": {
		"en": () => <O>Value</O>,
		"da": () => <O>Værdi</O>,
		"sv": () => <O>Värde</O>,
		"de": () => <O>Wert</O>,
	},

	"818f27769c0e": {
		"en": (gameTradeInfo, userTrades) => <O>You have used {userTrades} of {gameTradeInfo.tradeAmount} trades</O>,
		"da": (gameTradeInfo, userTrades) => <O>Du har brugt {userTrades} ud af {gameTradeInfo.tradeAmount} handler</O>,
		"sv": (gameTradeInfo, userTrades) => <O>Du har använt {userTrades} av {gameTradeInfo.tradeAmount} affärer</O>,
		"de": (gameTradeInfo, userTrades) => <O>Sie haben {userTrades} von {gameTradeInfo.tradeAmount} Trades genutzt</O>,
	},

	"449ec89a2d32": {
		"en": () => <O>Trading disabled due to negative or missing risk score.</O>,
		"da": () => <O>Handel deaktiveret på grund af negativ eller manglende risikoscore.</O>,
		"sv": () => <O>Handel inaktiverad på grund av negativ eller saknad riskpoäng.</O>,
		"de": () => <O>Handel deaktiviert aufgrund eines negativen oder fehlenden risikoscore.</O>,
	},

	"1bcc403228e2": {
		"en": () => <O>Risk Score</O>,
		"da": () => <O>Risikoscore</O>,
		"sv": () => <O>Riskpoäng</O>,
		"de": () => <O>Risikoscore</O>,
	},

	"3192ac401098": {
		"en": () => <O>Quote (Paid)</O>,
		"da": () => <O>Kurs nu (Gav)</O>,
		"sv": () => <O>Kurs nu (Betald)</O>,
		"de": () => <O>Angebot (bezahlt)</O>,
	},

	"8b6d8e3752d7": {
		"en": () => <O>Change</O>,
		"da": () => <O>Ændring</O>,
		"sv": () => <O>Utveckling</O>,
		"de": () => <O>Veränderung</O>,
	},

	"f05e0db04796": {
		"en": () => <O>Percent</O>,
		"da": () => <O>Procent</O>,
		"sv": () => <O>Procent</O>,
		"de": () => <O>Prozent</O>,
	},

	"c037b700771d": {
		"en": () => <O>Remove</O>,
		"da": () => <O>Fjern</O>,
		"sv": () => <O>Ta bort</O>,
		"de": () => <O>Entfernen</O>,
	},

	"ee034e344a02": {
		"en": () => <O>You have no equities</O>,
		"da": () => <O>Du har ingen værdipapirer</O>,
		"sv": () => <O>Du har inga värdepapper</O>,
		"de": () => <O>Du hast keine Aktien</O>,
	},

	"1376f96410f7": {
		"en": () => <O>Explore our markets and find some</O>,
		"da": () => <O>Udforsk vores markeder og find nogle</O>,
		"sv": () => <O>Utforska våra marknader och hitta några</O>,
		"de": () => <O>Entdecke unsere Märkte und finde deine Favoriten</O>,
	},

	"051bd37e3132": {
		"en": () => <O>There are no equities</O>,
		"da": () => <O>Der er ingen værdipapirer</O>,
		"sv": () => <O>Det finns inga värdepapper</O>,
		"de": () => <O>Es gibt keine Aktien</O>,
	},

	"c17f5fcd63e9": {
		"en": () => <O>This portfolio is pure cash</O>,
		"da": () => <O>Denne portefølje er kun i kontanter</O>,
		"sv": () => <O>Denna portfölj är endast kontanter</O>,
		"de": () => <O>Dieses Portfolio besteht nur aus Bargeld</O>,
	},

	"98f37f970ef9": {
		"en": () => <O>Equities previously traded in game</O>,
		"da": () => <O>Tidligere handlede værdipapirer</O>,
		"sv": () => <O>Tidigare värdepapper som handlats med</O>,
		"de": () => <O>Zuvor im Spiel gehandelte Aktien</O>,
	},

	"3f014f62806a": {
		"en": () => <O>Trades in queue</O>,
		"da": () => <O>Handler i kø</O>,
		"sv": () => <O>Handel som ligger i kö</O>,
		"de": () => <O>Handel in der Warteschleife</O>,
	},

	"9efac56d0eeb": {
		"en": () => <O>Trade</O>,
		"da": () => <O>Handl</O>,
		"sv": () => <O>Handla</O>,
		"de": () => <O>Handel</O>,
	},

	"eeef199dbc11": {
		"en": (rq) => <O>Buy {rq.Ticker} for {rq.BuyWithAmount}</O>,
		"da": (rq) => <O>Køb {rq.Ticker} for {rq.BuyWithAmount}</O>,
		"sv": (rq) => <O>Köp {rq.Ticker} för {rq.BuyWithAmount}</O>,
		"de": (rq) => <O>Kauf von {rq.Ticker} für {rq.BuyWithAmount}</O>,
	},

	"778312eaf92a": {
		"en": (rq) => <O>Sell {rq.SellQuantity} {rq.Ticker}</O>,
		"da": (rq) => <O>Sælg {rq.SellQuantity} {rq.Ticker}</O>,
		"sv": (rq) => <O>Sälj {rq.SellQuantity} {rq.Ticker}</O>,
		"de": (rq) => <O>Verkauf von {rq.Ticker} {rq.Ticker}</O>,
	},

	"4f9a4a53914e": {
		"en": (A, game_name, had_quantity, had_stock, link_to_portfolio, received_amount) => {
				return (
					<O>
						Your portfolio in the game <A className="ad_notification_link" href={link_to_portfolio}>{game_name}</A> had {had_quantity} &times; {had_stock} and received {received_amount}.
					</O>
				);
			},
		"da": (A, game_name, had_quantity, had_stock, link_to_portfolio, received_amount) => {
				return (
					<O>
						Din portefølje i spillet <A className="ad_notification_link" href={link_to_portfolio}>{game_name}</A> havde {had_quantity} &times; {had_stock} og modtog {received_amount}.
					</O>
				);
			},
		"sv": (A, game_name, had_quantity, had_stock, link_to_portfolio, received_amount) => {
				return (
					<O>
						Din portfölj i spelet <A className="ad_notification_link" href={link_to_portfolio}>{game_name}</A> hade {had_quantity} &times; {had_stock} och tog emot {received_amount}.
					</O>
				);
			},
		"de": (A, game_name, had_quantity, had_stock, link_to_portfolio, received_amount) => {
				return (
					<O>
						Ihr Portfolio in dem Spiel <A className="ad_notification_link"
						href={link_to_portfolio}>{game_name}</A> hatte {had_quantity} &times; {had_stock} und hat {received_amount} erhalten.
					</O>
				);
			},
	},

	"6ba00e83faad": {
		"en": (A, had_quantity, had_stock, link_to_portfolio, portfolio_name, received_amount) => {
				return (
					<O>
						Your portfolio <A className="ad_notification_link" href={link_to_portfolio}>{portfolio_name}</A> had {had_quantity} &times; {had_stock} and received {received_amount}.
					</O>
				);
			},
		"da": (A, had_quantity, had_stock, link_to_portfolio, portfolio_name, received_amount) => {
				return (
					<O>
						Din portefølje <A className="ad_notification_link" href={link_to_portfolio}>{portfolio_name}</A> havde {had_quantity} &times; {had_stock} og modtog {received_amount}.
					</O>
				);
			},
		"sv": (A, had_quantity, had_stock, link_to_portfolio, portfolio_name, received_amount) => {
				return (
					<O>
						Din portfölj <A className="ad_notification_link" href={link_to_portfolio}>{portfolio_name}</A> hade {had_quantity} &times; {had_stock} och tog emot {received_amount}.
					</O>
				);
			},
		"de": (A, had_quantity, had_stock, link_to_portfolio, portfolio_name, received_amount) => {
				return (
					<O>
						Dein Portfolio <A className="ad_notification_link"
						href={link_to_portfolio}>{portfolio_name}</A> hat {had_quantity} &times;
						{had_stock} und hat {received_amount} erhalten.
					</O>
				);
			},
	},

	"ae21ce3cfe3f": {
		"en": (up_stock) => <O>Your purchase of <span className="ad_notification_gamelink">{up_stock.Name}</span> has gone through</O>,
		"da": (up_stock) => <O>Dit køb af <span className="ad_notification_gamelink">{up_stock.Name}</span> er gået igennem</O>,
		"sv": (up_stock) => <O>Ditt köp av<span className="ad_notification_gamelink">{up_stock.Name}</span> har gått igenom</O>,
		"de": (up_stock) => <O>Kauf von <span className="ad_notification_gamelink">{up_stock.Name}</span> war erfolgreich</O>,
	},

	"11ce13b0fda1": {
		"en": (I18N, down_stock, up_stock) => <O>You bought {up_stock.Delta} <b>{up_stock.Ticker}</b> and your bankroll decreased with {I18N.floatval_unit(-down_stock.Delta, 2, down_stock.Ticker)}.</O>,
		"da": (I18N, down_stock, up_stock) => <O>Du købte {up_stock.Delta} <b>{up_stock.Ticker}</b> og det kostede {I18N.floatval_unit(-down_stock.Delta, 2, down_stock.Ticker)}</O>,
		"sv": (I18N, down_stock, up_stock) => <O>Du köpte {up_stock.Delta} <b>{up_stock.Ticker}</b> och det kostade{I18N.floatval_unit(-down_stock.Delta, 2, down_stock.Ticker)}</O>,
		"de": (I18N, down_stock, up_stock) => <O>Du hast {up_stock.Delta} <b>{up_stock.Ticker}</b> gekauft, was dich {I18N.floatval_unit(-down_stock.Delta, 2, down_stock.Ticker)} gekostet hat.</O>,
	},

	"568ee70defbd": {
		"en": () => <O>You had no profit or loss.</O>,
		"da": () => <O>Du havde hverken profit eller tab.</O>,
		"sv": () => <O>Du hade varken förtjänst eller förlust.</O>,
		"de": () => <O>Du hattest weder Gewinn noch Verlust.</O>,
	},

	"ba4f6e65ca2e": {
		"en": () => <O>Profit:</O>,
		"da": () => <O>Profit:</O>,
		"sv": () => <O>Vinst:</O>,
		"de": () => <O>Gewinn:</O>,
	},

	"c3ee728d783c": {
		"en": () => <O>Loss:</O>,
		"da": () => <O>Tab:</O>,
		"sv": () => <O>Förlust:</O>,
		"de": () => <O>Verlust:</O>,
	},

	"95439e7e8d66": {
		"en": (down_stock) => <O>Your sale of <span className="ad_notification_gamelink">{down_stock.Name}</span> has gone through</O>,
		"da": (down_stock) => <O>Dit salg af <span className="ad_notification_gamelink">{down_stock.Name}</span> er gået igennem</O>,
		"sv": (down_stock) => <O>Din försäljning av <span className="ad_notification_gamelink">{down_stock.Name}</span> har gått igenom</O>,
		"de": (down_stock) => <O>Verkauf von <span className="ad_notification_gamelink">{down_stock.Name}</span> war erfolgreich</O>,
	},

	"bda7904b5c25": {
		"en": (I18N, down_stock, up_stock) => <O>You sold {-down_stock.Delta} <b>{down_stock.Ticker}</b> and your bankroll increased with {I18N.floatval_unit(up_stock.Delta, 2, up_stock.Ticker)}.</O>,
		"da": (I18N, down_stock, up_stock) => <O>Du solgte {-down_stock.Delta} <b>{down_stock.Ticker}</b> og du fik {I18N.floatval_unit(up_stock.Delta, 2, up_stock.Ticker)} for handlen.</O>,
		"sv": (I18N, down_stock, up_stock) => <O>Du sålde {-down_stock.Delta} <b>{down_stock.Ticker}</b> och du fick {I18N.floatval_unit(up_stock.Delta, 2, up_stock.Ticker)} för handeln.</O>,
		"de": (I18N, down_stock, up_stock) => <O>Du hast {-down_stock.Delta} <b>{down_stock.Ticker}</b> verkauft und hast {I18N.floatval_unit(up_stock.Delta, 2, up_stock.Ticker)} für den Verkauf bekommen.</O>,
	},

	"f033cbed27b9": {
		"en": (A, game_link, name) => <O>The game <A className="ad_notification_gamelink" href={game_link}>{name}</A> has started. Good luck!</O>,
		"da": (A, game_link, name) => <O>Spillet <A className="ad_notification_gamelink" href={game_link}>{name}</A> er begyndt. Held og lykke!</O>,
		"sv": (A, game_link, name) => <O>Spelet <A className="ad_notification_gamelink" href={game_link}>{name}</A> har startat. Lycka till!</O>,
		"de": (A, game_link, name) => <O>Das Spiel <A className="ad_notification_gamelink" href={game_link}>{name}</A> ist gestartet. Viel Glück!</O>,
	},

	"8f99367b442c": {
		"en": (A, game_link, portfolio_name) => <O>Game <A className="ad_notification_gamelink" href={game_link}>{portfolio_name}</A> has ended</O>,
		"da": (A, game_link, portfolio_name) => <O>Spillet <A className="ad_notification_gamelink" href={game_link}>{portfolio_name}</A> er slut</O>,
		"sv": (A, game_link, portfolio_name) => <O>Spelet <A className="ad_notification_gamelink" href={game_link}>{portfolio_name}</A> är slut</O>,
		"de": (A, game_link, portfolio_name) => <O>Das Spiel <A className="ad_notification_gamelink" href={game_link}>{portfolio_name}</A> ist beendet</O>,
	},

	"d09e79d8117f": {
		"en": (player_count, ranking) => <O>You ranked no. {ranking} of {player_count} players</O>,
		"da": (player_count, ranking) => <O>Du blev nummer {ranking} ud af {player_count} spillere</O>,
		"sv": (player_count, ranking) => <O>Du blev nummer {ranking} utav {player_count} spelare</O>,
		"de": (player_count, ranking) => <O>Du hast den {ranking}. Platz von insgesamt {player_count} Spielern belegt.</O>,
	},

	"9db2ca958a6c": {
		"en": (final_value) => <O>The final value was {final_value} kr.</O>,
		"da": (final_value) => <O>Den endelige værdi var {final_value} kr.</O>,
		"sv": (final_value) => <O>Det sista värdet var {final_value} kr.</O>,
		"de": (final_value) => <O>Der Wert deines Portfolios betrug am Ende €{final_value}.</O>,
	},

	"654cf9575394": {
		"en": (x) => <O>Your sale of <span className="ad_notification_gamelink">{x.StockName}</span> has gone through</O>,
		"da": (x) => <O>Dit salg af <span className="ad_notification_gamelink">{x.StockName}</span> er gået igennem</O>,
		"sv": (x) => <O>Din försäljning av <span className="ad_notification_gamelink">{x.StockName}</span> har gått igenom</O>,
		"de": (x) => <O>Verkauf von <span className="ad_notification_gamelink">{x.StockName}</span> war erfolgreich</O>,
	},

	"83acaabdee70": {
		"en": (I18N, money, stock_delta, x) => <O>You sold {stock_delta} <b>{x.Ticker}</b> and your bankroll increased with {I18N.floatval_unit(money, 2, x.BankrollCurrency)}.</O>,
		"da": (I18N, money, stock_delta, x) => <O>Du solgte {stock_delta} <b>{x.Ticker}</b> og du fik {I18N.floatval_unit(money, 2, x.BankrollCurrency)} for handlen.</O>,
		"sv": (I18N, money, stock_delta, x) => <O>Du sålde {stock_delta} <b>{x.Ticker}</b> och du fick {I18N.floatval_unit(money, 2, x.BankrollCurrency)} för handeln.</O>,
		"de": (I18N, money, stock_delta, x) => <O>Du hast {stock_delta} <b>{x.Ticker}</b> verkauft und hast {I18N.floatval_unit(money, 2, x.BankrollCurrency)} für den Verkauf bekommen.</O>,
	},

	"a8319c2de083": {
		"en": (x) => <O>Your purchase of <span className="ad_notification_gamelink">{x.StockName}</span> has gone through</O>,
		"da": (x) => <O>Dit køb af <span className="ad_notification_gamelink">{x.StockName}</span> er gået igennem</O>,
		"sv": (x) => <O>Ditt köp av<span className="ad_notification_gamelink">{x.StockName}</span> har gått igenom</O>,
		"de": (x) => <O>Kauf von <span className="ad_notification_gamelink">{x.StockName}</span> war erfolgreich</O>,
	},

	"a55592936d69": {
		"en": (I18N, money, stock_delta, x) => <O>You bought {stock_delta} <b>{x.Ticker}</b> and your bankroll decreased with {I18N.floatval_unit(money, 2, x.BankrollCurrency)}.</O>,
		"da": (I18N, money, stock_delta, x) => <O>Du købte {stock_delta} <b>{x.Ticker}</b> og det kostede {I18N.floatval_unit(money, 2, x.BankrollCurrency)}</O>,
		"sv": (I18N, money, stock_delta, x) => <O>Du köpte {stock_delta} <b>{x.Ticker}</b> och det kostade{I18N.floatval_unit(money, 2, x.BankrollCurrency)}</O>,
		"de": (I18N, money, stock_delta, x) => <O>Du hast {stock_delta} <b>{x.Ticker}</b> gekauft, was dich {I18N.floatval_unit(money, 2, x.BankrollCurrency)} gekostet hat.</O>,
	},

	"228b8d620677": {
		"en": (A, data, game_href) => <O>{data.UserName} joined <A href={game_href} className="ad_notification_gamelink">{data.GameName}</A></O>,
		"da": (A, data, game_href) => <O>{data.UserName} sluttede sig til <A href={game_href} className="ad_notification_gamelink">{data.GameName}</A></O>,
		"sv": (A, data, game_href) => <O>{data.UserName} anslöt sig <A href={game_href} className="ad_notification_gamelink">{data.GameName}</A></O>,
		"de": (A, data, game_href) => <O>{data.UserName} ist <A href={game_href} className="ad_notification_gamelink">{data.GameName}</A> beigetreten</O>,
	},

	"f9c810371c7a": {
		"en": (quantity_after) => <O>Quantity after split: {quantity_after}</O>,
		"da": (quantity_after) => <O>Mængde efter split: {quantity_after}</O>,
		"sv": (quantity_after) => <O>Mängd efter split: {quantity_after}</O>,
		"de": (quantity_after) => <O>Aktienmenge nach dem: {quantity_after}</O>,
	},

	"710f2bcc42ae": {
		"en": () => <O>Asset was liquidated due to insufficient amount for split.</O>,
		"da": () => <O>Værdipapiret blev likvideret pga. manglende mængde ift. split.</O>,
		"sv": () => <O>Värdepappret avvecklades eftersom att summan var för låg efter split.</O>,
		"de": () => <O>Die Aktie wurde aufgrund eines unzureichenden Split-Betrags liquidiert.</O>,
	},

	"45c43a7922d1": {
		"en": (A, game_href, game_name) => <O>Your assets were automatically adjusted in the game <A href={game_href}>{game_name}</A></O>,
		"da": (A, game_href, game_name) => <O>Dine værdipapirer blev automatisk justeret i spillet <A href={game_href}>{game_name}</A></O>,
		"sv": (A, game_href, game_name) => <O>Dina värdepapper justerades automatiskt i spelet <A href={game_href}>{game_name}</A></O>,
		"de": (A, game_href, game_name) => <O>Deine Aktien wurden im Spiel <A href={game_href}>{game_name}</A> automatisch angepasst.</O>,
	},

	"3b2bc6c606c9": {
		"en": (denominator, numerator) => <O>Split ratio: {numerator} for {denominator}</O>,
		"da": (denominator, numerator) => <O>Split: {denominator}:{numerator}</O>,
		"sv": (denominator, numerator) => <O>Split: {denominator}:{numerator}</O>,
		"de": (denominator, numerator) => <O>Split: {denominator}:{numerator}</O>,
	},

	"5c7b98613534": {
		"en": (quantity_before) => <O>Quantity before split: {quantity_before}</O>,
		"da": (quantity_before) => <O>Mængde før split: {quantity_before}</O>,
		"sv": (quantity_before) => <O>Mängd innan split: {quantity_before}</O>,
		"de": (quantity_before) => <O>Aktienmenge vor dem Split: {quantity_before}</O>,
	},

	"e62f1a537625": {
		"en": (bankroll_delta) => <O>Liquidation added {bankroll_delta} to your bankroll.</O>,
		"da": (bankroll_delta) => <O>Likvidering tilføjede {bankroll_delta} til din bankkonto.</O>,
		"sv": (bankroll_delta) => <O>Avvecklingen adderade {bankroll_delta} till ditt bankkonto.</O>,
		"de": (bankroll_delta) => <O>Durch die Liquidation wurden {bankroll_delta} zu deinem Konto hinzugefügt.</O>,
	},

	"1fac22020558": {
		"en": (denominator, numerator, stock) => <O>{stock} underwent a stock split with ratio {numerator} for {denominator}.</O>,
		"da": (denominator, numerator, stock) => <O>{stock} undergik et aktiesplit {denominator}:{numerator}.</O>,
		"sv": (denominator, numerator, stock) => <O>{stock} undergick en aktiesplit {denominator}:{numerator}.</O>,
		"de": (denominator, numerator, stock) => <O>{stock} unterzog sich einem Aktiensplit mit einem Verhältnis von {numerator} zu {denominator}.</O>,
	},

	"b58aceae3be4": {
		"en": (A, portfolio_href) => <O><A href={portfolio_href}>One of your portfolios</A> contains this stock, and no action was taken.</O>,
		"da": (A, portfolio_href) => <O><A href={portfolio_href}>Én af dine porteføljer</A> indeholder denne aktie, og ingen handling er foretaget.</O>,
		"sv": (A, portfolio_href) => <O><A href={portfolio_href}>En av dina portföljer</A> innehåller denna aktie, och ingen åtgärd vidtogs.</O>,
		"de": (A, portfolio_href) => <O><A href={portfolio_href}>Eines Ihrer Portfolios</A> enthält diese Aktie und es wurden keine Maßnahmen ergriffen.</O>,
	},

	"d991e16915ae": {
		"en": () => <O>You can adjust the quantity yourself, if you want to compensate.</O>,
		"da": () => <O>Du kan justere mængden selv, hvis du vil kompensere.</O>,
		"sv": () => <O>Du kan justera mängden själv, om du vill kompensera.</O>,
		"de": () => <O>Du kannst die Menge selbst anpassen, solltest du sie ausgleichen wollen.</O>,
	},

	"99a188117518": {
		"en": (A, stock, stock_href) => <O><A href={stock_href}>{stock} had a stock split</A></O>,
		"da": (A, stock, stock_href) => <O><A href={stock_href}>Aktieplit for {stock}</A></O>,
		"sv": (A, stock, stock_href) => <O><A href={stock_href}>Aktieplit för {stock}</A></O>,
		"de": (A, stock, stock_href) => <O><A href={stock_href}>{stock} hat einen Aktiensplit durchgeführt.</A></O>,
	},

	"4298d987d381": {
		"en": (amount, quantity, stock) => <O>{quantity} &times; {stock} sold for {amount}</O>,
		"da": (amount, quantity, stock) => <O>{quantity} &times; {stock} solgt for {amount}</O>,
		"sv": (amount, quantity, stock) => <O>{quantity} &times; {stock} såld för {amount}</O>,
		"de": (amount, quantity, stock) => <O>{quantity} &times; {stock} wurde für {amount} verkauft</O>,
	},

	"670e746c5a58": {
		"en": () => {
				return (
					<O>
					The following assets have been sold
					because they are now delisted on
					{site_name}.
					</O>
				);
			},
		"da": () => {
				return (
					<O>
					De følgende værdipapirer er solgt fordi de nu
					er afnoteret på {site_name}.
					</O>
				);
			},
		"sv": () => {
				return (
					<O>
					Följande värdepapper har sålts eftersom de avnoterats från {site_name}.
					</O>
				);
			},
		"de": () => {
				return (
					<O>
					Die folgenden Aktien wurden verkauft, da sie nicht mehr auf {site_name} geführt werden.
					</O>
				);
			},
	},

	"5017291c5f6d": {
		"en": () => <O>Automatic sale of assets due to delisting</O>,
		"da": () => <O>Automatisk salg pga. afnotering</O>,
		"sv": () => <O>Säljs automatiskt pga avnotering</O>,
		"de": () => <O>Automatischer Verkauf von Anlagen, weil diese nicht mehr geführt werden.</O>,
	},

	"8cffdad43a5d": {
		"en": (amount_element, name) => <O>{amount_element} shares of {name}</O>,
		"da": (amount_element, name) => <O>{amount_element} aktier af {name}</O>,
		"sv": (amount_element, name) => <O>{amount_element} andelar av {name}</O>,
		"de": (amount_element, name) => <O>{amount_element} Aktien von {name}</O>,
	},

	"8a56e90c6ed1": {
		"en": () => <O>Game admin addition</O>,
		"da": () => <O>Administrator-tilføjelse</O>,
		"sv": () => <O>Administratör-tillägg</O>,
		"de": () => <O>Aktienvergabe durch Spieladministrator</O>,
	},

	"e781a437d558": {
		"en": (amount, give_stock_element, on_date, our_own_email) => <O>Game admin awarded {amount} &times; {give_stock_element} with effective date {on_date}. If you want to know why, please reach out to {our_own_email}.</O>,
		"da": (amount, give_stock_element, on_date, our_own_email) => <O>Spiladministratoren gav {amount} &times; {give_stock_element} med effektiv dato {on_date}. Hvis du vil vide hvorfor, skriv venligst til {our_own_email}.</O>,
		"sv": (amount, give_stock_element, on_date, our_own_email) => <O>Speladministratören gav {amount} &times; {give_stock_element} med ikraftträdande den {on_date}. Om du vill veta varför, skriv till {our_own_email}.</O>,
		"de": (amount, give_stock_element, on_date, our_own_email) => <O>Der Spieladministrator hat {amount} &times; {give_stock_element} am {on_date} an dich vergeben. Wenn du mehr erfahren möchtest, wende dich bitte an {our_own_email}.</O>,
	},

	"40e118f7d60f": {
		"en": () => <O>Company split</O>,
		"da": () => <O>Firmaopslitning</O>,
		"sv": () => <O>Företagssplittring</O>,
		"de": () => <O>Unternehmensaufteilung</O>,
	},

	"1c7836276259": {
		"en": (amount, give_stock_element, have_stock_element, on_date) => <O>{have_stock_element} split on {on_date} into {amount} &times; {give_stock_element} per share.</O>,
		"da": (amount, give_stock_element, have_stock_element, on_date) => <O>{have_stock_element} splittede d. {on_date} til {amount} &times; {give_stock_element} pr. aktie.</O>,
		"sv": (amount, give_stock_element, have_stock_element, on_date) => <O>{have_stock_element} splittade den {on_date} till {amount} &times; {give_stock_element} per andel.</O>,
		"de": (amount, give_stock_element, have_stock_element, on_date) => <O>{have_stock_element} wurde am {on_date} in {amount} &times; {give_stock_element} pro Anteil geteilt.</O>,
	},

	"4a92e338798d": {
		"en": () => <O>Dividend income</O>,
		"da": () => <O>Udbytte udbetalt</O>,
		"sv": () => <O>Utdelning utbetalt</O>,
		"de": () => <O>Dividendenerträge</O>,
	},

	"52d44241b0bd": {
		"en": (amount, have_stock_element, on_date) => <O>{have_stock_element} paid dividends on {on_date} and gave {amount} per share.</O>,
		"da": (amount, have_stock_element, on_date) => <O>{have_stock_element} udbetalte udbytte d. {on_date} og gav {amount} pr. aktie.</O>,
		"sv": (amount, have_stock_element, on_date) => <O>{have_stock_element} betalade utdelning den {on_date} och gav {amount} per andel.</O>,
		"de": (amount, have_stock_element, on_date) => <O>{have_stock_element} hat am {on_date} Dividenden ausgezahlt und pro Aktie {amount} ausgeschüttet.</O>,
	},

	"15e04b3ff0c6": {
		"en": () => <O>Conversion of equities</O>,
		"da": () => <O>Konvertering af værdier</O>,
		"sv": () => <O>Konvertering av aktier</O>,
		"de": () => <O>Umwandlung von Aktien</O>,
	},

	"b32c677a9f5d": {
		"en": (new_thing, old_thing) => {
				return (
					<O>
					Dear user,
					your {old_thing} equities has been converted to {new_thing}, that has replaced it in the game.
					Sincerely, {site_name}.
					</O>
				);
			},
		"da": (new_thing, old_thing) => {
				return (
					<O>
					Kære spiller,
					dine {old_thing} værdipapirer er blevet konverteret til {new_thing}, som har erstattet
					den i spillet. Med venlig hilsen {site_name}.
					</O>
				);
			},
		"sv": (new_thing, old_thing) => {
				return (
					<O>
					Kära användare,
					dina {old_thing}-aktier har konverterats till {new_thing}, som har ersatt de gamla i spelet.
					Vänliga hälsningar {site_name}.
					</O>
				);

			},
		"de": (new_thing, old_thing) => {
				return (
					<O>
					Lieber Spieler,
					deine {old_thing} Aktien wurden im Spiel umgewandelt in {new_thing}.
					MfG {site_name}.
					</O>
				);

			},
	},

	"0745d8bf8a65": {
		"en": () => <O>Banned from game</O>,
		"da": () => <O>Bandlyst fra spil</O>,
		"sv": () => <O>Bannlyst från spelet</O>,
		"de": () => <O>Aus dem Spiel geworfen</O>,
	},

	"d3817e639dfc": {
		"en": () => <O>Achievements won:</O>,
		"da": () => <O>Bedrifter vundet:</O>,
		"sv": () => <O>Bedrifter vunna:</O>,
		"de": () => <O>Erfolge gewonnen:</O>,
	},

	"073ab3b900d3": {
		"en": () => <O>Name</O>,
		"da": () => <O>Navn</O>,
		"sv": () => <O>Namn</O>,
		"de": () => <O>Name</O>,
	},

	"6358348b45a5": {
		"en": () => <O>Birthdate</O>,
		"da": () => <O>Fødselsdato</O>,
		"sv": () => <O>Födelsedatum</O>,
		"de": () => <O>Geburtsdatum</O>,
	},

	"2e3d20ff0356": {
		"en": (site_base) => <O>To change your birthdate please contact us per email at info@{site_base}.</O>,
		"da": (site_base) => <O>For at ændre din fødselsdato, bedes du kontakte os via email på info@{site_base}.</O>,
		"sv": (site_base) => <O>För att ändra ditt födelsedatum, vänligen kontakta oss via e-post på info@{site_base}.</O>,
		"de": (site_base) => <O>Um dein Geburtsdatum zu ändern, kontaktiere uns bitte per E-Mail unter info@{site_base}.</O>,
	},

	"e1eb91616a5b": {
		"en": () => <O>Email</O>,
		"da": () => <O>Email</O>,
		"sv": () => <O>Epost</O>,
		"de": () => <O>E-Mail</O>,
	},

	"1a975558c544": {
		"en": () => <O>Save changes</O>,
		"da": () => <O>Gem ændringer</O>,
		"sv": () => <O>Spara ändringar</O>,
		"de": () => <O>Änderungen speichern</O>,
	},

	"62e657845db8": {
		"en": (site_name) => {
			return (
				<O>You have already connected another
				{site_name} account with Facebook. If
				you want to connect this account, you
				can disconnect from Facebook on the
				other account.</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>Du har allerede forbundet en anden
				{site_name} konto med Facebook. Hvis du
				vil forbinde denne konto, skal du først
				slette Facebook login på den anden konto.</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>Du har redan kopplat ett annat
				{site_name} konto med Facebook. Om du vill koppla detta
				konto så behöver du koppla bort Facebook från
				det tidigare kontot.</O>
			);
		},
		"de": (site_name) => {
			return (
				<O>Du hast bereits ein anderes
				{site_name}-Konto mit Facebook verbunden. Wenn
				du dieses Konto verbinden möchtest, musst du
				die Verknüpfung mit Facebook auf dem anderen
				Konto entfernen.</O>
			);
		},
	},

	"0b0775051949": {
		"en": () => <O>DELETE ACCOUNT</O>,
		"da": () => <O>SLET DIN KONTO</O>,
		"sv": () => <O>RADERA KONTO</O>,
		"de": () => <O>KONTO LÖSCHEN</O>,
	},

	"a37bfdbb777e": {
		"en": () => <O>ENABLE E-MAIL LOGIN</O>,
		"da": () => <O>MULIGGØR E-MAIL LOGIN</O>,
		"sv": () => <O>AKTIVERA INLOGGNING MED E-MAIL</O>,
		"de": () => <O>Einloggen per E-Mail aktivieren</O>,
	},

	"992e14598c5c": {
		"en": () => <O>Add the posibility to login with e-mail</O>,
		"da": () => <O>Tilføj muligheden for logge ind med e-mail</O>,
		"sv": () => <O>Addera möjligheten att logga in med e-maill</O>,
		"de": () => <O>Füge die Möglichkeit hinzu, dich per E-Mail anzumelden</O>,
	},

	"e2b4eb91edae": {
		"en": () => <O>CHANGE PASSWORD</O>,
		"da": () => <O>SKIFT PASSWORD</O>,
		"sv": () => <O>ÄNDRA LÖSENORD</O>,
		"de": () => <O>PASSWORT ÄNDERN</O>,
	},

	"8dab96421b50": {
		"en": () => <O>Change your password here</O>,
		"da": () => <O>Du kan ændre dit password her</O>,
		"sv": () => <O>Du kan ändra ditt lösenord här</O>,
		"de": () => <O>Hier kannst du dein Passwort ändern</O>,
	},

	"5be5388ad870": {
		"en": () => <O>Permanently and therefore irrevocably delete your account. There really is no way to undo this.</O>,
		"da": () => <O>Permanent og dermed irreversibel sletning af din konto. Der er ingen mulighed for at fortryde.</O>,
		"sv": () => <O>Permanent och därmed irreversibel radering av ditt konto. Det finns inget sätt att ångra det.</O>,
		"de": () => <O>Löschen dein Konto dauerhaft und unwiderruflich. Es gibt keine Möglichkeit, dies rückgängig zu machen.</O>,
	},

	"7e7077288f48": {
		"en": () => <O>Wrong username or password</O>,
		"da": () => <O>Forkert brugernavn el. password</O>,
		"sv": () => <O>Fel användarnamn eller lösenord</O>,
		"de": () => <O>Falscher Benutzername oder falsches Passwort</O>,
	},

	"d041204ff927": {
		"en": (change, email_value, focus_once, input_classname) => {
				return (
					<O>
						<input
							ref={focus_once}
							placeholder="Email"
							className={input_classname("mb-5")}
							type="email"
							name="email"
							value={email_value}
							onChange={change}
						/>
					</O>
				);
			},
		"da": (change, email_value, focus_once, input_classname) => {
				return (
					<O>
						<input
							ref={focus_once}
							placeholder="Email"
							className={input_classname("mb-5")}
							type="email"
							name="email"
							value={email_value}
							onChange={change}
						/>
					</O>
				);
			},
		"sv": (change, email_value, focus_once, input_classname) => {
				return (
					<O>
						<input
							ref={focus_once}
							placeholder="Email"
							className={input_classname("mb-5")}
							type="email"
							name="email"
							value={email_value}
							onChange={change}
						/>
					</O>
				);
			},
		"de": (change, email_value, focus_once, input_classname) => {
				return (
					<O>
						<input
							ref={focus_once}
							placeholder="E-Mail"
							className={input_classname("mb-5")}
							type="email"
							name="email"
							value={email_value}
							onChange={change}
						/>
					</O>
				);
			},
	},

	"7afc26a69c7f": {
		"en": (change, input_classname, password_value) => {
			return (
				<O>
					<input
						placeholder="Password"
						className={input_classname("mb-3")}
						type="password"
						name="password"
						value={password_value}
						onChange={change}
					/>
				</O>
			);
		},
		"da": (change, input_classname, password_value) => {
			return (
				<O>
					<input
						placeholder="Password"
						className={input_classname("mb-3")}
						type="password"
						name="password"
						value={password_value}
						onChange={change}
					/>
				</O>
			);
		},
		"sv": (change, input_classname, password_value) => {
			return (
				<O>
					<input
						placeholder="Lösenord"
						className={input_classname("mb-3")}
						type="password"
						name="password"
						value={password_value}
						onChange={change}
					/>
				</O>
			);
		},
		"de": (change, input_classname, password_value) => {
			return (
				<O>
					<input
						placeholder="Passwort"
						className={input_classname("mb-3")}
						type="password"
						name="password"
						value={password_value}
						onChange={change}
					/>
				</O>
			);
		},
	},

	"e0eff7d433db": {
		"en": () => <O>Don't have an account?</O>,
		"da": () => <O>Har du ikke en konto?</O>,
		"sv": () => <O>Har du inte ett konto?</O>,
		"de": () => <O>Du hast kein Konto?</O>,
	},

	"e20088f264b8": {
		"en": (change, email_value, input_classname) => {
			return (
				<O><input
					placeholder="Type in your email"
					className={input_classname("mb-5")}
					type="email"
					name="email"
					value={email_value}
					onChange={change}
				/></O>
			);
		},
		"da": (change, email_value, input_classname) => {
			return (
				<O><input
					placeholder="Indtast din email"
					className={input_classname("mb-5")}
					type="email"
					name="email"
					value={email_value}
					onChange={change}
				/></O>
			);
		},
		"sv": (change, email_value, input_classname) => {
			return (
				<O><input
					placeholder="Ange din email"
					className={input_classname("mb-5")}
					type="email"
					name="email"
					value={email_value}
					onChange={change}
				/></O>
			);
		},
		"de": (change, email_value, input_classname) => {
			return (
				<O><input
					placeholder="E-Mail-Adresse eingeben"
					className={input_classname("mb-5")}
					type="email"
					name="email"
					value={email_value}
					onChange={change}
				/></O>
			);
		},
	},

	"57bb866330ce": {
		"en": () => <O>Send password reset email</O>,
		"da": () => <O>Send email for at skifte dit password</O>,
		"sv": () => <O>Skicka email för att återställa ditt lösenord</O>,
		"de": () => <O>E-Mail zum Zurücksetzen des Passworts senden</O>,
	},

	"a7bb23c5455d": {
		"en": () => <O>Sign up for {site_name}. It's free.</O>,
		"da": () => <O>Tilmeld dig {site_name}. Det er gratis.</O>,
		"sv": () => <O>Registrera dig på {site_name}. Det är gratis.</O>,
		"de": () => <O>Melde dich bei {site_name} an. Es ist kostenlos.</O>,
	},

	"aa9efab23a61": {
		"en": () => <O>Sign up with Facebook</O>,
		"da": () => <O>Tilmeld via Facebook</O>,
		"sv": () => <O>Registrera via Facebook</O>,
		"de": () => <O>Mit Facebook registrieren</O>,
	},

	"9fa183d810d6": {
		"en": () => <O>Sign up with Twitter</O>,
		"da": () => <O>Tilmeld via Twitter</O>,
		"sv": () => <O>Registrera via Twitter</O>,
		"de": () => <O>Mit Twitter registrieren</O>,
	},

	"dffafb357f54": {
		"en": () => <O>Sign up with email</O>,
		"da": () => <O>Tilmeld via email</O>,
		"sv": () => <O>Registrera via email</O>,
		"de": () => <O>Mit E-Mail-Adresse registrieren</O>,
	},

	"8cd105e8bf79": {
		"en": () => <O>Login here</O>,
		"da": () => <O>Log ind</O>,
		"sv": () => <O>Logga in</O>,
		"de": () => <O>Hier einloggen</O>,
	},

	"8df4f1465af8": {
		"en": () => <O>It's completely free. No hidden costs anywhere.</O>,
		"da": () => <O>Det er helt gratis. Ingen skjulte omkostninger.</O>,
		"sv": () => <O>Det är helt gratis. Inga dolda kostnader.</O>,
		"de": () => <O>Das Spiel ist vollkommen kostenlos. Es gibt keine versteckten Gebühren.</O>,
	},

	"bffb2ede0b4b": {
		"en": () => <O>Compete against friends or win prices in our competitions.</O>,
		"da": () => <O>Konkurrer mod dine venner eller vind præmier i vores konkurrencer.</O>,
		"sv": () => <O>Tävla mot dina vänner eller vin priser i våra tävlingar.</O>,
		"de": () => <O>Spiele gegen deine Freunde oder gewinne coole Preise bei unseren Börsen Battles.</O>,
	},

	"b024c44d5270": {
		"en": () => <O>We're completely transparent when it comes to privacy. Find out what we know about you, whenever you want.</O>,
		"da": () => <O>Der er 100% transparens vedrørende dit privatliv. Find ud af hvad vi ved om dig, hvornår du vil.</O>,
		"sv": () => <O>Vi är 100% transparenta när det gäller din integritet. Du kan ta reda på vad vi vet om dig, när du vill.</O>,
		"de": () => <O>Wir sind transparent, wenn es um deine Privatsphäre geht. Lies jederzeit alles nach, was du wissen möchtest.</O>,
	},

	"ebc71f862240": {
		"en": () => <O>Already have an account?</O>,
		"da": () => <O>Har du allerede en konto?</O>,
		"sv": () => <O>Har du redan ett konto?</O>,
		"de": () => <O>Du hast bereits ein Konto?</O>,
	},

	"7f311a5ff8cb": {
		"en": (InputHeader) => {
			return (
				<O><InputHeader
					txt={[["screen_name", "Name"]]}
					err={[
						["err_screen_name_missing", "You must enter a name"]
					]}
				/></O>
			);
		},
		"da": (InputHeader) => {
			return (
				<O><InputHeader
					txt={[["screen_name", "Navn"]]}
					err={[
						["err_screen_name_missing", "Du skal indtaste et navn"]
					]}
				/></O>
			);
		},
		"sv": (InputHeader) => {
                        return (
                                <O><InputHeader
                                        txt={[["screen_name", "Name"]]}
                                        err={[
                                                ["err_screen_name_missing", "Du måste ange ett namn"]
                                        ]}
                                /></O>
                        );
		},
		"de": (InputHeader) => {
                        return (
                                <O><InputHeader
                                        txt={[["screen_name", "Name"]]}
                                        err={[
                                                ["err_screen_name_missing", "Bitte gib einen Namen ein"]
                                        ]}
                                /></O>
                        );
                },
	},

	"6ce9ed6fbd27": {
		"en": (blur, change, focus_once, input_classname, screen_name_value, state_err_screen_name_missing) => {
			return (
				<O><input
					ref={focus_once}
					placeholder="Name"
					className={input_classname("mb-4", state_err_screen_name_missing)}
					type="text"
					name="screen_name"
					value={screen_name_value}
					onChange={change}
					onBlur={blur}
				/></O>
			);
		},
		"da": (blur, change, focus_once, input_classname, screen_name_value, state_err_screen_name_missing) => {
			return (
				<O><input
					ref={focus_once}
					placeholder="Navn"
					className={input_classname("mb-4", state_err_screen_name_missing)}
					type="text"
					name="screen_name"
					value={screen_name_value}
					onChange={change}
					onBlur={blur}
				/></O>
			);
		},
		"sv": (blur, change, focus_once, input_classname, screen_name_value, state_err_screen_name_missing) => {
                        return (
                                <O><input
                                        ref={focus_once}
                                        placeholder="Namn"
                                        className={input_classname("mb-4", state_err_screen_name_missing)}
                                        type="text"
                                        name="screen_name"
                                        value={screen_name_value}
                                        onChange={change}
                                        onBlur={blur}
                                /></O>
                        );
		},
		"de": (blur, change, focus_once, input_classname, screen_name_value, state_err_screen_name_missing) => {
			return (
				<O><input
					ref={focus_once}
					placeholder="Name"
					className={input_classname("mb-4", state_err_screen_name_missing)}
					type="text"
					name="screen_name"
					value={screen_name_value}
					onChange={change}
					onBlur={blur}
				/></O>
			);
		},
	},

	"9d137b4c2687": {
		"en": (InputHeader) => {
			return (
				<O><InputHeader
					txt={[["email", "Email"]]}
					err={[
						["err_email_missing", "You must enter an email"],
						["err_invalid_email", "The email is invalid"],
						["err_email_taken", "The email is already taken"]
					]}
				/></O>
			);
		},
		"da": (InputHeader) => {
			return (
				<O><InputHeader
					txt={[["email", "Email"]]}
					err={[
						["err_email_missing", "Du skal skrive en email"],
						["err_invalid_email", "Ugyldig email"],
						["err_email_taken", "Email er allerede brugt"]
					]}
				/></O>
			);
		},
		"sv": (InputHeader) => {
			return (
				<O><InputHeader
					txt={[["email", "Email"]]}
					err={[
						["err_email_missing", "Ange en email"],
						["err_invalid_email", "Ogiltig email"],
						["err_email_taken", "Email används redan"]
					]}
				/></O>
			);
		},
		"de": (InputHeader) => {
                        return (
                                <O><InputHeader
                                        txt={[["email", "E-Mail-Adresse"]]}
                                        err={[
                                                ["err_email_missing", "Bitte gib eine E-Mail-Adresse ein"],
                                                ["err_invalid_email", "Die E-Mail-Adresse ist ungültig"],
                                                ["err_email_taken", "Die E-Mail-Adresse wird bereits verwendet"]
                                        ]}
                                /></O>
                        );
                },
	},

	"1ebdd5bac5eb": {
		"en": (blur, change, email_value, input_classname, state_err_email_missing, state_err_email_taken, state_err_invalid_email) => {
			return (
				<O><input
					placeholder="Email"
					className={input_classname("mb-4", state_err_email_missing || state_err_invalid_email || state_err_email_taken)}
					type="email"
					name="email"
					value={email_value}
					onChange={change}
					onBlur={blur}
				/></O>
			);
		},
		"da": (blur, change, email_value, input_classname, state_err_email_missing, state_err_email_taken, state_err_invalid_email) => {
			return (
				<O><input
					placeholder="Email"
					className={input_classname("mb-4", state_err_email_missing || state_err_invalid_email || state_err_email_taken)}
					type="email"
					name="email"
					value={email_value}
					onChange={change}
					onBlur={blur}
				/></O>
			);
		},
		"sv": (blur, change, email_value, input_classname, state_err_email_missing, state_err_email_taken, state_err_invalid_email) => {
                        return (
                                <O><input
                                        placeholder="Email"
                                        className={input_classname("mb-4", state_err_email_missing || state_err_invalid_email || state_err_email_taken)}
                                        type="email"
                                        name="email"
                                        value={email_value}
                                        onChange={change}
                                        onBlur={blur}
                                /></O>
                        );
		},
		"de": (blur, change, email_value, input_classname, state_err_email_missing, state_err_email_taken, state_err_invalid_email) => {
                        return (
                                <O><input
                                        placeholder="E-Mail-Adresse"
                                        className={input_classname("mb-4", state_err_email_missing || state_err_invalid_email || state_err_email_taken)}
                                        type="email"
                                        name="email"
                                        value={email_value}
                                        onChange={change}
                                        onBlur={blur}
                                /></O>
                        );
                },
	},

	"0487e01d0000": {
		"en": (InputHeader) => {
			return (
				<O><InputHeader
					txt={[["password", "Password"]]}
					err={[
						["err_no_password", "You must enter a password"],
						["err_password_mismatch", "Passwords don't match"],
					]}
				/></O>
			);
		},
		"da": (InputHeader) => {
			return (
				<O><InputHeader
					txt={[["password", "Password"]]}
					err={[
						["err_no_password", "Du skal indtaste et password"],
						["err_password_mismatch", "Passwords er ikke ens"],
					]}
				/></O>
			);
		},
		"sv": (InputHeader) => {
                        return (
                                <O><InputHeader
                                        txt={[["password", "Lösenord"]]}
                                        err={[
                                                ["err_no_password", "Du måste ange ett lösenord"],
                                                ["err_password_mismatch", "Lösenorden stämmer inte överrens"],
                                        ]}
                                /></O>
                        );
		},
		"de": (InputHeader) => {
                        return (
                                <O><InputHeader
                                        txt={[["password", "Passwort"]]}
                                        err={[
                                                ["err_no_password", "Bitte gib ein Passwort ein"],
                                                ["err_password_mismatch", "Die Passwörter stimmen nicht überein"],
                                        ]}
                                /></O>
                        );
                },
	},

	"099d5e8e6516": {
		"en": (blur, change, input_classname, password_value, state_err_password_mismatch) => {
			return (
				<O><input
					placeholder="Password"
					className={input_classname("mb-4", state_err_password_mismatch)}
					type="password"
					name="password"
					value={password_value}
					onChange={change}
					onBlur={blur}
				/></O>
			);
		},
		"da": (blur, change, input_classname, password_value, state_err_password_mismatch) => {
			return (
				<O><input
					placeholder="Password"
					className={input_classname("mb-4", state_err_password_mismatch)}
					type="password"
					name="password"
					value={password_value}
					onChange={change}
					onBlur={blur}
				/></O>
			);
		},
		"sv": (blur, change, input_classname, password_value, state_err_password_mismatch) => {
                        return (
                                <O><input
                                        placeholder="Lösenord"
                                        className={input_classname("mb-4", state_err_password_mismatch)}
                                        type="password"
                                        name="password"
                                        value={password_value}
                                        onChange={change}
                                        onBlur={blur}
                                /></O>
                        );
		},
		"de": (blur, change, input_classname, password_value, state_err_password_mismatch) => {
                        return (
                                <O><input
                                        placeholder="Passwort"
                                        className={input_classname("mb-4", state_err_password_mismatch)}
                                        type="password"
                                        name="password"
                                        value={password_value}
                                        onChange={change}
                                        onBlur={blur}
                                /></O>
                        );
                },
	},

	"915351404635": {
		"en": (InputHeader) => {
			return (
				<O><InputHeader
					txt={[["password_again", "Password again"]]}
					err={[
						["err_no_password_again", "You must enter your password twice"]
					]}
				/></O>
			);
		},
		"da": (InputHeader) => {
			return (
				<O><InputHeader
					txt={[["password_again", "Password igen"]]}
					err={[
						["err_no_password_again", "Du skal indtaste password to gange"]
					]}
				/></O>
			);
		},
		"sv": (InputHeader) => {
                        return (
                                <O><InputHeader
                                        txt={[["password_again", "Lösenord igen"]]}
                                        err={[
                                                ["err_no_password_again", "Du måste skriva lösenordet två gånger"]
                                        ]}
                                /></O>
                        );
		},
		"de": (InputHeader) => {
                        return (
                                <O><InputHeader
                                        txt={[["password_again", "Erneutes Passwort"]]}
                                        err={[
                                                ["err_no_password_again", "Bitte gib dein Passwort ein zweites Mal ein"]
                                        ]}
                                /></O>
                        );
                },
	},

	"5cf0aadf8679": {
		"en": (blur, change, input_classname, password_again_value, state_err_password_mismatch) => {
			return (
				<O><input
					placeholder="Password again"
					className={input_classname("mb-4", state_err_password_mismatch)}
					type="password"
					name="password_again"
					value={password_again_value}
					onChange={change}
					onBlur={blur}
				/></O>
			);
		},
		"da": (blur, change, input_classname, password_again_value, state_err_password_mismatch) => {
			return (
				<O><input
					placeholder="Password igen"
					className={input_classname("mb-4", state_err_password_mismatch)}
					type="password"
					name="password_again"
					value={password_again_value}
					onChange={change}
					onBlur={blur}
				/></O>
			);
		},
		"sv": (blur, change, input_classname, password_again_value, state_err_password_mismatch) => {
                        return (
                                <O><input
                                        placeholder="Lösenord igen"
                                        className={input_classname("mb-4", state_err_password_mismatch)}
                                        type="password"
                                        name="password_again"
                                        value={password_again_value}
                                        onChange={change}
                                        onBlur={blur}
                                /></O>
                        );
		},
		"de": (blur, change, input_classname, password_again_value, state_err_password_mismatch) => {
                        return (
                                <O><input
                                        placeholder="Erneutes Passwort"
                                        className={input_classname("mb-4", state_err_password_mismatch)}
                                        type="password"
                                        name="password_again"
                                        value={password_again_value}
                                        onChange={change}
                                        onBlur={blur}
                                /></O>
                        );
                },
	},

	"ed6906b53453": {
		"en": (site_name) => <O>I've read and accepted both the {site_name} <a target="_blank" className="ad_blue_link" href="/privacy">Privacy Policy</a> and <a target="_blank" className="ad_blue_link" href="/termsofuse">Terms of Use</a>.</O>,
		"da": (site_name) => <O>Jeg har læst og accepteret {site_name}s <a target="_blank" className="ad_blue_link"  href="/privacy">privatlivspolitik</a> og <a target="_blank" className="ad_blue_link"  href="/termsofuse">brugsbetingelser</a>.</O>,
		"sv": (site_name) => <O>Jag har läst och accepterat {site_name}s <a target="_blank" className="ad_blue_link"  href="/privacy">integritetspolicy</a> och <a target="_blank" className="ad_blue_link"  href="/termsofuse">användarvillkor</a>.</O>,
		"de": (site_name) => <O>Ich habe sowohl die <a target="_blank" className="ad_blue_link" href="/privacy">Datenschutzerklärung</a> als auch die <a target="_blank" className="ad_blue_link" href="/termsofuse">Geschäftsbedingungen</a> von {site_name} gelesen und akzeptiert.</O>,
	},

	"4a23f8a1b752": {
		"en": () => <O>Sign up</O>,
		"da": () => <O>Tilmeld</O>,
		"sv": () => <O>Bli medlem</O>,
		"de": () => <O>Registrieren</O>,
	},

	"f82723a86c4e": {
		"_deleted": true, // FIXME translation has no references; delete this entire section if you no longer need it
		"en": () => <O>Education</O>,
		"da": () => <O>Undervisning</O>,
		"sv": () => <O>Bildning</O>,
		"de": () => <O>Bildung</O>,
	},

	"49eb0207300a": {
		"en": () => <O>Game ends in &nbsp;</O>,
		"da": () => <O>Spillet slutter om &nbsp;</O>,
		"sv": () => <O>Spelet avslutas om &nbsp;</O>,
		"de": () => <O>Das Spiel endet in &nbsp;</O>,
	},

	"abf196c60c4a": {
		"en": () => <O>The game has begun</O>,
		"da": () => <O>Spillet er startet</O>,
		"sv": () => <O>Spelet har startat</O>,
		"de": () => <O>Das Spiel hat begonnen</O>,
	},

	"510bb92f80e7": {
		"en": () => <O>Join</O>,
		"da": () => <O>Deltag</O>,
		"sv": () => <O>Gå med</O>,
		"de": () => <O>Beitreten</O>,
	},

	"725b44f1efd1": {
		"en": () => <O>NAME</O>,
		"da": () => <O>NAVN</O>,
		"sv": () => <O>NAMN</O>,
		"de": () => <O>NAME</O>,
	},

	"0621601b11ea": {
		"en": () => <O>TRADES</O>,
		"da": () => <O>HANDLER</O>,
		"sv": () => <O>UTBYTEN</O>,
		"de": () => <O>HANDEL</O>,
	},

	"854fb783390b": {
		"en": () => <O>TOTAL CHANGE</O>,
		"da": () => <O>TOTAL ÆNDRING</O>,
		"sv": () => <O>TOTAL ÄNDRING</O>,
		"de": () => <O>GESAMTE VERÄNDERUNG</O>,
	},

	"16e025f9ae0a": {
		"en": () => <O>VALUE</O>,
		"da": () => <O>VÆRDI</O>,
		"sv": () => <O>VÄRDE</O>,
		"de": () => <O>WERT</O>,
	},

	"e0e3e5b8ec7d": {
		"en": () => <O>The risk score of the portfolio (Beta)</O>,
		"da": () => <O>Porteføljens risikoscore (Beta)</O>,
		"sv": () => <O>Riskpoängen för portföljen (beta)</O>,
		"de": () => <O>Der Risiko-Score des Portfolios (Beta)</O>,
	},

	"8143cadca421": {
		"en": () => <O>The risk-adjusted return of the portfolio</O>,
		"da": () => <O>Porteføljens risikojusteret afkast</O>,
		"sv": () => <O>Portföljens riskjusterade avkastning</O>,
		"de": () => <O>Die risikoadjustierte Rendite des Portfolios</O>,
	},

	"800f7d97fa4e": {
		"en": () => <O>Admin Actions</O>,
		"da": () => <O>Admin handlinger</O>,
		"sv": () => <O>Admin åtgärder</O>,
		"de": () => <O>Admin Aktionen</O>,
	},

	"e008b94a4023": {
		"en": () => <O>Thanks for playing!</O>,
		"da": () => <O>Tak fordi du spiller!</O>,
		"sv": () => <O>Tack för att du spelar!</O>,
		"de": () => <O>Danke fürs Spielen!</O>,
	},

	"1992b74eac63": {
		"en": () => <O>Coming soon...</O>,
		"da": () => <O>Kommer snart...</O>,
		"sv": () => <O>Kommer snart...</O>,
		"de": () => <O>Kommt bald...</O>,
	},

	"e16f9539cb2e": {
		"en": () => {
			return (
				<O>Keep an eye on this space. We'll soon launch a bunch of public games
				with cool prizes here.</O>
			);
		},
		"da": () => {
			return (
				<O>Hold øje med siden her. Vi lancerer snart en række offentlige spil
				med fede præmier, som du kan finde her.</O>
			);
		},
		"sv": () => {
                        return (
                                <O>Håll ett öga på denna sida. Här lanserar vi snart en mängd olika
				publika spel med häftiga priser.</O>
                        );
		},
		"de": () => {
                        return (
                                <O>Behalte diese Seite im Auge. Wir werden hier bald eine Reihe von öffentlichen Spielen mit coolen Preisen starten.</O>
                        );
		},
	},

	"ab4e02b924db": {
		"en": (n_started) => <O>Current ({n_started})</O>,
		"da": (n_started) => <O>Igangværende ({n_started})</O>,
		"sv": (n_started) => <O>Aktuella ({n_started})</O>,
		"de": (n_started) => <O>Aktuelle ({n_started})</O>,
	},

	"821784b92397": {
		"en": (n_prestart) => <O>Upcoming ({n_prestart})</O>,
		"da": (n_prestart) => <O>Kommende ({n_prestart})</O>,
		"sv": (n_prestart) => <O>Kommande ({n_prestart})</O>,
		"de": (n_prestart) => <O>Bevorstehende ({n_prestart})</O>,
	},

	"98850d040398": {
		"en": () => <O>Prize games</O>,
		"da": () => <O>Præmiespil</O>,
		"sv": () => <O>Prisspel</O>,
		"de": () => <O>Kostenlose Börsen Battles</O>,
	},

	"ad8eee9b3600": {
		"en": (n_all) => <O>All ({n_all})</O>,
		"da": (n_all) => <O>Alle ({n_all})</O>,
		"sv": (n_all) => <O>Alla ({n_all})</O>,
		"de": (n_all) => <O>Alle ({n_all})</O>,
	},

	"ceb6a3b354d1": {
		"en": () => <O>Game assets</O>,
		"da": () => <O>Aktiver i spillet</O>,
		"sv": () => <O>Tillgångar i spelet</O>,
		"de": () => <O>Aktien im Spiel</O>,
	},

	"fc5f5ededa61": {
		"en": () => <O>Overview</O>,
		"da": () => <O>Overblik</O>,
		"sv": () => <O>Överblick</O>,
		"de": () => <O>Überblick</O>,
	},

	"57d6354c4f92": {
		"en": () => <O>Following</O>,
		"da": () => <O>Følger</O>,
		"sv": () => <O>Följer</O>,
		"de": () => <O>Folgen</O>,
	},

	"33d162c5ca6a": {
		"en": (my_placement, number_of_players) => <O>You finished no. <b>{my_placement}</b> out of <b>{number_of_players}</b> players.</O>,
		"da": (my_placement, number_of_players) => <O>Du sluttede som nr. <b>{my_placement}</b> ud af <b>{number_of_players}</b> spillere.</O>,
		"sv": (my_placement, number_of_players) => <O>Du slutade som nr. <b>{my_placement}</b> utav <b>{number_of_players}</b> spelare.</O>,
		"de": (my_placement, number_of_players) => <O>Du hast den <b>{my_placement}</b>. Platz von insgesamt <b>{number_of_players}</b> Spielern belegt.</O>,
	},

	"9626a921a2be": {
		"en": (number_of_players) => {
			if(number_of_players === 2) {
				return <O>Congratulations! You won by defeating <b>{number_of_players - 1}</b> other player!</O>;
			} else {
				return <O>Congratulations! You won by defeating <b>{number_of_players - 1}</b> other players!</O>;
			}
		},
		"da": (number_of_players) => {
			if(number_of_players === 2) {
				return <O>Tillykke! Du vandt ved at besejre <b>{number_of_players - 1}</b> anden spiller!</O>;
			} else {
				return <O>Tillykke! Du vandt ved at besejre <b>{number_of_players - 1}</b> andre spillere!</O>;
			}
		},
		"sv": (number_of_players) => {
			return <O>Grattis! Du vann genom att besegra <b>{number_of_players - 1}</b> andra spelare!</O>;
		},
		"de": (number_of_players) => {
                        if(number_of_players === 2) {
                                return <O>Herzlichen Glückwünsch! Du hast gewonnen, indem du <b>{number_of_players - 1}</b> Spieler geschlagen hast!</O>;
                        } else {
                                return <O>Herzlichen Glückwünsch! Du hast gewonnen, indem du  <b>{number_of_players - 1}</b> Spieler besiegt haben!</O>;
                        }
                },
	},

	"83ae2d808036": {
		"en": (my_placement, number_of_players) => <O>Good game - you made the podium! You finished no. <b>{my_placement}</b> out of <b>{number_of_players}</b> players.</O>,
		"da": (my_placement, number_of_players) => <O>Godt spillet - du kom på podiet! Du sluttede nr. <b>{my_placement}</b> ud af <b>{number_of_players}</b> spillere.</O>,
		"sv": (my_placement, number_of_players) => <O>Bra spelat - du fick en plats på pallen! Du slutade som nr <b>{my_placement}</b> utav<b>{number_of_players}</b> spelare.</O>,
		"de": (my_placement, number_of_players) => <O>Herzlichen Glückwunsch - du bist auf dem Podium gelandet! Du hast den <b>{my_placement}</b>. Platz von insgesamt <b>{number_of_players}</b> Spielern belegt.</O>,
	},

	"b59a9a1ce5f1": {
		"en": (my_placement, number_of_players) => <O>You made the top 10! You finished no. <b>{my_placement}</b> out of <b>{number_of_players}</b> players.</O>,
		"da": (my_placement, number_of_players) => <O>Du kom i top 10! Du sluttede nr. <b>{my_placement}</b> ud af <b>{number_of_players}</b> spillere.</O>,
		"sv": (my_placement, number_of_players) => <O>Du hamnade topp 10! Du slutade nr <b>{my_placement}</b> utav <b>{number_of_players}</b> spelare.</O>,
		"de": (my_placement, number_of_players) => <O>Herzlichen Glückwunsch - du bist in den Top 10 gelandet! Du hast den <b>{my_placement}</b>. Platz von insgesamt <b>{number_of_players}</b> Spielern belegt.</O>,
	},

	"c66b867936dc": {
		"en": (number_of_players) => <O>It's an achievement of its own to finish last out of <b>{number_of_players}</b> players. Better luck next time!</O>,
		"da": (number_of_players) => <O>Det er en bedrift i sig selv at slutte sidst ud af <b>{number_of_players}</b> spillere. Bedre held næste gang!</O>,
		"sv": (number_of_players) => <O>Det är en bedrift i sig att hamna sist utav <b>{number_of_players}</b> spelare. Bättre lycka nästa gång!</O>,
		"de": (number_of_players) => <O>Es ist eine besondere Leistung als letzter von <b>{number_of_players}</b> Spielern abzuschließen. Das nächste Mal hast du bestimmt mehr Glück!</O>,
	},

	"5a4e348061e1": {
		"en": () => <O>The game has <i>ended</i>.</O>,
		"da": () => <O>Spillet er <i>slut</i>.</O>,
		"sv": () => <O>Spelet är<i>slut</i>.</O>,
		"de": () => <O>Das Spiel ist <i>beendet</i>.</O>,
	},

	"9c6b5e7ead30": {
		"en": () => <O>Invites will close at 8:00 AM on the chosen date</O>,
		"da": () => <O>Invitationer lukker kl. 08:00 på den valgte dato</O>,
		"sv": () => <O>Inbjudningar stängs klockan 08:00 på det valda datumet</O>,
		"de": () => <O>Einladungen werden am gewählten Datum um 08:00 Uhr geschlossen</O>,
	},

	"341da63f4c5f": {
		"en": () => <O>The game will start at 8:00 AM on the chosen date</O>,
		"da": () => <O>Spillet starter kl. 8:00 på den valgte dato</O>,
		"sv": () => <O>Spelet startar kl 8:00 på det valda datumet</O>,
		"de": () => <O>Das Spiel beginnt um 8:00 Uhr am gewählten Datum</O>,
	},

	"41bebddad34c": {
		"en": () => <O>*Crypto currencies are very volatile and alters the game dynamics</O>,
		"da": () => <O>*Kryptovalutaer er meget volatile og ændrer spildynamikke. Kryptovaluta sidestilles desuden i mange sammenhæng med gambling.</O>,
		"sv": () => <O>*Kryptovalutor är mycket instabila och ändrar speldynamiken</O>,
		"de": () => <O>*Kryptowährungen sind sehr volatil und verändern die Spieldynamik</O>,
	},

	"14756d135879": {
		"en": () => <O>*With the addition of risk values, only assets with a risk score can be included in the game.</O>,
		"da": () => <O>*Med tilføjelse af risikoværdier, er det kun aktiver med en risikoscore, der kan indgå i spillet.</O>,
		"sv": () => <O>*Med tillägg av riskvärden är det endast tillgångar med en riskscore som kan inkluderas i spelet.</O>,
		"de": () => <O>*Mit der Hinzufügung von Risikowerten können nur Vermögenswerte mit einer Risikobewertung am Spiel teilnehmen.</O>,
	},

	"f72560862d84": {
		"en": () => <O>*Volume gameplay is enabled, but the following markets have no volume data, and will be treated as having infinite volume:</O>,
		"da": () => <O>*Volumen-spildynamik er slået til, men følgende markeder har ikke volumendata og vil derfor køre med uendelig volumen:</O>,
		"sv": () => <O>*Volymspelet är aktiverat, men följande marknader har ingen volymdata, och kommer att behandlas som att ha oändlig volym:</O>,
		"de": () => <O>*Das Handelsvolumen ist aktiviert. Allerdings haben die folgenden Märkte keine Volumendaten und werden daher mit unendlichem Handelsvolumen dargestellt:</O>,
	},

	"f78181dc4cc4": {
		"en": () => <O>Other</O>,
		"da": () => <O>Andet</O>,
		"sv": () => <O>Annan</O>,
		"de": () => <O>Andere</O>,
	},

	"305303a1fe79": {
		"en": () => <O>No limit</O>,
		"da": () => <O>Ingen grænse</O>,
		"sv": () => <O>Ingen gräns</O>,
		"de": () => <O>Kein Limit</O>,
	},

	"a68db4477adf": {
		"en": () => <O>Create game</O>,
		"da": () => <O>Start nyt spil</O>,
		"sv": () => <O>Starta nytt spel</O>,
		"de": () => <O>Spiel erstellen</O>,
	},

	"4d91d593e84c": {
		"en": () => <O>Invite your friends and find out who is the best investor. Invest in stocks, crypto-currencies and more</O>,
		"da": () => <O>Invitér dine venner og find ud af hvem der den bedste investor. Investér i aktier, kryptovaluta og mere</O>,
		"sv": () => <O>Bjud in dina vänner och få reda på vem som är den bästa investeraren. Investera i aktier, kryptovalutor och mer</O>,
		"de": () => <O>Lade deine Freunde ein und findet heraus, wer der beste Investor ist. Investiert in Aktien, Kryptowährungen und weitere Anlagen</O>,
	},

	"4b5a0417d813": {
		"en": () => <O>Name of the game</O>,
		"da": () => <O>Spillets navn</O>,
		"sv": () => <O>Spelets namn</O>,
		"de": () => <O>Name des Spiels</O>,
	},

	"dce30d14c58d": {
		"en": () => <O>The name is used for invites and game overview</O>,
		"da": () => <O>Navnet bliver brugt til invitationer og spiloversigten</O>,
		"sv": () => <O>Namnet används för inbjudningar och spelöversikt</O>,
		"de": () => <O>Der Name wird für Einladungen und die Spielübersicht verwendet</O>,
	},

	"e3eba2fd2ce6": {
		"en": () => <O>Game start</O>,
		"da": () => <O>Spilstart</O>,
		"sv": () => <O>Spel startar</O>,
		"de": () => <O>Spielbeginn</O>,
	},

	"fb42052ba07c": {
		"en": () => <O>When the game starts</O>,
		"da": () => <O>Hvornår spillet starter</O>,
		"sv": () => <O>När spelet startar</O>,
		"de": () => <O>Wann soll das Spiel beginnen?</O>,
	},

	"11505781558f": {
		"en": (RadiosAndCustom, this_parent) => {
			return (
				<O><RadiosAndCustom
					name="gamestartcustom"
					parent={this_parent}
					no_custom={true}
					defaults={[
						{"value": "0", "text": "Start game now"},
						{"value": "1", "text": "Choose a start date"},
					]}
				/></O>
			);
		},
		"da": (RadiosAndCustom, this_parent) => {
			return (
				<O><RadiosAndCustom
					name="gamestartcustom"
					parent={this_parent}
					no_custom={true}
					defaults={[
						{"value": "0", "text": "Start spillet nu"},
						{"value": "1", "text": "Vælg en startdato"},
					]}
				/></O>
			);
		},
		"sv": (RadiosAndCustom, this_parent) => {
                        return (
                                <O><RadiosAndCustom
                                        name="gamestartcustom"
                                        parent={this_parent}
                                        no_custom={true}
                                        defaults={[
                                                {"value": "0", "text": "Starta spelet nu"},
                                                {"value": "1", "text": "Välj ett startdatum"},
                                        ]}
                                /></O>
                        );
		},
		"de": (RadiosAndCustom, this_parent) => {
                        return (
                                <O><RadiosAndCustom
                                        name="gamestartcustom"
                                        parent={this_parent}
                                        no_custom={true}
                                        defaults={[
                                                {"value": "0", "text": "Beginnt jetzt"},
                                                {"value": "1", "text": "Wähle ein Startdatum"},
                                        ]}
                                /></O>
                        );
                },
	},

	"9be4e5e6848c": {
		"en": () => <O>Game time</O>,
		"da": () => <O>Varighed</O>,
		"sv": () => <O>Varaktighet</O>,
		"de": () => <O>Spieldauer</O>,
	},

	"60d43a77cd55": {
		"en": () => <O>For how long will the game run</O>,
		"da": () => <O>Hvor lang tid spillet løber</O>,
		"sv": () => <O>Hur länge kommer spelet hålla på</O>,
		"de": () => <O>Wie lange soll das Spiel laufen?</O>,
	},

	"99462836c445": {
		"en": (RadiosAndCustom, state_invalid_input, this_parent) => {
			return (
				<O><RadiosAndCustom
					name="gametime"
					parent={this_parent}
					defaults={[
						{"value": 30, "text": "30 days"},
						{"value": 90, "text": "90 days"},
					]}
					custom_text="Other (max 365)"
					custom_placeholder="days"
					custom_error={state_invalid_input === "gametime"}
					min={1}
					max={365}
				/></O>
			);
		},
		"da": (RadiosAndCustom, state_invalid_input, this_parent) => {
			return (
				<O><RadiosAndCustom
					name="gametime"
					parent={this_parent}
					defaults={[
						{"value": 30, "text": "30 dage"},
						{"value": 90, "text": "90 dage"},
					]}
					custom_text="Andet (max. 365)"
					custom_placeholder="dage"
					custom_error={state_invalid_input === "gametime"}
					min={1}
					max={365}
				/></O>
			);
		},
		"sv": (RadiosAndCustom, state_invalid_input, this_parent) => {
			return (
				<O><RadiosAndCustom
					name="gametime"
					parent={this_parent}
					defaults={[
						{"value": 30, "text": "30 dagar"},
						{"value": 90, "text": "90 dagar"},
					]}
					custom_text="Andet (max. 365)"
					custom_placeholder="dagar"
					custom_error={state_invalid_input === "gametime"}
					min={1}
					max={365}
				/></O>
			);
		},
		"de": (RadiosAndCustom, state_invalid_input, this_parent) => {
                        return (
                                <O><RadiosAndCustom
                                        name="gametime"
                                        parent={this_parent}
                                        defaults={[
                                                {"value": 30, "text": "30 Tage"},
                                                {"value": 90, "text": "90 Tage"},
                                        ]}
                                        custom_text="Andere (max. 365)"
                                        custom_placeholder="Tage"
                                        custom_error={state_invalid_input === "gametime"}
                                        min={1}
                                        max={365}
                                /></O>
                        );
                },
	},

	"9154466d694b": {
		"en": () => <O>Game Registration Closes</O>,
		"da": () => <O>Luk for tilmelding</O>,
		"sv": () => <O>Anmälan till spelet stänger</O>,
		"de": () => <O>Die Spielanmeldung schließt</O>,
	},

	"944bed954862": {
		"en": () => <O>The date when registration for the game closes</O>,
		"da": () => <O>Den dato, hvor tilmeldingen til spillet lukker</O>,
		"sv": () => <O>Det datum då anmälan till spelet stänger</O>,
		"de": () => <O>Das Datum, an dem die Spielanmeldung schließt</O>,
	},

	"a31b815c9cb9": {
		"_new": true, // FIXME remove this line when translation is done
		"en": (RadiosAndCustom, this_parent) => {
			return (
				<O><RadiosAndCustom
					name="inviteendcustom"
					parent={this_parent}
					no_custom={true}
					is_premium_feature={true}
					defaults={[
						{ "value": "0", "text": "None" },
						{"value": "1", "text": "Select registration closing date"},
					]}
				/></O>
			);
		},
		"da": (RadiosAndCustom, this_parent) => {
			return (
				<O><RadiosAndCustom
					name="inviteendcustom"
					parent={this_parent}
					no_custom={true}
					is_premium_feature={true}
					defaults={[
						{ "value": "0", "text": "Ingen" },
						{"value": "1", "text": "Vælg en dato for lukning af tilmelding"},
					]}
				/></O>
			);
		},
		"sv": (RadiosAndCustom, this_parent) => {
			return (
				<O><RadiosAndCustom
					name="inviteendcustom"
					parent={this_parent}
					no_custom={true}
					is_premium_feature={true}
					defaults={[
						{ "value": "0", "text": "Ingen" },
						{"value": "1", "text": "Välj ett datum för att stänga anmälan"},
					]}
				/></O>
			);
		},
		"de": (RadiosAndCustom, this_parent) => {
			return (
				<O><RadiosAndCustom
					name="inviteendcustom"
					parent={this_parent}
					no_custom={true}
					is_premium_feature={true}
					defaults={[
						{ "value": "0", "text": "Keine" },
						{"value": "1", "text": "Wählen Sie ein Datum für den Anmeldeschluss"},
					]}
				/></O>
			);
		},
	},

	"68b9515552bf": {
		"en": () => <O>Find &amp; Buy equities</O>,
		"da": () => <O>Find &amp; Køb værdipapirer</O>,
		"sv": () => <O>Hitta &amp; Köp värdepapper</O>,
		"de": () => <O>Finde &amp; Kaufe Wertpapiere</O>,
	},

	"7e3d14923479": {
		"en": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="DK" className="ad_25x25px" src="/gfx/denmark-icon-0.svg"/><span className="pl-2">Danish</span></Button></O>,
		"da": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="DK" className="ad_25x25px" src="/gfx/denmark-icon-0.svg"/><span className="pl-2">Danske</span></Button></O>,
		"sv": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="DK" className="ad_25x25px" src="/gfx/denmark-icon-0.svg"/><span className="pl-2">Danska</span></Button></O>,
		"de": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="DK" className="ad_25x25px" src="/gfx/denmark-icon-0.svg"/><span className="pl-2">Dänisch</span></Button></O>,
	},

	"0d5a70441198": {
		"en": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="SE" className="ad_25x25px" src="/gfx/sweden-icon-0.svg"/><span className="pl-2">Swedish</span></Button></O>,
		"da": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="SE" className="ad_25x25px" src="/gfx/sweden-icon-0.svg"/><span className="pl-2">Svenske</span></Button></O>,
		"sv": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="SE" className="ad_25x25px" src="/gfx/sweden-icon-0.svg"/><span className="pl-2">Svenska</span></Button></O>,
		"de": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="SE" className="ad_25x25px" src="/gfx/sweden-icon-0.svg"/><span className="pl-2">Schwedisch</span></Button></O>,
	},

	"0f2cfc22738d": {
		"en": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="FI" className="ad_25x25px" src="/gfx/finland-icon-0.svg"/><span className="pl-2">Finnish</span></Button></O>,
		"da": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="FI" className="ad_25x25px" src="/gfx/finland-icon-0.svg"/><span className="pl-2">Finske</span></Button></O>,
		"sv": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="FI" className="ad_25x25px" src="/gfx/finland-icon-0.svg"/><span className="pl-2">Finska</span></Button></O>,
		"de": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="FI" className="ad_25x25px" src="/gfx/finland-icon-0.svg"/><span className="pl-2">Finnisch</span></Button></O>,
	},

	"17b84c1b3209": {
		"en": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="IS" className="ad_25x25px" src="/gfx/iceland-icon-0.svg"/><span className="pl-2">Icelandic</span></Button></O>,
		"da": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="IS" className="ad_25x25px" src="/gfx/iceland-icon-0.svg"/><span className="pl-2">Islandske</span></Button></O>,
		"sv": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="IS" className="ad_25x25px" src="/gfx/iceland-icon-0.svg"/><span className="pl-2">Isländska</span></Button></O>,
		"de": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="IS" className="ad_25x25px" src="/gfx/iceland-icon-0.svg"/><span className="pl-2">Isländisch</span></Button></O>,
	},

	"507f6cf0fd32": {
		"en": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="DE" className="ad_25x25px" src="/gfx/germany-icon-0.svg"/><span className="pl-2">German</span></Button></O>,
		"da": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="DE" className="ad_25x25px" src="/gfx/germany-icon-0.svg"/><span className="pl-2">Tyske</span></Button></O>,
		"sv": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="DE" className="ad_25x25px" src="/gfx/germany-icon-0.svg"/><span className="pl-2">Tyska</span></Button></O>,
		"de": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="DE" className="ad_25x25px" src="/gfx/germany-icon-0.svg"/><span className="pl-2">Deutsch</span></Button></O>,
	},

	"7d0aa53e6915": {
		"en": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="AT" className="ad_25x25px" src="/gfx/austria-icon-0.svg"/><span className="pl-2">Austrian</span></Button></O>,
		"da": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="AT" className="ad_25x25px" src="/gfx/austria-icon-0.svg"/><span className="pl-2">Østrigske</span></Button></O>,
		"sv": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="AT" className="ad_25x25px" src="/gfx/austria-icon-0.svg"/><span className="pl-2">Österrikiska</span></Button></O>,
		"de": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="AT" className="ad_25x25px" src="/gfx/austria-icon-0.svg"/><span className="pl-2">Österreichisch</span></Button></O>,
	},

	"b1d2d2b7589c": {
		"en": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="Bank" className="ad_25x25px" src="/gfx/currencies-0.svg"/><span className="pl-2">Currencies</span></Button></O>,
		"da": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="Bank" className="ad_25x25px" src="/gfx/currencies-0.svg"/><span className="pl-2">Valuta</span></Button></O>,
		"sv": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="Bank" className="ad_25x25px" src="/gfx/currencies-0.svg"/><span className="pl-2">Valutor</span></Button></O>,
		"de": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="Bank" className="ad_25x25px" src="/gfx/currencies-0.svg"/><span className="pl-2">Währungen</span></Button></O>,
	},

	"206d8328405f": {
		"en": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="USA" className="ad_25x25px" src="/gfx/usa-icon-0.svg"/><span className="pl-2">American</span></Button></O>,
		"da": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="USA" className="ad_25x25px" src="/gfx/usa-icon-0.svg"/><span className="pl-2">Amerikanske</span></Button></O>,
		"sv": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="USA" className="ad_25x25px" src="/gfx/usa-icon-0.svg"/><span className="pl-2">Amerikanska</span></Button></O>,
		"de": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="USA" className="ad_25x25px" src="/gfx/usa-icon-0.svg"/><span className="pl-2">Amerikanisch</span></Button></O>,
	},

	"ea7de55453cc": {
		"en": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="Commodities" className="ad_25x25px" src="/gfx/commodities-icon-0.svg"/><span className="pl-2">Commodities</span></Button></O>,
		"da": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="Commodities" className="ad_25x25px" src="/gfx/commodities-icon-0.svg"/><span className="pl-2">Råvarer</span></Button></O>,
		"sv": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="Commodities" className="ad_25x25px" src="/gfx/commodities-icon-0.svg"/><span className="pl-2">Råvaror</span></Button></O>,
		"de": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="Commodities" className="ad_25x25px" src="/gfx/commodities-icon-0.svg"/><span className="pl-2">Rohstoffe</span></Button></O>,
	},

	"a65a300f5359": {
		"en": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="Crypto" className="ad_25x25px" src="/gfx/crypto-icon-0.svg"/><span className="pl-2">Crypto</span></Button></O>,
		"da": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="Crypto" className="ad_25x25px" src="/gfx/crypto-icon-0.svg"/><span className="pl-2">Krypto</span></Button></O>,
		"sv": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="Crypto" className="ad_25x25px" src="/gfx/crypto-icon-0.svg"/><span className="pl-2">Krypto</span></Button></O>,
		"de": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="Crypto" className="ad_25x25px" src="/gfx/crypto-icon-0.svg"/><span className="pl-2">Krypto</span></Button></O>,
	},

	"1f64321d06bb": {
		"en": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="EFTs" className="ad_25x25px" src="/gfx/eft-icon-0.svg"/><span className="pl-2">ETFs</span></Button></O>,
		"da": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="EFTs" className="ad_25x25px" src="/gfx/eft-icon-0.svg"/><span className="pl-2">ETF'er</span></Button></O>,
		"sv": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="EFTs" className="ad_25x25px" src="/gfx/eft-icon-0.svg"/><span className="pl-2">ETFs</span></Button></O>,
		"de": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="EFTs" className="ad_25x25px" src="/gfx/eft-icon-0.svg"/><span className="pl-2">ETFs</span></Button></O>,
	},

	"d7345aeba2da": {
		"en": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="Indexes" className="ad_25x25px" src="/gfx/indexes-icon-0.svg"/><span className="pl-2">Indexes</span></Button></O>,
		"da": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="Indexes" className="ad_25x25px" src="/gfx/indexes-icon-0.svg"/><span className="pl-2">Indekse</span></Button></O>,
		"sv": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="Indexes" className="ad_25x25px" src="/gfx/indexes-icon-0.svg"/><span className="pl-2">Indekse</span></Button></O>,
		"de": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="Indexes" className="ad_25x25px" src="/gfx/indexes-icon-0.svg"/><span className="pl-2">Indeks</span></Button></O>,
	},

	"a3ec73de8fb9": {
		"en": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="Funds-and-bonds" className="ad_25x25px" src="/gfx/funds-and-bonds-icon-0.svg"/><span className="pl-2">Funds and Bonds</span></Button></O>,
		"da": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="Funds-and-bonds" className="ad_25x25px" src="/gfx/funds-and-bonds-icon-0.svg"/><span className="pl-2">Fonde og obligationer</span></Button></O>,
		"sv": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="Funds-and-bonds" className="ad_25x25px" src="/gfx/funds-and-bonds-icon-0.svg"/><span className="pl-2">Fonder och obligationer</span></Button></O>,
		"de": (Button) => <O><Button className="ad_explore_market_button mt-2"><img alt="Funds-and-bonds" className="ad_25x25px" src="/gfx/funds-and-bonds-icon-0.svg"/><span className="pl-2">Fonds und Anleihen</span></Button></O>,
	},

	"67f26e0db6a0": {
		"en": () => <O>Bankroll</O>,
		"da": () => <O>Bankkonto</O>,
		"sv": () => <O>Bankkonto</O>,
		"de": () => <O>Bankkonto</O>,
	},

	"6b966c114215": {
		"en": () => <O>Initial bankroll is</O>,
		"da": () => <O>Startbeløb er</O>,
		"sv": () => <O>Startbelopp är</O>,
		"de": () => <O>Der Startbetrag ist</O>,
	},

	"fcb6bd0af41b": {
		"en": () => <O>How much money each player will get to begin with</O>,
		"da": () => <O>Så mange penge får hver spiller til at begynde med</O>,
		"sv": () => <O>Så mycket pengar får varje spelare börja med</O>,
		"de": () => <O>Wie viel Geld bekommt jeder Spieler am Anfang?</O>,
	},

	"6a2b0fb2b207": {
		"en": (RadiosAndCustom, initial_bankroll_defaults, state_invalid_input, this_parent) => {
			return (
				<O><RadiosAndCustom
					name="bankroll"
					parent={this_parent}
					defaults={initial_bankroll_defaults}
					custom_text="Other"
					custom_placeholder="Amount"
					custom_error={state_invalid_input === "bankroll"}
					min={1}
					max={10000000}
				/></O>
			);
		},
		"da": (RadiosAndCustom, initial_bankroll_defaults, state_invalid_input, this_parent) => {
			return (
				<O><RadiosAndCustom
					name="bankroll"
					parent={this_parent}
					defaults={initial_bankroll_defaults}
					custom_text="Andet"
					custom_placeholder="Beløb"
					custom_error={state_invalid_input === "bankroll"}
					min={1}
					max={10000000}
				/></O>
			);
		},
		"sv": (RadiosAndCustom, initial_bankroll_defaults, state_invalid_input, this_parent) => {
			return (
				<O><RadiosAndCustom
					name="bankroll"
					parent={this_parent}
					defaults={initial_bankroll_defaults}
					custom_text="Annan"
					custom_placeholder="Belopp"
					custom_error={state_invalid_input === "bankroll"}
					min={1}
					max={10000000}
				/></O>
			);
		},
		"de": (RadiosAndCustom, initial_bankroll_defaults, state_invalid_input, this_parent) => {
			return (
				<O><RadiosAndCustom
					name="bankroll"
					parent={this_parent}
					defaults={initial_bankroll_defaults}
					custom_text="Andere"
					custom_placeholder="Betrag"
					custom_error={state_invalid_input === "bankroll"}
					min={1}
					max={10000000}
				/></O>
			);
		},
	},

	"b48ac1a3be2b": {
		"en": () => <O>Trade volume reflection</O>,
		"da": () => <O>Reflektér handelsvolumen</O>,
		"sv": () => <O>Reflektera handelsvolymen</O>,
		"de": () => <O>Reales Handelsvolumen</O>,
	},

	"ae4e10a5f4a5": {
		"en": () => <O>Use volume in real markets to reflect how much can be bought in your game.</O>,
		"da": () => <O>Brug handelsvolumen fra de rigtige markeder til at reflektere hvor meget der kan købes i spillet.</O>,
		"sv": () => <O>Använd volym på verkliga marknader för att spegla hur mycket som kan köpas i ditt spel.</O>,
		"de": () => <O>Nutze das reale Handelsvolumen des Aktienmarktes, um zu bestimmen, wie viele Aktien in dem Spiel gekauft werden können.</O>,
	},

	"bead35af7e98": {
		"en": () => <O>Use volume for markets that support it</O>,
		"da": () => <O>Brug volumen for markeder der supporterer det</O>,
		"sv": () => <O>Använd volym för marknader som stöder det"</O>,
		"de": () => <O>Volumen für Märkte nutzen, die die Funktion unterstützen</O>,
	},

	"f2b4c7178295": {
		"en": () => <O>Leaderboard risk values</O>,
		"da": () => <O>Risikoværdier på leaderboard</O>,
		"sv": () => <O>Riskvärden på leaderboard</O>,
		"de": () => <O>Risikowerte in der leaderboard</O>,
	},

	"af801b5b421a": {
		"en": () => <O>Enable risk values so you can compete on both regular returns and risk-adjusted returns.</O>,
		"da": () => <O>Aktiver risikoværdier så I både kan dyste på almindeligt afkast og på det risikojusterede afkast.</O>,
		"sv": () => <O>Aktivera riskvärden så att ni kan tävla både på vanlig avkastning och på riskjusterad avkastning.</O>,
		"de": () => <O>Aktivieren Sie Risikowerte, damit Sie sowohl mit normalen Renditen als auch mit risikobereinigten Renditen konkurrieren können.</O>,
	},

	"8c325351ca79": {
		"en": () => <O>Use risk values for markets that support it</O>,
		"da": () => <O>Brug risikoværdier for markeder, der understøtter det</O>,
		"sv": () => <O>Använd riskvärden för marknader som stöder det</O>,
		"de": () => <O>Verwenden Sie Risikowerte für Märkte, die dies unterstützen</O>,
	},

	"3edde7aed314": {
		"en": () => <O>Player trade limits</O>,
		"da": () => <O>Handelsgrænser for spillere</O>,
		"sv": () => <O>Handelsgränser för spelare</O>,
		"de": () => <O>Handelslimits für Spieler</O>,
	},

	"8f9c3e59d72e": {
		"en": () => <O>Max amount of trades for each player</O>,
		"da": () => <O>Maksimalt antal handler for hver spiller</O>,
		"sv": () => <O>Maximalt antal affärer för varje spelare</O>,
		"de": () => <O>Maximale Anzahl von Trades pro Spieler</O>,
	},

	"a865d672f349": {
		"en": () => <O>Limit the maximum number of trades per player</O>,
		"da": () => <O>Begræns det maksimale antal handler per spiller</O>,
		"sv": () => <O>Begränsa det maximala antalet affärer per spelare</O>,
		"de": () => <O>Begrenzen Sie die maximale Anzahl von Trades pro Spieler</O>,
	},

	"8a9d14e34255": {
		"en": () => <O>Information about assets</O>,
		"da": () => <O>Oplysninger om aktiver</O>,
		"sv": () => <O>Information om tillgångar</O>,
		"de": () => <O>Informationen zu Vermögenswerten</O>,
	},

	"0593c5593d16": {
		"en": () => <O>Asset information includes the options below and is a good way</O>,
		"da": () => <O>Aktivoplysninger inkluderer mulighederne nedenfor og er en god måde</O>,
		"sv": () => <O>Tillgångsinformation inkluderar alternativen nedan och är ett bra sätt</O>,
		"de": () => <O>Die Asset-Informationen umfassen die folgenden Optionen und sind eine gute Möglichkeit</O>,
	},

	"396d7fd14e31": {
		"en": () => <O>Use information about assets</O>,
		"da": () => <O>Brug information om aktiver</O>,
		"sv": () => <O>Använd information om tillgångar</O>,
		"de": () => <O>Verwenden Sie Informationen über Vermögenswerte</O>,
	},

	"48588435d11d": {
		"en": () => <O>Company information</O>,
		"da": () => <O>Virksomhedsoplysninger</O>,
		"sv": () => <O>Företagsinformation</O>,
		"de": () => <O>Informationen zum Konzern</O>,
	},

	"b7a2c995b53a": {
		"en": () => <O>Industry indication</O>,
		"da": () => <O>Brancheindikation</O>,
		"sv": () => <O>Branstindikation</O>,
		"de": () => <O>Branchenhinweis</O>,
	},

	"734be3b51ee2": {
		"en": () => <O>Financial information</O>,
		"da": () => <O>Finansielle oplysninger</O>,
		"sv": () => <O>Finansiell information</O>,
		"de": () => <O>Finanzielle Informationen</O>,
	},

	"f25e9b1cf49f": {
		"en": () => <O>Key figures</O>,
		"da": () => <O>Nøgletal</O>,
		"sv": () => <O>Nyckeltal</O>,
		"de": () => <O>Kennzahlen</O>,
	},

	"07d119f65f0e": {
		"en": () => <O>Maximum amount of trades</O>,
		"da": () => <O>Maksimalt antal handler</O>,
		"sv": () => <O>Maximalt antal affärer</O>,
		"de": () => <O>Maximale Anzahl von Trades</O>,
	},

	"e211e9bbec33": {
		"en": () => <O>How many trades for each player</O>,
		"da": () => <O>Hvor mange handler for hver spiller</O>,
		"sv": () => <O>Hur många affärer för varje spelare</O>,
		"de": () => <O>Wie viele Trades für jeden Spieler</O>,
	},

	"acb62f4e7308": {
		"en": (RadiosAndCustom, state_invalid_input, this_parent) => {
			return (
				<O><RadiosAndCustom
					name="trade_amount"
					parent={this_parent}
					defaults={[
						{"value": 50, "text": "50 trades"},
						{"value": 100, "text": "100 trades"},
					]}
					custom_text="Other (max x)"
					custom_placeholder="Enter max trades"
					custom_error={state_invalid_input === "trade_amount"}
					min={1}
					max={500}
				/></O>
			);
		},
		"da": (RadiosAndCustom, state_invalid_input, this_parent) => {
			return (
				<O><RadiosAndCustom
					name="trade_amount"
					parent={this_parent}
					defaults={[
						{"value": 50, "text": "50 handler"},
						{"value": 100, "text": "100 handler"},
					]}
					custom_text="Andet (maks x)"
					custom_placeholder="Indtast maksimale handler"
					custom_error={state_invalid_input === "trade_amount"}
					min={1}
					max={500}
				/></O>
			);
		},
		"sv": (RadiosAndCustom, state_invalid_input, this_parent) => {
			return (
				<O><RadiosAndCustom
					name="trade_amount"
					parent={this_parent}
					defaults={[
						{"value": 50, "text": "50 affärer"},
						{"value": 100, "text": "100 affärer"},
					]}
					custom_text="Annat (max x)"
					custom_placeholder="Ange max antal affärer"
					custom_error={state_invalid_input === "trade_amount"}
					min={1}
					max={500}
				/></O>
			);
		},
		"de": (RadiosAndCustom, state_invalid_input, this_parent) => {
			return (
				<O><RadiosAndCustom
					name="trade_amount"
					parent={this_parent}
					defaults={[
						{"value": 50, "text": "50 Trades"},
						{"value": 100, "text": "100 Trades"},
					]}
					custom_text="Andere (max x)"
					custom_placeholder="Maximale Trades eingeben"
					custom_error={state_invalid_input === "trade_amount"}
					min={1}
					max={500}
				/></O>
			);
		},
	},

	"b1be8f314a0b": {
		"en": () => <O>Max. amount in one equity</O>,
		"da": () => <O>Max beløb i ét værdipapir </O>,
		"sv": () => <O>Max belopp i ett värdepapper</O>,
		"de": () => <O>Maximaler Betrag pro Anlage</O>,
	},

	"c53ac79b9f71": {
		"en": () => <O>How much is the maximum amount that a player kan invest in one equity?</O>,
		"da": () => <O>Hvad er maximum beløbet man kan bruge på ét enkelt værdipapir?</O>,
		"sv": () => <O>Vad är maxbeloppet som en spelare kan investera i ett värdepapper?</O>,
		"de": () => <O>Wie viel kann ein Spieler maximal in eine Anlage investieren?</O>,
	},

	"1dcbcb5c5f3c": {
		"en": () => <O>Brokerage</O>,
		"da": () => <O>Kurtage</O>,
		"sv": () => <O>Kurtage</O>,
		"de": () => <O>Vermittlungsgebühr</O>,
	},

	"eab94744cd4d": {
		"en": () => <O>Max. amount per equity</O>,
		"da": () => <O>Max. beløb pr. værdipapir</O>,
		"sv": () => <O>Maxbelopp per värdepapper</O>,
		"de": () => <O>Maximaler Betrag pro Anlage</O>,
	},

	"ab99fc347c6f": {
		"en": () => <O>How big a percentage of a trade to pay in broker commission</O>,
		"da": () => <O>Procentdel som betales i kommission for handlen</O>,
		"sv": () => <O>Hur stor andel av en handel att betala i mäklarkommission</O>,
		"de": () => <O>Der Prozentsatz der Vermittlungsgebühr eines Handels</O>,
	},

	"f39ea13186ee": {
		"en": (RadiosAndCustom, state_invalid_input, this_parent) => {
			return (
				<O><RadiosAndCustom
					name="brokerage_pct"
					parent={this_parent}
					defaults={[
						{"value": 0, "text": "None"},
						{"value": 0.15, "text": "0.15%"},
					]}
					custom_text="Other"
					custom_postfix="%&nbsp;&nbsp;"
					custom_placeholder="pct."
					custom_error={state_invalid_input === "brokerage_pct"}
					min={0}
					max={50}
					step={0.01}
				/></O>
			);
		},
		"da": (RadiosAndCustom, state_invalid_input, this_parent) => {
			return (
				<O><RadiosAndCustom
					name="brokerage_pct"
					parent={this_parent}
					defaults={[
						{"value": 0, "text": "Ingen"},
						{"value": 0.15, "text": "0,15%"},
					]}
					custom_text="Andet"
					custom_postfix="%&nbsp;&nbsp;"
					custom_placeholder="pct."
					custom_error={state_invalid_input === "brokerage_pct"}
					min={0}
					max={50}
					step={0.01}
				/></O>
			);
		},
		"sv": (RadiosAndCustom, state_invalid_input, this_parent) => {
			return (
				<O><RadiosAndCustom
					name="brokerage_pct"
					parent={this_parent}
					defaults={[
						{"value": 0, "text": "Ingen"},
						{"value": 0.15, "text": "0,15%"},
					]}
					custom_text="Annan"
					custom_postfix="%&nbsp;&nbsp;"
					custom_placeholder="pct."
					custom_error={state_invalid_input === "brokerage_pct"}
					min={0}
					max={50}
					step={0.01}
				/></O>
			);
		},
		"de": (RadiosAndCustom, state_invalid_input, this_parent) => {
			return (
				<O><RadiosAndCustom
					name="brokerage_pct"
					parent={this_parent}
					defaults={[
						{"value": 0, "text": "Keine"},
						{"value": 0.15, "text": "0,15%"},
					]}
					custom_text="Andere"
					custom_postfix="%&nbsp;&nbsp;"
					custom_placeholder="Satz"
					custom_error={state_invalid_input === "brokerage_pct"}
					min={0}
					max={50}
					step={0.01}
				/></O>
			);
		},
	},

	"ef70d742f2bd": {
		"en": () => <O>Stock Exchange</O>,
		"da": () => <O>Børsen</O>,
		"sv": () => <O>Börs</O>,
		"de": () => <O>Börse</O>,
	},

	"a54952613d20": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/spoerg-panelet/" className="d-flex">
						<img alt="Ask the Panel" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/ask-panel-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Ask the Panel</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/spoerg-panelet/" className="d-flex">
						<img alt="Spørg Panelet" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/ask-panel-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Spørg Panelet</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiekampen.se/fraaga-panelen/" className="d-flex">
						<img alt="Fråga panelen" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/ask-panel-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Fråga panelen</span>
					</NavLink>
				</O>
			);
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/spoerg-panelet/" className="d-flex">
						<img alt="Frag das Panel" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/ask-panel-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Frag das Panel</span>
					</NavLink>
				</O>
			);
		},
	},

	"229357356603": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/talks-om-investering/" className="d-flex">
						<img alt="Talks about Investing" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/investment-talks-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Talks about Investing</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/talks-om-investering/" className="d-flex">
						<img alt="Talks om investering" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/investment-talks-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Talks om investering</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return null;
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/talks-om-investering/" className="d-flex">
						<img alt="Gespräche über Investitionen" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/investment-talks-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Gespräche über Investitionen</span>
					</NavLink>
				</O>
			);
		},
	},

	"ec8e8899e957": {
		"en": (text_css) => {
			return (
				<O>
					<img alt="Teaching" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/education-icon-red-2024.png"/>
					<span className={"pl-2 " + text_css}>Teaching</span>
				</O>
			);
		},
		"da": (text_css) => {
			return (
				<O>
					<img alt="Undervisning" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/education-icon-red-2024.png"/>
					<span className={"pl-2 " + text_css}>Undervisning</span>
				</O>
			);
		},
		"sv": (text_css) => {
			return (
				<O>
					<img alt="Undervisning" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/education-icon-red-2024.png"/>
					<span className={"pl-2 " + text_css}>Undervisning</span>
				</O>
			);
		},
		"de": (text_css) => {
			return (
				<O>
					<img alt="Unterricht" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/education-icon-red-2024.png"/>
					<span className={"pl-2 " + text_css}>Unterricht</span>
				</O>
			);
		},
	},

	"fac62da8eec7": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/saadan-goer-du/" className="d-flex">
						<img alt="How-To" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/how-to-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>How-To</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/saadan-goer-du/" className="d-flex">
						<img alt="Sådan gør du" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/how-to-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Sådan gør du</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiekampen.se/saa-gor-du/" className="d-flex">
						<img alt="Sä gör du" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/how-to-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Sä gör du</span>
					</NavLink>
				</O>
			);
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/saadan-goer-du/" className="d-flex">
						<img alt="So geht's" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/how-to-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>So geht's</span>
					</NavLink>
				</O>
			);
		},
	},

	"dd21c89a0176": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/grundskole/" className="d-flex">
						<img alt="Primary School" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/education-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Primary School</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/grundskole/" className="d-flex">
						<img alt="Grundskole" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/education-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Grundskole</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiekampen.se/grundskolan/" className="d-flex">
						<img alt="Grundskola" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/education-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Grundskola</span>
					</NavLink>
				</O>
			);
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/grundskole/" className="d-flex">
						<img alt="Grundschule" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/education-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Grundschule</span>
					</NavLink>
				</O>
			);
		},
	},

	"0c0268c2dc9f": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/ungdomsuddannelse/" className="d-flex">
						<img alt="Secondary Education" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/education-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Secondary Education</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/ungdomsuddannelse/" className="d-flex">
						<img alt="Ungdomsuddannelse" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/education-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Ungdomsuddannelse</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiekampen.se/ungdomsutbildning/" className="d-flex">
						<img alt="Ungdomsutbildning" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/education-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Ungdomsutbildning</span>
					</NavLink>
				</O>
			);
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/ungdomsuddannelse/" className="d-flex">
						<img alt="Sekundarbildung" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/education-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Sekundarbildung</span>
					</NavLink>
				</O>
			);
		},
	},

	"593422d53cec": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/historier-fra-skolebaenken/" className="d-flex">
						<img alt="Stories from the Classroom" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/stories-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Stories from the Classroom</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/historier-fra-skolebaenken/" className="d-flex">
						<img alt="Historier fra skolebænken" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/stories-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Historier fra skolebænken</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiekampen.se/berattelser-fraan-skolbanken/" className="d-flex">
						<img alt="Berattelser-fraan-skolbanken" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/stories-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Berattelser fraan skolbanken</span>
					</NavLink>
				</O>
			);
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/historier-fra-skolebaenken/" className="d-flex">
						<img alt="Geschichten aus dem Klassenzimmer" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/stories-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Geschichten aus dem Klassenzimmer</span>
					</NavLink>
				</O>
			);
		},
	},

	"13314c9a03e6": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/sikker-havn/" className='d-flex'>
						<img alt="Safe haven" className='ad_margin_bottom_2px ad_sub_navbar_image' src="https://static.aktiedysten.dk/gfx/harbor-icon-red-2025.png"/>
						<span className={"pl-2 " + text_css}>The safe harbor</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/sikker-havn/" className='d-flex'>
						<img alt="Safe haven" className='ad_margin_bottom_2px ad_sub_navbar_image' src="https://static.aktiedysten.dk/gfx/harbor-icon-red-2025.png"/>
						<span className={"pl-2 " + text_css}>Sikker havn</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiekampen.se/den-trygga-hamnen/" className='d-flex'>
						<img alt="Safe haven" className='ad_margin_bottom_2px ad_sub_navbar_image' src="https://static.aktiedysten.dk/gfx/harbor-icon-red-2025.png"/>
						<span className={"pl-2 " + text_css}>Den Trygga Hamnen</span>
					</NavLink>
				</O>
			);
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/sikker-havn/" className='d-flex'>
						<img alt="Safe haven" className='ad_margin_bottom_2px ad_sub_navbar_image' src="https://static.aktiedysten.dk/gfx/harbor-icon-red-2025.png"/>
						<span className={"pl-2 " + text_css}>Der sichere Hafen</span>
					</NavLink>
				</O>
			);
		},
	},

	"026a2b0eb535": {
		"en": (text_css) => {
			return (
				<O>
					<img alt="Toolbox" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/toolbox-icon-red-2024.png"/>
					<span className={"pl-2 " + text_css}>Toolbox</span>
				</O>
			);
		},
		"da": (text_css) => {
			return (
				<O>
					<img alt="Værktøjskassen" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/toolbox-icon-red-2024.png"/>
					<span className={"pl-2 " + text_css}>Værktøjskassen</span>
				</O>
			);
		},
		"sv": (text_css) => {
			return (
				<O>
					<img alt="Værktøjskassen" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/toolbox-icon-red-2024.png"/>
					<span className={"pl-2 " + text_css}>Verktygslådan</span>
				</O>
			);
		},
		"de": (text_css) => {
			return (
				<O>
					<img alt="Werkzeugkasten" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/toolbox-icon-red-2024.png"/>
					<span className={"pl-2 " + text_css}>Werkzeugkasten</span>
				</O>
			);
		},
	},

	"78e0531f4252": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/aktie-ordbog/" className="d-flex">
						<img alt="Stock Dictionary" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/stock-dictionary-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Stock Dictionary</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/aktie-ordbog/" className="d-flex">
						<img alt="Aktieordbog" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/stock-dictionary-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Aktieordbog</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiekampen.se/aktieordlistan/" className="d-flex">
						<img alt="Aktieordlistan" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/stock-dictionary-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Aktieordlistan</span>
					</NavLink>
				</O>
			);
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/aktie-ordbog/" className="d-flex">
						<img alt="Aktienlexikon" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/stock-dictionary-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Aktienlexikon</span>
					</NavLink>
				</O>
			);
		},
	},

	"73a46b64fa8a": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/test-din-viden-om-aktiemarkedet/" className="d-flex">
						<img alt="Stock Quiz" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/stock-quiz-red.2024.png"/>
						<span className={"pl-2 " + text_css}>Stock Quiz</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/test-din-viden-om-aktiemarkedet/" className="d-flex">
						<img alt="Aktiequizzen" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/stock-quiz-red.2024.png"/>
						<span className={"pl-2 " + text_css}>Aktiequizzen</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiekampen.se/testa-din-kunskap-om-aktiemarknaden/" className="d-flex">
						<img alt="Stock Quiz" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/stock-quiz-red.2024.png"/>
						<span className={"pl-2 " + text_css}>Aktiequizzen</span>
					</NavLink>
				</O>
			);
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/test-din-viden-om-aktiemarkedet/" className="d-flex">
						<img alt="Aktienquiz" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/stock-quiz-red.2024.png"/>
						<span className={"pl-2 " + text_css}>Aktienquiz</span>
					</NavLink>
				</O>
			);
		},
	},

	"06b804dc4fff": {
		"en": (NavLink, text_css) => {
			return null;
		},
		"da": (NavLink, text_css) => {
			return null;
		},
		"sv": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiekampen.se/satta-sin-position-paa-spel/" className="d-flex">
						<img alt="Sätta sin position på spel" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/strategy-tactics-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Sätta sin position på spel</span>
					</NavLink>
				</O>
			);
		},
		"de": (NavLink, text_css) => {
			return null;
		},
	},

	"8bea563ab4c1": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/etisk-investeringskompas/" className="d-flex">
						<img alt="Ethical Investment Compass" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/ethical-investment-plan-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Ethical Investment Compass</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/etisk-investeringskompas/" className="d-flex">
						<img alt="Etisk investeringskompas" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/ethical-investment-plan-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Etisk investeringskompas</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiekampen.se/etisk-investeringskompass/" className="d-flex">
						<img alt="Ethical Investment Compass" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/ethical-investment-plan-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Etisk investeringskompass</span>
					</NavLink>
				</O>
			);
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/etisk-investeringskompas/" className="d-flex">
						<img alt="Ethischer Investitionskompass" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/ethical-investment-plan-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Ethischer Investitionskompass</span>
					</NavLink>
				</O>
			);
		},
	},

	"dfd0d5bb22f2": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/tag-din-risikoprofil/" className="d-flex">
						<img alt="Create Your Risk Profile" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/risk-profile-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Create Your Risk Profile</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/tag-din-risikoprofil/" className="d-flex">
						<img alt="Lav din risikoprofil" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/risk-profile-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Lav din risikoprofil</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiekampen.se/skapa-din-egen-riskprofil/" className="d-flex">
						<img alt="Skapa-din-egen-riskprofil" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/risk-profile-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Skapa din egen riskprofil</span>
					</NavLink>
				</O>
			);
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/tag-din-risikoprofil/" className="d-flex">
						<img alt="Erstelle dein Risikoprofil" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/risk-profile-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Erstelle dein Risikoprofil</span>
					</NavLink>
				</O>
			);
		},
	},

	"ebfd019fbb52": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/haandbogen/" className="d-flex">
						<img alt="Video Course" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/video-course-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Video Course</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/haandbogen/" className="d-flex">
						<img alt="Videokursus" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/video-course-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Videokursus</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiekampen.se/haandbogen/" className="d-flex">
						<img alt="Videokurs" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/video-course-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Videokurs</span>
					</NavLink>
				</O>
			);
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/haandbogen/" className="d-flex">
						<img alt="Videokurs" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/video-course-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Videokurs</span>
					</NavLink>
				</O>
			);
		},
	},

	"a3cec359fec6": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/laer-at-laese-et-regnskab/" className="d-flex">
						<img alt="Learn to Read Financial Statements" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/education-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Learn to Read Financial Statements</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/laer-at-laese-et-regnskab/" className="d-flex">
						<img alt="Lær at læse regnskab" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/education-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Lær at læse regnskab</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiekampen.se/lar-dig-lasa-ett-bokslut/" className="d-flex">
						<img alt="Läsa en balansräkning" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/education-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Lär dig att läsa bokslut</span>
					</NavLink>
				</O>
			);
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/laer-at-laese-et-regnskab/" className="d-flex">
						<img alt="Lerne, Bilanzen zu lesen" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/education-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Lerne, Bilanzen zu lesen</span>
					</NavLink>
				</O>
			);
		},
	},

	"f9aeb23fb25d": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/strategi-og-taktik/" className="d-flex">
						<img alt="Strategy & Tactics" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/strategy-tactics-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Strategy & Tactics</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/strategi-og-taktik/" className="d-flex">
						<img alt="Strategi & taktik" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/strategy-tactics-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Strategi & taktik</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/strategi-og-taktik/" className="d-flex">
						<img alt="Strategy & Tactics" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/strategy-tactics-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Strategi & taktik</span>
					</NavLink>
				</O>
			);
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/strategi-og-taktik/" className="d-flex">
						<img alt="Strategie & Taktik" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/strategy-tactics-icon-red-2024.png"/>
						<span className={"pl-2 " + text_css}>Strategie & Taktik</span>
					</NavLink>
				</O>
			);
		},
	},

	"e87c910f3547": {
		"en": (NavLink, text_css) => {
			return null;
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/haanden-paa-kogepladen-2/" className="d-flex">
						<img alt="Hånden på kogepladen" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/cooking-icon-red-2025.png"/>
						<span className={"pl-2 " + text_css}>Hånden på kogepladen</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return null;
		},
		"de": (NavLink, text_css) => {
			return null;
		},
	},

	"83192d1b33c8": {
		"en": (text_css) => {
			return (
				<O>
					<img alt="Latest News" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/latest-news-icon-green-2024.png"/>
					<span className={"pl-2 " + text_css}>Latest News</span>
				</O>
			);
		},
		"da": (text_css) => {
			return (
				<O>
					<img alt="Seneste nyt" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/latest-news-icon-green-2024.png"/>
					<span className={"pl-2 " + text_css}>Seneste nyt</span>
				</O>
			);
		},
		"sv": (text_css) => {
			return (
				<O>
					<img alt="Seneste nyt" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/latest-news-icon-green-2024.png"/>
					<span className={"pl-2 " + text_css}>Senaste nytt</span>
				</O>
			);
		},
		"de": (text_css) => {
			return (
				<O>
					<img alt="Neueste Nachrichten" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/latest-news-icon-green-2024.png"/>
					<span className={"pl-2 " + text_css}>Neueste Nachrichten</span>
				</O>
			);
		},
	},

	"cd643eaa6d8b": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/" className="d-flex">
						<img alt="News" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/latest-news-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>News</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/" className="d-flex">
						<img alt="News" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/latest-news-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Nyheder</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiekampen.se/oka-din-kunskap/" className="d-flex">
						<img alt="News" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/latest-news-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Vad är nytt</span>
					</NavLink>
				</O>
			);
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/" className="d-flex">
						<img alt="News" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/latest-news-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>News</span>
					</NavLink>
				</O>
			);
		},
	},

	"822cefea65a5": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/disse-aktier-er-de-mest-handlede-paa-aktiedysten-lige-nu-2/" className="d-flex">
						<img alt="Most Traded" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/borsen-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Most Traded</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/disse-aktier-er-de-mest-handlede-paa-aktiedysten-lige-nu-2/" className="d-flex">
						<img alt="Mest Handlet" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/borsen-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Mest Handlet</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiekampen.se/det-har-ar-de-mest-omsatta-aktierna-paa-borsen-just-nu/" className="d-flex">
						<img alt="Mest omsatta" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/borsen-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Mest omsatta</span>
					</NavLink>
				</O>
			);
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/disse-aktier-er-de-mest-handlede-paa-aktiedysten-lige-nu-2/" className="d-flex">
						<img alt="Meistgehandelt" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/borsen-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Meistgehandelt</span>
					</NavLink>
				</O>
			);
		},
	},

	"66d1376e0198": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/aktiesplits-paa-aktiedysten-2024/" className="d-flex">
						<img alt="Stock Splits" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/stock-splits-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Stock Splits</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/aktiesplits-paa-aktiedysten-2024/" className="d-flex">
						<img alt="Aktiesplits" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/stock-splits-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Aktiesplits</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiekampen.se/aktiesplit/" className="d-flex">
						<img alt="Aktiesplit" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/stock-splits-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Aktiesplit</span>
					</NavLink>
				</O>
			);
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/aktiesplits-paa-aktiedysten-2024/" className="d-flex">
						<img alt="Aktiensplits" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/stock-splits-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Aktiensplits</span>
					</NavLink>
				</O>
			);
		},
	},

	"6f31287aea17": {
		"en": (text_css) => {
			return (
				<O>
					<img alt="Deep Dive into the Topic" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/dive-down-icon-green-2024.png"/>
					<span className={"pl-2 " + text_css}>Deep Dive into the Topic</span>
				</O>
			);
		},
		"da": (text_css) => {
			return (
				<O>
					<img alt="Dyk ned i emnet" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/dive-down-icon-green-2024.png"/>
					<span className={"pl-2 " + text_css}>Dyk ned i emnet</span>
				</O>
			);
		},
		"sv": (text_css) => {
			return null;
		},
		"de": (text_css) => {
			return (
				<O>
					<img alt="Tiefer Einblick ins Thema" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/dive-down-icon-green-2024.png"/>
					<span className={"pl-2 " + text_css}>Tiefer Einblick ins Thema</span>
				</O>
			);
		},
	},

	"5b1c5521b659": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-aktien-i-fokus/" className="d-flex">
						<img alt="Stock in Focus" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/stock-focus-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Stock in Focus</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-aktien-i-fokus/" className="d-flex">
						<img alt="Aktien i fokus" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/stock-focus-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Aktien i fokus</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return null;
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-aktien-i-fokus/" className="d-flex">
						<img alt="Aktie im Fokus" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/stock-focus-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Aktie im Fokus</span>
					</NavLink>
				</O>
			);
		},
	},

	"8c40c1fe28ef": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-baeredygtighed/" className="d-flex">
						<img alt="Sustainability" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/sustainability-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Sustainability</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-baeredygtighed/" className="d-flex">
						<img alt="Bæredygtighed" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/sustainability-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Bæredygtighed</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return null;
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-baeredygtighed/" className="d-flex">
						<img alt="Nachhaltigkeit" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/sustainability-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Nachhaltigkeit</span>
					</NavLink>
				</O>
			);
		},
	},

	"57d75946e4fc": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-etik/" className="d-flex">
						<img alt="Ethics" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/sustainability-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Ethics</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-etik/" className="d-flex">
						<img alt="Etik" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/sustainability-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Etik</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return null;
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-etik/" className="d-flex">
						<img alt="Ethik" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/sustainability-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Ethik</span>
					</NavLink>
				</O>
			);
		},
	},

	"8dc639cf5842": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-hvem-og-hvad-er-aktivet/" className="d-flex">
						<img alt="Who and What" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/who-and-what-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Who and What</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-hvem-og-hvad-er-aktivet/" className="d-flex">
						<img alt="Hvem og hvad" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/who-and-what-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Hvem og hvad</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return null;
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-hvem-og-hvad-er-aktivet/" className="d-flex">
						<img alt="Wer und Was" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/who-and-what-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Wer und Was</span>
					</NavLink>
				</O>
			);
		},
	},

	"0b3dc3cc32e1": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-kom-igang/" className="d-flex">
						<img alt="Get Started" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/get-started-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Get Started</span>
					</NavLink>	
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-kom-igang/" className="d-flex">
						<img alt="Kom i gang" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/get-started-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Kom i gang</span>
					</NavLink>	
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return null;
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-kom-igang/" className="d-flex">
						<img alt="Einstieg" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/get-started-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Einstieg</span>
					</NavLink>	
				</O>
			);
		},
	},

	"8d249c5fa687": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-krypto/" className="d-flex">
						<img alt="Crypto" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/crypto-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Crypto</span>
					</NavLink>	
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-krypto/" className="d-flex">
						<img alt="Krypto" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/crypto-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Krypto</span>
					</NavLink>	
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return null;
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-krypto/" className="d-flex">
						<img alt="Krypto" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/crypto-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Krypto</span>
					</NavLink>	
				</O>
			);
		},
	},

	"a0c870f216b6": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-opsparing/" className="d-flex">
						<img alt="Save to Invest" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/afford-investing-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Save to Invest</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-opsparing/" className="d-flex">
						<img alt="Få råd til at investere" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/afford-investing-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Få råd til at investere</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return null;
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-opsparing/" className="d-flex">
						<img alt="Sparen für Investitionen" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/afford-investing-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Sparen für Investitionen</span>
					</NavLink>
				</O>
			);
		},
	},

	"8530ee1af22b": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-tech-og-ai/" className="d-flex">
						<img alt="Tech & AI" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/tech-ai-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Tech & AI</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-tech-og-ai/" className="d-flex">
						<img alt="Tech & AI" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/tech-ai-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Tech & AI</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return null;
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-tech-og-ai/" className="d-flex">
						<img alt="Technologie & KI" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/tech-ai-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Technologie & KI</span>
					</NavLink>
				</O>
			);
		},
	},

	"e15a6f92794a": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-regnskab/" className="d-flex">
						<img alt="Financial Statements" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/accounting-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Financial Statements</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-regnskab/" className="d-flex">
						<img alt="Regnskab" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/accounting-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Regnskab</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return null;
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-regnskab/" className="d-flex">
						<img alt="Bilanz" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/accounting-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Bilanz</span>
					</NavLink>
				</O>
			);
		},
	},

	"aa3145e89822": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-risiko/" className="d-flex">
						<img alt="Risk" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/risk-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Risk</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-risiko/" className="d-flex">
						<img alt="Risiko" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/risk-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Risiko</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return null;
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/vaekst-din-viden-risiko/" className="d-flex">
						<img alt="Risiko" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/risk-icon-green-2024.png"/>
						<span className={"pl-2 " + text_css}>Risiko</span>
					</NavLink>
				</O>
			);
		},
	},

	"3365f6d052e7": {
		"en": () => {
			return (
				<O>
					<img alt="games" className="ad_margin_bottom_2px" src="https://static.aktiedysten.dk/gfx/games-icon-2023-0.svg" />
				                    	<span className="ad_navbar_textlink ad_navbar_text_500 pl-2">Games</span>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
					<img alt="Spil" className="ad_margin_bottom_2px" src="https://static.aktiedysten.dk/gfx/games-icon-2023-0.svg" />
				                    	<span className="ad_navbar_textlink ad_navbar_text_500 pl-2">Spil</span>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
					<img alt="Spel" className="ad_margin_bottom_2px" src="https://static.aktiedysten.dk/gfx/games-icon-2023-0.svg" />
				                    	<span className="ad_navbar_textlink ad_navbar_text_500 pl-2">Spel</span>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
					<img alt="Spiele" className="ad_margin_bottom_2px" src="https://static.aktiedysten.dk/gfx/games-icon-2023-0.svg" />
				                    	<span className="ad_navbar_textlink ad_navbar_text_500 pl-2">Spiele</span>
				</O>
			);
		},
	},

	"6019c2ed8a71": {
		"en": () => {
			return (
				<O>
					<img alt="games" className="ad_margin_bottom_2px" src="https://static.aktiedysten.dk/gfx/games-icon-2023-0.svg" />
				                    	<span className="ad_navbar_textlink pl-2">Games</span>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
					<img alt="spil" className="ad_margin_bottom_2px" src="https://static.aktiedysten.dk/gfx/games-icon-2023-0.svg" />
				                    	<span className="ad_navbar_textlink pl-2">Spil</span>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
					<img alt="games" className="ad_margin_bottom_2px" src="https://static.aktiedysten.dk/gfx/games-icon-2023-0.svg" />
				                    	<span className="ad_navbar_textlink pl-2">Spel</span>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
					<img alt="games" className="ad_margin_bottom_2px" src="https://static.aktiedysten.dk/gfx/games-icon-2023-0.svg" />
				                    	<span className="ad_navbar_textlink pl-2">Spiele</span>
				</O>
			);
		},
	},

	"20d6a3f94d09": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="/games" className="d-flex">
						<img alt="My Games" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/my-games-icon-yellow-2024.png"/>
						<span className={"pl-2 " + text_css}>My Games</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="/games" className="d-flex">
						<img alt="Mine spil" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/my-games-icon-yellow-2024.png"/>
						<span className={"pl-2 " + text_css}>Mine spil</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="/games" className="d-flex">
						<img alt="Mine spel" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/my-games-icon-yellow-2024.png"/>
						<span className={"pl-2 " + text_css}>Mine spel</span>
					</NavLink>
				</O>
			);
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="/games" className="d-flex">
						<img alt="Meine Spiele" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/my-games-icon-yellow-2024.png"/>
						<span className={"pl-2 " + text_css}>Meine Spiele</span>
					</NavLink>
				</O>
			);
		},
	},

	"3edb159db983": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="/new_game" className="d-flex">
						<img alt="New Game" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/new-game-icon-yellow-2024.png"/>
						<span className={"pl-2 " + text_css}>New Game</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="/new_game" className="d-flex">
						<img alt="Nyt spil" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/new-game-icon-yellow-2024.png"/>
						<span className={"pl-2 " + text_css}>Nyt spil</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="/new_game" className="d-flex">
						<img alt="Nyt spel" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/new-game-icon-yellow-2024.png"/>
						<span className={"pl-2 " + text_css}>Nyt spel</span>
					</NavLink>
				</O>
			);
		},
		"de": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="/new_game" className="d-flex">
						<img alt="Neues Spiel" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/new-game-icon-yellow-2024.png"/>
						<span className={"pl-2 " + text_css}>Neues Spiel</span>
					</NavLink>
				</O>
			);
		},
	},

	"10c77cb033f4": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/konkurrencer-for-skoler/" className="d-flex">
						<img alt="Competitions for Schools" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/school-competitions-icon-yellow-2024.png"/>
						<span className={"pl-2 " + text_css}>Competitions for Schools</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiedysten.dk/konkurrencer-for-skoler/" className="d-flex">
						<img alt="Konkurrencer for skoler" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/school-competitions-icon-yellow-2024.png"/>
						<span className={"pl-2 " + text_css}>Konkurrencer for skoler</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiekampen.se/tavlingar-for-skolor/" className="d-flex">
						<img alt="Tävlingar-for-skolor" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/school-competitions-icon-yellow-2024.png"/>
						<span className={"pl-2 " + text_css}>Tävlingar-for-skolor</span>
					</NavLink>
				</O>
			);
		},
		"de": (NavLink, text_css) => {
			return null
        },
	},

	"343b8feba963": {
		"en": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/tutorial/" className="d-flex">
						<img alt="Tutorials" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/tutorials-icon-yellow-2024.png"/>
						<span className={"pl-2 " + text_css}>Tutorials</span>
					</NavLink>
				</O>
			);
		},
		"da": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://inspiration.aktiedysten.dk/tutorial/" className="d-flex">
						<img alt="Tutorials" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/tutorials-icon-yellow-2024.png"/>
						<span className={"pl-2 " + text_css}>Tutorials</span>
					</NavLink>
				</O>
			);
		},
		"sv": (NavLink, text_css) => {
			return (
				<O>
					<NavLink href="https://undervisning.aktiekampen.se/tutorials/" className="d-flex">
						<img alt="Tutorials" className="ad_margin_bottom_2px ad_sub_navbar_image" src="https://static.aktiedysten.dk/gfx/tutorials-icon-yellow-2024.png"/>
						<span className={"pl-2 " + text_css}>Tutorials</span>
					</NavLink>
				</O>
			);
		},
		"de": (NavLink, text_css) => {
			return null;
		},
	},

	"6a8e498a0473": {
		"en": () => {
			return (
				<O>
					<img alt="Learn with Us" className="ad_margin_bottom_2px ad_navbar_image" src="https://static.aktiedysten.dk/gfx/education-icon-red-2024.png"/>
					<span className="ad_navbar_textlink ad_navbar_text_500 pl-2">Learn with Us</span>	
				</O>
			);
		},
		"da": () => {
			return (
				<O>
					<img alt="Lær med os" className="ad_margin_bottom_2px ad_navbar_image" src="https://static.aktiedysten.dk/gfx/education-icon-red-2024.png"/>
					<span className="ad_navbar_textlink ad_navbar_text_500 pl-2">Lær med os</span>	
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
					<img alt="Lær med os" className="ad_margin_bottom_2px ad_navbar_image" src="https://static.aktiedysten.dk/gfx/education-icon-red-2024.png"/>
					<span className="ad_navbar_textlink ad_navbar_text_500 pl-2">Lär med oss</span>	
				</O>
			);
		},
		"de": () => {
			return (
				<O>
					<img alt="Lernen mit uns" className="ad_margin_bottom_2px ad_navbar_image" src="https://static.aktiedysten.dk/gfx/education-icon-red-2024.png"/>
					<span className="ad_navbar_textlink ad_navbar_text_500 pl-2">Lernen mit uns</span>	
				</O>
			);
		},
	},

	"6417309a628a": {
		"en": () => <O>Teaching</O>,
		"da": () => <O>Undervisning</O>,
		"sv": () => <O>Undervisning</O>,
		"de": () => <O>Unterricht</O>,
	},

	"c7d419d6bfc4": {
		"en": () => <O>Toolbox</O>,
		"da": () => <O>Værktøjskassen</O>,
		"sv": () => <O>Toolbox</O>,
		"de": () => <O>Werkzeugkasten</O>,
	},

	"c569e93915f5": {
		"en": (DropdownToggle) => {
			return (
				<O>
				                    <DropdownToggle nav className="ad_no_outline_focus">
				                        <img alt="Markets" className="ad_margin_bottom_2px" src="https://static.aktiedysten.dk/gfx/market-icon-2023-0.svg"/>
				                        <span className="ad_navbar_textlink ad_navbar_text_500 pl-2">Markets</span>
				    </DropdownToggle>
				                </O>
			);
		},
		"da": (DropdownToggle) => {
			return (
				<O>
				                    <DropdownToggle nav className="ad_no_outline_focus">
				                        <img alt="Markeder" className="ad_margin_bottom_2px" src="https://static.aktiedysten.dk/gfx/market-icon-2023-0.svg"/>
				                        <span className="ad_navbar_textlink ad_navbar_text_500 pl-2">Markeder</span>
				    </DropdownToggle>
				                </O>
			);
		},
		"sv": (DropdownToggle) => {
			return (
				<O>
				                    <DropdownToggle nav className="ad_no_outline_focus">
				                        <img alt="Marknader" className="ad_margin_bottom_2px" src="https://static.aktiedysten.dk/gfx/market-icon-2023-0.svg"/>
				                        <span className="ad_navbar_textlink ad_navbar_text_500 pl-2">Marknader</span>
				    </DropdownToggle>
				                </O>
			);
		},
		"de": (DropdownToggle) => {
			return (
				<O>
				                    <DropdownToggle nav className="ad_no_outline_focus">
				                        <img alt="Märkte" className="ad_margin_bottom_2px" src="https://static.aktiedysten.dk/gfx/market-icon-2023-0.svg"/>
				                        <span className="ad_navbar_textlink ad_navbar_text_500 pl-2">Märkte</span>
				    </DropdownToggle>
				                </O>
			);
		},
	},

	"0d6005e6afb7": {
		"en": () => <O>Markets</O>,
		"da": () => <O>Markeder</O>,
		"sv": () => <O>Marknader</O>,
		"de": () => <O>Märkte</O>,
	},

	"035dccb76e43": {
		"en": () => <O>Latest News</O>,
		"da": () => <O>Seneste nyt</O>,
		"sv": () => <O>Senaste nytt</O>,
		"de": () => <O>Neueste Nachrichten</O>,
	},

	"794ea9a07037": {
		"en": () => <O>Deep Dive into the Topic</O>,
		"da": () => <O>Dyk ned i emnet</O>,
		"sv": () => <O>Deep Dive into the Topic</O>,
		"de": () => <O>Tiefer Einblick ins Thema</O>,
	},

	"bf4641936fc3": {
		"en": () => <O>Join game</O>,
		"da": () => <O>Tilmeld spil</O>,
		"sv": () => <O>Gå med i spel</O>,
		"de": () => <O>Spiel beitreten</O>,
	},

	"c61e5e53c5aa": {
		"en": () => <O>Invitation for</O>,
		"da": () => <O>Tilmeld</O>,
		"sv": () => <O>Inbjudan för</O>,
		"de": () => <O>Einladung für</O>,
	},

	"2cb192176cf0": {
		"en": () => <O>You have joined</O>,
		"da": () => <O>Du er tilmeldt</O>,
		"sv": () => <O>Du har gått med i spelet</O>,
		"de": () => <O>Du bist beigetreten</O>,
	},

	"c2b75d5551d0": {
		"en": () => <O>Choose which markets the game players can invest in</O>,
		"da": () => <O>Vælg hvilke markeder deltagerne kan investere i</O>,
		"sv": () => <O>Välj vilka marknader spelarna kan investera i</O>,
		"de": () => <O>Wähle aus, auf welchen Märkten die Spieler investieren können</O>,
	},

	"be7cfa149e20": {
		"en": () => <O>Assets</O>,
		"da": () => <O>Aktiver</O>,
		"sv": () => <O>Tillgångar</O>,
		"de": () => <O>Vermögenswerte</O>,
	},

	"a32f5bbe4a7e": {
		"en": () => <O>Denmark</O>,
		"da": () => <O>Danmark</O>,
		"sv": () => <O>Danmark</O>,
		"de": () => <O>Dänemark</O>,
	},

	"07365b76de7c": {
		"en": () => <O>Explore assets exclusively from Denmark</O>,
		"da": () => <O>Udforsk værdipapirer eksklusivt fra Danmark</O>,
		"sv": () => <O>Utforska tillgångar exklusivt från Danmark</O>,
		"de": () => <O>Entdecke dänische Aktien</O>,
	},

	"a864dbffc25e": {
		"en": () => <O>Phone</O>,
		"da": () => <O>Telefon</O>,
		"sv": () => <O>Telefon</O>,
		"de": () => <O>Telefon</O>,
	},

	"cb5057b9da38": {
		"en": () => <O>Country</O>,
		"da": () => <O>Land</O>,
		"sv": () => <O>Land</O>,
		"de": () => <O>Land</O>,
	},

	"62a492bcffb7": {
		"en": () => <O>City</O>,
		"da": () => <O>By</O>,
		"sv": () => <O>Stad</O>,
		"de": () => <O>Stadt</O>,
	},

	"2793b2164745": {
		"en": () => <O>Postal code</O>,
		"da": () => <O>Postnummer</O>,
		"sv": () => <O>Postnummer</O>,
		"de": () => <O>Postleitzahl</O>,
	},

	"770fa23ad39a": {
		"en": () => <O>Address</O>,
		"da": () => <O>Adresse</O>,
		"sv": () => <O>Adress</O>,
		"de": () => <O>Adresse</O>,
	},

	"7e34903a0cec": {
		"en": () => <O>Managing director</O>,
		"da": () => <O>Administrerende direktør</O>,
		"sv": () => <O>Verkställande direktör</O>,
		"de": () => <O>Vertretungsberechtigter Geschäftsführer</O>,
	},

	"faa9d213ef04": {
		"en": () => <O>VAT information</O>,
		"da": () => <O>CVR-nummer</O>,
		"sv": () => <O>Momsregistreringsnummer</O>,
		"de": () => <O>Umsatzsteuer</O>,
	},

	"7aa640681d8b": {
		"en": () => <O>VAT ID</O>,
		"da": () => <O>CVR-no</O>,
		"sv": () => <O>VAT ID</O>,
		"de": () => <O>Umsatzsteuer-ID</O>,
	},

	"d6b6376eb593": {
		"en": () => <O>Nasdaq OMX Nordic</O>,
		"da": () => <O>Nasdaq OMX Nordic</O>,
		"sv": () => <O>Nasdaq OMX Nordic</O>,
		"de": () => <O>Nasdaq OMX Nordic</O>,
	},

	"038318dd8648": {
		"en": () => <O>Iceland</O>,
		"da": () => <O>Island</O>,
		"sv": () => <O>Island</O>,
		"de": () => <O>Island</O>,
	},

	"7eb90c1a12a3": {
		"en": () => <O>Commodity</O>,
		"da": () => <O>Råvarer</O>,
		"sv": () => <O>Råvaror</O>,
		"de": () => <O>Rohstoff</O>,
	},

	"8c83ede774ae": {
		"en": () => <O>Funds / Bonds</O>,
		"da": () => <O>Fonde / Obligationer</O>,
		"sv": () => <O>Fonder / Obligationer</O>,
		"de": () => <O>Fonde / Obligationer</O>,
	},

	"163e8f1f1a7f": {
		"en": () => <O>A bond fund is a mutual fund or an exchange-traded fund (ETF) that buys and sells debt instruments like government and corporate bonds. The primary goal of a bond fund is to generate monthly income for investors.</O>,
		"da": () => <O>En obligationsfond er en investeringsforening eller en børshandlet fond (ETF), der køber og sælger gældsinstrumenter som stats- og virksomhedsobligationer. Det primære mål med en obligationsfond er at generere månedlig indkomst til investorer.</O>,
		"sv": () => <O>En obligationsfond är en värdepappersfond eller en börshandlad fond (ETF) som köper och säljer skuldinstrument som stats- och företagsobligationer. Det primära målet med en obligationsfond är att generera månadsinkomster för investerare.</O>,
		"de": () => <O>Ein Anleihenfonds ist ein Investmentfonds oder ein börsengehandelter Fonds (ETF), der Schuldtitel wie Staats- und Unternehmensanleihen kauft und verkauft. Das Hauptziel eines Anleihenfonds besteht darin, monatliche Erträge für Anleger zu generieren.</O>,
	},

	"94591d34e236": {
		"en": () => <O>Finnish collections</O>,
		"da": () => <O>Finske lister</O>,
		"sv": () => <O>Finska samlingar</O>,
		"de": () => <O>Finnische Liste</O>,
	},

	"9f209ed3f648": {
		"en": () => <O>Selected Finnish stocks</O>,
		"da": () => <O>Udvalgte finske værdipapirer</O>,
		"sv": () => <O>Utvalda finska aktier</O>,
		"de": () => <O>Ausgewählte finnische Aktien</O>,
	},

	"71e434f47d5d": {
		"en": () => <O>Random Finnish stocks</O>,
		"da": () => <O>Tilfældige finske værdipapirer</O>,
		"sv": () => <O>Slumpmässiga finska aktier</O>,
		"de": () => <O>Zufällige finnische Aktien</O>,
	},

	"f5aed1ae1ab2": {
		"en": () => <O>Search Finnish assets</O>,
		"da": () => <O>Søg finske værdipapirer</O>,
		"sv": () => <O>Sök finska tillgångar</O>,
		"de": () => <O>Nach finnischen Aktien suchen</O>,
	},

	"2542979ad8f7": {
		"en": () => <O>Finland</O>,
		"da": () => <O>Finland</O>,
		"sv": () => <O>Finland</O>,
		"de": () => <O>Finnland</O>,
	},

	"1840db168329": {
		"en": () => <O>Explore assets exclusively from Finland</O>,
		"da": () => <O>Udforsk værdipapirer eksklusivt fra Finland</O>,
		"sv": () => <O>Utforska tillgångar exklusivt från Finland</O>,
		"de": () => <O>Entdecke finnische Aktien</O>,
	},

	"5dc734c0694a": {
		"en": () => <O>Sweden</O>,
		"da": () => <O>Sverige</O>,
		"sv": () => <O>Sverige</O>,
		"de": () => <O>Schweden</O>,
	},

	"68b106ee86c8": {
		"en": () => <O>USA</O>,
		"da": () => <O>USA</O>,
		"sv": () => <O>USA</O>,
		"de": () => <O>USA</O>,
	},

	"f491b4d1c06f": {
		"en": () => <O>NYSE &amp; Nasdaq</O>,
		"da": () => <O>NYSE &amp; Nasdaq</O>,
		"sv": () => <O>NYSE &amp; Nasdaq</O>,
		"de": () => <O>NYSE &amp; Nasdaq</O>,
	},

	"a552ed2d8d67": {
		"en": () => <O>Several crypto currencies</O>,
		"da": () => <O>Mange kryptovalutaer</O>,
		"sv": () => <O>Många kryptovalutor</O>,
		"de": () => <O>Verschiedene Kryptowährungen</O>,
	},

	"76ececf3aebf": {
		"en": () => <O>The game starts</O>,
		"da": () => <O>Spillet starter</O>,
		"sv": () => <O>Spelet startar</O>,
		"de": () => <O>Das Spiel beginnt</O>,
	},

	"dc61c9a7b4ed": {
		"en": () => <O>The game ends</O>,
		"da": () => <O>Spillet slutter</O>,
		"sv": () => <O>Spelet slutar</O>,
		"de": () => <O>Das Spiel endet</O>,
	},

	"781d5a302648": {
		"en": () => <O>The game has ended</O>,
		"da": () => <O>Spillet er afsluttet</O>,
		"sv": () => <O>Spelet är avslutat</O>,
		"de": () => <O>Das Spiel ist beendet</O>,
	},

	"bbf819f4c0e9": {
		"_context": "invite_game_page",
		"en": () => <O>Duration</O>,
		"da": () => <O>Varighed</O>,
		"sv": () => <O>Varaktighet</O>,
		"de": () => <O>Spieldauer</O>,
	},

	"1b8fdcddd753": {
		"en": () => <O>The game runs for</O>,
		"da": () => <O>Spillet kører i</O>,
		"sv": () => <O>Spelat körs i</O>,
		"de": () => <O>Das Spiel läuft</O>,
	},

	"3b914314b1cd": {
		"en": () => <O>There is <strong>no</strong> brokerage in this game</O>,
		"da": () => <O>Der er <strong>ingen</strong> kurtage i dette spil</O>,
		"sv": () => <O>Det är <strong>inget</strong> courtage i detta spel</O>,
		"de": () => <O>Es gibt in diesem Spiel <strong>keine</strong> Vermittlungsgebühr</O>,
	},

	"a61f4ff45779": {
		"en": () => <O>There is <strong>no</strong> limit on equity purchase</O>,
		"da": () => <O>Der er <strong>ingen</strong> grænse pr. værdipapir</O>,
		"sv": () => <O>Det är <strong>ingen</strong> gräns per värdepapper</O>,
		"de": () => <O>Es gibt <strong>kein</strong> Limit für den Kauf von Aktien</O>,
	},

	"7f253b725172": {
		"en": (I18N, game) => <O>You can max. buy for <strong>{I18N.floatval_unit(game.MaxAmountPerStock, 2, game.Currency)}</strong> per equity</O>,
		"da": (I18N, game) => <O>Du kan max. købe for <strong>{I18N.floatval_unit(game.MaxAmountPerStock, 2, game.Currency)}</strong> pr. værdipapir</O>,
		"sv": (I18N, game) => <O>Du kan köpa för max <strong>{I18N.floatval_unit(game.MaxAmountPerStock, 2, game.Currency)}</strong> per värdepapper</O>,
		"de": (I18N, game) => <O>Du kannst max. <strong>{I18N.floatval_unit(game.MaxAmountPerStock, 2, game.Currency)}</strong> in eine Aktie investieren</O>,
	},

	"56941717590d": {
		"en": (site_name) => <O>Welcome to {site_name}!</O>,
		"da": (site_name) => <O>Velkommen til {site_name}!</O>,
		"sv": (site_name) => <O>Välkommen till {site_name}!</O>,
		"de": (site_name) => <O>Willkommen bei {site_name}!</O>,
	},

	"c6cb57daec05": {
		"en": (site_name) => {
			return (
				<O>
				{site_name} is an investment game where participants can trade stocks with fictional
				money but at real, live-updated prices. - This allows everyone to try their hand
				at the stock market in a fun, secure, and realistic way through play.
				</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>
				{site_name} er et investeringsspil, hvor deltagerne kan handle med aktier for fiktive penge, men
				til de rigtige, liveopdaterede kurser. - Dermed kan alle, gennem leg, prøve kræfter med
				aktiemarkedet på en sjov, sikker og realistisk måde.
				</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>
				{site_name} Aktiekampen är ett investeringsspel där deltagarna kan handla aktier för fiktiva
				pengar, men till verkliga realtidskurser. - Därmed kan alla pröva aktiemarknaden genom lek på ett
				roligt, säkert och realistiskt sätt.
				</O>
			);
		},
		"de": (site_name) => {
			return (
				<O>
				{site_name} ist ein Börsenspiel, in dem die Teilnehmer Aktien mit fiktivem Geld, aber zu realen,
				stets aktuellen Kursen handeln können – so kann jeder den Aktienmarkt spielerisch, sicher und
				realistisch ausprobieren.
				</O>
			);
		},
	},

	"c4cb76a316fa": {
		"en": () => {
			return (
				<O>
				Play, Learning,<br />
				and Community
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Leg, læring<br />
				og fællesskab
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				Lek, lärande<br />
				och gemenskap
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Spaß, Lernen<br />
				und Community
				</O>
			);
		},
	},

	"791d192613c4": {
		"en": (site_name) => {
			return (
				<O>
				The game appeals to anyone interested in stocks and investing, especially beginners
				who want to learn how to invest, as well as more experienced investors who want to
				practice and perhaps try out new strategies. {site_name} is used today in education at all
				levels, from elementary school to university. Finally, {site_name} is widely used by friends
				and colleagues as a fun game to challenge each other and find out who the best investor
				is - an entertaining way to cultivate and strengthen communities.
				</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>
				Spillet henvender sig til alle, der interesserer sig for aktier og investering, især nybegynderen,
				der gerne vil lære at investere, men også den mere erfarne investor, som vil træne og måske prøve
				nye strategier af. {site_name} bruges i dag i undervisning på alle niveauer fra grundskole til
				universitet. Endelig bruges {site_name} i høj grad af venner og kollegaer, som et sjovt spil, hvor
				man udfordrer hinanden for at finde ud af, hvem er den bedste investor? - En underholdende måde at
				dyrke og styrke fællesskaber på.
				</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>
				Spelet riktar sig till alla som är intresserade av aktier och investeringar, särskilt nybörjare
				som vill lära sig investera, men även till mer erfarna investerare som vill öva och kanske prova
				nya strategier. Idag används {site_name} i utbildning på alla nivåer, från grundskola till
				universitet. Slutligen används {site_name} ofta av vänner och kollegor som ett roligt spel där ni
				utmanar varandra och tar reda på vem som är den bästa investeraren - Ett underhållande sätt att
				skapa och stärka gemenskaper.
				</O>
			);
		},
		"de": (site_name) => {
			return (
				<O>
				Das Spiel richtet sich an alle, die sich für Aktien und Investitionen interessieren: Insbesondere
				an Anfänger, die lernen wollen, wie man investiert, aber auch an erfahrenere Anleger, die üben und
				neue Strategien ausprobieren möchten. Heutzutage wird {site_name} vermehrt im Unterricht
				eingesetzt, von der Grundschule bis zur Universität. Darüber hinaus nutzen Freunde und Kollegen
				{site_name} gerne als unterhaltsames Spiel, bei dem sie sich gegenseitig herausfordern, um den
				besten Investor zu küren – eine unterhaltsame Art, Gemeinschaften zu pflegen und zu stärken.
				</O>
			);
		},
	},

	"0e67c8e592d4": {
		"en": (site_name) => {
			return (
				<O>
				{site_name} is created with the ambition of providing users with insight and a
				responsible approach to investments. Therefore, we combine the game with learning and
				dissemination of investment knowledge through inspiring articles, podcasts, Q&amp;A formats,
				and educational materials. A range of experts share knowledge and perspectives, and users
				also have the opportunity to share experiences and insights with each other.
				</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>
				{site_name} er skabt med ambitionen om at give brugerne indsigt og et ansvarligt forhold til
				investeringer. Derfor kombinerer vi spillet med læring og formidling om investering gennem
				inspirerende artikler, podcasts, Q&amp;A-formater og undervisningsmateriale. Her deler en række
				eksperter viden og perspektiver, ligesom brugerne også har mulighed for at dele erfaringer og
				indsigter med hinanden.
				</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>
				{site_name} skapades med ambitionen att ge användarna insikter och ett ansvarsfullt
				förhållningssätt till investeringar. Därför kombinerar vi spelet med lärande och kommunikation om
				investeringar genom inspirerande artiklar, podcasts, frågeformat och utbildningsmaterial. Här
				delar en rad experter med sig av kunskap och perspektiv, och användarna har dessutom möjlighet att
				dela erfarenheter och insikter med varandra.
				</O>
			);
		},
		"de": (site_name) => {
			return (
				<O>
				{site_name} wurde mit dem Ziel gegründet, den Nutzern Einblicke und einen verantwortungsvollen
				Umgang mit Investitionen zu ermöglichen. Deshalb kombinieren wir das Spiel mit Lernerlebnissen
				rund ums Thema Investieren durch inspirierende Beiträge, Podcasts, Q&amp;A-Formate und
				Bildungsmaterial. Hier teilen eine Reihe von Experten ihr Wissen und ihre Perspektiven, und die
				Nutzer haben darüber hinaus die Möglichkeit, Erfahrungen und Erkenntnisse miteinander
				auszutauschen.
				</O>
			);
		},
	},

	"1353909986a6": {
		"en": () => <O>- An entertaining way to create, grow and strenghten communities.</O>,
		"da": () => <O>- En underholdende måde at skabe, udvikle og styrke fællesskaber.</O>,
		"sv": () => <O>- Ett underhållande sätt att skapa, växa och stärka gemenskaper.</O>,
		"de": () => <O>- Eine unterhaltsame Möglichkeit, Gemeinschaften zu schaffen, zu wachsen und zu stärken.</O>,
	},

	"ea32e63127ff": {
		"en": () => <O>Start game</O>,
		"da": () => <O>Start spil</O>,
		"sv": () => <O>Starta spel</O>,
		"de": () => <O>Spiel starten</O>,
	},

	"e883ee637443": {
		"en": () => <O>*To create a game with premium features, you'll need to purchase Premium first.</O>,
		"da": () => <O>*For at oprette et spil med premium-funktioner, skal du først købe Premium.</O>,
		"sv": () => <O>*För att skapa ett spel med premiumfunktioner måste du först köpa Premium.</O>,
		"de": () => <O>*Um ein Spiel mit Premium-Funktionen zu erstellen, musst du zuerst Premium kaufen.</O>,
	},

	"123582030ae9": {
		"en": () => <O>A Growing Community</O>,
		"da": () => <O>Et voksende fællesskab</O>,
		"sv": () => <O>En växande gemenskap</O>,
		"de": () => <O>Eine wachsende Community</O>,
	},

	"c12956394c65": {
		"en": () => <O>Edit game</O>,
		"da": () => <O>Rediger spil</O>,
		"sv": () => <O>Redigera spelet</O>,
		"de": () => <O>Spiel bearbeiten</O>,
	},

	"4edcea4f91e0": {
		"en": () => <O>*You do not have enough keys to enable premium</O>,
		"da": () => <O>*Du har ikke nok nøgler til at aktivere premium</O>,
		"sv": () => <O>*Du har inte tillräckligt med nycklar för att aktivera premium</O>,
		"de": () => <O>*Du hast nicht genügend Schlüssel, um Premium zu aktivieren</O>,
	},

	"00c1d90692a0": {
		"en": () => <O>This market has already been traded in the game and cannot be deselected now</O>,
		"da": () => <O>Dette marked har været handlet i spillet og kan derfor ikke fravælges.</O>,
		"sv": () => <O>Den här marknaden har redan handlats under spelets gång och kan inte avmarkeras.</O>,
		"de": () => <O>Da auf diesem Markt im laufenden Spiel bereits gehandelt wurde, kann dieser nicht mehr deaktiviert werden.</O>,
	},

	"7dcf0bcd20e6": {
		"en": () => <O>Here you can edit some of the settings for the game.</O>,
		"da": () => <O>Her kan du redigere nogle af indstillingerne for spillet.</O>,
		"sv": () => <O>Här kan du redigera några av inställningarna för spelet.</O>,
		"de": () => <O>Hier kannst du einige Einstellungen für das Spiel bearbeiten.</O>,
	},

	"3d89fef82614": {
		"en": (n_unread) => <O>New messages ({n_unread})</O>,
		"da": (n_unread) => <O>Nye beskeder ({n_unread})</O>,
		"sv": (n_unread) => <O>Nya meddelanden ({n_unread})</O>,
		"de": (n_unread) => <O>Neue Nachrichten ({n_unread})</O>,
	},

	"69068ff0ed88": {
		"en": (n_read) => <O>Archive ({n_read})</O>,
		"da": (n_read) => <O>Arkiv ({n_read})</O>,
		"sv": (n_read) => <O>Arkiv ({n_read})</O>,
		"de": (n_read) => <O>Archiv ({n_read})</O>,
	},

	"d5b7d97219be": {
		"en": () => <O>(Note: notifications will be deleted after 30 days, or when they exceed 100)</O>,
		"da": () => <O>(Note: notifikationer bliver slettet efter 30 dage, eller når der er flere end 100)</O>,
		"sv": () => <O>(Note: notifikationer raderas efter 30 dagar eller när det är fler än 100)</O>,
		"de": () => <O>(Hinweis: Benachrichtigungen werden nach 30 Tagen, oder wenn sie 100 überschreiten, gelöscht)</O>,
	},

	"9986f0d7233c": {
		"en": () => <O>You have no notifications.</O>,
		"da": () => <O>Du har ingen notifikationer.</O>,
		"sv": () => <O>Du har inga notifikationer.</O>,
		"de": () => <O>Du hast keine Benachrichtigungen</O>,
	},

	"ed354cc95f61": {
		"en": () => <O>Your trading history</O>,
		"da": () => <O>Din handelshistorik</O>,
		"sv": () => <O>Din handelshistorik</O>,
		"de": () => <O>Dein Handelsverlauf</O>,
	},

	"0593bdec3b30": {
		"en": () => <O>Here you can examine your trading history across all portfolios and games.</O>,
		"da": () => <O>Her kan du se din handelshistorik for alle porteføljer og spil.</O>,
		"sv": () => <O>Här kan du se din handelshistorik för alla portföljer och spel.</O>,
		"de": () => <O>Hier kannst du deinen Handelsverlauf über alle Portfolios und Spiele hinweg verfolgen.</O>,
	},

	"aa962550f6f5": {
		"en": () => <O>Transaction id</O>,
		"da": () => <O>Transaction id</O>,
		"sv": () => <O>Transaction id</O>,
		"de": () => <O>Transaction id</O>,
	},

	"b9186985c2d0": {
		"en": () => <O>Type</O>,
		"da": () => <O>Type</O>,
		"sv": () => <O>Type</O>,
		"de": () => <O>Type</O>,
	},

	"d1fd311e6a71": {
		"en": () => <O>Time</O>,
		"da": () => <O>Tidspunkt</O>,
		"sv": () => <O>Tid</O>,
		"de": () => <O>Zeit</O>,
	},

	"d1f3cdab449b": {
		"en": () => <O>Portfolio</O>,
		"da": () => <O>Portefølje</O>,
		"sv": () => <O>Portfölj</O>,
		"de": () => <O>Portfolio</O>,
	},

	"35a7a43e7402": {
		"en": () => <O>Equity</O>,
		"da": () => <O>Værdipapir</O>,
		"sv": () => <O>Värdepapper</O>,
		"de": () => <O>Anlagen</O>,
	},

	"8e09b9789362": {
		"en": () => <O>Amount</O>,
		"da": () => <O>Antal</O>,
		"sv": () => <O>Antal</O>,
		"de": () => <O>Betrag</O>,
	},

	"8f080726be13": {
		"en": () => <O>Currency</O>,
		"da": () => <O>Valuta</O>,
		"sv": () => <O>Valuta</O>,
		"de": () => <O>Währung</O>,
	},

	"871042dcb981": {
		"en": () => <O>Disable Facebook login</O>,
		"da": () => <O>Afbryd Facebook login</O>,
		"sv": () => <O>Inaktivera inloggning med Facebook</O>,
		"de": () => <O>Deaktivieren des Facebook-Logins</O>,
	},

	"f87aeb85004d": {
		"en": () => <O>Enable Facebook login</O>,
		"da": () => <O>Brug Facebook login</O>,
		"sv": () => <O>Använd inloggning med Facebook</O>,
		"de": () => <O>Aktivieren des Facebook-Logins</O>,
	},

	"080581bc40b6": {
		"en": (site_name) => {
			return (
				<O>You have already connected another
				{site_name} account with Twitter. If
				you want to connect this account, you
				can disconnect from Twitter on the
				other account.</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>Du har allerede forbundet en anden
				{site_name} konto med Twitter. Hvis du
				vil forbinde denne konto, skal du først
				slette Twitter login på den anden konto.</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>Du har redan kopplat ett annat
				{site_name} konto med Twitter. Om du vill koppla detta
				konto så behöver du koppla bort Twitter från
				det tidigare konton.</O>
			);
		},
		"de": (site_name) => {
			return (
				<O>Du hast bereits ein anderes
				{site_name}-Konto mit Twitter verbunden. Wenn
				du dieses Konto verbinden möchtest, musst du
				die Verknüpfung mit Twitter auf dem anderen
				Konto entfernen.</O>
			);
		},
	},

	"1e35ca6df8db": {
		"en": () => <O>Disable Twitter login</O>,
		"da": () => <O>Afbryd Twitter login</O>,
		"sv": () => <O>Inaktivera inloggning med Twitter</O>,
		"de": () => <O>Deaktivieren des Twitter-Logins</O>,
	},

	"76cec808eede": {
		"en": () => <O>Enable Twitter login</O>,
		"da": () => <O>Brug Twitter login</O>,
		"sv": () => <O>Använd inloggning med Twitter</O>,
		"de": () => <O>Aktivieren des Twitter-Logins</O>,
	},

	"121bb6318976": {
		"en": () => <O>DETAILS</O>,
		"da": () => <O>DETALJER</O>,
		"sv": () => <O>DETALJER</O>,
		"de": () => <O>DEIN PROFIL</O>,
	},

	"0ad26c32de5b": {
		"en": () => <O>Your personal info is never shared with anyone</O>,
		"da": () => <O>Dine personlige data bliver aldrig delt med nogen</O>,
		"sv": () => <O>Personlig information och data delas aldrig med någon</O>,
		"de": () => <O>Deine persönlichen Daten werden niemals an Dritte weitergegeben.</O>,
	},

	"a9c26df78c41": {
		"en": () => <O>Privacy</O>,
		"da": () => <O>Privatliv</O>,
		"sv": () => <O>Integritet</O>,
		"de": () => <O>Privatsphäre</O>,
	},

	"804128092149": {
		"en": (mailto_link) => {
			return (
				<O>
				<p>We store the following info about you, if you provided it to us:</p>
				<ul>
					<li>You email address</li>
					<li>Your screen name</li>
					<li>Username and user id for Twitter / Facebook</li>
					<li>Your trade history</li>
					<li>Postal code</li>
					<li>Age</li>
					<li>Gender</li>
					<li>Profession</li>				
				</ul>
				<p>You can see the email address and screen name when
				you edit your profile. Your trade history is available
				under trade history.</p> <p>If you would like to delete
				any of it, send us an email at {mailto_link} and we'll get right to it. Be aware, that if you want to delete your trade
				history, it might affect ongoing games.</p>
				</O>
			);
		},
		"da": (mailto_link) => {
			return (
				<O>
					<p>Vi gemmer følgende info om dig, hvis du har givet det til os:</p>
				<ul>
					<li>Din emailadresse</li>
					<li>Dit navn</li>
					<li>Dit brugernavn og -id til Twitter / Facebook</li>
					<li>Din handelshistorik</li>
					<li>Postnummer</li>
					<li>Alder</li>
					<li>Køn</li>
					<li>Profession</li>
				</ul>
				<p>Du kan se din emailadresse og dit navn når du redigerer
				din profil.</p> <p>Hvis du vil slette noget af det, så send
				os en email på {mailto_link} og vi vil straks gå igang.
				Vær opmærksom på at hvis du vil slette handelshistorik, kan det påvirke igangværende spil.</p>
				</O>
			);
		},
		"sv": (mailto_link) => {
			return (
				<O>
			  <p>Vi lagrar följande information om dig, om du har tillhandahållit det till oss:</p>
                                <ul>
                                        <li>Din emailadress</li>
                                        <li>Ditt namn</li>
                                        <li>Ditt användarnamn och ID till Twitter / Facebook</li>
                                        <li>Din handelshistorik</li>
										<li>Postnummer</li>
										<li>Åldras</li>
										<li>Kön</li>
										<li>Profession</li>
                                </ul>
				<p>Du kan se din emailadress och ditt namn när du redigerar din profil.</p>
				<p>Om du vill ta bort något, skicka oss ett email till {mailto_link} så tar vi bort det på en gång.
                                Notera att om du vill radera din handelshistorik så kan det påverka pågående spel.</p>
				</O>
			);
		},
		"de": (mailto_link) => {
			return (
				<O>
			<p>Wir speichern folgende Informationen von dir, solltest du uns diese mitteilen:</p>
                                <ul>
                                        <li>Deine E-Mail-Adresse</li>
                                        <li>Deinen Namen</li>
                                        <li>Deinen Benutzernamen und die genutzten Twitter- / Facebook-Daten</li>
                                        <li>Deinen Handelsverlauf</li>
										<li>Postleitzahl</li>
										<li>Zeitalter</li>
										<li>Geschlecht</li>
										<li>Profession</li>
                                </ul>
				<p>Du kannst deine E-Mail-Adresse und deinen Benutzernamen sehen, wenn du auf dein Profil klickst.
				Dein Handelsverlauf ist unter Handelsverlauf im Menü verfügbar.</p>
				<p>Wenn du etwas davon löschen möchten, sende uns eine E-Mail an {mailto_link} und wir
				kümmern uns sofort darum. Bitte beachte jedoch, dass sich das Löschen deines Handelsverlaufs auf
				laufende Spiele auswirken kann.</p>
				</O>
			);
		},
	},

	"3cdf109e015a": {
		"en": () => <O>Please log in to see your orders.</O>,
		"da": () => <O>Log ind for at se dine ordrer.</O>,
		"sv": () => <O>Logga in för att se dina beställningar.</O>,
		"de": () => <O>Bitte melden Sie sich an, um Ihre Bestellungen zu sehen.</O>,
	},

	"f81b8560740f": {
		"en": () => <O>Incomplete</O>,
		"da": () => <O>Ikke fuldført</O>,
		"sv": () => <O>Ej betald</O>,
		"de": () => <O>Nicht bezahlt</O>,
	},

	"4d8c444c52f2": {
		"en": () => <O>Your orders</O>,
		"da": () => <O>Dine ordrer</O>,
		"sv": () => <O>Dina beställningar</O>,
		"de": () => <O>Ihre Bestellungen</O>,
	},

	"156ef9af96b5": {
		"en": () => <O>Order ID</O>,
		"da": () => <O>Ordre-ID</O>,
		"sv": () => <O>Order-ID</O>,
		"de": () => <O>Bestell-ID</O>,
	},

	"828626601e3d": {
		"en": () => <O>Order date</O>,
		"da": () => <O>Ordredato</O>,
		"sv": () => <O>Orderdatum</O>,
		"de": () => <O>Bestelldatum</O>,
	},

	"99e675f6f662": {
		"en": () => <O>Status</O>,
		"da": () => <O>Status</O>,
		"sv": () => <O>Status</O>,
		"de": () => <O>Status</O>,
	},

	"f8430d58e7bd": {
		"en": () => <O>Order total</O>,
		"da": () => <O>Ordretotal</O>,
		"sv": () => <O>Ordersumma</O>,
		"de": () => <O>Bestellsumme</O>,
	},

	"955c16b499bd": {
		"en": () => <O>Actions</O>,
		"da": () => <O>Handlinger</O>,
		"sv": () => <O>Åtgärder</O>,
		"de": () => <O>Aktionen</O>,
	},

	"436ce7e31cee": {
		"en": () => <O>Details</O>,
		"da": () => <O>Detaljer</O>,
		"sv": () => <O>Detaljer</O>,
		"de": () => <O>Details</O>,
	},

	"9ea3410f1a99": {
		"en": () => <O>Account</O>,
		"da": () => <O>Konto</O>,
		"sv": () => <O>Konto</O>,
		"de": () => <O>Profil</O>,
	},

	"13d41c0f3c71": {
		"en": () => <O>Current password</O>,
		"da": () => <O>Nuværende password</O>,
		"sv": () => <O>Nuvarande lösenord</O>,
		"de": () => <O>Derzeitiges Passwort</O>,
	},

	"15a76e8fc214": {
		"en": () => <O>Enable e-mail login</O>,
		"da": () => <O>Muliggør e-mail login</O>,
		"sv": () => <O>Möjliggör inloggning med e-mail</O>,
		"de": () => <O>Aktivieren der E-Mail-Anmeldung</O>,
	},

	"484f9573380d": {
		"en": () => <O>Password</O>,
		"da": () => <O>Password</O>,
		"sv": () => <O>Lösenord</O>,
		"de": () => <O>Passwort</O>,
	},

	"e0328d79a1e7": {
		"en": () => <O>Automatic email</O>,
		"da": () => <O>System emails</O>,
		"sv": () => <O>System email</O>,
		"de": () => <O>E-Mail-Einstellungen</O>,
	},

	"adf8a7414a42": {
		"en": () => <O>Password again</O>,
		"da": () => <O>Password igen</O>,
		"sv": () => <O>Lösenord igen</O>,
		"de": () => <O>Erneutes Passwort</O>,
	},

	"2af3bfef8ea0": {
		"en": () => <O>Send an email when a game you have joined starts</O>,
		"da": () => <O>Send en email når et spil du er tilmeldt, starter</O>,
		"sv": () => <O>Skicka ett email när ett spel som du gått med i startar</O>,
		"de": () => <O>E-Mail erhalten, wenn ein Spiel beginnt, dem ich beigetreten bin</O>,
	},

	"e3a79964fc4c": {
		"en": () => <O>Send emails about the status of your active games</O>,
		"da": () => <O>Send emails om status på dine aktive spil</O>,
		"sv": () => <O>Skicka emails om statusen på dina aktiva spel</O>,
		"de": () => <O>E-Mails über den Status meiner aktiven Spiele erhalten</O>,
	},

	"66acab5507e1": {
		"en": () => {
			return (
				<O>Here you can update your email preferences. Please do note that this is not marketing
				emails, but mails about your games and the likes.</O>
			);
		},
		"da": () => {
			return (
				<O>Her kan du opdatere dine email preferencer. Vær opmærksom på at dette er ikke marketing
				emails, men mails vedrørende dine spil og lignende.</O>
			);
		},
		"sv": () => {
			return (
				<O>Här kan du uppdaterade dina email preferenser. Var uppmärksam på att detta inte är
				marknads-emails, utan emails om dina spel och liknande.</O>
		       );
		},
		"de": () => {
                        return (
				<O>Hier kannst du deine E-Mail-Einstellungen ändern. Wir senden lediglich E-Mails, die dein
				Spielerlebnis weiter bereichern.</O>
			);
                },
	},

	"b31972b10f56": {
		"en": () => <O>The password reset email has expired</O>,
		"da": () => <O>Din email til at skifte password er udløbet</O>,
		"sv": () => <O>Ditt email för att återställa lösenordet har gått ut</O>,
		"de": () => <O>Die E-Mail zum Zurücksetzen des Passworts ist abgelaufen</O>,
	},

	"72e12874457b": {
		"en": () => {
			return (
				<O>This can happen if you
				waited too long after sending
				the email or if you have
				already used it to reset your
				password.</O>
			);
		},
		"da": () => {
			return (
				<O>Dette kan ske hvis du har ventet for længe, eller hvis
				den allerede er blevet brugt til at skifte dit password.</O>
			);
		},
		"sv": () => {
			return (
				<O>Detta kan ske när du har väntat för länge efter att ha
				skickat emailet, eller om du redan har använt det för att återställa ditt lösenord.</O>
			);
		},
		"de": () => {
                        return (
				<O>Dies kann passieren, wenn du nach dem Senden der E-Mail zu lange gewartet hast oder
				wenn du die E-Mail bereits zum Zurücksetzen deines Passworts verwendet hast</O>
			);
                },
	},

	"087022bfe432": {
		"en": () => <O>Click here to try again</O>,
		"da": () => <O>Klik her for at prøve igen</O>,
		"sv": () => <O>Klicka här för att prova igen</O>,
		"de": () => <O>Klicke hier, um es erneut zu versuchen</O>,
	},

	"a9d8c54eec99": {
		"en": () => <O>Reset your password</O>,
		"da": () => <O>Nulstil dit password</O>,
		"sv": () => <O>Återställ ditt lösenord</O>,
		"de": () => <O>Passwort zurücksetzen</O>,
	},

	"71981c963a87": {
		"en": () => <O>New password</O>,
		"da": () => <O>Nyt password</O>,
		"sv": () => <O>Nytt lösenord</O>,
		"de": () => <O>Neues Passwort</O>,
	},

	"9bf5daedf85f": {
		"en": () => <O>New password again</O>,
		"da": () => <O>Nyt password igen</O>,
		"sv": () => <O>Nytt lösenord igen</O>,
		"de": () => <O>Passwort wiederholen</O>,
	},

	"36738d9cd592": {
		"en": () => <O>Change password</O>,
		"da": () => <O>Skift password</O>,
		"sv": () => <O>Ändra lösenord</O>,
		"de": () => <O>Passwort ändern</O>,
	},

	"5d6bb3258ec0": {
		"en": (change, name) => <O><input className="ad_input ad_new_portfolio_input pl-3" placeholder="Name your portfolio" value={name} onChange={change}/></O>,
		"da": (change, name) => <O><input className="ad_input ad_new_portfolio_input pl-3" placeholder="Navn på portefølje" value={name} onChange={change}/></O>,
		"sv": (change, name) => <O><input className="ad_input ad_new_portfolio_input pl-3" placeholder="Namn på portfölj" value={name} onChange={change}/></O>,
		"de": (change, name) => <O><input className="ad_input ad_new_portfolio_input pl-3" placeholder="Name des Portfolios" value={name} onChange={change}/></O>,
	},

	"13fe36a8baa8": {
		"en": () => <O>Create</O>,
		"da": () => <O>Opret</O>,
		"sv": () => <O>Skapa</O>,
		"de": () => <O>Erstellen</O>,
	},

	"22c19473a856": {
		"en": () => <O>Invite your friends</O>,
		"da": () => <O>Invitér dine venner</O>,
		"sv": () => <O>Bjud in dina vänner</O>,
		"de": () => <O>Lade deine Freunde ein</O>,
	},

	"ee2793512f20": {
		"en": () => {
			return (
				<O>
				As soon as you click "Start Game" after creating it, you will have the opportunity to
				invite friends or colleagues. Playing together is much more fun!
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Så snart du har klikket start spil, vil du få muligheden for at invitere venner eller kollegaer.
				At spille sammen er meget sjovere!
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				När du startat spelet får du möjlighet att bjuda in vänner eller kollegor. Att spela tillsammans
				är mycket roligare!
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Sobald du auf Spiel starten klickst, hast du die Möglichkeit, Freunde oder Kollegen einzuladen.
				Gemeinsam zu spielen macht viel mehr Spaß!
				</O>
			);
		},
	},

	"cc515ee9c21a": {
		"en": () => <O>Set up your portfolio</O>,
		"da": () => <O>Opret din portfølje</O>,
		"sv": () => <O>Skapa din portfölj</O>,
		"de": () => <O>Bau dein Portfolio auf</O>,
	},

	"27358bc63310": {
		"en": () => {
			return (
				<O>
				Play and learn on the training ground. Here, you can take risks without any real
				consequences and see if you can earn the title of the best investor. Find your first stock
				in the search field and click on the trade button.
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Leg og lær på træningsbanen. Her kan du risikofrit tage chancer og se om du kan vinde æren som den
				bedste investor. Find din første aktie i søgefeltet og klik på handelsknappen.
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				Spela och lär dig på träningsområdet. Här kan du riskfritt ta chanser och se om du kan vinna äran
				att vara den bästa investeraren. Hitta din första aktie i sökfältet och klicka på handelsknappen.
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Spiele und lerne auf dem Übungsplatz. Hier kannst du risikofrei Handel ausprobieren und testen, ob
				du das Zeug zum besten Investor hast. Finde deine erste Aktie im Suchfeld und klicke auf den
				Button „Handeln“.
				</O>
			);
		},
	},

	"28cc9d3ef555": {
		"en": () => <O>Need help?</O>,
		"da": () => <O>Brug for hjælp?</O>,
		"sv": () => <O>Behöver du hjälp?</O>,
		"de": () => <O>Brauchst du hilfe?</O>,
	},

	"9e9e9103aece": {
		"en": (A, site_base) => {
			return (
				<O>
				We are always ready to assist you in getting started. Don't hesitate to contact us at
				info@{site_base}. You can also check out our <A href="/faq">FAQ</A>, where your questions may
				already be answered.
				</O>
			);
		},
		"da": (A, site_base) => {
			return (
				<O>
				Vi står altid klar til at hjælpe dig med at komme i gang. Så tøv ikke med at kontakte os på
				info@{site_base}. Kig også gerne i vores <A href="/faq">FAQ</A>, hvor dit spørgsmål måske allerede
				er besvaret?
				</O>
			);
		},
		"sv": (A, site_base) => {
			return (
				<O>
				Vi är alltid redo att hjälpa dig att komma igång. Tveka inte att kontakta oss på
				info@{site_base}. Du kan också ta en titt på vår <A href="/faq">FAQ</A>, där din fråga kanske redan är besvarad?
				</O>
			);
		},
		"de": (A, site_base) => {
			return (
				<O>
				Wir stehen bereit, um dir bei deinen ersten Schritten zu helfen. Zögere nicht, uns unter
				info@{site_base} zu kontaktieren. Schaue auch gerne in unsere <A href="/faq">FAQ</A> rein,
				vielleicht wurde deine Frage hier bereits beantwortet.
				</O>
			);
		},
	},

	"baf2c2d582d4": {
		"en": () => <O>E-mail</O>,
		"da": () => <O>E-mail</O>,
		"sv": () => <O>E-mail</O>,
		"de": () => <O>E-Mail</O>,
	},

	"7e9ed6266a21": {
		"en": () => <O>Share the link below with people you want to invite to the game.</O>,
		"da": () => <O>Dél linket nedenunder med dem du vil invitere til spillet.</O>,
		"sv": () => <O>Dela länken nedan med dem du vill bjuda in till spelet.</O>,
		"de": () => <O>Teile den unten stehenden Link mit Personen, die du zum Spiel einladen möchtest.</O>,
	},

	"a196c00d0de7": {
		"en": (info) => <O>You have {info.RemainingInvites} invites left</O>,
		"da": (info) => <O>Du har {info.RemainingInvites} invitationer tilbage</O>,
		"sv": (info) => <O>Du har {info.RemainingInvites} inbjudningar kvar</O>,
		"de": (info) => <O>Du hast noch {info.RemainingInvites} Einladungen übrig</O>,
	},

	"d930fbfc873f": {
		"en": () => <O>No invites left</O>,
		"da": () => <O>Ingen invitationer tilbage</O>,
		"sv": () => <O>Inga inbjudningar kvar</O>,
		"de": () => <O>Keine Einladungen mehr übrig</O>,
	},

	"f7f71ba6f7bd": {
		"en": () => <O>The time for invitations has expired</O>,
		"da": () => <O>Tiden for invitationer er udløbet</O>,
		"sv": () => <O>Tiden för inbjudningar har gått ut</O>,
		"de": () => <O>Die Einladungsfrist ist abgelaufen</O>,
	},

	"b5f49877285e": {
		"en": () => <O>(this will revoke all previous invites)</O>,
		"da": () => <O>(dette vil ophæve alle tidligere invitationer)</O>,
		"sv": () => <O>(detta upphäver alla tidigare inbjudningar)</O>,
		"de": () => <O>(Dadurch werden alle vorherigen Einladungen ungültig)</O>,
	},

	"9ae7bd7014ba": {
		"en": () => {
			return (
				<O>Are you sure you want to revoke this invite and make a new one?
				If you have already shared the link with anyone, they will no longer be able to use it.</O>
			);
		},
		"da": () => {
			return (
				<O>Er du sikker på du vil ophæve denne invition og lave en ny?
				Alle der allerede har det gamle link, vil ikke længere kunne bruge det.</O>
			);
		},
		"sv": () => {
			return (
				<O>Är du säker på att du vill upphäva denna inbjudan och skapa en ny?
				Om du redan har skickat länken till någon så kommer de inte längre kunna använda sig utav den.</O>
			);
		},
		"de": () => {
                        return (
				<O>Möchtest du diese Einladung wirklich widerrufen und eine neue erstellen? Wenn du den Link
				bereits mit jemandem geteilt hast, kann diese Person ihn nicht mehr verwenden.</O>
			);
                },
	},

	"8689e762a2a4": {
		"en": () => <O>Create new invite</O>,
		"da": () => <O>Lav ny invitation</O>,
		"sv": () => <O>Skapa ny inbjudan</O>,
		"de": () => <O>Neue Einladung erstellen</O>,
	},

	"277aa95e6539": {
		"en": () => <O>Yes, do it!</O>,
		"da": () => <O>Ja, gør det!</O>,
		"sv": () => <O>Ja, gör det!</O>,
		"de": () => <O>Ja!</O>,
	},

	"acd9dceb35fc": {
		"en": () => <O>Remove participant</O>,
		"da": () => <O>Fjern deltager</O>,
		"sv": () => <O>Ta bort deltagare</O>,
		"de": () => <O>Teilnehmer entfernen</O>,
	},

	"da273292504b": {
		"en": (portfolios, remainingInvites) => <O>You’re about to upgrade the game, which will use {portfolios} invites. You currently have {remainingInvites} invites remaining.</O>,
		"da": (portfolios, remainingInvites) => <O>You’re about to upgrade the game, which will use {portfolios} invites. You currently have {remainingInvites} invites remaining.</O>,
		"sv": (portfolios, remainingInvites) => <O>You’re about to upgrade the game, which will use {portfolios} invites. You currently have {remainingInvites} invites remaining.</O>,
		"de": (portfolios, remainingInvites) => <O>You’re about to upgrade the game, which will use {portfolios} invites. You currently have {remainingInvites} invites remaining.</O>,
	},

	"8c4b374a73b5": {
		"en": () => <O>Upgrade Now</O>,
		"da": () => <O>Opgrader nu</O>,
		"sv": () => <O>Uppgradera nu</O>,
		"de": () => <O>Jetzt upgraden</O>,
	},

	"cd4846163eb1": {
		"en": () => <O>Cancel</O>,
		"da": () => <O>Afbryd</O>,
		"sv": () => <O>Afbryt</O>,
		"de": () => <O>Abbrechen</O>,
	},

	"fbf6f4cc82a2": {
		"en": () => <O>You have successfully changed </O>,
		"da": () => <O>Du har justeret </O>,
		"sv": () => <O>Du har justeret </O>,
		"de": () => <O>Sie haben erfolgreich angepasst </O>,
	},

	"a2d0555c0efc": {
		"en": () => <O> portfolio value by </O>,
		"da": () => <O> porteføljeværdi med </O>,
		"sv": () => <O> portföljvärde med </O>,
		"de": () => <O> Portfoliowert um </O>,
	},

	"2d6a01e441d5": {
		"en": () => <O>Close</O>,
		"da": () => <O>Luk</O>,
		"sv": () => <O>Stäng</O>,
		"de": () => <O>schließen</O>,
	},

	"a4359969152f": {
		"en": () => <O>Participant</O>,
		"da": () => <O>Deltager</O>,
		"sv": () => <O>Deltagare</O>,
		"de": () => <O>Teilnehmer</O>,
	},

	"9d4eec192e02": {
		"en": () => <O>Name: </O>,
		"da": () => <O>Navn: </O>,
		"sv": () => <O>Namn: </O>,
		"de": () => <O>Name: </O>,
	},

	"ebed62ef4eb6": {
		"en": () => <O>Current portfolio value: </O>,
		"da": () => <O>Nuværende porteføljeværdi: </O>,
		"sv": () => <O>Nuvarande portföljvärde: </O>,
		"de": () => <O>Aktueller Portfoliowert: </O>,
	},

	"ac6b8c63fb24": {
		"en": () => <O>Portfolio value after change: </O>,
		"da": () => <O>Porteføljeværdi efter justering: </O>,
		"sv": () => <O>Portföljvärde efter ändring: </O>,
		"de": () => <O>Portfoliowert nach Änderung: </O>,
	},

	"ea251a228061": {
		"en": () => <O>Adjust portfolio value</O>,
		"da": () => <O>Justér porteføljeværdi</O>,
		"sv": () => <O>Justera portföljvärde</O>,
		"de": () => <O>Portfoliowert anpassen</O>,
	},

	"14fac5fb14e2": {
		"en": () => <O>You can adjust the value of the portfolio by any number between 100.000 and -100.000</O>,
		"da": () => <O>Du kan justere porteføljens værdi med et tal mellem 100.000 og -100.000</O>,
		"sv": () => <O>Du kan justera portföljens värde med ett tal mellan 100.000 och -100.000</O>,
		"de": () => <O>Sie können den Portfoliowert um einen beliebigen Betrag zwischen 100.000 und -100.000 anpassen</O>,
	},

	"34f3f8999c03": {
		"en": () => <O>Please enter a valid number.</O>,
		"da": () => <O>Indtast venligst et gyldigt tal.</O>,
		"sv": () => <O>Ange ett giltigt nummer, tack.</O>,
		"de": () => <O>Bitte geben Sie eine gültige Zahl ein.</O>,
	},

	"3730f1ca7f3c": {
		"en": () => <O>Amount must be between -100000 and 100000.</O>,
		"da": () => <O>Beløbet skal være mellem -100000 og 100000.</O>,
		"sv": () => <O>Beloppet måste vara mellan -100000 och 100000.</O>,
		"de": () => <O>Der Betrag muss zwischen -100000 und 100000 liegen.</O>,
	},

	"126354589ea6": {
		"en": () => <O>An unknown error occurred</O>,
		"da": () => <O>En ukendt fejl opstod</O>,
		"sv": () => <O>Ett okänt fel inträffade</O>,
		"de": () => <O>Ein unbekannter Fehler ist aufgetreten</O>,
	},

	"eca5628f4b29": {
		"en": () => <O>Confirm Capital Change</O>,
		"da": () => <O>Bekræft porteføljeændring</O>,
		"sv": () => <O>Bekräfta portföljändring</O>,
		"de": () => <O>Bestätige Kapitaländerung</O>,
	},

	"44f30930467e": {
		"en": () => <O>You are now about to disburse or deduct capital (money) from a participant. Are you sure you want to continue?</O>,
		"da": () => <O>Du er nu i gang med at udbetale eller fratrække kapital (penge) til en deltager. Er du sikker på, at du vil fortsætte?</O>,
		"sv": () => <O>Du håller nu på att utbetala eller dra ifrån kapital (pengar) från en deltagare. Är du säker på att du vill fortsätta?</O>,
		"de": () => <O>Sie sind dabei, Kapital (Geld) an einen Teilnehmer auszuzahlen oder abzuziehen. Sind Sie sicher, dass Sie fortfahren möchten?</O>,
	},

	"7cbbf65da55b": {
		"en": () => <O>Adjust</O>,
		"da": () => <O>Justér</O>,
		"sv": () => <O>Justera</O>,
		"de": () => <O>Anpassen</O>,
	},

	"b56930dbed1d": {
		"en": () => <O>No orders found.</O>,
		"da": () => <O>Ingen ordrer fundet.</O>,
		"sv": () => <O>Inga beställningar hittades.</O>,
		"de": () => <O>Keine Bestellungen gefunden.</O>,
	},

	"6dbaeb4184f6": {
		"en": () => <O>Trades in approx.</O>,
		"da": () => <O>Handles om ca.</O>,
		"sv": () => <O>Handler om ca.</O>,
		"de": () => <O>Handel in ca.</O>,
	},

	"3a300565aa32": {
		"en": () => <O>Trade in progress</O>,
		"da": () => <O>Handlen er igang</O>,
		"sv": () => <O>Handel pågår</O>,
		"de": () => <O>Laufender Handel</O>,
	},

	"91b3cbae4282": {
		"en": () => <O><strong>Note:</strong> trades in stocks with low volume can take a long time to complete.</O>,
		"da": () => <O><strong>Note:</strong> handler i aktier med lav volumen kan tage lang tid at gennemføre.</O>,
		"sv": () => <O><strong>Note:</strong> handel med aktier med låg volym kan ta lång tid att genomföra.</O>,
		"de": () => <O><strong>Note:</strong> Es kann lange dauern, bis der Handel von Aktien mit geringem Volumen durchgeführt ist.</O>,
	},

	"a3abde349a84": {
		"en": () => <O>Read more here</O>,
		"da": () => <O>Læs mere her</O>,
		"sv": () => <O>Läs mer här</O>,
		"de": () => <O>Hier mehr lesen</O>,
	},

	"6e89f51c6e64": {
		"en": () => <O>Cancels in approx.</O>,
		"da": () => <O>Annulleres om ca.</O>,
		"sv": () => <O>Avbryter om ca.</O>,
		"de": () => <O>Abbruch in ca.</O>,
	},

	"799605045c1a": {
		"en": (A, R) => {
			return (
				<O><strong>Note:</strong> You requested a cancellation. Please note that your trade might still go through. Read more in our <A className="ad_main_blue" href={R.href("/faq")}>FAQ</A>.</O>
			);
		},
		"da": (A, R) => {
			return (
				<O><strong>Note:</strong> Du har anmodet om en annullering. Vær opmærksom på at handler stadig kan gå igennem. Læs mere i vores <A className="ad_main_blue" href={R.href("/faq")}>FAQ</A>.</O>
			);
		},
		"sv": (A, R) => {
			return (
				<O><strong>Note:</strong> Du begärde en avbokning. Observera att din handel fortfarande kan gå igenom. Läs mer i vår <A className="ad_main_blue" href={R.href("/faq")}>FAQ</A>.</O>
			);
		},
		"de": (A, R) => {
			return (
				<O><strong>Note:</strong> Du hast einen Abbruch des Handels angefragt. Bitte beachte, dass der Handel trotzdem zustande kommen kann. Lies mehr dazu in unseren <A className="ad_main_blue" href={R.href("/faq")}>FAQ</A>.</O>
			);
		},
	},

	"2fd6102e4038": {
		"en": () => <O>minutes</O>,
		"da": () => <O>minutter</O>,
		"sv": () => <O>minutes</O>,
		"de": () => <O>Minuten</O>,
	},

	"09515aa8483d": {
		"en": (current_amount, orig_amount, ticker_element) => <O>Buy {ticker_element} for {current_amount} (was {orig_amount})</O>,
		"da": (current_amount, orig_amount, ticker_element) => <O>Køb {ticker_element} for {current_amount} (var {orig_amount})</O>,
		"sv": (current_amount, orig_amount, ticker_element) => <O>Köp {ticker_element} för {current_amount} (var {orig_amount})</O>,
		"de": (current_amount, orig_amount, ticker_element) => <O>Kauf von {ticker_element} für {current_amount} (vorher {orig_amount})</O>,
	},

	"055a51fde5ac": {
		"en": (current_amount, ticker_element) => <O>Buy {ticker_element} for {current_amount}</O>,
		"da": (current_amount, ticker_element) => <O>Køb {ticker_element} for {current_amount}</O>,
		"sv": (current_amount, ticker_element) => <O>Köp {ticker_element} för {current_amount}</O>,
		"de": (current_amount, ticker_element) => <O>Kauf von {ticker_element} für {current_amount}</O>,
	},

	"948dc101febf": {
		"en": (current_sale_amount, orig_sale_amount, ticker_element) => <O>Sell {current_sale_amount} (was {orig_sale_amount}) {ticker_element}</O>,
		"da": (current_sale_amount, orig_sale_amount, ticker_element) => <O>Sell {current_sale_amount} (var {orig_sale_amount}) {ticker_element}</O>,
		"sv": (current_sale_amount, orig_sale_amount, ticker_element) => <O>Sälj {current_sale_amount} (var {orig_sale_amount}) {ticker_element}</O>,
		"de": (current_sale_amount, orig_sale_amount, ticker_element) => <O>Verkauf von {current_sale_amount} (vorher {orig_sale_amount}) {ticker_element}</O>,
	},

	"91b426219abc": {
		"en": (current_sale_amount, ticker_element) => <O>Sell {current_sale_amount} {ticker_element}</O>,
		"da": (current_sale_amount, ticker_element) => <O>Sælg {current_sale_amount} {ticker_element}</O>,
		"sv": (current_sale_amount, ticker_element) => <O>Sälj {current_sale_amount} {ticker_element}</O>,
		"de": (current_sale_amount, ticker_element) => <O>Verkauf von {current_sale_amount} {ticker_element}</O>,
	},

	"d722c129a284": {
		"en": () => <O>Leaderboard URL</O>,
		"da": () => <O>Leaderboard URL</O>,
		"sv": () => <O>Leaderboard URL</O>,
		"de": () => <O>Ranglisten-URL</O>,
	},

	"d81952825496": {
		"en": () => <O>You can use this link for dashboards, etc.</O>,
		"da": () => <O>Du kan bruge dette link til dashboards, m.m.</O>,
		"sv": () => <O>Du kan använda den här länken för dashboards, etc.</O>,
		"de": () => <O>Nutze diesen Link für Dashboards, etc.</O>,
	},

	"f8d11a32ae33": {
		"en": () => {
			return (
				<O>
				I invest in stocks myself and I enjoy a good gamble with my buddies. The investment game unites both
				hobbies and it's great that we can just spontaneously start a game whenever we feel like it. Also, it is
				pretty entertaining and quite informative to exchange thoughts via the game chat and being able to look
				at the other players' portfolios.<br />
				<b>Marc E., buddy gambler</b>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Jeg investerer selv i aktier, og nyder en god dyst med mine venner. Investeringsspillet
				forener begge hobbyer, og det er fantastisk at vi bare kan starte et spil spontant, når
				vi følger for det. Ydermere er det meget underholdende og informativt at udveksle tanker
				og strategier i spilchatten, samt at kunne se medspillernes porteføljevalg.<br />
				<b>Marc E., vennespiller</b>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				Jag investerar i aktier själv och jag njuter av en bra chansning med mina kompisar.
				Investeringsspelet förenar båda hobbies och det är bra att vi bara spontant kan starta
				ett spel när vi känner för det. Dessutom är det ganska underhållande och ganska
				informativt att utbyta tankar via spelchatten och att kunna titta på de andra spelarnas
				portföljer.<br />
				<b>Marc E., kompisspelare</b>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Ich investiere selbst an der Börse und zocke gerne mit meinen Kumpels. Das Börsenspiel
				vereint beide Hobbies, und es ist top, dass wir einfach spontan ein Spiel starten
				können, wenn uns danach ist. Vor allem der Austausch mit den Mitspielern über den
				Spiele-Chat und die Möglichkeit, auch mal bei Anderen ins Portfolio zu gucken, ist
				unterhaltsam und ziemlich lehrreich.<br />
				<b>Marc E., Freundezocker</b>
				</O>
			);
		},
	},

	"969854f1d165": {
		"en": () => {
			return (
				<O>
				At first I thought, ok just join in, put some stocks in your portfolio, see where you end up. But in
				the end it actually got really exciting since the battle ended in the middle of the earnings. I'd like
				to send greetings to Pinterest, they got me into first place :-). Your site is well developed, a bit like
				kickbase, just for stocks instead of football.<br />
				<b>Romario K., winner of Sommer Börsen Battle</b>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Først tænkte jeg, ok bare deltag i spillet, find nogle aktier til porteføljen og se hvor
				du ender. Men så blev det faktisk rigtig spændende &mdash; især da jeg begyndte at komme
				op i toppen. Jeg vil gerne sende venlige hilsener til Pinterest da de bragte mig op i
				toppen :-). Jeres websted er veludviklet, lidt som Kickbase, bare med aktier istedet for
				fodbold.<br />
				<b>Romario K., vinder af Sommer Börsen Battle</b>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				Först tänkte jag, ok bara gå med, sätt några aktier i din portfölj, se var du hamnar.
				Men sen blev det faktiskt riktigt spännande speciellt sen jag började vinna. Jag skulle
				vilja skicka hälsningar till Pinterest, de tipsade mig från början :-). Din webbplats är
				väl utvecklad, lite som Kickbase, bara för aktier istället för fotboll.<br />
				<b>Romario K., vinnare av Sommer Börsen Battle</b>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Am Anfang hab ich gedacht, komm, machste einfach mit, legst dir ein paar Aktien ins
				Portfolio und guckst mal wo du landest. Am Ende wurde es aber richtig spannend, weil das
				Battle mitten in den Earnings endete. An der Stelle nochmal Grüße an Pinterest, die
				haben mich nach oben gebracht :-). Eure Seite ist gut ausgereift, ein bisschen wie
				Kickbase, nur für Aktien statt Fußball.<br />
				<b>Romario K., Gewinner des Sommer Börsen Battles</b>
				</O>
			);
		},
	},

	"0956ec5eda51": {
		"en": () => {
			return (
				<O>
				During the Börsen Battle, I checked my stock performances several times a day. It's fun to see how you
				slowly move up the ranking – and it's really nerve-racking when it goes down again! I didn't know much
				about the stock market beforehand and I think it's great that I am able to build up some knowledge
				thanks to the user-friendly interface of the game.<br />
				<b>Linus E., Battle lover</b>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Under Börsen Battle spillet checkede jeg mine aktier flere gange om dagen. Det er sjovt
				at følge hvordan man langsomt rykker op i stillingen &mdash; og virkelig nervepirrende
				når det går ned igen. Jeg vidste ikke meget om aktiemarkedet på forhånd, så det er stort
				at jeg kan opbygge noget viden i det meget brugervenlige spil.<br />
				<b>Linus E., Battle-elsker</b>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				Under Börsen Battle kollade jag mina aktier flera gånger om dagen. Det är kul att se hur
				du sakta rör dig upp på rankningen – och det är verkligen nervkittlande när det går ner
				igen! Jag visste inte mycket om aktiemarknaden i innan och jag tycker det är bra att jag
				kan bygga upp lite kunskap tack vare det användarvänliga gränssnittet i spelet.<br />
				<b>Linus E., Battleälskare</b>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Während des Börsen Battles habe ich mehrmals täglich die Entwicklung meiner Aktien
				verfolgt. Es macht einfach Spaß zu sehen, wie man sich langsam in der Community nach
				oben spielt - und es ist ganz schön nervenaufreibend, wenn es wieder runter geht! Ich
				kannte mich vorher kaum mit der Börse aus und finde es toll, dass ich dank der
				benutzerfreundlichen Oberfläche des Spiels etwas dazulerne.<br />
				<b>Linus E., Battle-Freund</b>
				</O>
			);
		},
	},

	"c3a05f3d6ca4": {
		"en": () => {
			return (
				<O>
				I find it exciting to watch the sometimes extreme stock price fluctuations and it is pretty challenging
				to strike at the right moment. I don't want to buy stocks on the stock exchange for now. So far I've
				almost always ended up in the red with the stocks I bought. But that's the good thing about the game: I
				can try trading without losing my own money. And maybe at some point I will get the hang of the real
				stock exchange!<br />
				<b>Ana S., portfolio tracker</b>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Jeg finder det spændende at følge de nogen gange ekstreme prisfluktueringer, og det er
				ret udfordrende at slå til på det rigtige tidspunkt. Jeg vil ikke købe aktier i
				virkeligheden lige nu, da jeg indtil videre mest har oplevet tab ved mine køb. Men det
				er det gode ved spillet: jeg kan handle uden at tabe mine penge. Så kan det godt være i
				fremtiden, at jeg vil begynde at handle på de rigtige børser!<br />
				<b>Ana S., porteføljetracker</b>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				Jag tycker det är spännande att titta på de ibland extrema aktiekursfluktuationer och
				det är ganska utmanande att slå till i rätt ögonblick. Jag vill inte köpa aktier på
				börsen för nu. Hittills har jag nästan alltid hamnat fel med de aktier jag köpt. Men det
				är det som är bra med spelet: Jag kan försöka handla utan att förlora mina egna pengar.
				Och kanske någon gång kommer jag att få kläm på den verkliga börsen!<br />
				<b>Ana S., portföljtracker</b>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Ich finde es spannend, die teils extremen Kursschwankungen zu beobachten und es ist eine
				Herausforderung, im richtigen Moment zuzuschlagen. Ich will erstmal keine Aktien an der
				Börse kaufen. Bisher bin ich mit meinen gekauften Aktien nämlich fast immer im Minus
				geendet. Aber das ist ja das Gute am Spiel: ich kann das Investieren ausprobieren, ohne
				gleich mein eigenes Geld zu verlieren. Und vielleicht habe ich ja irgendwann den Dreh
				für die echte Börse raus!<br />
				<b>Ana S., Portfolio-Verfolgerin</b>
				</O>
			);
		},
	},

	"2be58d6daa7a": {
		"en": () => {
			return (
				<O>
				I really enjoyed the Sommer Battle. The market was very exciting due to the pandemic, and accordingly
				the portfolio values went up and down all the time. A big thanks to the organizers and see you at the
				next Battle.<br />
				<b>Lukas H., 3rd place at Sommer Börsen Battle</b>
				</O>
			);
		},
		"da": () => {
			return (
				<O>
				Jeg nød virkelig at spille Sommer Börsen Battle. Markedet var meget interessant pga.
				pandemien, så porteføljeværdien gik op og ned hele tiden. En stor tak til
				arrangørerne &mdash; vi ses til det næste spil!<br />
				<b>Lukas H., 3.-plads i Sommer Börsen Battle</b>
				</O>
			);
		},
		"sv": () => {
			return (
				<O>
				Jag njöt verkligen av Sommer Battle. Marknaden var mycket spännande på grund av
				pandemin, och därmed gick portföljvärden upp och ner hela tiden. Ett stort tack till
				arrangörerna och vi ses vid nästa strid.<br />
				<b>Lukas H., 3:e plats i Sommer Börsen Battle</b>
				</O>
			);
		},
		"de": () => {
			return (
				<O>
				Mir hat das Sommer Battle viel Spaß gemacht. Der Markt war aufgrund der Pandemie super
				spannend, und dementsprechend gingen auch die Depotwerte stetig rauf und runter. Vielen
				Dank an die Veranstalter und bis zum nächsten Battle.<br />
				<b>Lukas H., Platz 3 im Sommer Börsen Battle</b>
				</O>
			);
		},
	},

	"5f18272d3578": {
		"_context": "American stocks",
		"en": () => <O>American</O>,
		"da": () => <O>Amerikanske</O>,
		"sv": () => <O>Amerikanska</O>,
		"de": () => <O>Amerikanisch</O>,
	},

	"ad6ee11c06c6": {
		"_context": "Danish stocks",
		"en": () => <O>Danish</O>,
		"da": () => <O>Danske</O>,
		"sv": () => <O>Danska</O>,
		"de": () => <O>Dänisch</O>,
	},

	"26824b41774a": {
		"_context": "Swedish stocks",
		"en": () => <O>Swedish</O>,
		"da": () => <O>Svenske</O>,
		"sv": () => <O>Svenska</O>,
		"de": () => <O>Swedisch</O>,
	},

	"be31d9f3cd29": {
		"_context": "Finnish stocks",
		"en": () => <O>Finnish</O>,
		"da": () => <O>Finske</O>,
		"sv": () => <O>Finska</O>,
		"de": () => <O>Finnisch</O>,
	},

	"78b80a6e2ad7": {
		"_context": "Icelandic stocks",
		"en": () => <O>Icelandic</O>,
		"da": () => <O>Islandske</O>,
		"sv": () => <O>Isländska</O>,
		"de": () => <O>Isländisch</O>,
	},

	"6f2839305280": {
		"_context": "Nordic stocks",
		"en": () => <O>Nordic</O>,
		"da": () => <O>Nordiske</O>,
		"sv": () => <O>Nordiska</O>,
		"de": () => <O>Nordisch</O>,
	},

	"211c345b1450": {
		"en": () => <O>You have no active game</O>,
		"da": () => <O>Du har ingen aktive spil</O>,
		"sv": () => <O>Du har inget aktivt spel</O>,
		"de": () => <O>Du hast kein laufendes Spiel</O>,
	},

	"fd4cd57eb478": {
		"en": (my_placement, number_of_players) => {
			if(number_of_players === 1) {
				return <O><span className="pr-2">Ranking:</span><b>{my_placement}</b> of <b>{number_of_players}</b> player.</O>;
			} else {
				return <O><span className="pr-2">Ranking:</span><b>{my_placement}</b> of <b>{number_of_players}</b> players.</O>;
			}
		},
		"da": (my_placement, number_of_players) => {
			if(number_of_players === 1) {
				return <O><span className="pr-2">Placering:</span><b>{my_placement}</b> ud af <b>{number_of_players}</b> spiller.</O>;
			} else {
				return <O><span className="pr-2">Placering:</span><b>{my_placement}</b> ud af <b>{number_of_players}</b> spillere.</O>;
			}
		},
		"sv": (my_placement, number_of_players) => {
			return <O><span className="pr-2">Placering:</span><b>{my_placement}</b> utav <b>{number_of_players}</b> spelare.</O>;
		},
		"de": (my_placement, number_of_players) => {
                        if(number_of_players === 1) {
                                return <O><span className="pr-2">Platzierung:</span><b>{my_placement}</b>. von <b>{number_of_players}</b> Spieler.</O>;
                        } else {
                                return <O><span className="pr-2">Platzierung:</span><b>{my_placement}</b>. von <b>{number_of_players}</b> Spielern.</O>;
                        }
                },
	},

	"903c47ca325c": {
		"en": () => <O>No messages yet... type a message in the field above</O>,
		"da": () => <O>Ingen beskeder... skriv en besked i feltet ovenfor</O>,
		"sv": () => <O>Inget meddelande än... skriv ett meddelande i fältet ovanför</O>,
		"de": () => <O>Noch keine Nachrichten... Gib eine Nachricht in das oben stehende Feld ein</O>,
	},

	"15f34df16795": {
		"en": () => <O>Delete message?</O>,
		"da": () => <O>Slet beskeden?</O>,
		"sv": () => <O>Radera meddelande?</O>,
		"de": () => <O>Nachricht löschen?</O>,
	},

	"443c089fc74f": {
		"en": () => <O><b>PERMANENTLY</b> delete?</O>,
		"da": () => <O>Slet <b>PERMANENT</b>?</O>,
		"sv": () => <O>Ta bort <b>PERMANENT</b>?</O>,
		"de": () => <O><b>DAUERHAFT</b> löschen?</O>,
	},

	"cd376d944b68": {
		"en": () => <O>Yes</O>,
		"da": () => <O>Ja</O>,
		"sv": () => <O>Ja</O>,
		"de": () => <O>Ja</O>,
	},

	"b7e99171873e": {
		"en": () => <O>No</O>,
		"da": () => <O>Nej</O>,
		"sv": () => <O>Nej</O>,
		"de": () => <O>Nein</O>,
	},

	"e589ef59c64b": {
		"en": () => <O>Premium challenge</O>,
		"da": () => <O>Premium dyst</O>,
		"sv": () => <O>Premium utmaning</O>,
		"de": () => <O>Premium-Herausforderung</O>,
	},

	"b3d5f25d7844": {
		"en": (playerCount) => <O>{playerCount} invitations used</O>,
		"da": (playerCount) => <O>{playerCount} invitationer brugt</O>,
		"sv": (playerCount) => <O>{playerCount} inbjudningar använda</O>,
		"de": (playerCount) => <O>{playerCount} Einladungen verwendet</O>,
	},

	"890c0798944d": {
		"en": () => <O>Older&hellip;</O>,
		"da": () => <O>Ældre&hellip;</O>,
		"sv": () => <O>Äldre&hellip;</O>,
		"de": () => <O>Älter&hellip;</O>,
	},

	"0b131a36a696": {
		"en": () => <O>Newer&hellip;</O>,
		"da": () => <O>Nyere&hellip;</O>,
		"sv": () => <O>Nyare&hellip;</O>,
		"de": () => <O>Neuer&hellip;</O>,
	},

	"ed6758617774": {
		"en": () => <O>Premium Key</O>,
		"da": () => <O>Premium Nøgle</O>,
		"sv": () => <O>Premium Nyckel</O>,
		"de": () => <O>Premium Schlüssel</O>,
	},

	"f8fc45ac5475": {
		"en": () => <O>Invitations</O>,
		"da": () => <O>Invitationer</O>,
		"sv": () => <O>Inbjudningar</O>,
		"de": () => <O>Einladungen</O>,
	},

	"fa0764437f0f": {
		"en": () => <O>Game Upgrade</O>,
		"da": () => <O>Spilopgradering</O>,
		"sv": () => <O>Speluppgradering</O>,
		"de": () => <O>Spiel-Upgrade</O>,
	},

	"b4faac273ee7": {
		"en": () => <O>Months: </O>,
		"da": () => <O>Måneder: </O>,
		"sv": () => <O>Månader: </O>,
		"de": () => <O>Monate: </O>,
	},

	"a6ee26d6462f": {
		"en": () => <O>VAT</O>,
		"da": () => <O>Moms</O>,
		"sv": () => <O>Moms</O>,
		"de": () => <O>MwSt</O>,
	},

	"6f147403ff33": {
		"en": () => <O>Total price</O>,
		"da": () => <O>Total pris</O>,
		"sv": () => <O>Totalpris</O>,
		"de": () => <O>Gesamtpreis</O>,
	},

	"4c60087018e1": {
		"en": () => <O>excl. VAT</O>,
		"da": () => <O>eks. moms</O>,
		"sv": () => <O>exkl. moms</O>,
		"de": () => <O>exkl. MwSt.</O>,
	},

	"2d6a01e441d5": {
		"en": () => <O>Close</O>,
		"da": () => <O>Luk</O>,
		"sv": () => <O>Stäng</O>,
		"de": () => <O>Schließen</O>,
	},

	"2ac3e97fab7a": {
		"en": () => <O>An e-mail with further details has been sent to: </O>,
		"da": () => <O>En e-mail med yderligere oplysninger er blevet sendt til: </O>,
		"sv": () => <O>Ett mejl med ytterligare detaljer har skickats till: </O>,
		"de": () => <O>Eine E-Mail mit weiteren Details wurde gesendet an: </O>,
	},

	"f613e82f49a2": {
		"en": () => <O>Players: </O>,
		"da": () => <O>Spillere: </O>,
		"sv": () => <O>Spelare: </O>,
		"de": () => <O>Spieler: </O>,
	},

	"d3bd83070521": {
		"_deleted": true, // FIXME translation has no references; delete this entire section if you no longer need it
		"en": () => <O>Players</O>,
		"da": () => <O>Spillere</O>,
		"sv": () => <O>Spelare</O>,
		"de": () => <O>Spieler</O>,
	},

	"162a37ddce1c": {
		"en": () => <O>Months</O>,
		"da": () => <O>Måneder</O>,
		"sv": () => <O>Månader</O>,
		"de": () => <O>Monate</O>,
	},

	"b4976e25e0c9": {
		"en": () => <O>Start Date</O>,
		"da": () => <O>Startdato</O>,
		"sv": () => <O>Startdatum</O>,
		"de": () => <O>Startdatum</O>,
	},

	"f97aa83bcd1a": {
		"en": () => <O>Premium valid until</O>,
		"da": () => <O>Premium gældende til</O>,
		"sv": () => <O>Premium giltigt till</O>,
		"de": () => <O>Premium gültig bis</O>,
	},

	"137a0e113c03": {
		"en": () => <O>Period</O>,
		"da": () => <O>Periode</O>,
		"sv": () => <O>Period</O>,
		"de": () => <O>Zeitraum</O>,
	},

	"2a299b14bd90": {
		"en": () => <O>Discount</O>,
		"da": () => <O>Rabat</O>,
		"sv": () => <O>Rabatt</O>,
		"de": () => <O>Rabatt</O>,
	},

	"d02d1c448f18": {
		"en": () => <O>Per user per month</O>,
		"da": () => <O>Pr. bruger pr. måned</O>,
		"sv": () => <O>Per användare per månad</O>,
		"de": () => <O>Pro Nutzer pro Monat</O>,
	},

	"157bb1df97c1": {
		"en": () => <O>Premium valid until:</O>,
		"da": () => <O>Premium gældende til:</O>,
		"sv": () => <O>Premium giltigt till:</O>,
		"de": () => <O>Premium gültig bis:</O>,
	},

	"0b4e93ade1fe": {
		"en": () => <O>excl. VAT)</O>,
		"da": () => <O>eks. moms)</O>,
		"sv": () => <O>exkl. moms)</O>,
		"de": () => <O>exkl. MwSt.)</O>,
	},

	"221241a32a2d": {
		"en": () => <O>Edit</O>,
		"da": () => <O>Rediger</O>,
		"sv": () => <O>Redigera</O>,
		"de": () => <O>Bearbeiten</O>,
	},

	"8f69f575ecec": {
		"en": () => <O>Remove</O>,
		"da": () => <O>Fjern</O>,
		"sv": () => <O>Ta bort</O>,
		"de": () => <O>Entfernen</O>,
	},

	"867a53b8e715": {
		"en": () => <O>You have not added any products to the cart yet.</O>,
		"da": () => <O>Du har endnu ikke tilføjet nogen produkter til kurven.</O>,
		"sv": () => <O>Du har ännu inte lagt till några produkter i varukorgen.</O>,
		"de": () => <O>Sie haben noch keine Produkte zum Warenkorb hinzugefügt.</O>,
	},

	"18e2d6bdfb1b": {
		"en": () => <O>Price:</O>,
		"da": () => <O>Pris:</O>,
		"sv": () => <O>Pris:</O>,
		"de": () => <O>Preis:</O>,
	},

	"cde91ccd93bc": {
		"en": () => <O>Back</O>,
		"da": () => <O>Tilbage</O>,
		"sv": () => <O>Tillbaka</O>,
		"de": () => <O>Zurück</O>,
	},

	"a411dff1b68e": {
		"en": () => <O>Next</O>,
		"da": () => <O>Næste</O>,
		"sv": () => <O>Nästa</O>,
		"de": () => <O>Weiter</O>,
	},

	"15fec154b8eb": {
		"en": () => <O>Your Cart</O>,
		"da": () => <O>Din kurv</O>,
		"sv": () => <O>Din kundvagn</O>,
		"de": () => <O>Ihr Warenkorb</O>,
	},

	"d976365de3d1": {
		"en": () => <O>Checkout</O>,
		"da": () => <O>Til kassen</O>,
		"sv": () => <O>Kassan</O>,
		"de": () => <O>Zur Kasse</O>,
	},

	"a9d733a1361d": {
		"en": () => <O>Compose your post here...</O>,
		"da": () => <O>Skriv dit indlæg her...</O>,
		"sv": () => <O>Skriv ditt inlägg här...</O>,
		"de": () => <O>Schreibe deinen Kommentar hier...</O>,
	},

	"8c2c8069bb11": {
		"en": () => <O>Share your experiences</O>,
		"da": () => <O>Del dine erfaringer</O>,
		"sv": () => <O>Dela dina erfarenheter</O>,
		"de": () => <O>Teile deine Erfahrungen</O>,
	},

	"19f158fc46c7": {
		"en": (site_base) => {
			return (
				<O>This forum is for sharing experiences/considerations
				with other players in order to learn and get inspired. Do you
				need support, please send an email to info@{site_base}.
				Furthermore, we would like to stress that obscene language,
				condescending behaviour, spam, etc. will result in message and
				user account <strong>deletion without warning</strong>.</O>
			);
		},
		"da": (site_base) => {
			return (
				<O>
				Dette forum er til at dele erfaringer/overvejelser med andre spillere, så man kan
				lære og blive inspireret af hinanden. Har du brug for support, skriv da venligst til
				info@{site_base}. Ydermere gør vi opmærksom på at groft sprogbrug, nedladende adfærd,
				spam, m.m., uden varsel vil medføre <strong>sletning af indlægget og brugerkontoen</strong>.
				</O>
			);
		},
		"sv": (site_base) => {
			return (
				<O>
				Det här forumet är till för att dela erfarenheter/överväganden med andra spelare, så att
				ni kan lära er och inspireras av varandra. Om du behöver stöd, skriv till
				info@{site_base}. Dessutom uppmärksammar vi dig på det faktum att grovt språk,
				nedlåtande beteende, spam, etc. kommer utan förvarning att resultera i <strong>radering av
				inlägget och användarkontot</strong>.
				</O>
			);
		},
		"de": (site_base) => {
			return (
				<O>
				Dieser Spielchat dient dazu, Erfahrungen und Überlegungen mit anderen Spielern zu
				teilen, damit ihr voneinander lernen und euch inspirieren lassen könnt. Wenn du Hilfe
				benötigst, schreibe bitte direkt an info@{site_base}. Wir weisen darauf hin, dass
				negative Sprache, herablassendes Verhalten, Spam etc. ohne Vorankündigung zur <strong>Löschung
				des Beitrags und des Benutzerkontos</strong> führen.
				</O>
			);
		},
	},

	"b578611bc275": {
		"en": () => <O>Add more invitations to your game</O>,
		"da": () => <O>Tilføj flere invitationer til dit spil</O>,
		"sv": () => <O>Lägg till fler inbjudningar till ditt spel</O>,
		"de": () => <O>Fügen Sie Ihrem Spiel weitere Einladungen hinzu</O>,
	},

	"236591edac9c": {
		"en": () => <O>Open to all assets</O>,
		"da": () => <O>Åben for alle aktiver</O>,
		"sv": () => <O>Öppet för alla tillgångar</O>,
		"de": () => <O>Offen für alle Vermögenswerte</O>,
	},

	"c3e44c050ccc": {
		"en": () => <O>Option for more than 30 participants</O>,
		"da": () => <O>Mulighed for mere end 30 deltagere</O>,
		"sv": () => <O>Option for more than 30 participants</O>,
		"de": () => <O>Option for more than 30 participants</O>,
	},

	"935d1a0ec3cd": {
		"en": () => <O>Setups</O>,
		"da": () => <O>Opsætninger</O>,
		"sv": () => <O>Inställningar</O>,
		"de": () => <O>Einstellungen</O>,
	},

	"77bb55ea9b5c": {
		"en": () => <O>Option to play for up to 365 days</O>,
		"da": () => <O>Mulighed for spil på op til 365 dage</O>,
		"sv": () => <O>Möjlighet att spela i upp till 365 dagar</O>,
		"de": () => <O>Möglichkeit, bis zu 365 Tage zu spielen</O>,
	},

	"dea2b42bc96d": {
		"en": () => <O>Option for up to 30 participants in your games</O>,
		"da": () => <O>Mulighed for op til 30 deltagere på dine spil</O>,
		"sv": () => <O>Möjlighet till upp till 30 deltagare i dina spel</O>,
		"de": () => <O>Möglichkeit für bis zu 30 Teilnehmer in deinen Spielen</O>,
	},

	"9c2dc9e9f27c": {
		"en": () => <O>Choose your starting capital</O>,
		"da": () => <O>Vælg selv startkapital</O>,
		"sv": () => <O>Välj din startkapital</O>,
		"de": () => <O>Wähle dein Startkapital</O>,
	},

	"df5ba157fd54": {
		"en": () => <O>Reflect your trading volume</O>,
		"da": () => <O>Reflektér handelsvolumen</O>,
		"sv": () => <O>Spegla din handelsvolym</O>,
		"de": () => <O>Spiegle dein Handelsvolumen wider</O>,
	},

	"caf1758537de": {
		"en": () => <O>Maximum amount per security</O>,
		"da": () => <O>Max beløb i ét værdipapir</O>,
		"sv": () => <O>Maxbelopp per värdepapper</O>,
		"de": () => <O>Höchstbetrag pro Wertpapier</O>,
	},

	"0dcfaeb496c1": {
		"en": () => <O>Commission fees</O>,
		"da": () => <O>Kurtage</O>,
		"sv": () => <O>Courtageavgifter</O>,
		"de": () => <O>Provisionen</O>,
	},

	"ab13df53db06": {
		"en": () => <O>Investment opportunities</O>,
		"da": () => <O>Investeringsmuligheder</O>,
		"sv": () => <O>Investeringsmöjligheter</O>,
		"de": () => <O>Anlagemöglichkeiten</O>,
	},

	"376654deda2d": {
		"en": () => <O>Trading on the Danish market</O>,
		"da": () => <O>Handel på det danske marked</O>,
		"sv": () => <O>Handel på den danska marknaden</O>,
		"de": () => <O>Handel auf dem dänischen Markt</O>,
	},

	"c8e5de33d55e": {
		"en": () => <O>Support & flexibility</O>,
		"da": () => <O>Support & fleksibilitet</O>,
		"sv": () => <O>Support och flexibilitet</O>,
		"de": () => <O>Support und Flexibilität</O>,
	},

	"ea606bde5b02": {
		"en": () => <O>Email support</O>,
		"da": () => <O>Mail support</O>,
		"sv": () => <O>E-postsupport</O>,
		"de": () => <O>E-Mail-Support</O>,
	},

	"e1f8f8018a3d": {
		"en": () => <O>No binding commitment</O>,
		"da": () => <O>Ingen binding</O>,
		"sv": () => <O>Ingen bindning</O>,
		"de": () => <O>Keine vertragliche Bindung</O>,
	},

	"5bfd53fd65b6": {
		"en": () => <O>Expanded setups</O>,
		"da": () => <O>Udvidet opsætninger</O>,
		"sv": () => <O>Utökade inställningar</O>,
		"de": () => <O>Erweiterte Einstellungen</O>,
	},

	"617b1f027551": {
		"en": () => <O>Option for more than 30 participants in your games</O>,
		"da": () => <O>Mulighed for mere end 30 deltagere på dine spil</O>,
		"sv": () => <O>Möjlighet för fler än 30 deltagare i dina spel</O>,
		"de": () => <O>Möglichkeit für mehr als 30 Teilnehmer in deinen Spielen</O>,
	},

	"31543933ae92": {
		"en": () => <O>Option to compete on risk-weighted returns</O>,
		"da": () => <O>Mulighed for at konkurrere på risikovægtet afkast</O>,
		"sv": () => <O>Möjlighet att tävla på riskjusterad avkastning</O>,
		"de": () => <O>Möglichkeit, sich auf risikogewichtete Renditen zu messen</O>,
	},

	"24dc40f20b98": {
		"en": () => <O>Information and financial statements on assets</O>,
		"da": () => <O>Informationer og regnskabstal på aktiver</O>,
		"sv": () => <O>Information och finansiella rapporter om tillgångar</O>,
		"de": () => <O>Informationen und Finanzberichte zu Vermögenswerten</O>,
	},

	"dbf808dc753f": {
		"en": () => <O>Option to set a trading limit for participants</O>,
		"da": () => <O>Mulighed for handels grænse på deltagere</O>,
		"sv": () => <O>Möjlighet att sätta en handelsgräns för deltagare</O>,
		"de": () => <O>Möglichkeit, ein Handelslimit für Teilnehmer festzulegen</O>,
	},

	"4c998443a86c": {
		"en": () => <O>Expanded Investment Opportunities</O>,
		"da": () => <O>Udvidet investeringsmuligheder</O>,
		"sv": () => <O>Utökade investeringsmöjligheter</O>,
		"de": () => <O>Erweiterte Investitionsmöglichkeiten</O>,
	},

	"da5c424aba99": {
		"en": () => <O>Trading in all countries – DK, SE, DE, US, ISL, FIN</O>,
		"da": () => <O>Handel i alle lande - DK, SE, DE, US, ISL, FIN</O>,
		"sv": () => <O>Handel i alla länder – DK, SE, DE, US, ISL, FIN</O>,
		"de": () => <O>Handel in allen Ländern – DK, SE, DE, US, ISL, FIN</O>,
	},

	"56f9268a523c": {
		"en": () => <O>Trading in all countries - DK, SE, DE, US, ISL, FIN</O>,
		"da": () => <O>Handel i alle lande - DK, SE, DE, US, ISL, FIN</O>,
		"sv": () => <O>Handel i alla länder - DK, SE, DE, US, ISL, FIN</O>,
		"de": () => <O>Handel in allen Ländern - DK, SE, DE, US, ISL, FIN</O>,
	},

	"2b99274c115c": {
		"en": () => <O>Option for games with ETFs, funds, crypto, bonds, and commodities</O>,
		"da": () => <O>Mulighed for spil med ETF'er, fonde, krypto, obligationer og råvarer</O>,
		"sv": () => <O>Alternativ för spel med ETF:er, fonder, krypto, obligationer och råvaror</O>,
		"de": () => <O>Option für Spiele mit ETFs, Fonds, Krypto, Anleihen und Rohstoffen</O>,
	},

	"bb96cbe488ff": {
		"en": () => <O>User Management</O>,
		"da": () => <O>Brugeradministration</O>,
		"sv": () => <O>Användarhantering</O>,
		"de": () => <O>Benutzerverwaltung</O>,
	},

	"727eb3b779c7": {
		"en": () => <O>Remove unwanted participants from your contest</O>,
		"da": () => <O>Fjern uønskede deltagere fra din dyst</O>,
		"sv": () => <O>Ta bort oönskade deltagare från din tävling</O>,
		"de": () => <O>Entferne unerwünschte Teilnehmer aus deinem Wettbewerb</O>,
	},

	"279a19462a59": {
		"en": () => <O>Disburse or withdraw capital from participants</O>,
		"da": () => <O>Udbetal eller fratag kapital fra deltagere</O>,
		"sv": () => <O>Betala ut eller dra in kapital från deltagare</O>,
		"de": () => <O>Kapital an Teilnehmer auszahlen oder entziehen</O>,
	},

	"b9c952d2fe76": {
		"en": () => <O>Close registrations at the deadline</O>,
		"da": () => <O>Luk for tilmeldinger ved indsættelse af frist</O>,
		"sv": () => <O>Stäng registreringar vid sista anmälningsdag</O>,
		"de": () => <O>Schließe die Registrierung zum Anmeldeschluss</O>,
	},

	"45810142c92f": {
		"en": () => <O>Option for students to log in without an email (available only for primary schools)</O>,
		"da": () => <O>Mulighed for elever kan logge ind uden e-mail (udbydes kun til grundskoler)</O>,
		"sv": () => <O>Möjlighet för elever att logga in utan e-post (endast tillgängligt för grundskolor)</O>,
		"de": () => <O>Möglichkeit für Schüler, sich ohne E-Mail anzumelden (nur für Grundschulen verfügbar)</O>,
		"de": () => <O>Option for students to log in without an email (available only for primary schools)</O>,
	},

	"c641b20bf774": {
		"en": () => <O>Support & Flexibility</O>,
		"da": () => <O>Support & fleksibilitet</O>,
		"sv": () => <O>Support och flexibilitet</O>,
		"de": () => <O>Support und Flexibilität</O>,
	},

	"34e1b3997152": {
		"en": () => <O>Manage your participants</O>,
		"da": () => <O>Administrer dine deltagere</O>,
		"sv": () => <O>Hantera dina deltagare</O>,
		"de": () => <O>Verwalten Sie Ihre Teilnehmer</O>,
	},

	"e6ca47a6cecd": {
		"en": () => <O>/ per participant / per month</O>,
		"da": () => <O>/ pr. deltager / pr. måned</O>,
		"sv": () => <O>per deltagare för hela perioden</O>,
		"de": () => <O>pro Teilnehmer für den gesamten Zeitraum</O>,
	},

	"6ffc03aadd9c": {
		"en": () => <O>per participant for the entire period</O>,
		"da": () => <O>pr. deltager for hele periodend</O>,
		"sv": () => <O>per deltagare för hela perioden</O>,
		"de": () => <O>pro Teilnehmer für den gesamten Zeitraum</O>,
	},

	"6e2f84e3d8d3": {
		"en": () => <O>Upgrade my game</O>,
		"da": () => <O>Opgrader mit spil</O>,
		"sv": () => <O>Uppgradera mitt spel</O>,
		"de": () => <O>Mein Spiel upgraden</O>,
	},

	"44d8d6a8e1fb": {
		"en": () => <O>All prices are including VAT</O>,
		"da": () => <O>Alle priser er inkl. moms</O>,
		"sv": () => <O>Alla priser inkluderar moms</O>,
		"de": () => <O>Alle Preise sind inklusive MwSt.</O>,
	},

	"edf559859ed6": {
		"en": () => <O>When you have completed the order, you will automatically gain access to more invitations</O>,
		"da": () => <O>Når du har gennemført bestillingen, får du automatisk adgang til flere invitationer</O>,
		"sv": () => <O>När du har slutfört beställningen får du automatiskt tillgång till fler inbjudningar</O>,
		"de": () => <O>Nach Abschluss der Bestellung erhalten Sie automatisch Zugriff auf weitere Einladungen</O>,
	},

	"1ec385aac3a8": {
		"en": (portfolioUpgrades) => <O>You have {portfolioUpgrades.RemainingProInvites} invitations left</O>,
		"da": (portfolioUpgrades) => <O>Du har {portfolioUpgrades.RemainingProInvites} invitationer tilbage</O>,
		"sv": (portfolioUpgrades) => <O>Du har {portfolioUpgrades.RemainingProInvites} inbjudningar kvar</O>,
		"de": (portfolioUpgrades) => <O>Sie haben {portfolioUpgrades.RemainingProInvites} Einladungen übrig</O>,
	},

	"86d743e7b8f7": {
		"en": () => <O>Add invitations</O>,
		"da": () => <O>Tilføj invitationer</O>,
		"sv": () => <O>Lägg till inbjudningar</O>,
		"de": () => <O>Einladungen hinzufügen</O>,
	},

	"c1cc8686376f": {
		"en": () => <O>Upgrade this game to premium</O>,
		"da": () => <O>Opgrader dette spil til premium</O>,
		"sv": () => <O>Uppgradera detta spel till premium</O>,
		"de": () => <O>Dieses Spiel auf Premium upgraden</O>,
	},

	"97bf259b3fef": {
		"en": (ActionLink, goto_new_game) => {
			return (
				<O>
				<div>
				You're not currently playing any game where this stock can be traded.
				</div>
				<div className="mt-2">
				<ActionLink on_click={goto_new_game} className="ad_new_game_button ad_rbtn_25px py-2 px-5">Start a game</ActionLink>
				</div>
				</O>
			);
		},
		"da": (ActionLink, goto_new_game) => {
			return (
				<O>
				<div>
				Du deltager ikke i et spil hvor dette værdipapir kan handles.
				</div>
				<div className="mt-2">
				<ActionLink on_click={goto_new_game} className="ad_new_game_button ad_rbtn_25px py-2 px-5">Start et spil</ActionLink>
				</div>
				</O>
			);
		},
		"sv": (ActionLink, goto_new_game) => {
                        return (
                                <O>
                                <div>
                                Du är för närvarande inte med i något spel där detta värdepapper kan handlas med.
                                </div>
                                <div className="mt-2">
                                <ActionLink on_click={goto_new_game} className="ad_new_game_button ad_rbtn_25px py-2 px-5">Starta ett spel</ActionLink>
                                </div>
                                </O>
                        );
		},
		"de": (ActionLink, goto_new_game) => {
                        return (
                                <O>
                                <div>
                                Du spielst derzeit kein Spiel, in dem diese Aktie gehandelt werden kann.
                                </div>
                                <div className="mt-2">
                                <ActionLink on_click={goto_new_game} className="ad_new_game_button ad_rbtn_25px py-2 px-5">Ein Spiel starten</ActionLink>
                                </div>
                                </O>
                        );
                },
	},

	"48d6c4904efd": {
		"_context": "Ticker page trade button",
		"en": () => <O>Trade</O>,
		"da": () => <O>Køb / Salg</O>,
		"sv": () => <O>Köp / Sälj</O>,
		"de": () => <O>Handel</O>,
	},

	"9020e36b162a": {
		"en": () => <O>Game participants</O>,
		"da": () => <O>Spildeltagere</O>,
		"sv": () => <O>Speldeltagare</O>,
		"de": () => <O>Spielteilnehmer</O>,
	},

	"11ec42abe1ed": {
		"en": (my_placement) => <O>Your ranking: {my_placement}</O>,
		"da": (my_placement) => <O>Din placering: {my_placement}</O>,
		"sv": (my_placement) => <O>Din placering: {my_placement}</O>,
		"de": (my_placement) => <O>Deine Platzierung: {my_placement}</O>,
	},

	"9e30437de578": {
		"en": () => <O>stock</O>,
		"da": () => <O>aktier</O>,
		"sv": () => <O>aktier</O>,
		"de": () => <O>Aktien</O>,
	},

	"3f681db67ca0": {
		"en": () => <O>Crypto</O>,
		"da": () => <O>Krypto</O>,
		"sv": () => <O>Krypto</O>,
		"de": () => <O>Krypto</O>,
	},

	"ca80865928e8": {
		"en": (props) => <O><div title="The distribution of equities across all players in the game.">{props.children}</div></O>,
		"da": (props) => <O><div title="Fordelingen af værdipapirer på tværs af alle spillere.">{props.children}</div></O>,
		"sv": (props) => <O><div title="Fördelningen av värdepapper över alla spelare i spelet.">{props.children}</div></O>,
		"de": (props) => <O><div title="Die Verteilung der Aktien auf alle Spieler im Spiel.">{props.children}</div></O>,
	},

	"633118f0851a": {
		"en": () => <O>Game equities</O>,
		"da": () => <O>Værdipapirer i spillet</O>,
		"sv": () => <O>Värdepapper i spelet</O>,
		"de": () => <O>Anlagen im Spiel</O>,
	},

	"ece50733525c": {
		"en": () => <O>Commodities</O>,
		"da": () => <O>Råvarer</O>,
		"sv": () => <O>Råvaror</O>,
		"de": () => <O>Rohstoffe</O>,
	},

	"8c32934e1754": {
		"en": () => <O>(metals, oil, etc.)</O>,
		"da": () => <O>(metaller, olie, etc.)</O>,
		"sv": () => <O>(metaller, olja, etc.)</O>,
		"de": () => <O>(Metalle, Öl, etc.)</O>,
	},

	"9b45561baf98": {
		"en": () => <O>Cash</O>,
		"da": () => <O>Kontanter</O>,
		"sv": () => <O>Kontanter</O>,
		"de": () => <O>Geld</O>,
	},

	"fdd6f5e24418": {
		"en": () => <O>Currencies</O>,
		"da": () => <O>Valuta</O>,
		"sv": () => <O>Valuta</O>,
		"de": () => <O>Währungen</O>,
	},

	"44a3aaa8fb26": {
		"en": () => <O>Explore our supported currencies</O>,
		"da": () => <O>Udforsk vores understøttede valutaer</O>,
		"sv": () => <O>Utforska de valutor vi stödjer</O>,
		"de": () => <O>Entdecke unsere unterstützten Währungen</O>,
	},

	"56615d1deab1": {
		"en": () => <O>Today</O>,
		"da": () => <O>I dag</O>,
		"sv": () => <O>I dag</O>,
		"de": () => <O>Heute</O>,
	},

	"52e193e1c6f4": {
		"en": () => <O>7 days</O>,
		"da": () => <O>7 dage</O>,
		"sv": () => <O>7 dagar</O>,
		"de": () => <O>7 Tage</O>,
	},

	"04e8f1e79df6": {
		"en": () => <O>30 days</O>,
		"da": () => <O>30 dage</O>,
		"sv": () => <O>30 dagar</O>,
		"de": () => <O>30 Tage</O>,
	},

	"bd2eae48ebce": {
		"en": () => <O>Start a game</O>,
		"da": () => <O>Start et spil</O>,
		"sv": () => <O>Starta ett spel</O>,
		"de": () => <O>Ein Spiel starten</O>,
	},

	"07ea90cff707": {
		"en": () => <O> premium invitations remaining</O>,
		"da": () => <O> premium invitationer tilbage</O>,
		"sv": () => <O> premium inbjudningar kvar</O>,
		"de": () => <O> Premium-Einladungen verbleibend</O>,
	},

	"1b464d29da2a": {
		"en": () => <O>Premium invitations</O>,
		"da": () => <O>Premium invitationer</O>,
		"sv": () => <O>Premium inbjudningar</O>,
		"de": () => <O>Premium-Einladungen</O>,
	},

	"adc400952aed": {
		"en": () => <O>An invitation is not personal. That means that when one of your games is finished and you are still an active premium user, you can use the invitation again for your next game.</O>,
		"da": () => <O>En invitation er ikke personlig. Det betyder at når et af dine spil er slut og du stadig er aktiv premium, så kan du bruge invitationen igen til dit næste spil.</O>,
		"sv": () => <O>En inbjudan är inte personlig. Det betyder att när ett av dina spel är slut och du fortfarande är en aktiv premiumanvändare, kan du använda inbjudan igen till ditt nästa spel.</O>,
		"de": () => <O>Eine Einladung ist nicht persönlich. Das bedeutet, dass wenn eines deiner Spiele vorbei ist und du weiterhin ein aktiver Premium-Nutzer bist, du die Einladung erneut für dein nächstes Spiel verwenden kannst.</O>,
	},

	"263c8690d4ec": {
		"en": (A) => {
			return (
				<O>
				Click the <A href="/new_game">New Game</A> button on the game page (or one of the other buttons on the site that
				starts a game). This will start a new investment game with the markets, fees, timeframes,
				etc. that suit your preferences. You can visit our <A href="/equity-abc">Investment ABC</A> for guidance, terms, etc.
				</O>
			);
		},
		"da": (A) => {
			return (
				<O>
				Klik på <A href="/new_game">Nyt spil</A>-knappen på spilsiden (eller én af de andre knapper på
				sitet, der starter et spil). Så starter du et nyt investeringsspil med de markeder, gebyrer,
				tidsrammer, m.m., der falder i din smag. Hvis du har nogle spørgsmål ift. investeringsbegreber kan
				du med fordel frekventere vores <A href="/equity-abc">Investerings-ABC</A>.
				</O>
			);
		},
		"sv": (A) => {
			return (
				<O>
				Klicka på knappen <A href="/new_game">Nytt spel</A> på spelsidan (eller någon av de andra
				knapparna på webbplatsen som startar ett spel). Detta kommer att starta ett nytt investeringsspel
				med de marknader, avgifter, tidsramar osv. som du föredrar. Om du har några frågor om
				investeringsbegrepp kan du besöka vår <A href="/equity-abc">Investerings-ABC</A>.
				</O>
			);
		},
		"de": (A) => {
			return (
				<O>
				Klicke auf der Spielseite auf den Button <A href="/new_game">Neues Spiel</A> (oder einen der
				anderen Buttons, die ein Spiel starten). So startest du ein neues Börsenspiel mit den Märkten,
				Gebühren, der Spiellänge usw., die zu dir passen. Wenn du Fragen zu Börsenbegriffen hast, kannst
				du unser <A href="/equity-abc">Börsenlexikon</A> besuchen.
				</O>
			);
		},
	},

	"482367b9251b": {
		"en": () => <O>Ended</O>,
		"da": () => <O>Afsluttede</O>,
		"sv": () => <O>Avslutade</O>,
		"de": () => <O>Beendet</O>,
	},

	"7bb335235c6c": {
		"en": () => <O>Create my first game</O>,
		"da": () => <O>Opret mit første spil</O>,
		"sv": () => <O>Skapa mitt första spel</O>,
		"de": () => <O>Mein erstes Spiel erstellen</O>,
	},

	"b37bc81cfe4d": {
		"en": () => <O>Start a new game</O>,
		"da": () => <O>Start et nyt spil</O>,
		"sv": () => <O>Starta ett nytt spel</O>,
		"de": () => <O>Ein neues Spiel starten</O>,
	},

	"d71d371a51fd": {
		"en": () => <O>You have no active games</O>,
		"da": () => <O>Du har ingen aktive spil</O>,
		"sv": () => <O>Du har inga aktiva spel</O>,
		"de": () => <O>Du hast keine laufenden Spiele</O>,
	},

	"25743130f3af": {
		"en": () => <O>Start a new game. Invite and compete against your friends.</O>,
		"da": () => <O>Start et nyt spil. Invitér og dyst imod dine venner.</O>,
		"sv": () => <O>Starta ett nytt spel. Bjud in och utmana dina vänner.</O>,
		"de": () => <O>Starte ein neues Spiel, lade deine Freunde ein und tretet gegeneinander an.</O>,
	},

	"1f06a5adcc9e": {
		"en": () => <O>Ongoing</O>,
		"da": () => <O>Igangværende</O>,
		"sv": () => <O>Pågående</O>,
		"de": () => <O>Laufend</O>,
	},

	"db1d32db0998": {
		"en": () => <O>Your games</O>,
		"da": () => <O>Dine spil</O>,
		"sv": () => <O>Dina spel</O>,
		"de": () => <O>Deine Spiele</O>,
	},

	"642e7043b8eb": {
		"en": () => <O>New game</O>,
		"da": () => <O>Nyt spil</O>,
		"sv": () => <O>Nytt spel</O>,
		"de": () => <O>Neues Spiel</O>,
	},

	"5db3e3e9f8d1": {
		"en": () => <O>Boomers</O>,
		"da": () => <O>Højdespringere</O>,
		"sv": () => <O>Höjdare</O>,
		"de": () => <O>Stärkste Anlagen</O>,
	},

	"6715fe73fa0c": {
		"en": () => <O>Welcome</O>,
		"da": () => <O>Velkommen</O>,
		"sv": () => <O>Välkommen</O>,
		"de": () => <O>Willkommen</O>,
	},

	"7e5d2ab0356c": {
		"_context": "Change game",
		"en": () => <O>Change</O>,
		"da": () => <O>Skift</O>,
		"sv": () => <O>Byta</O>,
		"de": () => <O>Wechseln</O>,
	},

	"79bfdefea6f1": {
		"_context": "Edit game",
		"en": () => <O>Edit</O>,
		"da": () => <O>Rediger</O>,
		"sv": () => <O>Redigera</O>,
		"de": () => <O>Bearbeiten</O>,
	},

	"2989725ec608": {
		"en": () => <O>TRADE COUNT:</O>,
		"da": () => <O>ANTAL HANDLER:</O>,
		"sv": () => <O>ANTAL HANDLINGAR:</O>,
		"de": () => <O>ANZAHL DER HANDEL:</O>,
	},

	"b3fd23d333a0": {
		"en": () => <O>TOTAL TRADE SUM:</O>,
		"da": () => <O>SAMLET HANDELSSUM:</O>,
		"sv": () => <O>TOTAL HANDELSSUMMA:</O>,
		"de": () => <O>TOTALE HANDELSSUMME:</O>,
	},

	"dabd92cc5e00": {
		"en": () => <O>MOST TRADED:</O>,
		"da": () => <O>MEST HANDLET:</O>,
		"sv": () => <O>MEST HANDLAT:</O>,
		"de": () => <O>AM MEISTEN GEHANDELT:</O>,
	},

	"f33f0bbd6374": {
		"en": () => <O>Enter your email</O>,
		"da": () => <O>Indtast din email</O>,
		"sv": () => <O>Ange din email</O>,
		"de": () => <O>E-Mail-Adresse eingeben</O>,
	},

	"7060a3117495": {
		"en": () => <O>We need your email address</O>,
		"da": () => <O>Vi behøver din emailadresse</O>,
		"sv": () => <O>Vi behöver din emailadress</O>,
		"de": () => <O>Wir benötigen Ihre E-Mail-Adresse</O>,
	},

	"44742eb7c56d": {
		"en": (A) => {
			return (
				<O>
				To be eligible for winning a prize we need your email address. See <A href="/termsofuse"
				alt="termslink">terms</A> for more.
				</O>
			);
		},
		"da": (A) => {
			return (
				<O>
				For at kunne vinde en præmie skal vi bruge din emailadresse. Se <A href="/termsofuse"
				alt="termslink">brugsbetingelserne</A>.
				</O>
			);
		},
		"sv": (A) => {
                        return (
                                <O>
                                För att kunna vinna ett pris behöver vi din emailadress. Se <A href="/termsofuse"
                                alt="termslink">Upphovsrätt</A>.
                                </O>
                        );
		},
		"de": (A) => {
                        return (
                                <O>
                                Um den Preis gewinnen zu können, benötigen wir deine E-Mail-Adresse. In den
				<A href="/termsofuse" alt="termslink">Geschäftsbedingungen</A> kannst du mehr darüber lesen.
                                </O>
                        );
                },
	},

	"1d89ad10b0e8": {
		"en": () => <O>Markets:</O>,
		"da": () => <O>Markeder:</O>,
		"sv": () => <O>Marknader:</O>,
		"de": () => <O>Märkte:</O>,
	},

	"22cf9d598b83": {
		"en": () => <O>Ends:</O>,
		"da": () => <O>Slutter:</O>,
		"sv": () => <O>Slutar:</O>,
		"de": () => <O>Endet:</O>,
	},

	"df706622be73": {
		"en": () => <O>Active game:</O>,
		"da": () => <O>Valgt spil:</O>,
		"sv": () => <O>Aktivt spel:</O>,
		"de": () => <O>Laufendes Spiel:</O>,
	},

	"a91443fa26db": {
		"en": () => <O>Compete and Win</O>,
		"da": () => <O>Konkurrér og vind</O>,
		"sv": () => <O>Tävla och vinn</O>,
		"de": () => <O>Teilnehmen und gewinnen</O>,
	},

	"b761ceec3a4f": {
		"en": () => <O>Create a game, invite your friends and see who's the best investor</O>,
		"da": () => <O>Dyst mod venner, familie, kollegaer eller studiekammerater: hvem er den bedste investor?</O>,
		"sv": () => <O>Skapa ett spel, bjud in dina vänner och ta reda på vem som är den bästa investeraren</O>,
		"de": () => <O>Spiele gegen Freunde, Familie und Kollegen: Wer wird der nächste Börsenexperte?</O>,
	},

	"3155694427be": {
		"en": () => <O>Create Portfolios</O>,
		"da": () => <O>Opret porteføljer</O>,
		"sv": () => <O>Skapa portföljer</O>,
		"de": () => <O>Portfolios erstellen</O>,
	},

	"f537a8ef429e": {
		"en": () => <O>Create portfolios to track stocks and see how a potential investment would pan out</O>,
		"da": () => <O>Hold øje med udvalgte aktier og interessefelter i din personlige portefølje</O>,
		"sv": () => <O>Skapa portföljer för att hålla reda på dina aktier och hur det går med dina investeringar</O>,
		"de": () => <O>Erstelle Portfolios um Aktien zu verfolgen und zu sehen, wie sich potenzielle Investments entwickeln würden</O>,
	},

	"4c6c546e0bc9": {
		"en": () => <O>Play and Learn</O>,
		"da": () => <O>Spil og lær</O>,
		"sv": () => <O>Spela och lär</O>,
		"de": () => <O>Spielen und lernen</O>,
	},

	"5abcaace7652": {
		"en": () => <O>Learn to invest by trying it out as it would be in real life investing</O>,
		"da": () => <O>Masser af indsigt helt uden tab: investér i virkelige aktier med fiktive midler</O>,
		"sv": () => <O>Lär dig investera genom att testa på det som att det vore riktigt investeringar</O>,
		"de": () => <O>Lerne zu investieren, indem du es einfach ausprobierst: reale Aktien und Kurse, aber fiktive Mittel und Investitionen</O>,
	},

	"7507bf3fba63": {
		"en": () => <O>Thousands of companies you can invest in</O>,
		"da": () => <O>Tusinder af firmaer du kan investere i</O>,
		"sv": () => <O>Tusentals företag du kan investera i</O>,
		"de": () => <O>Tausende Unternehmen, in die du investieren kannst</O>,
	},

	"703531b7f520": {
		"en": (A, site_name) => {
			return (
				<O>
					At {site_name} you can invest in <A href='/explore-us'>American</A>, <A href='/explore-xetra'>German
					</A>, <A href='explore-cph'>Danish</A>, <A href='explore-sto'>Swedish</A>, <A href='/explore-hel'>
					Finnish</A> stocks and more.
				</O>
			);
		},
		"da": (A, site_name) => {
			return (
				<O>
					Hos {site_name} kan du investere i <A href='/explore-cph'>danske</A>, <A href='/explore-us'>amerikanske
					</A>, <A href='explore-xetra'>tyske</A>, <A href='explore-sto'>svenske</A>, <A href='/explore-hel'>
					finske</A> aktier, med mere.
				</O>
			);
		},
		"sv": (A, site_name) => {
			return (
				<O>
					Hos {site_name} kan du investera i <A href='/explore-sto'>svenska</A>, <A href='/explore-us'>amerikanska
					</A>, <A href='explore-xetra'>tyska</A>, <A href='explore-cph'>danska</A>, <A href='/explore-hel'>
					finska</A> aktier, med mera.
				</O>
			);
		},
		"de": (A, site_name) => {
			return (
				<O>
					Bei {site_name} kannst du in <A href='explore-xetra'>deutsche</A>, <A href='/explore-us'>
					amerikanische</A>, <A href='/explore-cph'>dänische</A>, <A href='/explore-sto'>
					schwedische</A>, <A href='/explore-hel'>finnische</A> Aktien und mehr investieren.
				</O>
			);
		},
	},

	"96cfb7896602": {
		"en": () => <O>Find more assets</O>,
		"da": () => <O>Find flere værdipapirer</O>,
		"sv": () => <O>Hitta fler tillgångar</O>,
		"de": () => <O>Weitere Aktien finden</O>,
	},

	"c60f80cb7aa3": {
		"en": () => <O>Prize games. 100% free to participate!</O>,
		"da": () => <O>Præmiespil. 100% gratis at deltage!</O>,
		"sv": () => <O>Prisspel. 100% gratis att delta!</O>,
		"de": () => <O>Börsen Battles - kostenlos teilnehmen und Preise gewinnen!</O>,
	},

	"ec74e7cbb2d5": {
		"en": () => <O>Simply click one of the games below to join. You can join as many games as you want.</O>,
		"da": () => <O>Bare klik på spillene nedenfor. Du kan tilmelde dig så mange spil du har lyst til.</O>,
		"sv": () => <O>Klicka bara på ett spel nedanför för att gå med. Du kan gå med i så många spel som du vill.</O>,
		"de": () => <O>Hast du Lust, auch mal gegen Andere zu spielen? Melde dich für unsere Community-Gewinnspiele an und erspiele coole Preise.</O>,
	},

	"ad4d58bc3d5d": {
		"en": (site_name) => <O>See what other {site_name} players have to say</O>,
		"da": (site_name) => <O>Se hvad andre {site_name} brugere siger</O>,
		"sv": (site_name) => <O>Se vad andra {site_name} användare har att säga</O>,
		"de": (site_name) => <O>Das sagen andere {site_name}er</O>,
	},

	"ff6662f3f0d1": {
		"en": () => <O>One investment game,<br />many possibilities</O>,
		"da": () => <O>Ét investeringsspil,<br />mange muligheder</O>,
		"sv": () => <O>Ett investeringsspel,<br />många möjligheter</O>,
		"de": () => <O>Ein Börsenspiel,<br />viele Möglichkeiten</O>,
	},

	"aef8ff2bf18d": {
		"en": (site_name) => {
			return (
				<O>
				Would you like to play with your friends and compete for the title of
				best investor? Do you want to understand finances in a playful way at
				school? Are you looking for a user-friendly platform to track your stock
				portfolios and learn to trade while having fun? Or would you like to
				compete against our community in our games and win cool prizes?
				Then {site_name} is for you.
				</O>
			);
		},
		"da": (site_name) => {
			return (
				<O>
				Vil du spille med dine venner og konkurrere om titlen som den bedste investor? Ønsker du
				at forstå investering på en legende måde i skolen? Søger du en brugervenlig platform til
				at holde øje med dine portefølger? Vil du lære at investere mens du har det sjovt? Eller
				vil du konkurrere imod andre brugere og vinde fede præmier? Så er {site_name} lige noget
				for dig.
				</O>
			);
		},
		"sv": (site_name) => {
			return (
				<O>
				Vill du spela med dina vänner och tävla om titeln som bästa investeraren? Vill du förstå
				ekonomin på ett lekfullt sätt i skolan? Letar du efter en användarvänlig plattform för
				att spåra dina aktieportföljer och lära sig att handla samtidigt som du har kul? Eller
				vill du tävla mot vår community i våra prisspel och vinna häftiga priser?
				Då är {site_name} för dig.
				</O>
			);
		},
		"de": (site_name) => {
			return (
				<O>
				Hast du Lust gegen deine Freunde zu spielen und den besten Investor zu küren? Möchtest
				du in deiner Klasse das Thema Finanzen anschaulich erleben? Suchst du eine
				nutzerfreundliche Plattform, um deine Aktienportfolios zu verfolgen und spielerisch
				handeln zu lernen? Oder möchtest du in unseren Börsen Battles gegen unsere Community
				antreten und coole Preise gewinnen? Dann bist du bei {site_name} genau richtig.
				</O>
			);
		},
	},

	"92bc37c4d6b5": {
		"en": () => <O>Start your first game</O>,
		"da": () => <O>Start dit første aktiespil</O>,
		"sv": () => <O>Starta ditt första spel</O>,
		"de": () => <O>Erstes Spiel starten</O>,
	},

	"66f5bd382d0d": {
		"en": () => <O>My shortlist</O>,
		"da": () => <O>Min shortlist</O>,
		"sv": () => <O>Min shortlist</O>,
		"de": () => <O>Meine Shortlist</O>,
	},

	"1c5df2c1a412": {
		"en": () => <O>My portfolios</O>,
		"da": () => <O>Mine porteføljer</O>,
		"sv": () => <O>Mina portföljer</O>,
		"de": () => <O>Meine Portfolios</O>,
	},

	"cd81337a6f15": {
		"en": () => <O>Who's the best investor?</O>,
		"da": () => <O>Hvem er den bedste investor?</O>,
		"sv": () => <O>Vem är den bästa investeraren?</O>,
		"de": () => <O>Wer ist der beste Investor?</O>,
	},

	"35feef7cac98": {
		"en": () => {
			return (
				<O>Create a free investment game, invite your friends and compete for the title
				as the best investor.</O>
			);
		},
		"da": () => {
			return (
				<O>Opret et gratis aktiespil, invitér dine venner og kæmp om titlen som den
				bedste investor.</O>
			);
		},
		"sv": () => {
                        return (
                                <O>Skapa ett gratis investeringsspel, bjud in dina vänner och tävla om titeln
				som den bästa investeraren.</O>
                        );
		},
		"de": () => {
                        return (
				<O>Erstelle ein kostenloses Börsenspiel, lade deine Freunde ein und spiele um den Titel des besten Investors</O>
			);
                },
	},

	"3ffc90b3a37c": {
		"en": (Butt, start_new_game) => <O>The game has ended, but you can <Butt className="ad_btn_sm mx-1" onClick={start_new_game}><b>start your own game</b></Butt> and invite your friends.</O>,
		"da": (Butt, start_new_game) => <O>Spillet er desværre slut, men du kan <Butt className="ad_btn_sm mx-1" onClick={start_new_game}><b>starte dit eget spil</b></Butt> og invitere dine venner.</O>,
		"sv": (Butt, start_new_game) => <O>Spelet är slut, men du kan <Butt className="ad_btn_sm mx-1" onClick={start_new_game}><b>starta ditt eget spel</b></Butt> och bjuda in dina vänner.</O>,
		"de": (Butt, start_new_game) => <O>Das Spiel ist beendet, aber du kannst <Butt className="ad_btn_sm mx-1" onClick={start_new_game}><b>dein eigenes Spiel erstellen</b></Butt> und deine Freunde einladen.</O>,
	},

	"4a6e9623c62f": {
		"en": () => <O>The game has already ended!</O>,
		"da": () => <O>Spillet er slut!</O>,
		"sv": () => <O>Spelet är slut!</O>,
		"de": () => <O>Das Spiel ist bereits beendet!</O>,
	},

	"bd214adc9359": {
		"en": () => <O>Join game!</O>,
		"da": () => <O>Deltag i spillet!</O>,
		"sv": () => <O>Gå med i spelet!</O>,
		"de": () => <O>Spiel beitreten!</O>,
	},

	"babc45f43d3f": {
		"en": () => <O>Enter your e-mail to get info about the game. Your email will <b>not</b> be used for unwanted marketing.</O>,
		"da": () => <O>Indtast din e-mail for at få løbende info om spillet. Den vil <b>ikke</b> blive brugt til uønsket marketing.</O>,
		"sv": () => <O>Ange din e-mail för att få information om spelet. Din e-mail kommer <b>inte</b> användas för oönskad marknadsföring.</O>,
		"de": () => <O>Gib deine E-Mail-Adresse ein, um Informationen zu diesem Spiel zu erhalten. Deine E-Mail-Adresse wird <b>nicht</b> für unerwünschte Marketingzwecke verwendet.</O>,
	},

	"86514a8b7215": {
		"en": () => <O>Enter your e-mail</O>,
		"da": () => <O>Indtast din e-mail</O>,
		"sv": () => <O>Ange din e-mail</O>,
		"de": () => <O>Gib deine E-Mail-Adresse ein</O>,
	},

	"a6b78f2139a7": {
		"en": () => <O>Sorry, the time for invitations has expired</O>,
		"da": () => <O>Beklager, tiden for invitationer er udløbet</O>,
		"sv": () => <O>Tyvärr, tiden för inbjudningar har gått ut</O>,
		"de": () => <O>Entschuldigung, die Einladungsfrist ist abgelaufen</O>,
	},

	"a8241f8e4d2c": {
		"en": (game) => <O>There are <b>{game.RemainingInvites}</b> remaining invites</O>,
		"da": (game) => <O>Der er <b>{game.RemainingInvites}</b> invitationer tilbage</O>,
		"sv": (game) => <O>Det finns <b>{game.RemainingInvites}</b> inbjudningar kvar</O>,
		"de": (game) => <O>Es gibt noch <b>{game.RemainingInvites}</b> Einladungen</O>,
	},

	"af3ca5406980": {
		"en": () => <O>Sorry, the game is full</O>,
		"da": () => <O>Beklager, spillet er fuldt</O>,
		"sv": () => <O>Tyvärr, spelet är fullt</O>,
		"de": () => <O>Entschuldigung, das Spiel ist voll</O>,
	},

	"bb51920e925a": {
		"_context": "invite_game_page",
		"en": () => <O>Game time</O>,
		"da": () => <O>Spilletid</O>,
		"sv": () => <O>Speltid</O>,
		"de": () => <O>Spielzeit</O>,
	},

	"e20606c0c86a": {
		"en": () => <O>Enter your e-mail. This is required to win a prize. Your email will <b>not</b> be used for unwanted marketing.</O>,
		"da": () => <O>Indtast din e-mail. Dette er krævet for at vinde en præmie. Den vil <b>ikke</b> blive brugt til uønsket marketing.</O>,
		"sv": () => <O>Ange din e-mail. Detta är nödvändigt för att kunna vinna ett pris. Din e-mail kommer <b>inte</b> användas för oönskad marknadsföring.</O>,
		"de": () => <O>Gib deine E-Mail-Adresse ein. Diese ist erforderlich, um den Preis gewinnen zu können. Deine E-Mail-Adresse wird <b>nicht</b> für unerwünschtes Marketing verwendet.</O>,
	},
}



// WEBPACK FOOTER //
// ./src/traks-translations.js